import * as z from 'zod'
import type { API, ChargeRequest } from "./api"
import { formBase } from './shared/form.machine'
type Form = typeof formBase

// Accepts booleans as-is, and coerces common string representations
// ("true"/"false", "1"/"0", "on"/"off", "yes"/"no") into booleans so
// form payloads that serialize checkbox values as strings still validate.
const zBool = () => z.preprocess((v) => {
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase()
    if (['true', '1', 'on', 'yes'].includes(s)) return true
    if (['false', '0', 'off', 'no', ''].includes(s)) return false
  }
  return v
}, z.boolean())

export type Currency = 'USD' | 'CAD' | 'ILS'

export type RecaptchaSettings = {
  enabled: boolean
  siteKey?: string
  action?: string
  minScore?: number
}

type Application = {
  department: string,
  campaign: string,
  subCampaign?: string,
  percentage: number
}

export type FormSettings = {
  thankYouTemplate?: string,
  emailTemplate?: string,
  honoreeTemplate?: string,
  template?: string,
  department?: string,
  campaign?: string,
  subCampaign?: string,
  applications?: Array<Application>
  amounts?: Partial<{ [key in Currency]: number[] }> | number[] | [number, string][] | [Partial<{ [key in Currency]: number }>, string][],
  amountsAlt?: Partial<{ [key in Currency]: number[] }> | number[] | [number, string][] | [Partial<{ [key in Currency]: number }>, string][],
  message?: string,
  removeCVV?: boolean,
  adminEmails: string[] | ((request: ChargeRequest) => string[]),
  adminFromEmail: string,
  adminSubject?: string,
  userFromEmail: string,
  userSubject?: string,
  gaID?: string,
  defaultCurrency?: Currency
  title: string,
  silverPopDatabaseId?: number,
  silverPopContactLists?: number[] | ((request: API['chargeCard']['request']) => number[]),
  customValidations?: (form: Form) => z.ZodTypeAny,
  callback?: string
  recaptcha?: RecaptchaSettings
}


const jliccollegeBasic = {
  emailTemplate: 'jlic',
  title: 'JLIC Campus Donation form',
  department: 'JLIC',
  campaign: 'General Donation',
  subCampaign: '',
  message: 'Thank you for donating to JLIC',
  amounts: [
    1000,
    500,
    360,
    180,
    100,
    90,
    72,
    36,
    18,
    5
  ],
  adminEmails: ['rosenthalp@ou.org'],
  adminFromEmail: 'OU-JLIC <jlic@ou.org>',
  adminSubject: 'OU-JLIC Donation notification',
  userFromEmail: 'jlic@ou.org',
  userSubject: 'Thank you for your support',
  gaID: process.env.NODE_ENV == 'production' ? 'UA-18329904-1' : 'UA-25738407-2'
}

const yachadBasic = {
  template: 'yachad-general',
  title: 'Yachad Donation Form',
  honoreeTemplate: 'yachad',
  emailTemplate: 'yachad',
  department: 'Yachad',
  campaign: 'General Donation',
  subCampaign: '',
  thankYouTemplate: 'yachad',
  message: 'Thank you so much for your generous donation!',
  adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org'],
  adminFromEmail: 'YACHAD <yachad@ou.org>',
  adminSubject: 'YACHAD Donation',
  userFromEmail: 'YACHAD <yachad@ou.org>',
  userSubject: 'Thank you for your support',
  gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2',
  silverPopContactLists: [1876556,1850355]
}
const ouisraelBasic = {
  emailTemplate: 'ouisrael',
  department: 'OUIsrael',
  message: 'Thank you for donating to OUIsrael',
  adminEmails: (request:any) => {
    const to ='ABerman@ouisrael.org,batyaw@ouisrael.org,JoshC@ouisrael.org,joshc@ouisrael.org,jordanr@ouisrael.org,lizb@ouisrael.org'.split(',')
    if (request.currency! == 'CAD') {
       to.push('Roz.English@ou.org')
    }
    return to
  },
  adminFromEmail: 'OUIsrael <ABerman@ouisrael.org>',
  adminSubject: 'OUIsrael donation notification',
  userFromEmail: 'OUIsrael <ABerman@ouisrael.org>',
  userSubject: 'Thank you for supporting OU Israel',
  gaID: process.env.NODE_ENV == 'production' ? 'UA-18329904-1' : 'UA-25738407-2',
  defaultCurrency: 'ILS'
} as const

const ncsyChileBasic = {
  emailTemplate: 'ncsy-chile',
  honoreeTemplate: 'ncsy',
  title: 'NCSY Chile Donation Form',
  department: 'NCSY_Chile',
  campaign: 'General Donation',
  subCampaign: '',
  message: 'Thank you so much for helping NCSY continue to inspire the Jewish future in Latin America. We look forward to sharing only joy from this beautiful project',
  adminEmails: ['funding@ncsychile.com', 'goldmani@ou.org', 'mbengio@ncsychile.com', 'sigal@ncsychile.com', 'dbengio@ncsychile.com'],
  adminFromEmail: 'NCSY Chile <funding@ncsychile.com>',
  adminSubject: 'A payment has been made to NCSY Chile - Donation',
  userFromEmail: 'NCSY Chile <funding@ncsychile.com>',
  userSubject: 'Thank you for your support!',
  gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-5' : 'UA-3009720-5',
}
const jlicCampusBasics = {
  emailTemplate: 'jlic',
  honoreeTemplate: 'jlic',
  department: 'JLIC',
  campaign: 'General Donation',
  subCampaign: '',
  title: 'JLIC Donation form',
  message: "Thank you for your contribution to JLIC",
  amounts: [
    10000,
    7500,
    5000,
    3600,
    1800,
    1000,
    500,
    360
  ],
  adminEmails: ['steinreiche@ou.org', 'rosenthalp@ou.org', 'zemas@ou.org', 'rossj@ou.org', 'goldmani@ou.org','zemas@ou.org'],
  adminFromEmail: 'JLIC jlic@ou.org',
  userFromEmail: 'JLIC jlic@ou.org',
  adminSubject: 'You have received a new donation from JLIC ',
  userSubject: 'Thank you from JLIC',
  gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2',
}

const ncsyParnesBasic = {
  emailTemplate: 'ncsy-parnes-camps',
  honoreeTemplate: 'ncsy',
  title: 'NCSY Camp Sport Donation Form',
  department: 'NCSY_Alumni',
  campaign: 'Camp Sports',
  subCampaign: '',
  message: 'Thank you for your support of NCSY Summer programs. May the learning, chessed and growth that you support serve as a merit for you, your loved ones and all of Klal Yisrael.',
  adminEmails: ['wielgusa@ncsy.org', 'goldmani@ou.org', 'adira.glicksman@ncsy.org', 'Beth.Nussbaum@ncsy.org'],
  adminFromEmail: 'NCSY Camp Sports <info@ncsy.org>',
  adminSubject: 'A payment has been made to NCSY Camp Sports - Donation',
  userFromEmail: 'NCSY Camp Sports <info@ncsy.org>',
  userSubject: 'Thank you for your support!',
  gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-5' : 'UA-3009720-5',
}

const ougivingBasic = {
  emailTemplate: 'ougiving',
  title: 'OU Giving',
  department: 'OU',
  campaign: 'General Donation',
  subCampaign: '',
  message: 'Thank you for your donation',
  amounts: [
    18,
    36,
    54,
    100,
    250
  ],
  adminEmails: (request:any) => {
    const to = 'ouinternet@ou.org,rebecca.diamond@ou.org'.split(',')
    const mapping = {
      OU: 'goldmani@ou.org',
      CommunalResearch: 'shainm@ou.org,bebermanr@ou.org',
      CommunityProjectsAndPartnerships: 'staylor@ou.org',
      IsraelFreeSpirit: 'tamariy@ou.org',
      NCSY: 'goldmani@ou.org,lernerj@ncsy.org',
      NCSY_Alumni: 'marchuck@ncsy.org,lernerj@ncsy.org',
      Advocacy: 'tarer@ou.org,bojanl@ou.org',
      ReliefFunds: 'staylor@ou.org',
      ImpactAccelerator: 'rochlina@ou.org,bebermanr@ou.org,frydmant@ou.org',
      OUIsrael: 'bareld@ou.org,joshc@ouisrael.org,jordanr@ouisrael.org,lizb@ouisrael.org',
      JLIC: 'rosenthalp@ou.org',
      OUPress: 'breed@ou.org',
      OU_Torah: 'schwedm@ou.org,hirschr@ou.org',
      SynagogueInitiatives: 'posya@ou.org,naftalih@ou.org',
      TeachCoalition: 'benshabats@teachcoalition.org,Caleb.Fischer@teachcoalition.org',
      WomensInitative: 'ashmidman@ou.org,epsteinn@ou.org',
      Yachad: 'saibelk@ou.org',
    } as any
    const email = mapping[request.applications![0].department!]
    if (email) to.push(email)

    if (request.custom?.planningSpecialist) { to.push('legacygiving@ou.org') }

    return to
  },
  adminFromEmail: 'OU Donation <info@ou.org>',
  adminSubject: 'Orthodox Union - Donation',
  userFromEmail: 'Orthodox Union <info@ou.org>',
  userSubject: 'Thank you for Supporting the OU',
  gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
  silverPopContactLists: [1850355],
}

const outorahGiveBasic = {
  emailTemplate: 'outorah-give',
  template: 'outorah-give',
  title: 'Support the Torah You Learn Every Day',
  adminEmails: (request: any) => {
    const to = ['hannahf@ou.org', 'JackA@ou.org', 'Gil.Student@ou.org']
    if (request.custom?.planningSpecialist) to.push('legacygiving@ou.org')
    return to
  },
  adminFromEmail: 'OU Torah <torah@ou.org>',
  message: 'Thank you so much for your recent contribution',
  thankYouTemplate: 'outorah-give',
  adminSubject: 'Support the Torah You Learn Every Day - OU Torah Donation',
  userFromEmail: 'OU Torah <torah@ou.org>',
  userSubject: 'Thank you for supporting OU Torah',
  department: 'OU_Torah',
  campaign: 'Challenge Grant',
  subCampaign: '',
  gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-29' : '',
  silverPopContactLists: [1850355],
  customValidations (form: any) {
    return form.extend({
      custom: z.object({
        seriesType: z.string().nullable().optional(),
        programId: z.string().nullable().optional(),
        dedicationMessage: z.string().nullable().optional(),
        comments: z.string().nullable().optional(),
        planningSpecialist: zBool().nullable().optional(),
      })
    })
  }
}

const rsgAdm = {
  emailTemplate: 'adm',
  subCampaign: 'Telemarketing',
  removeCVV: true,
  adminEmails: ['atblock@gmail.com'],
  adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
  userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
  userSubject: 'Thank you for your support - Orthodox Union',
}
const yachad: FormSettings = {
  template: 'yachad',
  thankYouTemplate: 'yachad',
  emailTemplate: 'yachad',
  title: 'Yachad Donation Form',
  department: 'Yachad',
  campaign: 'General Donation',
  subCampaign: '',
  message: 'Thank you! Your donation was successful.',
  amounts: [
    [18000, `Give A Child A School Year Of Special Education At Yachad's IVDU School`],
    [10000, `Sponsor Yachad’s International Virtual Dueling Musicians Event`],
    [5000, `Sponsor A Month Of Virtual Activities Through Yachad On Demand`],
    [2500, `Sponsor A Young Adult For A Month Of Remote Vocational Training`],
    [800, `Sponsor A Zoom Social Skills Training Program For Participants Across The Country`],
    [500, `Sponsor A Participant For A Week Of Virtual Social Skills Training`],
    [360, `Sponsor A Specialist For Zoom Social Recreational Program`],
    [180, `Dedicate A Virtual Program In Honor Or In Memory Of A Loved One`],
    [100, `Sponsor One Day Of Virtual Programming For A Yachad Member`],
    [72, `Dedicate Four Chapters Of Learning For Yachad’s Shavuot Program`],
    [50, `Sponsor A Special Shabbat Package For A Yachad Member`],
    [36, `Sponsor A Family To Attend Yachad’s International Musical Event`],
  ],
  adminEmails: (request) => {
    const to = 'saibelk@ou.org,herrmann@ou.org,njcd@ou.org'.split(',')

    const regions = [
      { 'site': 'baltimore', 'email': ['penina.mandelbaum@ou.org', 'ruth.baars@ou.org'], 'state': 'MD', 'country': 'US' },
      { 'site': 'chicago', 'email': ['harrisd@ou.org', 'shlomo.crandall@ou.org', 'ruth.baars@ou.org'], 'state': 'IL', 'country': 'US' },
      { 'site': 'cleveland', 'email': ['coopermans@ou.org', 'ruth.baars@ou.org'], 'state': 'OH', 'country': 'US' },
      { 'site': 'dallas', 'email': [], 'state': 'TX', 'country': 'US' },
      { 'site': 'detroit', 'email': [], 'state': 'MI', 'country': 'US' },
      { 'site': 'los-angeles', 'email': ['miryum.schoen@ou.org', 'ruth.baars@ou.org'], 'state': 'CA', 'country': 'US' },
      { 'site': 'new-england', 'email': ['butlerl@ou.org', 'tolubj@ou.org', 'ruth.baars@ou.org'], 'state': 'MA', 'country': 'US' },
      { 'site': 'new-jersey', 'email': ['selevanr@ou.org', 'mayerr@ou.org'], 'state': 'NJ', 'country': 'US' },
      { 'site': 'new-york', 'email': ['shatzr@ou.org', 'zarj@ou.org', 'mayerr@ou.org'], 'state': 'NY', 'country': 'US' },
      { 'site': 'south-florida', 'email': ['tzippy.richmond@ou.org', 'mayerr@ou.org'], 'state': 'FL', 'country': 'US' },
      { 'site': 'philadelphia', 'email': [], 'state': 'PA', 'country': 'US' },
      { 'site': 'toronto', 'email': ['marmerd@ou.org', 'ruth.baars@ou.org'], 'state': '', 'country': 'CA' }
    ]

    let states = regions.filter(x => x.country == request.card.address.country)
    let state
    if (states.length) {
      if (states.length > 1) {
        state = states.find(x => x.state == request.card.address.state)
      } else
        state = states[0]

      if (state) {
        to.push(...state.email)
      }
    }

    let region = regions.find(x => x.site == request.form!.replace('yachad-', ''))

    if (region && (region != state))
      to.push(...region.email)

    return to
  },
  adminFromEmail: 'YACHAD <yachad@ou.org>',
  adminSubject: 'YACHAD Donation',
  userFromEmail: 'YACHAD <yachad@ou.org>',
  userSubject: 'Thank you for your support',
  gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2',
  silverPopContactLists: []
}
const womensSiyumBase: FormSettings = {
  callback: 'https://webhook.site/e69bc76e-8ab6-41ba-8fcf-c9a143c12d85',
  title: 'OUWomen Siyum Donation',
  message: "Thank you for your support of Torat Imecha Nach Yomi. If you will be attending the event, you will receive an email with event details by February 10th.  If you do not receive an email, please check your spam folder.  If it is not there, please reach out to us at ouwomen@ou.org.",
  adminEmails: ['ouwiforms@ou.org', 'epsteinn@ou.org'],
  adminFromEmail: 'OU Women <ouwomen@ou.org>',
  adminSubject: 'OU Women Siyum Donation',
  userFromEmail: 'OU Women <ouwomen@ou.org>',
  amounts: [
    [{ USD: 10000, ILS: 37000 }, `Dedicate one month, 1 reserved table (10 women)`],
    [{ USD: 5000, ILS: 18530 }, `Dedicate one week, 1 reserved table (10 women)`],
    [{ USD: 3600, ILS: 13350 }, `Dedicate 6 perakim, 2 Siyum reservations`],
    [{ USD: 2500, ILS: 9135 }, `Dedicate 5 perakim, 2 Siyum reservations`],
    [{ USD: 1800, ILS: 6670 }, `Dedicate 4 perakim, 2 Siyum reservations`],
    [{ USD: 1000, ILS: 3705 }, `Dedicate 3 perakim, 2 Siyum reservations`],
    [{ USD: 500, ILS: 1850 }, `Dedicate 2 perakim, 1 Siyum reservation`],
    [{ USD: 360, ILS: 1335 }, `Dedicate 1 perek, 1 Siyum reservation`],
    [{ USD: 180, ILS: 665 }, `1 Siyum reservation`],
    [{ USD: 54, ILS: 200 }, `1 Student Siyum reservation`],
  ],
  campaign: 'Torat Imecha Dinner',
  department: 'WomensInitative',
  silverPopContactLists: [8957749],
  gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
  customValidations (form) {
    return form.extend({
      custom: z.object({
        attending: z.string().nullable().optional(),
        listingName: z.string().nullable().optional(),
        mediaRelease: zBool().refine((v) => v === true, { message: "Required" }),
        newAutoAttend: z.string().nullable(),
        guestsName1: z.string().nullable().optional(),
        guestsEmail1: z.string().nullable().optional(),
        guestsName2: z.string().nullable().optional(),
        guestsEmail2: z.string().nullable().optional(),
        guestsName3: z.string().nullable().optional(),
        guestsEmail3: z.string().nullable().optional(),
        guestsName4: z.string().nullable().optional(),
        guestsEmail4: z.string().nullable().optional(),
        guestsName5: z.string().nullable().optional(),
        guestsEmail5: z.string().nullable().optional(),
        guestsName6: z.string().nullable().optional(),
        guestsEmail6: z.string().nullable().optional(),
        guestsName7: z.string().nullable().optional(),
        guestsEmail7: z.string().nullable().optional(),
        guestsName8: z.string().nullable().optional(),
        guestsEmail8: z.string().nullable().optional(),
        guestsName9: z.string().nullable().optional(),
        guestsEmail9: z.string().nullable().optional(),
        guestsName10: z.string().nullable().optional(),
        guestsEmail10: z.string().nullable().optional(),
      })
    })
  }
}
export const formSettings: { [k: string]: FormSettings } = {
  sensepass: {
    adminEmails: [],
    adminFromEmail: '',
    userFromEmail: '',
    title: '',
    template: 'sensepass',
    message: 'Thank you for your donation.',
    department: 'OU',
    campaign: 'ADM Israel Center Campaign',
    applications: [
      {
        department: 'NCSY',
        campaign: 'Youth Programs',
        subCampaign: 'Summer Camps',
        percentage: 60
      },
      {
        department: 'JLIC',
        campaign: 'Campus Outreach',
        subCampaign: 'College Programs',
        percentage: 25
      },
      {
        department: 'OU_Torah',
        campaign: 'Educational Content',
        percentage: 15
      }
    ]
  },
  cardknox: {
    adminEmails: [],
    adminFromEmail: '',
    userFromEmail: '',
    title: '',
    template: 'adm'
  },
  rsg: {
    title: 'RSG Donation form',
    department: 'Yachad',
    adminSubject: 'Orthodox Union SMG Payment',
    ...rsgAdm
  },
  adm: {
    title: 'ADM Donation form',
    adminSubject: 'Orthodox Union ADM Payment',
    ...rsgAdm
  },
  'adm-yachad': {
    title: 'ADM Yachad Donation form',
    adminSubject: 'Orthodox Union ADM Yachad Payment',
    ...rsgAdm
  },
  mds: {
    title: 'MDS Donation form',
    adminSubject: 'Orthodox Union MDS Payment',
    ...rsgAdm,
    department: 'OU',
    campaign: 'Membership Dues Campaign',
    subCampaign: 'MDSDU2410T'
  },
  signia: {
    title: 'Signia Donation form',
    adminSubject: 'Orthodox Union Signia Payment',
    ...rsgAdm,
    department: 'OU',
    campaign: 'ADM Membership Dues Campaign',
    subCampaign: 'Telemarketing - Spring'
  },
  pledge: {
    emailTemplate: 'israelcenter21',
    title: 'Israel Center Donation Form',
    department: 'OUIsrael',
    campaign: 'ADM Israel Center Campaign',
    subCampaign: 'Telemarketing',
    message: 'Thank you for your donation.',
    adminEmails: ['atblock@gmail.com', 'lizb@ouisrael.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'OU Israel Center donation',
    userFromEmail: 'OU Israel Center <ABerman@ouisrael.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-18329904-1' : 'UA-25738407-2'
  },
  'israelcenter21': {
    emailTemplate: 'israelcenter21',
    title: 'Israel Center Donation Form',
    department: 'OUIsrael',
    campaign: 'ADM Israel Center Campaign',
    subCampaign: 'Telemarketing',
    message: 'Thank you for your donation.',
    adminEmails: [ 'ABerman@ouisrael.org','JoshC@ouisrael.org', 'joshc@ouisrael.org', 'lizb@ouisrael.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'OU Israel Center donation',
    userFromEmail: 'OU Israel Center <ABerman@ouisrael.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-18329904-1' : 'UA-25738407-2'
  },
  'livingsmarterjewish': {
    emailTemplate: 'livingsmarterjewish',
    title: 'LivingSmarterJewish Donation Form',
    department: 'CommunityProjectsAndPartnerships',
    campaign: 'Living Smarter Jewish',
    message: 'Thank you for your donation.',
    adminEmails: [ 'info@livingsmarterjewish.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'LivingSmarterJewish.org donation',
    userFromEmail: 'LivingSmarterJewish.org <info@livingsmarterjewish.org>',
    userSubject: 'Thank you for your support',
    amounts: [
      18,
      25,
      50,
      100,
      180
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-18329904-1' : 'UA-25738407-2'
  },
  'outorah-rosner': {
    emailTemplate: 'outorah-rosner',
    title: 'Lzecher Nishmaso Saranne Rosner Donation Form',
    department: 'OU_Torah',
    campaign: 'Lzecher Nishmaso',
    subCampaign: 'Saranne Rosner',
    message: 'Thank you for your donation.',
    adminEmails: [ 'goldmani@ou.org', 'Jacka@ou.org', 'Schwedm@ou.org','hirschr@ou.org'],
    adminFromEmail: 'OU Torah <info@ou.org>',
    adminSubject: 'Lzecher Nishmaso Saranne Rosner Donation',
    userFromEmail: 'OU Torah <info@ou.org>',
    userSubject: 'Thank you for your support',
    amounts: [
      18,
      25,
      50,
      100,
      180
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-29' : 'UA-32047-29'
  },

  'jlic-valleyhub': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'JLIC YAVNEH VALLEY HUB Donation form',
    department: 'JLIC_CSUN',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at CSUN!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','kashanirokhd@ou.org','kashanirokhs@ou.org','zemas@ou.org'],
    adminSubject: 'OU-JLIC YAVNEH VALLEY HUB Donation notification',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          contactMe: zBool().nullable().optional(),
        })
      })
    },
  },
  'jlic-brooklyn': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'JLIC Brooklyn College Donation form',
    department: 'JLIC_BrooklynCollege',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at Brooklyn College!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','albertd@ou.org','albertm@ou.org','zemas@ou.org','boshnacks@ou.org'],
    adminSubject: 'OU-JLIC Brooklyn College Donation notification',
    customValidations (form) {
    return form.extend({
      custom: z.object({
        contactMe: zBool().nullable().optional(),
      })
    })
  },
  },
  'jlic-binghamton': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'JLIC at Binghamton University Donation form',
    department: 'JLIC_Binghamton',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at Binghamton University!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      [10000, ``],
      [5000, `Sponsor a month of Shabbatot`],
      [7500, ``],
      [3600, `Sponsor a month of programming`],
      [1800, `Sponsor a weekly Torah class`],
      [1000, `Sponsor a month of Kiddushes`],
      [500, `Sponsor one Seudah Shlishit`],
      [250, `Sponsor one Kiddush`],
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','anstandigs@ou.org','anstandigj@ou.org', 'zemas@ou.org'],
    adminSubject: 'OU-JLIC at Binghamton University Donation notification',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          contactMe: zBool().nullable().optional(),
        })
      })
    },
  },
    'jlic-general-chanukah': {
    ...jliccollegeBasic,
    emailTemplate: 'jlic-general-chanukah',
    template: 'jlic-general-chanuka',
    title: 'One Flame, Many Communities Donation form',
    department: 'JLIC',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC</p>',
    amounts: [
      [{ USD: 25000, ILS: 81300 }, `International Master Sponsor`],
      [{ USD: 18000, ILS: 58500 }, `US or Israel Master Sponsor`],
      [{ USD: 10000, ILS: 32500 }, `Sponsor all the Latkes`],
      [{ USD: 5000,  ILS: 16300 }, `Sponsor an entire Campus Chanukah Event of your choice`],
      [{ USD: 2500,  ILS: 8200 },  `Menorah Lightings Sponsor for one campus`],
      [{ USD: 1000,  ILS: 3300 },  `Food Sponsor for one event at one campus`],
      [{ USD: 500,   ILS: 1650 },  `Sufganiyot Sponsor for one campus`],
    ],
    adminEmails: ['goldmani@ou.org', 'rosenthalp@ou.org','steinreiche@ou.org', 'katzj@ou.org'],
    adminSubject: 'One Flame, Many Communities Donation notification',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          contactMe: zBool().nullable().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    },
  },
  'jlic-barilan-torah': {
    emailTemplate: 'jlic-barilan-torah',
    department: 'JLIC_BarIlan',
    campaign: 'Spring 2024',
    subCampaign: '',
    title: 'Bar Ilans Torah Campaign Donation form',
    message: "<p>Thank you for your contribution towards the Torah in the Givat Shmuel/Bar Ilan University community.</p> <p> Your generosity truly touches our hearts and makes a significant difference here in Israel.</p> <p>It's because of thoughtful individuals like you that we can continue our mission and make a positive change in our community.</p>",
    amounts: [
      [10000, `Full Torah`],
      [3600, `Each Sefer`],
      [1800, `Parsha`],
      [720, `Perek`],
      [180, `Pasuk`],
    ],
    adminFromEmail: 'jlic@ou.org',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC!',
    adminEmails: ['goldmani@ou.org',  'jlic@ou.org', 'steinreiche@ou.org', 'ttw613@gmail.com', 'jsjonathanshulman@gmail.com', 'tzviw08@gmail.com', 'zemas@ou.org'],
    adminSubject: 'Bar Ilans Torah Campaign Donation notification',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-18329904-1' : 'UA-25738407-2'
  },
  'jlic-brandeis': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'JLIC at Brandeis University Donation form',
    department: 'JLIC_Brandeis',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at Brandeis!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      [5400, `Sponsor a semester of Torah learning`],
      [3600, `Sponsor a month of OU-JLIC programming`],
      [1800, `Sponsor a week of OU-JLIC programming`],
      [750, `Sponsor a home-cooked shabbat or chag meal`],
      [540, `Sponsor a community wide Oneg Shabbat`],
      [360, `Sponsor an evening of learning with a home-cooked dinner`],
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','zemas@ou.org','Jennifer.Finkelstein@ou.org','Joel.Finkelstein@ou.org'],
    adminSubject: 'OU-JLIC at Brandeis University Donation notification',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          contactMe: zBool().nullable().optional(),
        })
      })
    },
  },
  'jlic-columbia': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'OU-JLIC at Columbia University Donation form',
    department: 'JLIC_Columbia',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at Columbia University/Barnard College!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','zemas@ou.org', 'Jasmine.Peled@ou.org', 'Daniel.Schwartz@ou.org'],
    adminSubject: 'OU-JLIC at Columbia University Donation notification',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          contactMe: zBool().nullable().optional(),
        })
      })
    },
  },
  'jlic-columbia-campaign': {
    emailTemplate: 'jlic-columbia-campaign',
    title: 'Support Orthodox Unions Jewish Learning Initiative on Campus at Columbia-Barnard.',
    template: 'jlic-columbia-campaign',
    honoreeTemplate: 'jlic',
    department: 'JLIC_Columbia',
    campaign: 'spring 2024',
    subCampaign: '',
    message: '<p>Thank you for your generous donation to OU-JLIC at Columbia University/Barnard College.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','zemas@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'OU-JLIC at Columbia University/Barnard College Donation notification',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'OU-JLIC at Columbia University/Barnard College',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          anonymous: zBool().nullable().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    },
  },
  'jlic-cornell': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'OU-JLIC at Cornell University Donation form',
    department: 'JLIC_Cornell',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at Cornell University!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','zemas@ou.org', 'Yakira.Baumol@ou.org', 'AJ.Baumol@ou.org'],
    adminSubject: 'OU-JLIC at Cornell University Donation notification',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          contactMe: zBool().nullable().optional(),
        })
      })
    },
  },
    'jlic-ufl': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'OU-JLIC at University of Florida Donation form',
    department: 'JLIC_University_of_Florida',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at University of Florida!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','zemas@ou.org', 'laura.vinger@ou.org', 'shimon.vinger@ou.org'],
    adminSubject: 'OU-JLIC at University of Florida Donation notification',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          contactMe: zBool().nullable().optional(),
        })
      })
    },
  },
      'jlic-studencenter': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'OU-JLIC at JLIC Jerusalem student center Donation form',
    department: 'JLIC_Jerusalem_Student_Center',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at JLIC Jerusalem student center!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','shulmanj@ou.org'],
    adminSubject: 'OU-JLIC at JLIC Jerusalem student center Donation notification',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          contactMe: zBool().nullable().optional(),
        })
      })
    },
  },
        'jlic-nachlaot': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'OU-JLIC at JLIC Nachlaot Donation form',
    department: 'JLIC_Jerusalem',
    campaign: 'Nachlaot FY27',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at JLIC Nachlaot!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['steinreiche@ou.org','TibbettsE@ou.org','RossJ@ou.org','Max.Librach@ou.org','Sylvie.Librach@ou.org'],
    adminSubject: 'OU-JLIC at JLIC Nachlaot Donation notification',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          contactMe: zBool().nullable().optional(),
        })
      })
    },
  },
  'jlic-mcmaster': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'OU-JLIC at MCMASTER Donation form',
    department: 'JLIC_McMaster',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at MCMASTER!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','greenberga@ou.org'],
    adminSubject: 'OU-JLIC at MCMASTER Donation notification',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          contactMe: zBool().nullable().optional(),
        })
      })
    },
  },
  'jlic-johnshopkins': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'OU-JLIC at Johns Hopkins University Donation form',
    department: 'JLIC_JohnsHopkins',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at Johns Hopkins University!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','moskowitzmo@ou.org','MoskowitzS@ou.org','zemas@ou.org'],
    adminSubject: 'OU-JLIC at Johns Hopkins University Donation notification',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          contactMe: zBool().nullable().optional(),
        })
      })
    },
  },
    'jlic-nyu': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'JLIC at New York University Donation form',
    department: 'JLIC_NYU',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at New York!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      [5000, `Semester Shabbat and Holiday sponsorship`],
      [3600, `Semester social programming sponsorship`],
      [2500, `One month sponsorship of THINK - our premier learning event`],
      [1800, `One month sponsorship of learning on campus`],
      [1000, `Sponsor one chessed event with our community`],
      [500, `Sponsor one seudah shlishit on campus`],
      [360, `Sponsor one THINK - our premier learning event`],
      [180, `Sponsor one friday night oneg`],
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','bachrachs@ou.org','zemas@ou.org', 'Zechariah.Rosenthal@ou.org', 'Elyanna.Saperstein@ou.org'],
    adminSubject: 'OU-JLIC at New York University Donation notification',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          contactMe: zBool().nullable().optional(),
        })
      })
    },
  },
    'jlic-princeton-college': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'JLIC at Princeton University Donation form',
    department: 'JLIC_Princeton',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at Princeton!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      [7200, `One month of OU-JLIC programming`],
      [3600, `One month of home hosting`],
      [2500, `One month of learning on campus`],
      [1800, `Communal holiday BBQ`],
      [1200, `Sponsor a young alumni event`],
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','zemas@ou.org', 'Noa.Bar-Shain@ou.org', 'ari.barshain@ou.org'],
    adminSubject: 'OU-JLIC at Princeton University Donation notification',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          contactMe: zBool().nullable().optional(),
        })
      })
    },
  },
  'jlic-queens': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'JLIC Queens College Donation form',
    department: 'JLIC_Queens',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at Queens College!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','brukirers@ou.org','brukirerr@ou.org','zemas@ou.org'],
    adminSubject: 'OU-JLIC Queens College Donation notification',
    customValidations (form) {
    return form.extend({
      custom: z.object({
        contactMe: zBool().nullable().optional(),
      })
    })
  },
  },
  'jlic-rutgers': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'JLIC Rutgers University Donation form',
    department: 'JLIC_Rutgers',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at Rutgers University!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','zemas@ou.org', 'Yael.Nitzanim@ou.org', 'Matthew.Nitzanim@ou.org'],
    adminSubject: 'OU-JLIC Rutgers University Donation notification',
    customValidations (form) {
    return form.extend({
      custom: z.object({
        contactMe: zBool().nullable().optional(),
      })
    })
  },
  },
  'jlic-santamonica': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'JLIC Santa Monica College Donation form',
    department: 'JLIC_SMC',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at Santa Monica College!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','najibir@ou.org','ornerd@ou.org','zemas@ou.org','kaplana@ou.org'],
    adminSubject: 'OU-JLIC Santa Monica College Donation notification',
    customValidations (form) {
    return form.extend({
      custom: z.object({
        contactMe: zBool().nullable().optional(),
      })
    })
  },
  },
  'jlic-ucla': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'JLIC UCLA Donation form',
    department: 'JLIC_UCLA',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at UCLA!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','kaplana@ou.org','kaplans@ou.org','zemas@ou.org'],
    adminSubject: 'OU-JLIC UCLA Donation notification',
    customValidations (form) {
    return form.extend({
      custom: z.object({
        contactMe: zBool().nullable().optional(),
      })
    })
  },
  },
  'jlic-ucla-toppings': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    emailTemplate: 'jlic-ucla-toppings',
    title: 'JLIC UCLA Toppings',
    department: 'JLIC_UCLA',
    campaign: 'Toppings',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at UCLA!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org', 'rossj@ou.org','steinreiche@ou.org','kaplana@ou.org','kaplans@ou.org'],
    adminSubject: 'OU-JLIC UCLA Toppings notification',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          contactMe: zBool().nullable().optional(),
        })
      })
    },
  },
  'jlic-chicago': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'JLIC University Of Chicago Donation form',
    department: 'JLIC_Chicago',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at University Of Chicago!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org'],
    adminSubject: 'OU-JLIC University Of Chicago Donation notification',
    customValidations (form) {
    return form.extend({
      custom: z.object({
        contactMe: zBool().nullable().optional(),
      })
    })
  },
  },
  'jlic-illinois': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'JLIC University Of Illinois Donation form',
    department: 'JLIC_Uillinois',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at University Of Illinois!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','HaussS@ou.org','haussj@ou.org','zemas@ou.org'],
    adminSubject: 'OU-JLIC University Of Illinois Donation notification',
    customValidations (form) {
    return form.extend({
      custom: z.object({
        contactMe: zBool().nullable().optional(),
      })
    })
  },
  },
  'jlic-maryland': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'JLIC University Of Maryland Donation form',
    department: 'JLIC_UMD',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at University Of Maryland!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','lehmanj@ou.org','steinreiche@ou.org','turkr@ou.org','zemas@ou.org'],
    adminSubject: 'OU-JLIC University Of Maryland Donation notification',
    customValidations (form) {
    return form.extend({
      custom: z.object({
        contactMe: zBool().nullable().optional(),
      })
    })
  },
  },
  'jlic-machon-tal': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    emailTemplate: 'jlic-machon-tal',
    title: 'JLIC Machon Tal Donation form',
    department: 'JLIC_MachonTal',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at Machon Tal!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['steinreiche@ou.org','rabbishulman@yavneh.us','rosenthalp@ou.org','rossj@ou.org','goldmani@ou.org'],
    adminSubject: 'OU-JLIC Machon Tal Donation notification',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          contactMe: zBool().nullable().optional(),
        })
      })
    },
  },
  'jlic-umass': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'JLIC University Of Massachusetts Donation form',
    department: 'JLIC_UMASS',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at University Of Massachusetts!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','zemas@ou.org'],
    adminSubject: 'OU-JLIC University Of Massachusetts Donation notification',
    customValidations (form) {
    return form.extend({
      custom: z.object({
        contactMe: zBool().nullable().optional(),
      })
    })
  },
  },
  'jlic-pennsylvania': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'JLIC University Of Pennsylvania Donation form',
    department: 'JLIC_Penn',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at University Of Pennsylvania!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','kleinj@ou.org','steinreiche@ou.org','kleinsa@ou.org','zemas@ou.org'],
    adminSubject: 'OU-JLIC University Of Pennsylvania Donation notification',
    customValidations (form) {
    return form.extend({
      custom: z.object({
        contactMe: zBool().nullable().optional(),
      })
    })
  },
  },
  'jlic-western': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'JLIC University Of Western Donation form',
    department: 'JLIC_Western',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at University Of Western!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','najibir@ou.org','steinreiche@ou.org','ornerd@ou.org','zemas@ou.org'],
    adminSubject: 'OU-JLIC University Of Western Donation notification',
    customValidations (form) {
    return form.extend({
      custom: z.object({
        contactMe: zBool().nullable().optional(),
      })
    })
  },
  },
  'jlic-yale': {
    ...jliccollegeBasic,
    template: 'jlic-college',
    title: 'JLIC University Of Yale Donation form',
    department: 'JLIC_Yale',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you for your support of OU-JLIC at University Of Yale!</p> <p>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org','rosenthalp@ou.org','rossj@ou.org','steinreiche@ou.org','zemas@ou.org','yehiel.rosenberg@ou.org','rachel.patinkin@ou.org'],
    adminSubject: 'OU-JLIC University Of Yale Donation notification',
    customValidations (form) {
    return form.extend({
      custom: z.object({
        contactMe: zBool().nullable().optional(),
      })
    })
  },
  },
  'jufnj': {
    emailTemplate: 'jufnj',
    title: 'JUF NJ Donation Form',
    department: '',
    campaign: 'General Donation',
    message: 'Thank you for your donation.',
    adminEmails: [ 'info@jufnj.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'JUFNJ Donation',
    userFromEmail: 'JUFNJ.org <info@jufnj.org>',
    userSubject: 'Thank you for your support',
    amounts: [
      18,
      25,
      50,
      100,
      180
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-3' : 'UA-1957949-2'
  },
  'genaleph': {
    emailTemplate: 'genaleph',
    title: 'GenAleph Donation Form',
    department: 'CommunityProjectsAndPartnerships',
    campaign: 'Gen Aleph',
    message: 'Thank you for your donation.',
    adminEmails: [ 'yair.menchel@gmail.com', 'kareshp@ou.org'],
    adminFromEmail: 'GenAleph <yair.menchel@gmail.com>',
    adminSubject: 'GenAleph Donation',
    userFromEmail: 'GenAleph.org <info@genAleph.org>',
    userSubject: 'Thank you for your support',
    amounts: [
      18,
      25,
      50,
      100,
      180
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-18329904-1' : 'UA-25738407-2'
  },
  'oupress-internal': {
    emailTemplate: 'oupress',
    title: ' OU Press Internal Payment Form',
    department: 'OUPress',
    campaign: 'One Time Payment',
    subCampaign: '',
    message: 'Thank you for your payment.',
    adminEmails: ['gellers@ou.org','breed@ou.org', 'goldmani@ou.org'],
    adminFromEmail: 'Orthodox Union<info@ou.org>',
    adminSubject: ' OU Press Internal Payment Form',
    userFromEmail: 'Orthodox Union<info@ou.org>',
    userSubject: 'Thank you for your payment',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-10882656-1' : 'UA-10882656-1'
  },
  'oukosher-invoices': {
    emailTemplate: 'oukosher-invoices',
    title: 'OU Kosher Initial Invoices',
    department: 'OU',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your payment.',
    adminEmails: ['blumbergb@ou.org', 'kaliksteind@ou.org', 'kleinmany@ou.org', 'umlasm@ou.org', 'miriamw@ou.org', 'asad', 'asad','neumanj@ou.org',  'goldmani@ou.org'],
    adminFromEmail: 'OU Kosher<info@ou.org>',
    adminSubject: 'OU Kosher Standalone Form',
    userFromEmail: 'OU Kosher<info@ou.org>',
    userSubject: 'Thank you for your payment',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          CompanyName: z.string(),
          CompanyNumber: z.string(),
          ContactName: z.string().nullable().optional(),
          ContactEmailAddress: z.string().nullable().optional(),
          InvoiceNumber: z.string(),
          Comments: z.string().nullable().optional(),
          SubmittersInformation: z.string(),
          SubmitterEmailAddress: z.string()
        })
      })
    },
    gaID: process.env.NODE_ENV == 'production' ? 'UA-10882656-1' : 'UA-10882656-1'
  },
  'tachlis': {
    emailTemplate: 'tachlis',
    title: 'Tchlis Media Sponsor Form',
    department: 'OU_Torah',
    campaign: 'Tachlis',
    message: 'Thank you for your sponsorship.',
    adminEmails: [ 'info@tachlismedia.com', 'hirschr@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'Tachlis Media Sponsorship',
    userFromEmail: 'tachlismedia.com <info@tachlismedia.org>',
    userSubject: 'Thank you for your support',
    amounts: [
      18,
      25,
      50,
      100,
      180
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-18329904-3' : 'UA-18329904-2'
  },
  'accelerator': {
    emailTemplate: 'accelerator',
    title: 'Invest In Our Community - OU Impact Accelerator',
    department: 'ImpactAccelerator',
    campaign: 'General Donation',
    message: 'Thank you for your donation.',
    amounts: [
      1000,
      3600,
      5000,
      10000,
      18000
    ],
    adminEmails: ['beltserj@ou.org', 'miriamw@ou.org', 'goldmani@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'Orthodox Union donation',
    userFromEmail: 'OU Impact Accelerator <beltserj@ou.org>',
    userSubject: 'Thank you for your donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists: [5075874],
  },
  'accelerator22': {
    emailTemplate: 'accelerator',
    title: 'Invest In Our Community - OU Impact Accelerator',
    department: 'ImpactAccelerator',
    campaign: 'General Donation',
    message: 'Thank you for your contribution to the OU Impact Accelerator.<br><br> We appreciate your commitment to strengthening our community by supporting startup nonprofits that are addressing our communal challenges in new and innovative ways. Together, we can improve Jewish life.',
    amounts: [
      1000,
      3600,
      5000,
      10000,
      18000
    ],
    adminEmails: ['beltserj@ou.org', 'miriamw@ou.org', 'goldmani@ou.org', 'frydmant@ou.org'],
    adminFromEmail: 'Orthodox Union <frydmant@ou.org>',
    adminSubject: 'Orthodox Union donation',
    userFromEmail: 'OU Impact Accelerator <frydmant@ou.org>',
    userSubject: 'Thank you for your donation.',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists: [5075874],
  },
  'hashavataveida': {
    emailTemplate: 'hashavataveida',
    title: 'Hashavat Aveidah - Donate',
    department: 'CommunityProjectsAndPartnerships',
    campaign: 'hashavataveida',
    message: 'Thank you for your donation.',
    amounts: [
      18,
      36,
      54,
      72,
      100
    ],
    adminEmails: ['sarnae@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'Orthodox Union donation',
    userFromEmail: 'OU Hashavat Aveidah <sarnae@ou.org>',
    userSubject: 'Thank you for your donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          designation: z.string().nullable().optional(),
        })
      })
    }
  },
  'india-fund': {
    emailTemplate: 'indiafund',
    title: 'Help the humanitarian crisis in India',
    department: 'ReliefFunds',
    campaign: 'Crisis in India',
    message: 'Thank you for your donation.',
    amounts: [
      5,
      18,
      25,
      50,
      100,
      180
    ],
    adminEmails: [ 'ouintranet@ou.org', 'staylor@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'Orthodox Union donation',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists: [1850355],
  },
  'navigator': {
    emailTemplate: 'navigator',
    title: 'OU Navigator',
    department: 'CommunityProjectsAndPartnerships',
    campaign: 'OU Navigator',
    subCampaign: '',
    message: 'Thank you for your donation',
    amounts: [
      36,
      250,
      1000,
      2500,
    ],
    adminEmails: [],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'OU Navigator donation',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists: [1850355],
  },
  'ck-conference': {
    emailTemplate: 'ck-conference',
    title: 'Chevra Kadisha Conference',
    department: 'CommunityProjectsAndPartnerships',
    campaign: 'Chevra Kadisha Conference',
    subCampaign: '',
    message: 'Thank you for your donation',
    amounts: [
      [5000, `Platinum Partner`],
      [3600, `Leadership Partner`],
      [1800, `Conference Partner`],
      [1000, `Chevra Partner`],
      [500, `Chevra Sustainer`],
    ],
    adminEmails: ['sarnae@ou.org','HirschR@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'Chevra Kadisha Conference Donation',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists: [1850355],
  },
  'ouresearch': {
    template: 'ouresearch',
    emailTemplate: 'ou',
    title: 'Donate to OU Research',
    department: 'CommunalResearch',
    campaign: 'General Donation',
    message: 'Thank you for your generous support of the Chevra Kadisha Conference! A receipt of your donation has been emailed to you.',
    amounts: [
      36,
      250,
      1000,
      2500,
    ],
    adminEmails: ['shainm@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'Orthodox Union Communal Research donation',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-26' : 'UA-32047-26',
  },
  'ukraine-fund': {
    emailTemplate: 'ukrainefund',
    title: 'OU Ukraine Relief Fund',
    department: 'ReliefFunds',
    campaign: 'Ukraine Emergency Appeal',
    message: 'Thank you for your support of the OU Ukraine Relief Fund.  We will keep you informed on our continued efforts on behalf of the Ukraine Jewish community.',
    amounts: [
      5,
      18,
      25,
      50,
      100,
      180,
      360,
      500,
      1000,
      1800,
      3600
    ],
    adminEmails: [ 'ouintranet@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'Orthodox Union donation',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists: [1850355],
  },
  'ukraine-75': {
    emailTemplate: 'ukrainefund',
    title: 'OU Ukraine Just 75',
    department: 'ReliefFunds',
    campaign: 'Ukraine Emergency Appeal',
    message: 'Thank you for your support of the OU Ukraine Relief Fund.  We will keep you informed on our continued efforts on behalf of the Ukraine Jewish community.',
    amounts: [
      5,
      18,
      25,
      50,
      75,
      180,
      360,
      500,
      1000,
      1800,
      3600
    ],
    adminEmails: ['ouintranet@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'Orthodox Union donation',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists: [1850355],
  },
  'springs-gymnastics': {
    emailTemplate: 'ukrainefund',
    title: 'OU Ukraine Relief Fund',
    department: 'ReliefFunds',
    campaign: 'Springs Gymnastics',
    message: 'Thank you for your support of the OU Ukraine Relief Fund.  We will keep you informed on our continued efforts on behalf of the Ukraine Jewish community.',
    amounts: [
      5,
      18,
      25,
      50,
      100,
      180,
      360,
      500,
      1000,
      1800,
      3600
    ],
    adminEmails: ['ouintranet@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'Orthodox Union donation - Springs Gymnastics',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists: [1850355],
  },
  'ukraine-foodshipment': {
    emailTemplate: 'ukrainefund',
    title: 'OU Ukraine Food Shipment',
    department: 'ReliefFunds',
    subCampaign: 'Food Shipment',
    campaign: 'Ukraine Emergency Appeal',
    message: 'Thank you for your support of the OU Ukraine Food Shipment.  We will keep you informed on our continued efforts on behalf of the Ukraine Jewish community.',
    adminEmails: [ 'ouintranet@ou.org', 'staylor@ou.org', 'goldmani@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'Orthodox Union Food Shipment donation',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists: [1850355],
  },
  'israel-crisis21': {
    emailTemplate: 'israel-crisis21',
    title: 'Israel Emergency Fund',
    department: 'OUIsrael',
    campaign: 'Southern Appeal',
    message: 'Thank you for your support.',
    amounts: [
      18,
      54,
      100,
      250,
      500,
      1000,
      1800,
      3600,
      5000,
    ],
    adminEmails: [ 'ouintranet@ou.org', 'joshc@ouisrael.org', 'lizb@ouisrael.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'Orthodox Union donation',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    silverPopContactLists: [1850355],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2'
  },
  'allparsha': {
    emailTemplate: 'allparsha',
    title: 'AllParsha Donation Form',
    department: 'OU_Torah',
    campaign: 'All Parsha',
    message: 'Thank you for your donation.',
    amounts: [
      18,
      36,
      54,
      180,
      360
    ],
    adminEmails: [ 'tessera@ou.org', 'schwedm@ou.org', 'goldmani@ou.org','hirschr@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'AllParsha Center donation',
    userFromEmail: 'AllParsha <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-XXXXXXXX-1' : 'UA-XXXXXXXX-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          amudei: zBool().optional(),
          programId: z.string().nullable(),
          startDate: z.string().nullable().optional(),
          endDate: z.string().nullable().optional(),
          sponsorshipType: z.string(),
          dedication: z.string().nullable().optional(),
          specialInstructions: z.string().nullable().optional(),
        })
      }).refine(form => { return form.custom.sponsorshipType == 'general' || (form.custom.startDate && form.custom.endDate) || (typeof form.amount != 'symbol' && form.amount && Number(form.amount) < 180) }, {
        message: 'Please select a date',
        path: ['custom', 'startDate']
      }).refine(form => { return form.custom.sponsorshipType != 'shiur' || form.custom.programId }, {
        message: 'Please select a shiur',
        path: ['custom', 'programId']
      })
    }
  },
  'allhalacha': {
    emailTemplate: 'allhalacha',
    title: 'AllHalacha Donation Form',
    department: 'OU_Torah',
    campaign: 'All Halacha',
    message: 'Thank you for your donation.',
    amounts: [
      18,
      36,
      54,
      180,
      360
    ],
    adminEmails: [ 'tessera@ou.org', 'schwedm@ou.org', 'goldmani@ou.org','hirschr@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'AllHalacha Center donation',
    userFromEmail: 'AllHalacha <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-XXXXXXXX-1' : 'UA-XXXXXXXX-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          amudei: zBool().optional(),
          programId: z.string().nullable(),
          startDate: z.string().nullable().optional(),
          endDate: z.string().nullable().optional(),
          sponsorshipType: z.string(),
          dedication: z.string().nullable().optional(),
          specialInstructions: z.string().nullable().optional(),
          dateOption: z.string().nullable().optional(),
        })
      }).refine(form => { return form.custom.sponsorshipType == 'general' || (form.custom.startDate && form.custom.endDate) || (typeof form.amount != 'symbol' && form.amount && Number(form.amount) < 180) }, {
        message: 'Please select a date',
        path: ['custom', 'startDate']
      }).refine(form => { return form.custom.sponsorshipType != 'shiur' || form.custom.programId }, {
        message: 'Please select a shiur',
        path: ['custom', 'programId']
      })
    }
  },
  'alldaf-general': {
    emailTemplate: 'alldaf-general',
    title: 'AllDaf Donation Form',
    department: 'OU_Torah',
    campaign: 'All Daf',
    subCampaign: 'General Donation',
    message: 'Thank you for your donation.',
    amounts: [
      18,
      36,
      54,
      180,
      360
    ],
    adminEmails: [ 'tessera@ou.org', 'schwedm@ou.org', 'goldmani@ou.org', 'hirschr@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'AllDaf donation',
    userFromEmail: 'AllDaf <ouintranet@ou.org>',
    userSubject: 'Thank You!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-XXXXXXXX-1' : 'UA-XXXXXXXX-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          programId: z.string().nullable(),
          startDate: z.string().nullable().optional(),
          dateOption: z.string().nullable().optional(),
          endDate: z.string().nullable().optional(),
          sponsorshipType: z.string(),
          dedication: z.string().nullable().optional(),
          specialInstructions: z.string().nullable().optional(),
          amudei: zBool().optional(),
        })
      }).refine(form => { return form.custom.sponsorshipType == 'general' || (form.custom.startDate && form.custom.endDate) || (typeof form.amount != 'symbol' && form.amount && Number(form.amount) < 360) }, {
        message: 'Please select a date',
        path: ['custom', 'startDate']
      }).refine(form => { return form.custom.sponsorshipType != 'series' || form.custom.programId }, {
        message: 'Please select a shiur',
        path: ['custom', 'programId']
      })
    }
  },
  'alldaf-campaign': {
    emailTemplate: 'alldaf-general',
    title: 'AllDaf Campaign Form',
    department: 'OU_Torah',
    campaign: 'All Daf',
    subCampaign: 'General Donation',
    message: 'Thank you for your donation.',
    amounts: [
      18,
      36,
      54,
      180,
      360
    ],
    adminEmails: [ 'tessera@ou.org', 'schwedm@ou.org', 'goldmani@ou.org', 'hirschr@ou.org'],
    adminFromEmail: 'Orthodox Union <dafinfo@alldaf.org>',
    adminSubject: 'AllDaf donation',
    userFromEmail: 'AllDaf <dafinfo@alldaf.org>',
    userSubject: 'Thank You!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-XXXXXXXX-1' : 'UA-XXXXXXXX-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          programId: z.string().nullable(),
          startDate: z.string().nullable().optional(),
          dateOption: z.string().nullable().optional(),
          endDate: z.string().nullable().optional(),
          sponsorshipType: z.string(),
          dedication: z.string().nullable().optional(),
          specialInstructions: z.string().nullable().optional()
        })
      })
    }
  },
  'alldaf-shas': {
    emailTemplate: 'alldaf-general',
    title: 'AllDaf In memory of Steve Neuwirth - Donation Form',
    department: 'OU_Torah',
    campaign: 'All Daf',
    subCampaign: 'General Donation',
    message: 'Thank you for your donation.',
    amounts: [
      18,
      36,
      54,
      180,
      360
    ],
    adminEmails: [ 'tessera@ou.org', 'schwedm@ou.org', 'goldmani@ou.org', 'hirschr@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'AllDaf In memory of Steve Neuwirth - Donation',
    userFromEmail: 'AllDaf <ouintranet@ou.org>',
    userSubject: '<h1>Thank You</h1><p>The All Daf platform strives to provide easy access to all our wonderful content in a sleek and user-friendly way.<br>Your dedication and partnership allows us to maintain and grow the platform, and encourages all those involved in making it a daily reality.<br>May you only see success in your all your endeavors, and enjoy good health together with the rest of Klal Yisroel.</p>',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-XXXXXXXX-1' : 'UA-XXXXXXXX-2'
  },
  'allmishnah': {
    emailTemplate: 'allmishnah',
    title: 'AllMishnah Donation Form',
    department: 'OU_Torah',
    campaign: 'All Mishnah',
    message: 'Thank you for your donation.',
    amounts: [
      18,
      36,
      54,
      180,
      360
    ],
    adminEmails: [ 'tessera@ou.org', 'schwedm@ou.org', 'goldmani@ou.org','hirschr@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'AllMishnah Center donation',
    userFromEmail: 'AllMishnah <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-XXXXXXXX-1' : 'UA-XXXXXXXX-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          amudei: zBool().optional(),
          programId: z.string().nullable(),
          startDate: z.string().nullable().optional(),
          dateOption: z.string().nullable().optional(),
          endDate: z.string().nullable().optional(),
          sponsorshipType: z.string(),
          dedication: z.string().nullable().optional(),
          specialInstructions: z.string().nullable().optional(),
        })
      }).refine(form => { return form.custom.sponsorshipType == 'general' || (form.custom.startDate && form.custom.endDate) || (typeof form.amount != 'symbol' && form.amount && Number(form.amount) < 360) }, {
        message: 'Please select a date',
        path: ['custom', 'startDate']
      }).refine(form => { return form.custom.sponsorshipType != 'shiur' || form.custom.programId }, {
        message: 'Please select a shiur',
        path: ['custom', 'programId']
      })
    }
  },
  'allmishnah-jr': {
    emailTemplate: 'allmishnah',
    title: 'AllMishnah Jr Donation Form',
    department: 'OU_Torah',
    campaign: 'All Mishnah JR',
    message: 'Thank you for your donation.',
    amounts: [
      [18, `Sponsor a prize`],
      [90, `Sponsor a school’s weekly prizes`],
      [180, `Dedicate a day of learning`],
      [360, `Dedicate two days of learning`],
      [540, `Dedicate a week of learning`],
      [1000, `Sponsor the full program’s weekly prizes`],
      [1800, `Sponsor a month of learning`],
      [5000, `Sponsor a year of learning`],
    ],
    adminEmails: ['avracenm@ou.org', 'goldmani@ou.org'],
    adminFromEmail: 'Orthodox Union <info@ou.org>',
    adminSubject: 'AllMishnah Jr donation',
    userFromEmail: 'AllMishnah Jr<info@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-XXXXXXXX-1' : 'UA-XXXXXXXX-2'
  },
  'alldaf-siyum': {
    emailTemplate: 'alldaf-siyum',
    title: 'Support All Daf with a Siyum dedication',
    department: 'OU_Torah',
    campaign: 'All Daf',
    subCampaign: 'General Donation',
    message: 'Thank you for your donation.',
    amounts: [
      [1800, `(Includes honorable mention on siyum advertising materials and a week sponsorship of an All Daf Shiur of your choice)`],
      [360, ` (Includes 3 sponsorships of an All Daf shiur of your choice)`],
      [180, `(Includes 1 sponsorship of an All Daf shiur of your choice)`],
    ],
    adminEmails: ['tessera@ou.org', 'schwedm@ou.org',  'goldmani@ou.org', 'hirschr@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'Support All Daf with a Siyum dedication',
    userFromEmail: 'AllDaf <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-XXXXXXXX-1' : 'UA-XXXXXXXX-2'
  },
  'advocacy-donate': {
    emailTemplate: 'advocacy',
    honoreeTemplate: 'advocacy_dedication',
    title: 'OUAdvocacy Donation Form',
    department: 'Advocacy',
    campaign: 'General Donation',
    message: 'Thank you for donating to OU Advocacy',
    amounts: [
      54,
      180,
      500,
      1800,
      5000,
      10000
    ],
    adminEmails: [ 'tarer@ou.org','bojanl@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'OUAdvocacy donation',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-1' : 'UA-71428538-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          designation: z.string().nullable().optional(),
        })
      })
    }
  },
  'advocacy-donation-form': {
    emailTemplate: 'advocacy',
    honoreeTemplate: 'advocacy_dedication',
    title: 'OUAdvocacy Donation Form',
    department: 'Advocacy',
    campaign: 'General Donation',
    message: 'Thank you for donating to OU Advocacy',
    amounts: [
      54,
      180,
      500,
      1800,
      5000,
      10000
    ],
    adminEmails: [ 'tarer@ou.org','bojanl@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'OUAdvocacy donation',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-1' : 'UA-71428538-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          designation: z.string().nullable().optional(),
        })
      })
    }
  },  
  'advocacy-actions': {
    template: 'advocacy-liat',
    emailTemplate: 'advocacy',
    honoreeTemplate: 'advocacy_dedication',
    title: 'OUAdvocacy Defend Jewish College Students - Donation Form',
    department: 'Advocacy',
    campaign: 'General Donation',
    message: 'Thank you for donating to OU Advocacy',
    amounts: [
      [10, `Take a Stand`],
      [18, `Chai for Change`],
      [36, `Turn Outrage Into Action`],
      [72, `Defend Civil Rights on Campus`],
      [100, `Protect Students Nationwide`],
      [180, `Lead the Fight`],
      [250, `Champion the Movement`],
      [500, `Secure our Jewish Future`],
    ],
    adminEmails: [ 'bojanl@ou.org', 'tarer@ou.org', 'ryan.c@coldspark.com'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'OUAdvocacy Defend Jewish College Students - donation',
    userFromEmail: 'Orthodox Union Defend Jewish College Students<ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-1' : 'UA-71428538-2'
  },
  'advocacy-security': {
    template: 'advocacy-liat',
    emailTemplate: 'advocacy',
    honoreeTemplate: 'advocacy_dedication',
    title: 'OUAdvocacy Security Ad Campaign - Donation Form',
    department: 'Advocacy',
    campaign: 'General Donation',
    message: 'Thank you for donating to OU Advocacy',
    amounts: [
      [18, `Supporter <span>Help strengthen emergency preparedness</span>`],
      [54, `Advocate <span>Support real-time threat monitoring</span>`],
      [180, `Guardian <span>Help reinforce secure entry points</span>`],
      [500, `Watchman <span>Advance funding for professional security personnel</span>`],
      [1800, `Defender <span>Support upgrades that fortify our buildings</span>`],
      [5000, `Community Partner <span>Help expand comprehensive security for Jewish institutions across the country</span>`],
    ],
    adminEmails: [ 'bojanl@ou.org', 'tarer@ou.org', 'ryan.c@coldspark.com', 'goldmani@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'OUAdvocacy Security Ad Campaign - donation',
    userFromEmail: 'Orthodox Union Security Ad Campaign <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-1' : 'UA-71428538-2'
  },
  'advocacy-liat': {
    emailTemplate: 'advocacy',
    honoreeTemplate: 'advocacy_dedication',
    title: 'OUAdvocacy Donation Form',
    department: 'Advocacy',
    campaign: 'LIAT',
    message: 'Thank you for donating to OU Advocacy',
    amounts: [
      [18, `Chai for Change <span>Support LIAT programming that is advancing Jewish student leadership.</span>`],
      [50, `Campus Program Sponsor <span>Enable a fellow to organize a campus program that engages and connects Jewish students.</span>`],
      [180, `Training Session Supporter <span>Support advocacy training that equips LIAT fellows with practical leadership tools.</span>`],
      [500, `Capstone Project Sponsor <span>Fund a fellow's signature campus project that builds community, addresses challenges, and creates lasting impact.</span>`],
      [1000, `Leadership Summit Sponsor <span>Help a fellow participate in the national leadership summit in Washington, DC, where they connect, learn, and bring inspiration back to campus.</span>`],
      [2500, `Trailblazer <span>Strengthen the LIAT Fellowship by underwriting advanced training and strategic programs on campuses across the country.</span>`],
      [5000, `Benefactor <span>Sponsor a returning LIAT Fellow, sustaining a proven advocate whose experience and relationships are already advancing Jewish communal priorities.</span>`],
      [10000, `Visionary <span>Sponsor a new LIAT Fellow, covering the full investment required to recruit and train a first year participant, expanding the pipeline of Jewish advocates.</span>`],
    ],
    adminEmails: [ 'tarer@ou.org','bojanl@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'OUAdvocacy LIAT donation',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-1' : 'UA-71428538-2'
  },
  'securethejewishfuture-donate': {
    emailTemplate: 'securethejewishfuture',
    honoreeTemplate: 'advocacy_dedication',
    title: 'Secure the Jewish future Donation Form',
    department: 'Advocacy',
    campaign: 'General Donation',
    message: 'Thank you for donating to this critical campaign',
    amounts: [
      54,
      180,
      500,
      1800,
      5000,
      10000
    ],
    adminEmails: [ 'tarer@ou.org','bojanl@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'Secure the Jewish future donation',
    userFromEmail: 'Nathan Diament & Sydney Altfield <ouintranet@ou.org>',
    userSubject: 'A message from OU Advocacy & Teach Coalition',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-1' : 'UA-71428538-2'
  },
  'advocacy-giving': {
    template: 'advocacy-donate',
    emailTemplate: 'advocacy',
    honoreeTemplate: 'advocacy_dedication',
    title: 'OUAdvocacy Donation Form',
    department: 'Advocacy',
    campaign: 'Giving Tuesday',
    message: 'Thank you for donating to OU Advocacy',
    amounts: [
      54,
      180,
      500,
      1800,
      5000,
      10000
    ],
    adminEmails: [ 'tarer@ou.org','bojanl@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'OUAdvocacy donation',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-1' : 'UA-71428538-2'
  },
  'advocacy-general': {
    emailTemplate: 'advocacy',
    honoreeTemplate: 'advocacy_dedication',
    title: 'OUAdvocacy Donation Form',
    department: 'Advocacy',
    campaign: 'General Donation',
    message: 'Thank you for donating to OU Advocacy',
    amounts: [
      54,
      180,
      500,
      1800,
      5000,
      10000
    ],
    adminEmails: [ 'tarer@ou.org','bojanl@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'OUAdvocacy donation',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-1' : 'UA-71428538-2'
  },
  'advocacy-protect': {
    emailTemplate: 'advocacy-protect',
    honoreeTemplate: 'advocacy_dedication',
    title: 'OUAdvocacy Protect Jewish Students Donation Form',
    department: 'Advocacy',
    campaign: 'General Donation',
    subCampaign: 'Direct Donate',
    message: 'Thank you for donating to Protect Jewish Students',
    amounts: [
      10,
      18,
      36,
      54,
      180,
      500,
      1800
    ],
    adminEmails: [ 'tarer@ou.org', 'bojanl@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'OUAdvocacy donation',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-1' : 'UA-71428538-2'
  },
  'jufny': {
    emailTemplate: 'jufny',
    title: 'Jewish Union Foundation Donation Form',
    department: 'Yachad_NewEngland',
    campaign: 'General Donation',
    subCampaign: '',
    message: '<p>Thank you so much for your generous donation to the Jewish Union Foundation.</p> <p>Your gift enables individuals with disabilities to live with the independence and dignity they deserve.</p> <p>For more information about our programs and services please feel free to contact Michael Appelbaum at 212-613-8284 or <a href="mailto:Appelbaumm@ou.org">Appelbaumm@ou.org</a>.</p>',
    amounts: [
      [10000, `Sponsor 1 year of Vocational Training`],
      [5000, `Sponsor Professional Development Conference`],
      [2000, `Sponsor 1 Continuing Education Conference`],
      [1000, `Sponsor 1 year of Drumming Classes`],
      [750, `Sponsor 1 week of Job Coaching`],
      [500, `Sponsor 1 participant in a 10-week Creative Arts Course`],
      [250, `Sponsor an Educational Trip`],
      [180, `Sponsor 1 week of Travel Training`],
      [150, `Sponsor 1 week of Music Therapy`],
      [100, `Sponsor 1 Parent Support Group`],
      [50, `Sponsor 1 Month Gym Membership for 1 Individual`],
    ],
    adminFromEmail: 'YACHAD <jufny@ou.org>',
    adminSubject: 'Jewish Union Foundation - Donation ',
    userFromEmail: 'YACHAD <jufny@ou.org>',
    adminEmails: ['jufny@ou.org','goldmani@ou.org','rosenbaumi@ou.org','njcd@ou.org','bindigers@ou.org','Appelbaumm@ou.org', 'TolubJ@ou.org'],
    userSubject: 'Jewish Union Foundation - Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2'
  },
  'yachad-dm': {
    emailTemplate: 'yachad',
    title: 'Yachad Donation Form',
    department: 'Yachad',
    campaign: 'ADM SMG Yachad/NJCD Campaign',
    subCampaign: 'Direct Mail',
    message: 'Thank you for donating to yachad',
    amounts: [
      18,
      36,
      54,
      72,
      100
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'Yachad DM payment',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2'
  },
  'yachad-la-dinner': {
    emailTemplate: 'yachad-la-dinner',
    title: 'Yachad LA Dinner',
    department: 'Yachad_LosAngeles',
    campaign: 'Dinner',
    subCampaign: '',
    message: 'Thank you for registering for Yachad LA Dinner.<br><br> We look forward to seeing you there.',
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'miryum.schoen@ou.org', 'ruth.baars@ou.org'],
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    adminSubject: 'A payment has been made to the Yachad LA Dinner',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    userSubject: 'Thank You For Registering - Yachad LA Dinner',
    amounts: [
      [5000, `Platinum (6 tickets)`],
      [2500, `Gold (4 tickets)`],
      [1800, `Silver (2 tickets)`],
      [1000, `Bronze (2 tickets)`]
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          AttendInPerson: z.string().nullable(),
          singleReservation: zBool().nullable().optional(),
          coupleReservation: zBool().nullable().optional(),
          yachadMember: zBool().nullable().optional(),
          coupon: z.string().nullable().optional(),
          guests: z.string().nullable(),
        })
      })
    }
  },
  'yachad-toronto-dinner': {
    emailTemplate: 'yachad-toronto-dinner',
    title: 'Yachad Toronto Event Donation',
    department: 'Yachad_Toronto',
    campaign: 'Event Donation',
    subCampaign: '',
    message: 'Thank you for registering for Yachad Toronto Event.<br><br> We look forward to seeing you there.',
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'marmerd@ou.org', 'ruth.baars@ou.org'],
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    adminSubject: 'A payment has been made to the Yachad Toronto Evening of Support',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    userSubject: 'Thank You For Registering - Yachad Toronto Evening of Support',
    amounts: [
      [5000, `Diamond <span>(8 tickets & VIP Reception)</span>`],
      [2500, `Platinum <span>(4 tickets & VIP Reception)</span>`],
      [1000, `Gold <span>(2  tickets & VIP Reception)</span>`],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          AttendInPerson: z.string().nullable(),
          guests: z.string().nullable().optional(),
          ticketsQnt: z.string().nullable().optional(),
          coupon: z.string().optional().nullable(),
        })
      })
    }
  },
  'yachad-negala-dinner': {
    emailTemplate: 'yachad-negala-dinner',
    title: 'Yachad New England Gala Event',
    department: 'Yachad_NewEngland',
    campaign: 'New England Gala',
    subCampaign: '',
    message: 'Thank you for registering for Yachad New England Event.<br><br> We look forward to seeing you there.',
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'butlerl@ou.org', 'tolubj@ou.org', 'ruth.baars@ou.org'],
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    adminSubject: 'A payment has been made to the Yachad New England Evening of Support',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    userSubject: 'Thank You for Registering – Yachad New England Gala',
    amounts: [
      [25000, `Platinum Sponsor <span>(10 tickets & Full Page Ad)</span>`],
      [18000, `Diamond Sponsor <span>(8 tickets & Full Page Ad)</span>`],
      [10000, `Ruby Sponsor <span>(6 tickets & Full Page Ad)</span>`],
      [7500, `Gold Sponsor <span>(4 tickets & Full Page Ad)</span>`],
      [5000, `Silver Sponsor <span>(2 tickets & Full Page Ad)</span>`],
      [3600, `Emerald Sponsor <span>(2 tickets & Full Page Ad)</span>`],
      [1800, `Bronze Sponsor <span>(2 tickets & Full Page Ad)</span>`],
      [1250, `Sapphire Sponsor <span>(2 tickets & Full Page Ad)</span>`],
      [750, `Copper Sponsor <span>(2 tickets & Half Page Ad)</span>`],
      [500, `Amethyst Sponsor <span>(2 tickets & Quarter Page)</span>`],
      [5000, `Full Page Ad`],
      [1800, `Half Page Ad`],
      [500, `Quarter Page Ad`],
      [360, `Business Card Ad`],
     ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          AttendInPerson: z.string().nullable(),
          guests: z.string().nullable().optional(),
          ticketsQnt: z.string().nullable().optional(),
          coupon: z.string().nullable().optional(),
          Notes: z.string().nullable().optional(),
          selectedDescription: z.string().nullable().optional(),
        })
      })
    }
  },
  'yachad-nejersey-dinner': {
    emailTemplate: 'yachad-nejersey-dinner',
    title: 'Yachad NJ Gala 2026',
    department: 'Yachad_NewJersey',
    campaign: 'Dinner',
    subCampaign: '',
    message: 'Thank you for registering for Yachad New Jersey Gala.<br><br> We look forward to seeing you there.',
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'selevanr@ou.org', 'mayerr@ou.org'],
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    adminSubject: 'A payment has been made to the Yachad NJ Gala 2026',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    userSubject: 'Thank You for Registering – Yachad NJ Gala 2026',
    amounts: [
      [50000, `Platinum Page`],
      [25000, `Diamond Page`],
      [18000, `Emerald Page`],
      [10000, `Sapphire Page`],
      [7500, `Ruby Page`],
      [5000, `Gold Page`],
      [2500, `Silver Page`],
      [1800, `Bronze Page`],
      [1000, `Patron Page`],
      [500, `Full Page`],
      [180, `Young Professional`],
     ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          AttendInPerson: z.string().nullable().optional(),
          guests: z.string().nullable().optional(),
          ticketsQnt: z.string().nullable().optional(),
          coupon: z.string().nullable().optional(),
          Notes: z.string().nullable().optional(),
          selectedDescription: z.string().nullable().optional(),
          honorOf: z.string().array().nullable().optional(),
        })
      }).refine((data) => {
        if (data.custom.AttendInPerson === 'yes' && (!data.custom.guests || data.custom.guests.trim() === '')) {
          return false;
        }
        return true;
      }, {
        message: 'Please enter all attendee names',
        path: ['custom', 'guests']
      })
    }
  },
    'yachad-newyork-gala-dinner': {
    emailTemplate: 'yachad-newyork-gala-dinner',
    title: 'Yachad NY Gala 2026',
    department: 'Yachad_NewYork',
    campaign: 'Dinner',
    subCampaign: '',
    message: 'Thank You for Your Donation - Yachad NY Gala 2026<br><br> We look forward to seeing you there.',
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'shatzr@ou.org', 'zarj@ou.org', 'mayerr@ou.org', 'straussr@ou.org', 'solomonas@ou.org'],
    adminFromEmail: 'YACHAD <nyyachad@ou.org>',
    adminSubject: 'A payment has been made to the Yachad NY Gala 2026',
    userFromEmail: 'YACHAD <nyyachad@ou.org>',
    userSubject: 'Thank You for Your Donation - Yachad NY Gala 2026',
    amounts: [
      [50000, `Platinum Page`],
      [25000, `Diamond Page`],
      [18000, `Emerald Page`],
      [10000, `Sapphire Page`],
      [7500, `Ruby Page`],
      [5000, `Gold Page`],
      [3600, `Silver Page`],
      [1800, `Bronze Page`],
      [1000, `Patron Page`],
      [500, `Full Page`]
     ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          AttendInPerson: z.string().nullable().optional(),
          guests: z.string().nullable().optional(),
          ticketsQnt: z.string().nullable().optional(),
          coupon: z.string().optional().nullable(),
          Notes: z.string().nullable().optional(),
          extras: z.string().nullable().optional(),
          selectedDescription: z.string().nullable().optional(),
          honorOf: z.string().array().nullable().optional(),
        })
      })
    }
  },
  'yachad-joseph-show': {
    emailTemplate: 'yachad-joseph-show',
    title: 'Yachad Joseph and the Technicolor Dreamcoat show in Chicago',
    department: 'Yachad_LosAngeles',
    campaign: 'Dinner',
    subCampaign: '',
    message: 'Thank you for registering for Yachad Joseph and the Technicolor Dreamcoat show in Chicago.<br><br> We look forward to seeing you there.',
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'harrisd@ou.org', 'shlomo.crandall@ou.org', 'ruth.baars@ou.org'],
    adminFromEmail: 'YACHAD <yachadjoseph@ou.org>',
    adminSubject: 'A payment has been made to the Yachad Joseph and the Technicolor Dreamcoat show in Chicago',
    userFromEmail: 'YACHAD <yachadjoseph@ou.org>',
    userSubject: 'Thank You For Registering - Yachad Joseph and the Technicolor Dreamcoat show in Chicago',
    amounts: [
      [118, `VIP Men's Section`],
      [118, `VIP Women's Section`],
      [118, `VIP Family Section`],
      [54, `Men's Section`],
      [54, `Women's Section`],
      [54, `Family Section`],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          AttendInPerson: z.string().nullable(),
          sponsorshipType: zBool().nullable().optional(),
          coupon: z.string().nullable().optional(),
          guests: z.string().nullable(),
        })
      })
    }
  },
  'yachad-reach': {
    emailTemplate: 'yachad-reach',
    title: 'Yachad REACH Donation Form',
    department: 'Yachad_Reach',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for donating to yachad',
    amounts: [
      180,
      250,
      360,
      500,
      720,
      1000,
      1800,
      2500,
      3600,
      5400,
      7200,
      10000,
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'Yachad REACH payment',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2'
  },
  'yachad-zev': {
    template: 'yachad-reach',
    emailTemplate: 'yachad',
    title: 'Yachad  Zev Kahn Van Donation Form',
    department: 'Yachad_NewYork',
    campaign: 'Zev Kahn Van',
    subCampaign: '',
    message: 'Thank you for donating to yachad',
    amounts: [
      180,
      250,
      360,
      500,
      720,
      1000,
      1800,
      2500,
      3600,
      5400,
      7200,
      10000,
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'shatzr@ou.org', 'zarj@ou.org', 'mayerr@ou.org'],
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    adminSubject: 'Yachad Zev Kahn Van donation',
    honoreeTemplate: 'yachad',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2'
  },
  'yachad-familysponsorship': {
    title: 'Yachad Family Shabbaton Sponsorship Form',
    honoreeTemplate: 'yachad',
    emailTemplate: 'yachad-familysponsorship',
    department: 'Yachad',
    campaign: 'Family Shabbaton',
    subCampaign: '',
    message: 'Thank you for donating to yachad',
    amounts: [
      1800,
      2500,
      3600,
      5000,
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org'],
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    adminSubject: 'Yachad Family Shabbaton Sponsorship',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2',
    silverPopContactLists: [1876556,1850355]
  },
  'yachad-aseretymeiteshuva': {
    template: 'yachad-registeration',
    emailTemplate: 'yachad-aseretymeiteshuva',
    title: 'Yachad Long Island Parlor Meeting Event - Donation Form',
    department: 'Yachad_NewYork',
    campaign: 'Scarsdale Parlor Meeting',
    subCampaign: 'Long Island Parlor Meeting',
    message: 'Thank you for your generous donation to Yachad! Yachad is dedicated to enriching the lives of Jewish individuals with disabilities and their families, by enhancing their communal participation and their connection to Judaism through social and educational programs and support services.<br> We do this on a daily basis in your community, offering many social, educational and vocational opportunities for individuals with disabilities.<br> We look forward to seeing you at the Blechner Residence on Tuesday, March 5<sup>th</sup>, at 7:30pm.<br><br> Thank you, <br>The Yachad New York Team',
    amounts: [
      180,
      250,
      360,
      500,
      720,
      1000,
      1800,
      2500,
      3600,
      5000,
      10000,
      18000,
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'shatzr@ou.org', 'zarj@ou.org', 'mayerr@ou.org'],
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    adminSubject: 'Yachad Long Island Parlor Meeting Event - Donation payment',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          AttendInPerson: z.string().nullable().optional(),
          guests: z.string().nullable().optional()
        })
      })
    }
  },
  'yachad-westhempstead': {
    template: 'yachad-registeration',
    emailTemplate: 'yachad-westhempstead',
    title: 'Yachad West Hempstead Reception Form',
    department: 'Yachad_NewYork',
    campaign: 'West Hempstead Reception',
    subCampaign: 'West Hempstead Reception',
    message: 'Thank you for your generous donation to Yachad! Yachad is dedicated to enriching the lives of Jewish individuals with disabilities and their families, by enhancing their communal participation and their connection to Judaism through social and educational programs and support services.<br> We do this on a daily basis in your community, offering many social, educational and vocational opportunities for individuals with disabilities.<br> We look forward to seeing you at 330 Elm Street, West Hempstead, NY 11552 on Saturday evening, March 2, 2024 - 8:00pm.<br><br> Thank you, <br>The Yachad Team',
    amounts: [
      180,
      250,
      360,
      500,
      720,
      1000,
      1800,
      2500,
      3600,
      5000,
      10000,
      18000,
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'shatzr@ou.org', 'zarj@ou.org', 'mayerr@ou.org'],
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    adminSubject: 'Yachad West Hempstead Reception Form',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          AttendInPerson: z.string().nullable().optional(),
          guests: z.string().nullable().optional()
        })
      })
    }
  },
  'yachad-northwoodmere': {
    template: 'yachad-registeration',
    emailTemplate: 'yachad-northwoodmere',
    title: 'Yachad North Woodmere Reception',
    department: 'Yachad_NewYork',
    campaign: 'Long Island',
    subCampaign: 'North Woodmere Reception',
    message: 'Thank you for your generous donation to Yachad! Yachad is dedicated to enriching the lives of Jewish individuals with disabilities and their families, by enhancing their communal participation and their connection to Judaism through social and educational programs and support services.<br> We do this on a daily basis in your community, offering many social, educational and vocational opportunities for individuals with disabilities.<br> We look forward to seeing you at 807 Van Dam Street, Valley Stream, NY 11581 on Tuesday, March 19, 2024 - 7:30pm.<br><br> Thank you, <br>The Yachad Team',
    amounts: [
      180,
      250,
      360,
      500,
      720,
      1000,
      1800,
      2500,
      3600,
      5000,
      10000,
      18000,
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'shatzr@ou.org', 'zarj@ou.org', 'mayerr@ou.org'],
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    adminSubject: 'Yachad North Woodmere Reception',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          AttendInPerson: z.string().nullable().optional(),
          guests: z.string().nullable().optional()
        })
      })
    }
  },
  'yachad-gems': {
    emailTemplate: 'yachad',
    title: 'Yachad GEMS Donation Form',
    department: 'Yachad',
    campaign: 'GEMS',
    subCampaign: '',
    message: 'Thank you for donating to Yachad Gems',
    amounts: [
      54,
      72,
      100,
      180,
      250,
      360,
      500,
      720,
      1000,
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org'],
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    adminSubject: 'Yachad GEMS payment',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2'
  },
  'yachad-ivdu': {
    emailTemplate: 'yachad-ivdu',
    title: 'Ivdu Donation Form',
    department: 'Yachad_IVDU',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you so much for your contribution to the IVDU schools! Your gift means so much to the students, staff, and parents of IVDU. Thank you for partnering with us to build a better world for all of our students. <br>May Hashem bless you for your kindness and support.',
    amounts: [
      180,
      250,
      360,
      500,
      720,
      1000,
      1800,
      2500,
      3600,
      5400,
      7200,
      10000,
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org'],
    adminFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    adminSubject: 'Ivdu donation',
    userFromEmail: 'Orthodox Union <ouintranet@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2'
  },
  'yachad-general': {
    template: 'yachad-main',
    title: 'Yachad Donation Form',
    department: 'Yachad',
    campaign: 'General Donation',
    subCampaign: '',
    amounts: [
      25000,
      18000,
      10000,
      5000,
      3600,
      2500,
      1800,
      1000,
      720,
      500,
      360,
      180,
      100,
      72,
      54,
      36,
    ],
    honoreeTemplate: 'yachad',
    emailTemplate: 'yachad',
    thankYouTemplate: 'yachad',
    message: 'Thank you so much for your generous donation!',
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    adminSubject: 'YACHAD Donation',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    userSubject: 'Thank you for your support!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2',
    silverPopContactLists: [1876556,1850355],
  adminEmails: (request:any) => {
    const to = 'saibelk@ou.org,herrmann@ou.org,njcd@ou.org'.split(',')
    const mapping = {
      Yachad_Israel: 'avrom.suslovich@ou.org',
    } as any
    const email = mapping[request.applications![0].department!]
    if (email) to.push(email)

    return to
  }
  },
  'yachad-payments': {
    template: 'yachad-payments',
    title: 'Yachad Payment Form',
    department: 'Yachad',
    campaign: 'General Donation',
    subCampaign: '',
    amounts: [],
    emailTemplate: 'yachad-payments',
    message: 'Thank you for your payment!',
    adminEmails: ['yachad@ou.org', 'njcd@ou.org'],
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    adminSubject: 'YACHAD/NJCD Payment Received',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    userSubject: 'Thank you for your payment',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          designation: z.string(),
          extras: z.string().nullable().optional(),
          Notes: z.string().nullable().optional(),
          chapter: z.string().nullable().optional(),
        }).refine(
          (data) => data.designation !== 'New York' || (data.chapter != null && data.chapter.trim().length > 0),
          {
            path: ['chapter'],
            message: 'Please Select a Chapter'
          }
        )
      })
    }
  },
  'yachad-baltimore': {
    ...yachadBasic,
    amounts: [
      25000,
      18000,
      10000,
      5000,
      3600,
      2500,
      1800,
      1000,
      720,
      500,
      360,
      180,
      100,
      72,
      54,
      36,
    ],
    title: 'Yachad Baltimore Donation Form',
    department: 'Yachad_Baltimore',
    campaign: 'General Donation',
    adminSubject: 'YACHAD Baltimore Donation'
  },
    'yachad-chaverim': {
    ...yachadBasic,
    emailTemplate: 'yachad-summer',
    title: 'Yachad Chaverim Donation Form',
    amounts: [
      25000,
      18000,
      10000,
      5000,
      3600,
      2500,
      1800,
      1000,
      720,
      500,
      360,
      180,
      100,
      72,
      54,
      36,
    ],
    department: 'Yachad_Summer',
    campaign: 'Camp Chaverim',
    honoreeTemplate: 'yachad',
    thankYouTemplate: 'yachad',
    adminSubject: 'YACHAD Chaverim Donation',
     adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'goldfarbj@ou.org', 'mindy.berman@ou.org'],
    adminFromEmail: 'Yachad Summer Summer <yachad@ou.org>',
    userFromEmail: 'Yachad Summer <yachad@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2'
  },
  'yachad-chicago': {
    ...yachadBasic,
    amounts: [
      25000,
      18000,
      10000,
      5000,
      3600,
      2500,
      1800,
      1000,
      720,
      500,
      360,
      180,
      100,
      72,
      54,
      36,
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'harrisd@ou.org', 'shlomo.crandall@ou.org', 'ruth.baars@ou.org'],
    title: 'Yachad Chicago Donation Form',
    department: 'Yachad_Chicago',
    campaign: 'General Donation',
    adminSubject: 'YACHAD Chicago Donation'
  },
  'yachad-abeautifultradition': {
    amounts: [
      [5000, `Platinum Sponsorship`],
      [3600, `Gold Sponsorship`],
      [1800, `Silver Sponsorship`],
      [1200, `Brass Sponsorship`],
      [250, `Full Page`],
      [125, `Half Page`],
      [36, `Greeting message in the Playbill`],
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'harrisd@ou.org', 'shlomo.crandall@ou.org', 'ruth.baars@ou.org'],
    title: 'Yachad Chicago A Beautiful Tradition Sponsorship Form',
    department: 'Yachad_Chicago',
    campaign: 'A Beautiful Tradition',
    adminSubject: 'YACHAD Chicago A Beautiful Tradition Sponsorship',
    honoreeTemplate: 'yachad',
    emailTemplate: 'yachad-abeautifultradition',
    subCampaign: '',
    thankYouTemplate: 'yachad',
    message: 'Thank you so much for your generous donation!',
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          journal: z.string().nullable().optional()
        })
      })
    }
  },
  'yachad-cleveland': {
    ...yachadBasic,
    amounts: [
      25000,
      18000,
      10000,
      5000,
      3600,
      2500,
      1800,
      1000,
      720,
      500,
      360,
      180,
      100,
      72,
      54,
      36,
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'coopermans@ou.org', 'ruth.baars@ou.org'],
    title: 'Yachad Cleveland Donation Form',
    department: 'Yachad_Cleveland',
    campaign: 'General Donation',
    adminSubject: 'YACHAD Cleveland Donation'
  },
  'yachad-losangeles': {
    ...yachadBasic,
    amounts: [
      25000,
      18000,
      10000,
      5000,
      3600,
      2500,
      1800,
      1000,
      720,
      500,
      360,
      180,
      100,
      72,
      54,
      36,
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'miryum.schoen@ou.org', 'ruth.baars@ou.org'],
    title: 'Yachad Los Angeles Donation Form',
    department: 'Yachad_LosAngeles',
    campaign: 'General Donation',
    adminSubject: 'YACHAD Los Angeles Donation'
  },
  'yachad-newjersey': {
    ...yachadBasic,
    amounts: [
      25000,
      18000,
      10000,
      5000,
      3600,
      2500,
      1800,
      1000,
      720,
      500,
      360,
      180,
      100,
      72,
      54,
      36,
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'selevanr@ou.org', 'mayerr@ou.org'],
    title: 'Yachad New Jersey Donation Form',
    department: 'Yachad_NewJersey',
    campaign: 'General Donation',
    adminSubject: 'YACHAD New Jersey Donation'
  },
  'yachad-nj-purim': {
    honoreeTemplate: 'yachad',
    thankYouTemplate: 'yachad',
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    adminSubject: 'YACHAD Donation',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    userSubject: 'Thank you for your support',
    emailTemplate: 'yachad-nj-purim',
    title: 'yachad nj purim',
    department: 'Yachad_NewJersey',
    campaign: 'Concert',
    subCampaign: '',
    message: 'Thank you',
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'selevanr@ou.org', 'mayerr@ou.org'],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2',
    amounts: {
      USD: [
        500,
        720,
        1000,
        1800,
      ],
    },
    customValidations (form) {
      return form.extend({
        custom: z.object({
          ticketsQnt: z.string().nullable().optional(),
          ticketsVIPQnt: z.string().nullable().optional(),
          coupon: z.string().nullable().optional(),
          donationAmount: z.string().nullable().optional()
        })
      })
    }
  },
  'yachad-newyork': {
    ...yachadBasic,
    amounts: [
      25000,
      18000,
      10000,
      5000,
      3600,
      2500,
      1800,
      1000,
      720,
      500,
      360,
      180,
      100,
      72,
      54,
      36,
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'shatzr@ou.org', 'zarj@ou.org', 'mayerr@ou.org'],
    title: 'Yachad New York Donation Form',
    department: 'Yachad_NewYork',
    campaign: 'General Donation',
    adminSubject: 'YACHAD New York Donation'
  },
  'yachad-newengland': {
    ...yachadBasic,
    amounts: [
      25000,
      18000,
      10000,
      5000,
      3600,
      2500,
      1800,
      1000,
      720,
      500,
      360,
      180,
      100,
      72,
      54,
      36,
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'butlerl@ou.org', 'tolubj@ou.org', 'ruth.baars@ou.org'],
    title: 'Yachad New England Donation Form',
    department: 'Yachad_NewEngland',
    campaign: 'General Donation',
    adminSubject: 'YACHAD New England Donation'
  },
  'yachad-southflorida': {
    ...yachadBasic,
    amounts: [
      25000,
      18000,
      10000,
      5000,
      3600,
      2500,
      1800,
      1000,
      720,
      500,
      360,
      180,
      100,
      72,
      54,
      36,
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'tzippy.richmond@ou.org', 'mayerr@ou.org'],
    title: 'Yachad Florida Donation Form',
    department: 'Yachad_Florida',
    campaign: 'General Donation',
    adminSubject: 'YACHAD Florida Donation'
  },
  'yachad-torontodonate': {
    ...yachadBasic,
    amounts: [
      25000,
      18000,
      10000,
      5000,
      3600,
      2500,
      1800,
      1000,
      720,
      500,
      360,
      180,
      100,
      72,
      54,
      36,
    ],
    defaultCurrency: 'CAD' as Currency,
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'marmerd@ou.org', 'ruth.baars@ou.org'],
    title: 'Yachad Toronto Donation Form',
    department: 'Yachad_Toronto',
    campaign: 'General Donation',
    adminSubject: 'YACHAD Toronto Donation'
  },
  'yachad-israel': {
    honoreeTemplate: 'yachad',
    emailTemplate: 'yachad-israel',
    subCampaign: '',
    thankYouTemplate: 'yachad',
    message: 'Thank you so much for your generous donation!',
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2',
    silverPopContactLists: [1876556,1850355],
    amounts: [
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'kelners@ou.org', 'avrom.suslovich@ou.org', 'mayerr@ou.org'],
    title: 'Yachad Israel Donation Form',
    department: 'Yachad_Israel',
    campaign: 'General Donation',
    adminSubject: 'YACHAD Israel Hebrew Donation'
  },
  'yachad-hebrew': {
    honoreeTemplate: 'yachad',
    emailTemplate: 'yachad',
    subCampaign: '',
    thankYouTemplate: 'yachad',
    message: 'תודה רבה על התרומה הנדיבה שלך!',
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2',
    silverPopContactLists: [1876556,1850355],
    amounts: [
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'kelners@ou.org', 'avrom.suslovich@ou.org', 'mayerr@ou.org'],
    title: 'Yachad Israel Donation Form',
    department: 'Yachad_Israel',
    campaign: 'General Donation',
    adminSubject: 'YACHAD Israel Donation',
    defaultCurrency: 'ILS' as Currency,
  },
  'yachad-summer': {
    emailTemplate: 'yachad-summer',
    title: 'Yachad Summer Donation Form',
    department: 'Yachad_Summer',
    campaign: 'General Donation',
    subCampaign: '',
    honoreeTemplate: 'yachad',
    thankYouTemplate: 'yachad',
    message: 'Thank you so much for your generous donation!',
      amounts: [
      18,
      36,
      54,
      100,
      180,
      250,
      500,
      1000,
      1800,
      2500,
      3600,
      5000
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'goldfarbj@ou.org', 'mindy.berman@ou.org'],
    adminFromEmail: 'Yachad Summer Summer <yachad@ou.org>',
    adminSubject: 'Yachad Summer Donation',
    userFromEmail: 'Yachad Summer <yachad@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2'
  },
  'yachad-summer-camp-chaverim': {
    emailTemplate: 'yachad-summer',
    title: 'Yachad Summer Camp Chaverim Shul Donation Form',
    department: 'Yachad_Summer',
    campaign: 'Camp Chaverim Shul',
    subCampaign: '',
    honoreeTemplate: 'yachad',
    thankYouTemplate: 'yachad',
    message: 'Thank you so much for your generous donation!',
      amounts: [
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'goldfarbj@ou.org', 'mindy.berman@ou.org'],
    adminFromEmail: 'Yachad Summer Camp Chaverim Shul <yachad@ou.org>',
    adminSubject: 'Yachad Summer Camp Chaverim Shul Donation',
    userFromEmail: 'Yachad Summer <yachad@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2'
  },
  'yachad-yby': {
    template: 'yachad-program',
    emailTemplate: 'yachad-summer',
    title: 'Yachad YBY Donation Form',
    department: 'Yachad_Summer',
    campaign: "Yad B'Yad (YBY)",
    subCampaign: '',
    honoreeTemplate: 'yachad',
    thankYouTemplate: 'yachad',
    message: 'Thank you so much for your generous donation!',
      amounts: [
      18,
      36,
      54,
      100,
      180,
      250,
      500,
      1000,
      1800,
      2500,
      3600,
      5000
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'goldfarbj@ou.org', 'mindy.berman@ou.org'],
    adminFromEmail: 'Yachad YBY <yachad@ou.org>',
    adminSubject: 'Yachad YBY Donation',
    userFromEmail: 'Yachad YBY <yachad@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2'
  },
  'yachad-sac': {
    template: 'yachad-program',
    emailTemplate: 'yachad-summer',
    title: 'Yachad Sephardic Adventure Camp Donation Form',
    department: 'Yachad_Summer',
    campaign: "Sephardic Adventure Camp",
    subCampaign: '',
    honoreeTemplate: 'yachad',
    thankYouTemplate: 'yachad',
    message: 'Thank you so much for your generous donation!',
      amounts: [
      18,
      36,
      54,
      100,
      180,
      250,
      500,
      1000,
      1800,
      2500,
      3600,
      5000
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'goldfarbj@ou.org', 'mindy.berman@ou.org'],
    adminFromEmail: 'Yachad Sephardic Adventure Camp <yachad@ou.org>',
    adminSubject: 'Yachad Sephardic Adventure Camp Donation',
    userFromEmail: 'Yachad Sephardic Adventure Camp <yachad@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2'
  },
  'yachad-moshava-wild-rose': {
    template: 'yachad-program',
    emailTemplate: 'yachad-summer',
    title: 'Yachad Moshava Wild Rose Donation Form',
    department: 'Yachad_Summer',
    campaign: "Moshava Wild Rose",
    subCampaign: '',
    honoreeTemplate: 'yachad',
    thankYouTemplate: 'yachad',
    message: 'Thank you so much for your generous donation!',
      amounts: [
      18,
      36,
      54,
      100,
      180,
      250,
      500,
      1000,
      1800,
      2500,
      3600,
      5000
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'goldfarbj@ou.org', 'mindy.berman@ou.org'],
    adminFromEmail: 'Yachad Moshava Wild Rose <yachad@ou.org>',
    adminSubject: 'Yachad Moshava Wild Rose Donation',
    userFromEmail: 'Yachad Moshava Wild Rose <yachad@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2'
  },
  'yachad-moshava-ennismore': {
    template: 'yachad-program',
    emailTemplate: 'yachad-summer',
    title: 'Yachad Moshava Ennismore Donation Form',
    department: 'Yachad_Summer',
    campaign: "Moshava Ennismore",
    subCampaign: '',
    honoreeTemplate: 'yachad',
    thankYouTemplate: 'yachad',
    message: 'Thank you so much for your generous donation!',
      amounts: [
      18,
      36,
      54,
      100,
      180,
      250,
      500,
      1000,
      1800,
      2500,
      3600,
      5000
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'goldfarbj@ou.org', 'mindy.berman@ou.org'],
    adminFromEmail: 'Yachad Moshava Ennismore <yachad@ou.org>',
    adminSubject: 'Yachad Moshava Ennismore Donation',
    userFromEmail: 'Yachad Moshava Ennismore <yachad@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2'
  },
  'yachad-bachutz': {
    template: 'yachad-program',
    emailTemplate: 'yachad-summer',
    title: 'Yachad BaChutz Donation Form',
    department: 'Yachad_Summer',
    campaign: "BaChutz",
    subCampaign: '',
    honoreeTemplate: 'yachad',
    thankYouTemplate: 'yachad',
    message: 'Thank you so much for your generous donation!',
      amounts: [
      18,
      36,
      54,
      100,
      180,
      250,
      500,
      1000,
      1800,
      2500,
      3600,
      5000
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org', 'goldfarbj@ou.org', 'mindy.berman@ou.org'],
    adminFromEmail: 'Yachad BaChutz <yachad@ou.org>',
    adminSubject: 'Yachad BaChutz Donation',
    userFromEmail: 'Yachad BaChutz <yachad@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2'
  },
  'yachad-concert21': {
    emailTemplate: 'yachad-concert21',
    title: 'An Evening to Benefit Yachad',
    department: 'Yachad',
    campaign: 'Battle of the Singers',
    message: 'Thank you for donating to yachad',
    amounts: [
      [1000, `Single Ticket`],
      [1800, `Couple Ticket`],
    ],
    adminEmails: ['saibelk@ou.org', 'herrmann@ou.org', 'njcd@ou.org'],
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    adminSubject: 'An Evening to benefit Yachad payment',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    userSubject: 'Thank you for your Yachad donation!',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          privacy: zBool(),
        })
      })
    },
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2'
  },
  'bat-mitzvah-chloe': {
    emailTemplate: 'bat-mitzvah-chloe',
    title: 'Chloe Heisler Bat mitzvah',
    department: 'Yachad_OurWay',
    subCampaign: 'Chloe Heisler',
    campaign: 'Bat Mitzvah Fundraiser',
    message: 'Thank you for your donation',
    amounts: [
      18,
      36,
      50,
      100,
      250
    ],
    adminEmails: [ 'federi@ou.org'],
    adminFromEmail: 'YACHAD <yachad@ou.org>',
    adminSubject: 'Chloe Heisler Bat mitzvah',
    userFromEmail: 'YACHAD <yachad@ou.org>',
    userSubject: 'Thank you for your donation!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2'
  },
  'jsu': {
    emailTemplate: 'jsu',
    title: 'JSU Donation Form',
    department: 'NCSY',
    campaign: 'JSU Donation',
    message: 'Thank you for your sponsorship.',
    adminEmails: ['devora@ncsy.org', 'birnbaume@ou.org', 'goldmani@ou.org', 'lernerj@ncsy.org'],
    adminFromEmail: 'JSU Donation Form <info@ncsy.org>',
    adminSubject: 'JSU Donation Form',
    userFromEmail: 'JSU Donation <info@ncsy.org>',
    userSubject: 'Thank you for your support',
    amounts: [
      18,
      25,
      50,
      100,
      180
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1'
  },
  'jsu-general': {
    emailTemplate: 'jsu',
    title: 'JSU Donation Form',
    department: 'NCSY',
    campaign: 'JSU Donation',
    message: 'Thank you for your sponsorship.',
    adminEmails: ['devora@ncsy.org', 'birnbaume@ou.org', 'goldmani@ou.org', 'lernerj@ncsy.org'],
    adminFromEmail: 'JSU Donation Form <info@ncsy.org>',
    adminSubject: 'JSU General Donation Form',
    userFromEmail: 'JSU Donation <info@ncsy.org>',
    userSubject: 'Thank you for your support',
    amounts: [
      18,
      25,
      50,
      100,
      180
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1'
  },
  'jsu-israel': {
    emailTemplate: 'jsu-israel',
    title: 'JSU Donation Form',
    department: 'NCSY',
    campaign: 'JSU Donation',
    message: 'Thank you for your partnership.',
    adminFromEmail: 'JSU Donation Form <info@jsu.org>',
    adminSubject: 'A payment has been made to JSU Donation form',
    userFromEmail: 'JSU Donation <info@jsu.org>',
    userSubject: 'Thank you for your support',
    adminEmails: (request:any) => {
      const to = 'info@ncsy.org,info@jsu.org,neiditchc@ncsy.org,simond@ncsy.org'.split(',')
      const mapping = {
        NCSY: 'lernerj@ncsy.org,adlersteinc@ncsy.org,info@ncsy.org',
        BenZakkai: 'drluchins@aol.com,marchuck@ncsy.org,Novamanor@aol.com,greenlandm@ncsy.org,beckerd@ncsy.org,goldmani@ou.org',
        NCSY_Alumni: 'marchuck@ncsy.org,lernerj@ncsy.org',
        NCSY_Argentina: 'martin@ncsy.org,contabilidad@ncsy.org',
        NCSY_Atlanta: 'Neiditch@ncsy.org',
        NCSY_AtlanticSeaboard: 'atsoffice@ncsy.org,lgluck@ncsy.org,mossb@ncsy.org',
        NCSY_Canada: 'Peter@ncsy.org,Budds@ncsy.ca,Melamedh@ncsy.org,saffer-allals@ncsy.org',
        NCSY_CentralEast: 'Centraleast@ncsy.org',
        NCSY_Chile: 'bengiom@ncsy.org',
        NCSY_Israel: 'ginsbergy@ncsy.org,Kahnm@ncsy.org',
        NCSY_Midwest: 'friedmans@ncsy.org,Levitanskym@ncsy.org,Midwest@ncsy.org,francojo@ncsy.org,strulowitza@ncsy.org,wedglej@ncsy.org',
        NCSY_Mexico: 'Danield@ncsy.org,Mexicodonations@ncsy.org,isenbergm@ncsy.org',
        NCSY_NationalGroup: 'gordond@ncsy.org,isenbergm@ncsy.org,lernerj@ncsy.org',
        NCSY_Tristate: 'ListmanE@ncsy.org,lernerj@ncsy.org,Tristate@ncsy.org,marchuck@ncsy.org,beth.nussbaum@ncsy.org',
        NCSY_NewEngland: 'weinstockd@ncsy.orgSandersg@ncsy.org',
        NCSY_Portland: 'spivakm@ncsy.org,Oregonoffice@ncsy.org',
        NCSY_Southern: 'levittev@ncsy.org,bardosl@ncsy.org,southernoffice@ncsy.org,rosem@ncsy.org',
        NCSY_Southwest: 'yaffee@ncsy.org,southwest@ncsy.org,steinbergk@ncsy.org,marchuck@ncsy.org',
        NCSY_WestCoast: 'tarkor@ncsy.org,wcoffice@ncsy.org,chana@ncsy.org,naimana@ncsy.org,goldmani@ou.org,bryan.borenstein@ncsy.org,steinera@ncsy.org',
        NCSY_Summer: 'tanzmane@ncsy.org,marchuck@ncsy.org,amy@ncsy.org,lernerj@ncsy.org,beth.nussbaum@ncsy.org',
      } as any
      const email = mapping[request.applications![0].department!]
      if (email) to.push(email)
      return to
    },
    amounts: [
      18,
      50,
      100,
      180,
      500,
      1000,
      1800,
      2500,
      3600,
    ],
    silverPopDatabaseId: 8492349,
    silverPopContactLists: [8492350],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1'
  },
  'jsu-southbay': {
    emailTemplate: 'jsu-southbay',
    title: 'JSU SouthBay Donation Form',
    department: 'NCSY_Norcal',
    campaign: 'JSU Emergency',
    message: 'Thank you for your partnership.',
    adminFromEmail: 'JSU southbay Donation Form <info@jsu.org>',
    adminSubject: 'A payment has been made to JSU southbay Donation form',
    userFromEmail: 'JSU Silicon Valley Donation <info@jsu.org>',
    userSubject: 'Thank you for your support',
    adminEmails: ['naimana@ncsy.org', 'simons@ncsy.org', 'steinera@ncsy.org', 'goldmani@ou.org'],
    amounts: [
      [{ USD: 360 }, `<span>Club sponsor</span><i>One month of club snacks, materials, and curriculum<i></i>`],
      [{ USD: 1500 }, `<span>Shabbat Dinner sponsor</span><i>A Friday-night dinner for 20–25 teens<i></i>`],
      [{ USD: 1000 }, `<span>Teen Experience Sponsor</span><i>Full scholarship for a Shabbat/weekend retreat<i></i>`],
      [{ USD: 2000 }, `<span>Israel Inspiration Sponsor</span><i>Major subsidy for one teen’s Israel summer trip<i></i>`],
      [{ USD: 2500 }, `<span>Club Champion</span><i>Supports one JSU club for an entire semester<i></i>`],
      [{ USD: 3600 }, `<span>Leadership Builder</span><i>Funds a cohort of teen leaders<i></i>`],
      [{ USD: 5000 }, `<span>Community Impact Partner</span><i>Year of programming across multiple schools<i></i>`],
      [{ USD: 10000 }, `<span>Visionary Sponsor</span><i>Launches a new JSU club or major regional initiative<i></i>`],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          contact: zBool().nullable().optional(),
        })
      })
    }
  },
  'jsu-jewishstudentunion': {
    emailTemplate: 'jsu-israel',
    title: 'JSU Atlanta Donation Form',
    department: 'NCSY_Atlanta',
    campaign: 'General Donation',
    message: 'Thank you for your partnership.',
    adminFromEmail: 'JSU Atlanta Donation Form <info@jsu.org>',
    adminSubject: 'A payment has been made to JSU Atlanta Donation form',
    userFromEmail: 'JSU Atlanta Donation <info@jsu.org>',
    userSubject: 'Thank you for your support',
    adminEmails: ['neiditchc@ncsy.org', 'simons@ncsy.org', 'goldmani@ou.org'],
    amounts: [
      18,
      50,
      100,
      180,
      500,
      1000,
      1800,
      2500
    ],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional(),
        })
      })
    }
  },
  'ncsy-jewishstudentunion': {
    emailTemplate: 'ncsy-jewishstudentunion',
    title: 'NCSY Atlanta Donation Form',
    department: 'NCSY_Atlanta',
    campaign: 'General Donation',
    message: 'Thank you for your partnership.',
    adminFromEmail: 'NCSY Atlanta Donation Form <info@jsu.org>',
    adminSubject: 'A payment has been made to NCSY Atlanta Donation form',
    userFromEmail: 'NCSY Atlanta Donation <info@jsu.org>',
    userSubject: 'Thank you for your support',
    adminEmails: ['neiditchc@ncsy.org', 'simons@ncsy.org', 'goldmani@ou.org'],
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      720,
      360,
      180
    ],
  },
  'bike-ncsy': {
    emailTemplate: 'bike-ncsy',
    title: 'Bike NCSY - Donate',
    department: 'NCSY_Alumni',
    campaign: 'Bike Ride',
    message: 'Thank you so much for your recent contribution to Bike NCSY 2022.',
    thankYouTemplate: 'bikencsy',
    adminEmails: ['bike@ncsy.org', 'marchuck@ncsy.org', 'chermakn@ncsy.org', 'goldmani@ou.org', 'lernerj@ncsy.org'],
    adminFromEmail: 'BikeNCSY <bike@ncsy.org>',
    adminSubject: 'New Donation - BikeNCSY',
    userFromEmail: 'BikeNCSY <bike@ncsy.org>',
    userSubject: 'Bike NCSY - Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-18' : 'UA-3009720-18'
  },
  'jaxncsy-teamncsy': {
    template: 'bike-ncsy',
    emailTemplate: 'jaxncsy-teamncsy',
    title: 'Bike NCSY - Donate',
    department: 'NCSY_Southern',
    campaign: 'Jacksonville Region',
    subCampaign: 'Bike Ride',
    message: 'Thank you so much for your recent contribution to Jacksonville NCSY.',
    adminEmails: ['tala@ncsy.org', 'goldmani@ou.org'],
    adminFromEmail: 'JacksonvilleNCSY <bike@ncsy.org>',
    adminSubject: 'New Donation - BikeNCSY',
    userFromEmail: 'JacksonvilleNCSY <bike@ncsy.org>',
    userSubject: 'Jacksonville NCSY - Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-18' : 'UA-3009720-18'
  },
  'bikepr-ncsy': {
    emailTemplate: 'bikepr-ncsy',
    title: 'Bike Puerto Rico NCSY - Donate',
    department: 'NCSY_Tristate',
    campaign: 'Bike NCSY PR',
    message: 'Thank you so much for your recent contribution to Bike NCSY Puerto Rico 2023.',
    thankYouTemplate: 'bikencsy',
    adminEmails: ['bike@ncsy.org', 'schwartzs@ncsy.org', 'goldmani@ou.org', 'marchuck@ncsy.org', 'beth.nussbaum@ncsy.org'],
    adminFromEmail: 'BikeNCSY <bike@ncsy.org>',
    adminSubject: 'New Donation - BikeNCSY Puerto Rico',
    userFromEmail: 'BikeNCSY Puerto Rico <bike@ncsy.org>',
    userSubject: 'Bike NCSY Puerto Rico - Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'GTM-M2D3LN9' : 'GTM-K8CM6PX'
  },
  'ncsy-pledge': {
    template: 'general-pledge',
    emailTemplate: 'ncsy',
    title: 'NCSY Payment Dues',
    department: '',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for donating to NCSY',
    adminEmails: ['info@ncsy.org', 'goldmani@ou.org', 'listmane@ncsy.org'],
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'NCSY Payment Dues',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
  'ncsy-winter21': {
    emailTemplate: 'ncsy',
    title: 'NCSY Donation Form',
    department: 'NCSY',
    campaign: 'ADM NCSY Winter Campaign',
    subCampaign: 'Direct Mail',
    message: 'Thank you for donating to NCSY',
    adminEmails: ['info@ncsy.org', 'lernerj@ncsy.org'],
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'NCSY payment',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
  'ncsy-ote-ats': {
    emailTemplate: 'ncsy-ote-ats',
    title: 'OTE ATS Sponsor Opportunities 2021',
    department: 'NCSY_AtlanticSeaboard',
    campaign: 'Rappelling',
    subCampaign: '',
    message: 'Thank you so much for your recent contribution to Atlantic Seabord OTE 2021',
    amounts: [
      20000,
      10000,
      5000,
      3600,
      2500,
      1800,
      1000,
      500,
      250
    ],
    adminEmails: ['info@ncsy.org', 'goldmani@ou.org', 'lauren@ncsy.org', 'freundm@ncsy.org', 'rabbiyitz@ncsy.org', 'asaf@ncsy.org'],
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'New submission from Sponsor Opportunities 2021 - ATS',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support Over The Edge - ATS',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-18' : 'UA-3009720-18'
  },
  'atlanticseaboard-concert': {
    emailTemplate: 'atlanticseaboard-concert',
    title: 'NCSY Atlantic Seaboard - Concert',
    department: 'NCSY_AtlanticSeaboard',
    campaign: 'Concert',
    subCampaign: 'Baltimore',
    message: '<h4 style="text-align:center;">Thank you for reserving seats at the NCSY Atlantic Seaboard - Concert. We look forward to seeing you there!</h4>',
    amounts: [
      20000,
      10000,
      5000,
      3600,
      2500,
      1800,
      1000,
      500,
      250
    ],
    adminEmails: ['atsconcert@ncsy.org'],
    adminFromEmail: 'NCSY <atsconcert@ncsy.org>',
    adminSubject: 'New submission from NCSY Atlantic Seaboard - Concert',
    userFromEmail: 'NCSY <atsconcert@ncsy.org>',
    userSubject: 'NCSY Atlantic Seaboard - Concert',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-18' : 'UA-3009720-18',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          sponsorshipAmount: z.string().nullable().optional(),
          sponsorshipType: z.string().nullable().optional(),
          sponsorshipNote: z.string().nullable().optional(),
          freeTickets: z.string().nullable().optional(),
          ticketPrice: z.string().nullable().optional(),
          ticketsQnt: z.string().nullable().optional(),
          country: z.string().nullable().optional(),
          street: z.string().nullable().optional(),
          street2: z.string().nullable().optional(),
          city: z.string().nullable().optional(),
          state: z.string().nullable().optional(),
          zip: z.string().nullable().optional(),
          adMessage: z.string().nullable().optional(),
        })
      })
    }
  },
  'ncsy-ignite-neiman': {
    emailTemplate: 'ncsy_neiman',
    title: 'NCSY Ignite - Membership',
    department: 'NCSY',
    campaign: 'Ignite Legacy Society',
    subCampaign: '',
    honoreeTemplate: 'ncsy',
    message: 'Thank you so much for your recent contribution to NCSY Ignite',
    amounts: [
      83.33,
      166.66,
      250
    ],
    adminEmails: (request) => {
      const to = 'ignite@ncsy.org,heger.israel@ncsy.org,listmane@ncsy.org,Beth.Nussbaum@ncsy.org,lernerj@ncsy.org'.split(',')
      const mapping = {
        NCSY_WestCoast: 'naimana@ncsy.org'
      } as any
      const email = mapping[request.applications![0].department!]
      if (email) to.push(email)
      return to
    },
    adminFromEmail: 'NCSY <ignite@ncsy.org>',
    adminSubject: 'New submission from NCSY Ignite',
    userFromEmail: 'NCSY <ignite@ncsy.org>',
    userSubject: 'Thank you for your support NCSY Ignite',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          SwagSameAddess: z.string().nullable().optional(),
          advisorOrAlumni: z.string().nullable().optional(),
          spouseFirstName: z.string().nullable(),
          spouseLastName: z.string().nullable(),
          birthdate: z.string().nullable().optional(),
          country: z.string().nullable().optional(),
          street: z.string().nullable().optional(),
          street2: z.string().nullable().optional(),
          city: z.string().nullable().optional(),
          state: z.string().nullable().optional(),
          zip: z.string().nullable().optional(),
          designation: z.string().nullable().optional(),
          subDesignation: z.string().nullable().optional(),
          anonymous: zBool().nullable().optional(),
        })
      })
    },
  },
  'ncsy-summer-ignite-society': {
    emailTemplate: 'ncsy_ignite',
    title: 'NCSY Summer Ignite - Membership',
    department: 'NCSY_Summer',
    campaign: 'Ignite Legacy Society',
    subCampaign: '',
    honoreeTemplate: 'ncsy',
    message: 'Thank you so much for your recent contribution to NCSY Summer Ignite',
    amounts: [
      83.33,
      166.66,
      250
    ],
    adminEmails: (request) => {
      const to = 'tanzmane@ncsy.org,glicksmany@ncsy.org,weinbergj@ncsy.org,marchuck@ncsy.org,lernerj@ncsy.org,Beth.Nussbaum@ncsy.org'.split(',')
      const mapping = {
        NCSY_WestCoast: 'naimana@ncsy.org'
      } as any
      const email = mapping[request.applications![0].department!]
      if (email) to.push(email)
      return to
    },
    adminFromEmail: 'NCSY Summer <info@ncsy.org>',
    adminSubject: 'New submission from NCSY Summer Ignite',
    userFromEmail: 'NCSY Summer <info@ncsy.org>',
    userSubject: 'Thank you for your support NCSY Summer Ignite',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          SwagSameAddess: z.string().nullable().optional(),
          advisorOrAlumni: z.string().nullable().optional(),
          favoriteMemory: z.string().nullable().optional(),
          spouseFirstName: z.string().nullable().optional(),
          spouseLastName: z.string().nullable().optional(),
          hearAboutUs: z.string().nullable().optional(),
          birthdate: z.string().nullable().optional(),
          country: z.string().nullable().optional(),
          street: z.string().nullable().optional(),
          street2: z.string().nullable().optional(),
          city: z.string().nullable().optional(),
          state: z.string().nullable().optional(),
          zip: z.string().nullable().optional(),
          designation: z.string().nullable(),
          subDesignation: z.string().nullable().optional(),
          anonymous: zBool().nullable().optional(),
        })
      })
    },
  },
  'ncsy-ignite-ats': {
    emailTemplate: 'ncsy_ignite',
    title: 'NCSY Ignite - Membership',
    department: 'NCSY_AtlanticSeaboard',
    campaign: 'Ignite Legacy Society',
    subCampaign: '',
    honoreeTemplate: 'ncsy',
    message: 'Thank you so much for your recent contribution to NCSY Atlanticseaboard Ignite',
    amounts: [
      83.33,
      166.66,
      250
    ],
    adminEmails: ['atsoffice@ncsy.org', 'lernerj@ncsy.org', 'mossb@ncsy.org', 'mizrahia@ncsy.org', 'lauren@ncsy.org'],
    adminFromEmail: 'NCSY Atlanticseaboard <atsoffice@ncsy.org>',
    adminSubject: 'New submission from NCSY Atlanticseaboard Ignite',
    userFromEmail: 'NCSY Atlanticseaboard <atsoffice@ncsy.org>',
    userSubject: 'Thank you for your support NCSY Atlanticseaboard Ignite',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          SwagSameAddess: z.string().nullable().optional(),
          advisorOrAlumni: z.string().nullable().optional(),
          // favoriteMemory: z.string().nullable().optional(),
          spouseFirstName: z.string().nullable().optional(),
          spouseLastName: z.string().nullable().optional(),
          hearAboutUs: z.string().nullable().optional(),
          // birthdate: z.string().nullable().optional(),
          country: z.string().nullable().optional(),
          street: z.string().nullable().optional(),
          street2: z.string().nullable().optional(),
          city: z.string().nullable().optional(),
          state: z.string().nullable().optional(),
          zip: z.string().nullable().optional(),
          designation: z.string().nullable(),
          // subDesignation: z.string().nullable().optional(),
          anonymous: zBool().nullable().optional(),
        })
      })
    },
  },
  'ncsy-ignite': {
    emailTemplate: 'ncsy_ignite',
    title: 'NCSY Ignite - Membership',
    department: 'NCSY',
    campaign: 'Ignite',
    subCampaign: '',
    message: 'Thank you so much for your recent contribution to NCSY Ignite',
    amounts: [
      83.33,
      166.66,
      250
    ],
    adminEmails: ['tanzmane@ncsy.org', 'glicksmany@ncsy.org', 'gluckl@ncsy.org', 'naimana@ncsy.org', 'SHess@ncsy.org', 'lernerj@ncsy.org'],
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'New submission from NCSY Ignite',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support NCSY Ignite',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          spouseFirstName: z.string().nullable().optional(),
          spouseLastName: z.string().nullable().optional(),
          hearAboutUs: z.string().nullable().optional(),
        })
      })
    },
  },
  'ncsy-ignite-society': {
    emailTemplate: 'ncsy_ignite',
    title: 'NCSY Ignite - Membership',
    department: 'NCSY',
    campaign: 'Ignite',
    subCampaign: '',
    message: 'Thank you so much for your recent contribution to NCSY Ignite',
    amounts: [
      1800,
      3600,
      5000
    ],
    adminEmails: ['tanzmane@ncsy.org', 'schwartzs@ncsy.org', 'naimana@ncsy.org', 'SHess@ncsy.org', 'lernerj@ncsy.org'],
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'New submission from NCSY Ignite Society',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support NCSY Ignite Society',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          spouseFirstName: z.string().nullable().optional(),
          spouseLastName: z.string().nullable().optional(),
          hearAboutUs: z.string().nullable().optional(),
          designation: z.string().nullable(),
          subDesignation: z.string().nullable().optional(),
        })
      })
    }
  },
  'ncsy-ignitekls': {
    emailTemplate: 'ncsy_ignite',
    title: 'NCSY Ignite KLS - Membership',
    department: 'NCSY_Summer',
    campaign: 'Kollel Legacy',
    subCampaign: '',
    message: 'Thank you so much for your recent contribution to NCSY Ignite KLS',
    amounts: [
      83.33,
      166.66,
      250
    ],
    adminEmails: ['tanzmane@ncsy.org', 'glicksmany@ncsy.org', 'marchuck@ncsy.org', 'lernerj@ncsy.org', 'Beth.Nussbaum@ncsy.org'],
    adminFromEmail: 'NCSY KLS <info@ncsy.org>',
    adminSubject: 'New submission from NCSY Ignite KLS',
    userFromEmail: 'NCSY KLS <info@ncsy.org>',
    userSubject: 'Thank you for your support NCSY Ignite KLS',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1'
  },
  'ncsy-lasvegas': {
    emailTemplate: 'ncsy',
    title: 'NCSY Donation Form',
    department: 'NCSY_LasVegas',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for donating to NCSY',
    adminEmails: ['info@ncsy.org'],
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'NCSY payment',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support',
    amounts: [
      1800,
      360,
      180,
      72,
      36,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
  'ncsy-gala': {
    emailTemplate: 'ncsy',
    title: 'NCSY Gala Donation Form',
    department: 'NCSY',
    campaign: 'NCSY Dinner',
    subCampaign: '',
    message: 'Thank you for donating to NCSY Gala',
    adminEmails: ['info@ncsy.org', 'gala@ncsy.org', 'lernerj@ncsy.org'],
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'NCSY GALA payment',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
  'ncsy-passover': {
    emailTemplate: 'ncsy',
    title: 'NCSY Donation Form',
    department: 'NCSY',
    campaign: 'ADM NCSY Passover Campaign',
    subCampaign: 'Direct Mail',
    message: 'Thank you for donating to NCSY',
    adminEmails: ['info@ncsy.org', 'lernerj@ncsy.org'],
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'NCSY payment',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
  'ncsy-israel': {
    emailTemplate: 'ncsy_israel',
    title: 'NCSY Israel Donation Form',
    department: 'NCSY_Israel',
    campaign: 'General Donation',
    subCampaign: '',
    thankYouTemplate: 'ncsyisrael',
    message: 'Thank you for donating to NCSY',
    adminEmails: ['info@ncsy.org', 'ginsbergy@ncsy.org', 'kahnm@ncsy.org'],
    adminFromEmail: 'NCSY Israel<Israel@ncsy.org>',
    adminSubject: 'NCSY Israel - Donation',
    userFromEmail: 'NCSY Israel<Israel@ncsy.org>',
    userSubject: 'Thank you for your support',
    amounts: {
      USD: [
        18,
        36,
        100,
        250,
        500,
      ],
      ILS: [
        18,
        36,
        100,
        250,
        500,
      ],
    },
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          designation: z.string().optional(),
          subDesignation: z.string().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    }
  },
  'ncsy-israel-region': {
    template: 'ncsy-region-template',
    emailTemplate: 'ncsy_israel',
    title: 'NCSY Israel Donation Form',
    department: 'NCSY_Israel',
    campaign: 'General Donation',
    subCampaign: '',
    thankYouTemplate: 'ncsyisrael',
    message: 'Thank you for donating to NCSY',
    adminEmails: ['info@ncsy.org', 'ginsbergy@ncsy.org', 'kahnm@ncsy.org'],
    adminFromEmail: 'NCSY Israel<Israel@ncsy.org>',
    adminSubject: 'NCSY Israel - Donation',
    userFromEmail: 'NCSY Israel<Israel@ncsy.org>',
    userSubject: 'Thank you for your support',
    amounts: {
      USD: [
        18,
        36,
        100,
        250,
        500,
      ],
      ILS: [
        18,
        36,
        100,
        250,
        500,
      ],
    },
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          designation: z.string().optional(),
          subDesignation: z.string().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    }
  },
  'ncsy-israel-sparks': {
    emailTemplate: 'ncsy_israel',
    title: 'NCSY Israel Donation Form',
    department: 'NCSY_Israel',
    campaign: 'Israel Spark',
    subCampaign: '',
    message: 'Thank you for donating to NCSY. A receipt will be sent to your email address',
    adminEmails: ['info@ncsy.org', 'ginsbergy@ncsy.org', 'goldmani@ou.org', 'kahnm@ncsy.org'],
    adminFromEmail: 'NCSY Israel<Israel@ncsy.org>',
    adminSubject: 'NCSY Israel - Donation',
    userFromEmail: 'NCSY Israel<Israel@ncsy.org>',
    userSubject: 'Thank you for your support',
    defaultCurrency: 'ILS' as Currency,
    amounts: [
      [18, `Bronze`],
      [50, `Silver`],
      [100, `Gold`],
      [180, `Platinum`],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          conditions: zBool().nullable(),
          shirtSize: z.string().nullable().optional(),
          dedication: z.string().nullable().optional(),
        })
      })
    },
  },
  'ncsy-israel-bencher': {
    emailTemplate: 'ncsy_israel',
    title: 'NCSY Israel Donation Form',
    department: 'NCSY_Israel',
    campaign: 'Bencher Sponsorship',
    subCampaign: '',
    message: 'Thank you for donating to NCSY',
    adminEmails: ['info@ncsy.org', 'ginsbergy@ncsy.org', 'neumanj@ou.org', 'kahnm@ncsy.org', 'goldmani@ou.org'],
    adminFromEmail: 'NCSY Israel<Israel@ncsy.org>',
    adminSubject: 'NCSY Israel - Donation',
    userFromEmail: 'NCSY Israel<Israel@ncsy.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2',
    amounts: {
      USD: [
        18
      ],
    },
  },
  'ncsy-stl': {
    emailTemplate: 'ncsy-stl',
    title: 'JSU St Louis MeatUp event',
    department: 'NCSY_STL',
    campaign: 'Meat Up',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['rovinskym@jsu.org', 'zitins@jsu.org','minoffa@ncsy.org', 'stlinfo@jsu.org'],
    adminFromEmail: 'NCSY <RabbiMike@jsu.org>',
    adminSubject: 'A payment has been made to JSU St Louis MeatUp event',
    userFromEmail: 'St Louis MeatUp event <RabbiMike@jsu.org>',
    userSubject: ' Thank you for registering for JSUs Meat Up',
    amounts: [
      [{ USD: 5400 }, `$5,400 Sponsorship`],
      [{ USD: 3600 }, `$3,600 Sponsorship`],
      [{ USD: 1800 }, `$1,800 Sponsorship`],
      [{ USD: 1250 }, `$1250 Sponsorship`],
      [{ USD: 500 }, `$500 Sponsorship`],
      [{ USD: 1 }, `Just tickets`]
    ],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          ticketsQnt: z.string().nullable().optional(),
          ticketsVIPQnt: z.string().nullable().optional(),
          coupon: z.string().nullable().optional(),
          donationAmount: z.string().nullable().optional(),
          isGlutenFree: zBool().nullable().optional(),
          isVegetarian: zBool().nullable().optional(),
          comments: z.string().nullable().optional(),
        })
      })
    },
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
  },
'jsustl-gala': {
    emailTemplate: 'jsustl-gala',
    title: 'JSU STL Gala Event',
    department: 'NCSY_STL',
    campaign: 'Gala Dinner',
    subCampaign: '',
    message: 'Thank you for registering for JSU St Louis Gala event.<br><br> We look forward to seeing you there.',
    adminEmails: ['AlanaM@jsustl.org','SamZ@jsustl.org', 'RabbiMike@jsustl.org', 'goldmani@ou.org'],
    adminFromEmail: 'JSUSTL <RabbiMike@jsu.org>',
    adminSubject: 'A payment has been made to JSU St Louis Gala event',
    userFromEmail: 'St Louis Gala event <RabbiMike@jsu.org>',
    userSubject: ' Thank you for registering for JSU St Louis Gala',
    amounts: [
      [15000, `VISIONARY <span>(Includes dedication of the Tribute Journal, naming of a JSU Club for a Year, 1 Table at the Gala, and 2 Gold Tribute Pages)</span>`],
      [10000, `TRAILBLAZER <span>(Includes naming of a JSU Club for a Year, 1 Table at the Gala, and 2 Gold Tribute Pages)</span>`],
      [5000, `AMPLIFIER <span>(Includes dedication of 2 issues of the JSU E-Newsletter, 1 Table at the Gala, and 2 Gold Tribute Pages)</span>`],
      [3600, `CHAMPION <span>(Includes dedication of 2 issues of the JSU E-Newsletter, 1 Table at the Gala, and 1 Gold Tribute Page)</span>`],
      [2500, `ILLUMINATOR <span>(Includes dedication of 2 issues of the JSU E-Newsletter, 4 Tickets to the Gala, and 1 Silver Tribute Page)</span>`],
      [1800, `CHAI <span>(Includes dedication of an issue of the JSU E-Newsletter and 4 Tickets to the Gala)</span>`],
      [1250, `GOLD PAGE <span>(Includes 2 Tickets to the Gala)</span>`],
      [750, `SILVER PAGE`],
      [613, `FULL PAGE`],
      [360, `HALF PAGE`],
      [180, `QUARTER PAGE`],
     ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          AttendInPerson: z.string().nullable().optional(),
          guests: z.string().nullable().optional(),
          ticketsQnt: z.string().nullable().optional(),
          coupon: z.string().optional().nullable(),
          Notes: z.string().nullable().optional(),
          seatingRequests: z.string().nullable().optional(),
          selectedDescription: z.string().nullable().optional(),
        })
      })
    }
  },
  'ncsy-israel-solomon-concert': {
    emailTemplate: 'ncsy_israel_solomon_concert',
    title: 'NCSY Israel Solomon Brothers Concert',
    department: 'NCSY_Israel',
    campaign: 'Concert',
    subCampaign: '',
    message: 'Thank you for registering!<br>The concert will take place at Nefesh B\'Nefesh Aliyah Campus (Cinema City, Jerusalem) on Sunday, April 14th at 19:15.',
    adminEmails: ['info@ncsy.org', 'ginsbergy@ncsy.org', 'goldmani@ou.org', 'kahnm@ncsy.org'],
    adminFromEmail: 'NCSY Israel<Israel@ncsy.org>',
    adminSubject: 'NCSY Israel - Concert',
    userFromEmail: 'NCSY Israel<Israel@ncsy.org>',
    userSubject: 'Thank you for your support',
    defaultCurrency: 'ILS' as Currency,
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2',
    amounts: {
      ILS: [
        500,
        720,
        1000,
        1800,
      ],
    },
    customValidations (form) {
      return form.extend({
        custom: z.object({
          ticketsQnt: z.string().nullable().optional(),
          ticketsVIPQnt: z.string().nullable().optional(),
          coupon: z.string().nullable().optional(),
          donationAmount: z.string().nullable().optional(),
          isGapStudent: zBool().nullable().optional(),
          Notes: z.string().nullable().optional(),
          seatingRequests: z.string().nullable().optional(),
        })
      })
    }
  },
  'ncsy-summer': {
    emailTemplate: 'ncsy_summer',
    title: 'NCSY Summer Donation',
    honoreeTemplate: 'ncsysummer',
    department: 'NCSY_Summer',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your donation.<br><br> – The NCSY Summer Team',
    amounts: [
      [18, ``],
      [36, ``],
      [54, ``],
      [180, ``],
      [250, ``],
      [500, ``],
      [1000, ``],
      [1800, `Friend`],
      [2500, `Sponsor`],
      [3600, `Patron`],
      [5000, `Benefactor`],
      [10000, `Partner`],
      [18000, `Chai Supporter`],
      [25000, `Ambassador`],
      [36000, `Builder`],
      [54000, `Founder`]
    ],
    adminEmails: [ 'ouinternet@ou.org', 'chernikoffj@ou.org', 'tanzmane@ncsy.org', 'goldmani@ou.org', 'amy@ncsy.org', 'marchuck@ncsy.org', 'lernerj@ncsy.org', 'Beth.Nussbaum@ncsy.org', 'wielgusa@ncsy.org', 'adira.glicksman@ncsy.org'],
    adminFromEmail: 'NCSY Summer Donation <summer@ncsy.org>',
    adminSubject: 'A Donation has been made to to NCSY Summer Programs',
    userFromEmail: 'NCSY Summer <summer@ncsy.org>',
    userSubject: 'Thank You for Donating to NCSY Summer',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'ncsy-summer-kumtziz': {
    emailTemplate: 'ncsy_summer_kumtziz',
    title: 'NCSY Summer Kumtziz Donation',
    honoreeTemplate: 'ncsysummer',
    department: 'NCSY_Summer',
    campaign: 'General Donation',
    subCampaign: '',
    message: "Thank you for partnering with us for this year's Tisha B'Av Kumzitz of the World. <br><br>While we pray that this year Tisha B'Av will no longer be necessary - that mourning will be replaced by redemption - your support of NCSY Summer helps inspire the next generation and brings us one step closer to that hope-filled future.",
    amounts: [
      180,
      360,
      500,
      1000,
      1800,
      3600
    ],
    adminEmails: ['tanzmane@ncsy.org', 'weinbergj@ncsy.org', 'marchuck@ncsy.org', 'lernerj@ncsy.org', 'Beth.Nussbaum@ncsy.org'],
    adminFromEmail: 'NCSY Summer Kumtziz Donation <summer@ncsy.org>',
    adminSubject: 'A Donation has been made to to NCSY Summer Programs Kumtziz',
    userFromEmail: 'NCSY Summer <summer@ncsy.org>',
    userSubject: 'Thank You for Donating to NCSY Summer',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          anonymous: zBool().nullable().optional(),
        })
      })
    },
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'ncsy-unite': {
    emailTemplate: 'ncsy_summer',
    title: 'NCSY Summer Unite Donation',
    honoreeTemplate: 'ncsysummer',
    department: 'NCSY_Summer',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your donation.<br><br> – The NCSY Summer Team',
    amounts: [
      180,
      360,
      500,
      1000,
      1800,
      3600
    ],
    adminEmails: ['ouinternet@ou.org', 'chernikoffj@ou.org', 'tanzmane@ncsy.org', 'goldmani@ou.org', 'weinbergj@ncsy.org', 'marchuck@ncsy.org', 'lernerj@ncsy.org', 'Beth.Nussbaum@ncsy.org'],
    adminFromEmail: 'NCSY Unite Summer Donation <summer@ncsy.org>',
    adminSubject: 'A Donation has been made to to NCSY Unite Summer Programs',
    userFromEmail: 'NCSY Unite Summer <summer@ncsy.org>',
    userSubject: 'Thank You for Donating to NCSY Unite Summer',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'ncsy-summer-ignite': {
    emailTemplate: 'ncsy_summer',
    title: 'NCSY Summer Donation',
    honoreeTemplate: 'ncsysummer',
    department: 'NCSY_Summer',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your donation.<br><br> – The NCSY Summer Team',
    amounts: [
      [1800, `Ambassador`],
      [3600, `Patron`],
      [5000, `Benefactor`],
      [10000, `Partner`],
      [18000, `Chai Supporter`],
      [36000, `Builder`],
      [54000, `Founder`],
      [72000, `Pillar`],
      [100000, `Guardian`],
      [180000, `Visionary`],
    ],
    adminEmails: ['ouinternet@ou.org', 'chernikoffj@ou.org', 'tanzmane@ncsy.org', 'goldmani@ou.org', 'marchuck@ncsy.org', 'lernerj@ncsy.org', 'Beth.Nussbaum@ncsy.org'],
    adminFromEmail: 'NCSY Summer Donation <summer@ncsy.org>',
    adminSubject: 'A Donation has been made to to NCSY Summer Programs',
    userFromEmail: 'NCSY Summer <summer@ncsy.org>',
    userSubject: 'Thank You for Donating to NCSY Summer',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'ncsy-summer-programs': {
    emailTemplate: 'ncsy-summer-programs',
    adminEmails: ['ouinternet@ou.org', 'chernikoffj@ou.org', 'tanzmane@ncsy.org', 'miriamw@ou.org', 'lernerj@ncsy.org', 'Beth.Nussbaum@ncsy.org'],
    adminFromEmail: 'NCSY Summer Programs Donation <summer@ncsy.org>',
    adminSubject: 'A Donation has been made to to NCSY Summer Programs',
    userFromEmail: 'NCSY Summer Programs <summer@ncsy.org>',
    userSubject: 'Thank You for Donating to NCSY Summer Programs',
    title: 'NCSY Summer Programs',
    amounts: [
      1800,
      1000,
      750,
      500,
      360,
      250,
      100,
      72,
      50,
      36
    ],
    department: 'NCSY_Summer',
    campaign: 'ADM NCSY Summer Programs Campaign',
    subCampaign: 'Telemarketing',
    message: 'Thank you for your donation.<br><br> – The NCSY Summer Team'
  },
  'ncsy-kls': {
    emailTemplate: 'ncsy-kls',
    title: 'NCSY KLS Donation Form',
    department: 'NCSY_Summer',
    campaign: 'Kollel Legacy',
    subCampaign: '',
    message: 'Thank you for donating to NCSY KLS',
    adminEmails: ['tanzmane@ncsy.org', 'benovitzm@ncsy.org', 'weinbergj@ncsy.org', 'marchuck@ncsy.org', 'lernerj@ncsy.org', 'Beth.Nussbaum@ncsy.org'],
    adminFromEmail: 'NCSY <summer@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Kollel Legacy Society',
    userFromEmail: 'NCSY <summer@ncsy.org>',
    userSubject: 'NCSY Kollel Legacy Society',
    amounts: [
      [8000, `KLS Family`],
      [1000, `KLS Builder`],
      [250, `KLS Young Leader`],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
  'ncsy-relief': {
    emailTemplate: 'ncsy-relief',
    title: 'OU Relief Donation Form',
    department: 'CommunityProjectsAndPartnerships',
    campaign: 'Relief Missions',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['miriamw@ou.org', 'goldmani@ou.org', 'katze@ncsy.org', 'glucksmanf@ncsy.org', 'rebecca.diamond@ou.org'],
    adminFromEmail: 'OU Relief <info@ou.org>',
    adminSubject: 'A payment has been made to OU Relief Sponsor A Mission',
    userFromEmail: 'OU Relief <info@ou.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      [118000, `“Ready Team” Program`],
      [72000, `Regional Mission Series`],
      [18000, `Relief Mission`],
      [10000, `Relief Missions Handbook`],
      [7500, `Certification Program`],
      [5000, `Travel Sponsor: One Mission`],
      [1800, `Teen`],
      [1000, `Travel Subsidy`],
    ],
    silverPopDatabaseId: 3954994,
    silverPopContactLists: [5927999],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1'
  },
  'ncsy-relief-give': {
    emailTemplate: 'ncsy-relief-give',
    title: 'OU Relief Donation Form',
    department: 'CommunityProjectsAndPartnerships',
    campaign: 'Relief Missions',
    subCampaign: 'Direct Mail',
    message: 'Thank you for your support!',
    adminEmails: ['miriamw@ou.org', 'goldmani@ou.org', 'katze@ncsy.org', 'glucksmanf@ncsy.org', 'rebecca.diamond@ou.org'],
    adminFromEmail: 'OU Relief <info@ncsy.org>',
    adminSubject: 'A payment has been made to OU Relief Sponsor A Mission',
    userFromEmail: 'OU Relief <info@ncsy.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      72,
      100,
      360,
      500,
      1000,
    ],
    silverPopDatabaseId: 3954994,
    silverPopContactLists: [5927999],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1'
  },
  'ncsy-missions-directmail': {
    emailTemplate: 'ncsy-relief',
    title: 'NCSY Relief Donation Form',
    department: 'NCSY_ReliefMissions',
    campaign: 'ADM NCSY Relief Missions Campaign',
    subCampaign: 'Direct Mail',
    message: 'Thank you for your support!',
    adminEmails: ['miriamw@ou.org', 'goldmani@ou.org', 'katze@ncsy.org', 'glucksmanf@ncsy.org', 'rebecca.diamond@ou.org'],
    adminFromEmail: 'NCSY Relief <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Relief Missions - Direct Mail',
    userFromEmail: 'NCSY Relief <info@ncsy.org>',
    userSubject: 'Thank you for your support!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1'
  },
  'ncsy-westcoast-summer-scholarships': {
    emailTemplate: 'ncsy',
    title: 'West Coast NCSY - Summer Scholarships',
    department: 'NCSY_WestCoast',
    campaign: 'Summer Scholarships',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['info@ncsy.org'],
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Midwest',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      10000,
      5000,
      2500,
      1800,
      1000,
      500,
      360,
      100,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
  },
  'ncsy-westcoast-alumni': {
    emailTemplate: 'ncsy',
    title: '$500 from 500 Alumni and Advisors',
    department: 'NCSY_WestCoast',
    campaign: 'Fortunate 500',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['info@ncsy.org'],
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY WestCoast Fortunate 500',
    userFromEmail: 'WestCoast <info@ncsy.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      500
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          currentOccupation: z.string().nullable().optional(),
          listingName: z.string().nullable().optional()
        })
      })
    },
  },
  'ncsy': {
    emailTemplate: 'ncsy',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Donation Form',
    department: 'NCSY',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: (request:any) => {
      const to = 'info@ncsy.org'.split(',')
      const mapping = {
        NCSY: 'lernerj@ncsy.org,adlersteinc@ncsy.org,info@ncsy.org',
        BenZakkai: 'drluchins@aol.com,lernerj@ncsy.org,Novamanor@aol.com,greenlandm@ncsy.org,beckerd@ncsy.org',
        NCSY_Alumni: 'marchuck@ncsy.org,lernerj@ncsy.org',
        NCSY_Argentina: 'martin@ncsy.org,contabilidad@ncsy.org',
        NCSY_Atlanta: 'Neiditch@ncsy.org',
        NCSY_AtlanticSeaboard: 'atsoffice@ncsy.org,lgluck@ncsy.org,mossb@ncsy.org',
        NCSY_Canada: 'Peter@ncsy.org,Budds@ncsy.ca,Melamedh@ncsy.org,saffer-allals@ncsy.org',
        NCSY_CentralEast: 'Centraleast@ncsy.org',
        NCSY_Chile: 'bengiom@ncsy.org',
        NCSY_Israel: 'ginsbergy@ncsy.org,Kahnm@ncsy.org',
        NCSY_Midwest: 'friedmans@ncsy.org,Levitanskym@ncsy.org,Midwest@ncsy.org,francojo@ncsy.org,strulowitza@ncsy.org,wedglej@ncsy.org',
        NCSY_Mexico: 'Danield@ncsy.org,Mexicodonations@ncsy.org,isenbergm@ncsy.org',
        NCSY_NationalGroup: 'gordond@ncsy.org,isenbergm@ncsy.org,lernerj@ncsy.org',
        NCSY_Tristate: 'ListmanE@ncsy.org,lernerj@ncsy.org,Tristate@ncsy.org,marchuck@ncsy.org,beth.nussbaum@ncsy.org',
        NCSY_NewEngland: 'weinstockd@ncsy.orgSandersg@ncsy.org',
        NCSY_Portland: 'spivakm@ncsy.org,Oregonoffice@ncsy.org',
        NCSY_Southern: 'levittev@ncsy.org,bardosl@ncsy.org,southernoffice@ncsy.org,rosem@ncsy.org',
        NCSY_Southwest: 'yaffee@ncsy.org,southwest@ncsy.org,steinbergk@ncsy.org,marchuck@ncsy.org',
        NCSY_WestCoast: 'tarkor@ncsy.org,wcoffice@ncsy.org,chana@ncsy.org,naimana@ncsy.org,goldmani@ou.org,bryan.borenstein@ncsy.org,steinera@ncsy.org',
        NCSY_Summer: 'tanzmane@ncsy.org,marchuck@ncsy.org,amy@ncsy.org,lernerj@ncsy.org,beth.nussbaum@ncsy.org',
      } as any
      const email = mapping[request.applications![0].department!]
      if (
        request.applications![0].department! === 'NCSY' &&
        request.applications![0].campaign! === 'Innovation Lab'
      ) {
        to.push('jonese@ncsy.org');
      }
      if (email) to.push(email)
      return to
    },
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
  },
   'ncsy-general': {
    emailTemplate: 'ncsy',
    honoreeTemplate: 'ncsy',
    thankYouTemplate: 'ncsy-general',
    title: 'NCSY Donation Form',
    department: 'NCSY',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: (request:any) => {
      const to = 'info@ncsy.org'.split(',')
      const mapping = {
        NCSY: 'lernerj@ncsy.org,adlersteinc@ncsy.org,info@ncsy.org',
        BenZakkai: 'drluchins@aol.com,lernerj@ncsy.org,Novamanor@aol.com,greenlandm@ncsy.org,beckerd@ncsy.org',
        NCSY_Alumni: 'marchuck@ncsy.org,lernerj@ncsy.org',
        NCSY_Argentina: 'martin@ncsy.org,contabilidad@ncsy.org',
        NCSY_Atlanta: 'Neiditch@ncsy.org',
        NCSY_AtlanticSeaboard: 'atsoffice@ncsy.org,lgluck@ncsy.org,mossb@ncsy.org',
        NCSY_Canada: 'Peter@ncsy.org,Budds@ncsy.ca,Melamedh@ncsy.org,saffer-allals@ncsy.org',
        NCSY_CentralEast: 'Centraleast@ncsy.org',
        NCSY_Chile: 'bengiom@ncsy.org',
        NCSY_Israel: 'ginsbergy@ncsy.org,Kahnm@ncsy.org',
        NCSY_Midwest: 'friedmans@ncsy.org,Levitanskym@ncsy.org,Midwest@ncsy.org,francojo@ncsy.org,strulowitza@ncsy.org,wedglej@ncsy.org',
        NCSY_Mexico: 'Danield@ncsy.org,Mexicodonations@ncsy.org,isenbergm@ncsy.org',
        NCSY_NationalGroup: 'gordond@ncsy.org,isenbergm@ncsy.org,lernerj@ncsy.org',
        NCSY_Tristate: 'ListmanE@ncsy.org,lernerj@ncsy.org,Tristate@ncsy.org,marchuck@ncsy.org,beth.nussbaum@ncsy.org',
        NCSY_NewEngland: 'weinstockd@ncsy.orgSandersg@ncsy.org',
        NCSY_Portland: 'spivakm@ncsy.org,Oregonoffice@ncsy.org',
        NCSY_Southern: 'levittev@ncsy.org,bardosl@ncsy.org,southernoffice@ncsy.org,rosem@ncsy.org',
        NCSY_Southwest: 'yaffee@ncsy.org,southwest@ncsy.org,steinbergk@ncsy.org,marchuck@ncsy.org',
        NCSY_WestCoast: 'tarkor@ncsy.org,wcoffice@ncsy.org,chana@ncsy.org,naimana@ncsy.org,goldmani@ou.org,bryan.borenstein@ncsy.org,steinera@ncsy.org',
        NCSY_Summer: 'tanzmane@ncsy.org,marchuck@ncsy.org,amy@ncsy.org,lernerj@ncsy.org,beth.nussbaum@ncsy.org',
      } as any
      const email = mapping[request.applications![0].department!]
      if (
        request.applications![0].department! === 'NCSY' &&
        request.applications![0].campaign! === 'Innovation Lab'
      ) {
        to.push('jonese@ncsy.org');
      }
      if (email) to.push(email)
      return to
    },
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      5000,
      2500,
      1800,
      720,
      500,
      360,
      180,
      54
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
  },
  'ncsy-midwest': {
    template: 'ncsy-midwest',
    emailTemplate: 'ncsy-midwest',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Midwest Donation Form',
    department: 'NCSY_Midwest',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['levitanskym@ncsy.org', 'friedmans@ncsy.org', 'goldmani@ou.org', 'francojo@ncsy.org', 'midwest@ncsy.org', 'strulowitza@ncsy.org', 'wedglej@ncsy.org'],
    adminFromEmail: 'NCSY Midwest <midwest@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY',
    userFromEmail: 'NCSY Midwest <midwest@ncsy.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
        customValidations (form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional()
        })
      })
    }
  },
  'ncsy-midwest-region': {
    template: 'ncsy-region-template',
    emailTemplate: 'ncsy-midwest',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Midwest Donation Form',
    department: 'NCSY_Midwest',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['levitanskym@ncsy.org', 'friedmans@ncsy.org', 'goldmani@ou.org', 'francojo@ncsy.org', 'midwest@ncsy.org', 'strulowitza@ncsy.org', 'wedglej@ncsy.org'],
    adminFromEmail: 'NCSY Midwest <midwest@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY',
    userFromEmail: 'NCSY Midwest <midwest@ncsy.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional()
        })
      })
    }
  },
  'ncsy-midwest-eoy': {
    template: 'ncsy-midwest-eoy',
    emailTemplate: 'ncsy-midwest-eoy',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Midwest End of Year Donation Form',
    department: 'NCSY_Midwest',
    campaign: 'End of Year',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['levitanskym@ncsy.org', 'friedmans@ncsy.org', 'goldmani@ou.org', 'francojo@ncsy.org', 'midwest@ncsy.org', 'strulowitza@ncsy.org', 'wedglej@ncsy.org'],
    adminFromEmail: 'NCSY Midwest <midwest@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Midwest End of Year',
    userFromEmail: 'NCSY Midwest <midwest@ncsy.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      36,
      54,
      75,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1'
  },
  'ncsy-midwest-book': {
    emailTemplate: 'ncsy-midwest-book',
    title: 'Midwest Back to School',
    department: 'NCSY_Midwest',
    campaign: 'Back to School',
    subCampaign: '',
    message: 'Thank you for your generous support to NCSY Midwest. You will receive a confirmation email shortly!',
    adminEmails: ['marcusj@ou.org', 'friedmans@ncsy.org', 'levitanskym@ncsy.org', 'FrancoJo@ncsy.org', 'hannahf@ou.org', 'strulowitza@ncsy.org', 'wedglej@ncsy.org'],
    adminFromEmail: 'NCSY Midwest Back to School <midwest@ncsy.org>',
    adminSubject: 'A payment has been made to Midwest Back to School',
    userFromEmail: 'NCSYMidwest <marcusj@ou.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      [28, `1 Book`],
      [56, `2 Books`],
      [84, `3 Books`],
      [112, `4 Books`],
      [140, `5 Books`],
      [280, `10 Books`],
      [560, `20 Books`],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1'
  },
  'jsu-rovinsky': {
    emailTemplate: 'jsu',
    honoreeTemplate: 'jsu',
    title: 'JSU STL Donation Form',
    department: 'NCSY_STL',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['rovinskym@ncsy.org', 'minoffa@ncsy.org','staumy@ncsy.org','judy.haber@ncsy.org', 'stlinfo@jsu.org', 'Staumy@ncsy.org'],
    adminFromEmail: 'JSU Donation Form <stlinfo@jsu.org>',
    adminSubject: 'A payment has been made to JSU STL',
    userFromEmail: 'JSU STL <stlinfo@jsu.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      50,
      100,
      180,
      250,
      360,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
    silverPopContactLists: [8517751],
  },
  'jsu-bricks': {
    emailTemplate: 'jsu-bricks',
    honoreeTemplate: 'jsu',
    title: 'JSU STL Bricks General Donation Form',
    department: 'NCSY_STL',
    campaign: 'Bricks Campaign',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['rovinskym@jsu.org', 'minoffa@ncsy.org','staumy@ncsy.org','judy.haber@ncsy.org', 'stlinfo@jsu.org', 'RabbiMike@jsustl.org'],
    adminFromEmail: 'JSU The Staenberg House Donation Form <stlinfo@jsu.org>',
    adminSubject: 'A payment has been made to JSU STL The Staenberg House',
    userFromEmail: 'JSU STL <stlinfo@jsu.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      50,
      100,
      180,
      250,
      360,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
    silverPopContactLists: [8517751],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          largeBrick: z.string().nullable().optional(),
          regBrick: z.string().nullable().optional(),
          smallBrick: z.string().nullable().optional(),
          donationAmount: z.string().nullable().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    }
  },
  'jsu-bricks-naming': {
    emailTemplate: 'jsu-bricks',
    honoreeTemplate: 'jsu',
    title: 'JSU STL Naming Opportunities Form',
    department: 'NCSY_STL',
    campaign: 'Bricks Campaign',
    subCampaign: '',
    message: 'Thank you for considering a partnership with JSU in strengthening and inspiring the Jewish Future.',
    adminEmails: ['rovinskym@jsu.org', 'minoffa@ncsy.org','staumy@ncsy.org','judy.haber@ncsy.org', 'stlinfo@jsu.org', 'RabbiMike@jsustl.org'],
    adminFromEmail: 'JSU The Staenberg House Naming Opportunities <stlinfo@jsu.org>',
    adminSubject: 'A payment has been made to JSU STL Naming Opportunities',
    userFromEmail: 'JSU STL <stlinfo@jsu.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      [200000, `JSU House <span>Funded</span>`],
      [175000, `1st Floor of House <span>(1)</span>`],
      [175000, `2nd Floor of House <span>(1)</span>`],
      [150000, `Great Room <span>(1)</span>`],
      [100000, `Library <span>(1)</span>`],
      [100000, `Living Room <span>(1)</span>`],
      [100000, `Kitchen <span>(1)</span>`],
      [100000, `Game Room <span>(1)</span>`],
      [100000, `Lower Level <span>(1)</span>`],
      [75000, `Torah Ark <span>(1)</span>`],
      [75000, `Executive Director's Office <span>(1)</span>`],
      [50000, `Programming Office #1 <span>(1)</span>`],
      [50000, `Programming Office #2 <span>(1)</span>`],
      [50000, `Development Office <span>(1)</span>`],
      [50000, `Driveway <span>(1)</span>`],
      [36000, `Ner Tamid - Eternal Flame <span>(1)</span>`],
      [36000, `Main Staircase <span>(1)</span>`],
      [36000, `Exterior Front Walkway <span>(1)</span>`],
      [25000, `Mechitzah <span>(1)</span>`],
      [25000, `1st Floor Meeting Nook <span>(1)</span>`],
      [25000, `Front Porch <span>(1)</span>`],
      [18000, `Snack Bar <span>(1)</span>`],
      [18000, `Washing Station <span>(1)</span>`],
      [18000, `Kitchen Meeting Nook <span>(1)</span>`],
      [10000, `Living Room Fireplace <span>(1)</span>`],
      [10000, `Library Bookcases <span>(1)</span>`],
      [10000, `Kitchen Cooking Island <span>(1)</span>`],
      [5000, `Great Room Podium <span>(1)</span>`],
      [5000, `Front Door Mezuzah <span>(1)</span>`],
      [5000, `Great Room Mezuzah <span>(2)</span>`],
      [5000, `Lower-Level Mezuzah <span>(1)</span>`],
      [5000, `Executive Director's Office Mezuzah <span>(1)</span>`],
      [3600, `Game Room Mezuzah <span>(3)</span>`],
      [3600, `Library Mezuzah <span>(2)</span>`],
      [3600, `Kitchen Mezuzah <span>(1)</span>`],
      [3600, `Side Door Mezuzah <span>(1)</span>`],
      [3600, `Office Mezuzah <span>(3)</span>`],
      [3600, `Lower-Level Room Mezuzah <span>(2)</span>`],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
    silverPopContactLists: [8517751],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          contact: z.string().nullable().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    }
  },
  'jsu-bricks-dedication': {
    emailTemplate: 'jsu-bricks',
    honoreeTemplate: 'jsu',
    title: 'JSU STL Brick Dedication Form',
    department: 'NCSY_STL',
    campaign: 'Bricks Campaign',
    subCampaign: '',
    message: 'Thank you for your dedication of brick(s) we will contact you regarding your brick inscription(s).',
    adminEmails: ['rovinskym@jsu.org', 'minoffa@ncsy.org','staumy@ncsy.org','judy.haber@ncsy.org', 'stlinfo@jsu.org', 'RabbiMike@jsustl.org'],
    adminFromEmail: 'JSU The Staenberg House Brick Dedication <stlinfo@jsu.org>',
    adminSubject: 'A payment has been made to JSU STL Brick Dedication',
    userFromEmail: 'JSU STL <stlinfo@jsu.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      50,
      100,
      180,
      250,
      360,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
    silverPopContactLists: [8517751],
  },
  'ncsy-midwest-monthly': {
    emailTemplate: 'ncsy-midwest-monthly',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Midwest Donation Form',
    department: 'NCSY_Midwest',
    campaign: 'Partners in Eternity',
    subCampaign: '',
    message: 'Your transaction is complete.<br> Thank you for becoming a Partner in Eternity.',
    adminEmails: ['levitanskym@ncsy.org', 'friedmans@ncsy.org', 'goldmani@ou.org', 'francojo@ncsy.org', 'friedmans@ncsy.org', 'strulowitza@ncsy.org', 'wedglej@ncsy.org'],
    adminFromEmail: 'NCSY Midwest <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Midwest Monthly Donation Form',
    userFromEmail: 'Midwest NCSY/JSU <info@ncsy.org>',
    userSubject: 'You’re Now a Partner in Eternity!',
    amounts: [
      10,
      18,
      36,
      50,
      100,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
  },
  'ncsy-westcoast-monthly': {
    emailTemplate: 'ncsy-westcoast-monthly',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Westcoast Donation Form',
    department: 'NCSY_WestCoast',
    campaign: 'Partners in Eternity',
    subCampaign: '',
    defaultCurrency: 'USD',
    message: 'Your transaction is complete.<br> Thank you for becoming a Partner in Eternity.',
    adminEmails: ['tarkor@ncsy.org','wcoffice@ncsy.org', 'chana@ncsy.org', 'naimana@ncsy.org', 'goldmani@ou.org', 'bryan.borenstein@ncsy.org', 'steinera@ncsy.org'],
    adminFromEmail: 'NCSY Westcoast <wcoffice@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Westcoast Monthly Donation Form',
    userFromEmail: 'NCSY West Coast <wcoffice@ncsy.org>',
    userSubject: 'You’re Now a Partner in Eternity!',
    amounts: [
      5,
      10,
      18,
      36,
      54,
      100,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
  },
  'norcal-monthly': {
    template: 'ncsy-monthly',
    emailTemplate: 'ncsy-monthly',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Partners in Eternity Donation Form',
    department: 'NCSY_Norcal',
    campaign: 'Partners in Eternity',
    subCampaign: '',
    defaultCurrency: 'USD',
    message: 'Your transaction is complete.<br> Thank you for becoming a Partner in Eternity.',
    adminEmails: ['naimana@ncsy.org', 'goldmani@ou.org'],
    adminFromEmail: 'NCSY Partners in Eternity <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Norcal Monthly Donation Form',
    userFromEmail: 'NCSY Partners in Eternity <info@ncsy.org>',
    userSubject: 'You’re Now a Partner in Eternity!',
    amounts: [
     18,
      54,
      72,
      118,
      180,
      360,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
  },
  'arizona-monthly': {
    template: 'ncsy-monthly',
    emailTemplate: 'ncsy-monthly',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Partners in Eternity Donation Form',
    department: 'NCSY_Arizona',
    campaign: 'Partners in Eternity',
    subCampaign: '',
    defaultCurrency: 'USD',
    message: 'Your transaction is complete.<br> Thank you for becoming a Partner in Eternity.',
    adminEmails: ['naimana@ncsy.org', 'goldmani@ou.org'],
    adminFromEmail: 'NCSY Partners in Eternity <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY arizona Monthly Donation Form',
    userFromEmail: 'NCSY Partners in Eternity <info@ncsy.org>',
    userSubject: 'You’re Now a Partner in Eternity!',
    amounts: [
      18,
      54,
      72,
      118,
      180,
      360,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
  },
    'westcoast-jsula': {
    template: 'ncsy-monthly',
    emailTemplate: 'westcoast-jsula',
    honoreeTemplate: 'ncsy',
    title: 'NCSY West Coast Donation Form',
    department: 'NCSY_LACity',
    campaign: 'General Donation',
    subCampaign: '',
    defaultCurrency: 'USD',
    message: 'Thank you for becoming a monthly donor to NCSY West Coast!',
    adminEmails: ['naimana@ncsy.org', 'steinera@ncsy.org'],
    adminFromEmail: 'NCSY West Coast Monthly Donation <israelm@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY West Coast Monthly Donation Form',
    userFromEmail: 'NCSY West Coast Monthly Donation <israelm@ncsy.org>',
    userSubject: 'You’re Now a Partner in Eternity!',
    amounts: [
      18,
      54,
      72,
      118,
      180,
      360,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
  },
  'ncsy-benzakai': {
    emailTemplate: 'ncsy',
    honoreeTemplate: 'ncsy',
    title: 'NCSY ben-zakkai Donation Form',
    department: 'BenZakkai',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['drluchins@aol.com', 'lernerj@ncsy.org', 'Novamanor@aol.com', 'goldmani@ou.org', 'greenlandm@ncsy.org', 'beckerd@ncsy.org'],
    adminFromEmail: 'NCSY ben-zakkai <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY ben-zakkai',
    userFromEmail: 'NCSY Ben-Zakkai <info@ncsy.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
  },
  'ncsy-alumni': {
    emailTemplate: 'ncsy-alumni',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Alumni Donation Form',
    department: 'NCSY_Alumni',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['alumni@ncsy.org', 'marchuck@ncsy.org', 'goldmani@ou.org', 'lernerj@ncsy.org'],
    adminFromEmail: 'NCSY Alumni <alumni@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Alumni',
    userFromEmail: 'NCSY Alumni <alumni@ncsy.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      5,
      18,
      36,
      72,
      100,
      180,
      250,
      360,
      500,
      1000,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-gap': {
    emailTemplate: 'ncsy-gap',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Gift A Yeshiva Year Form',
    department: 'NCSY_Alumni',
    campaign: 'Gap Year Scholarship',
    subCampaign: '',
    message: 'Thank You for Giving the Gift of Yeshiva!<br><br>Your gift is making it possible for a teen to embark on a meaningful Yeshiva/Seminary experience in Israel—an experience that will shape their lives, strengthen their connection to Klal Yisrael, and inspire future generations.',
    adminEmails: ['lernerj@ncsy.org', 'marchuck@ncsy.org', 'goldmani@ou.org'],
    adminFromEmail: 'NCSY Gap <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Yeshiva Year Sponsorship',
    userFromEmail: 'NCSY Yeshiva Year <info@ncsy.org>',
    userSubject: 'Thank You for Gifting a Yeshiva Year!',
    amounts: [
        [7500, 'Fund One Student for Yeshiva'],
        [15000, 'Fund Two Students for Yeshiva'],
        [22500, 'Fund Three Students for Yeshiva'],
        [30000, 'Fund Four Students for Yeshiva'],
        [37500, 'Fund Five Students for Yeshiva'],
        [45000, 'Fund Six Students for Yeshiva'],
        [52500, 'Fund Seven Students for Yeshiva'],
        [60000, 'Fund Eight Students for Yeshiva'],
        [67500, 'Fund Nine Students for Yeshiva'],
        [75000, 'Fund Ten Students for Yeshiva']
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-newjersey': {
    emailTemplate: 'ncsy-newjersey',
    honoreeTemplate: 'ncsy',
    title: 'NCSY New Jersey Donation Form',
    department: 'NCSY_Tristate',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['lernerj@ncsy.org','Tristate@ncsy.org', 'marchuck@ncsy.org', 'beth.nussbaum@ncsy.org'],
    adminFromEmail: 'NCSY New Jersey <newjersey@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY New Jersey - Donation',
    userFromEmail: 'NCSY New Jersey <newjersey@ncsy.org>',
    userSubject: 'Thank you for your support!',
    amounts: {
      USD: [
        18,
        36,
        54,
        72,
        100,
        180,
        250,
        360,
        500,
        1800,
        1000
      ],
      ILS: [
        18,
        36,
        54,
        72,
        100,
        180,
        250,
        360,
        500,
        1800,
        1000,
      ]
    },
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          Comments: z.string().nullable().optional(),
        })
      })
    },
  },
  'ncsy-westcoast': {
    emailTemplate: 'ncsy-westcoast',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Westcoast Donation Form',
    department: 'NCSY_WestCoast',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['tarkor@ncsy.org','wcoffice@ncsy.org', 'chana@ncsy.org', 'naimana@ncsy.org', 'goldmani@ou.org', 'bryan.borenstein@ncsy.org', 'steinera@ncsy.org'],
    adminFromEmail: 'NCSY Westcoast <wcoffice@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY West Coast - Donation',
    userFromEmail: 'NCSY West Coast <wcoffice@ncsy.org>',
    userSubject: 'NCSY West Coast - Donation',
    amounts: [
      [5000, `<span>Benefactor $5,000</span>Sponsor a teen for a full year of Jewish Experiences including a summer trip to Israel
      `],
      [3600, `<span>Partner $3,600</span>The Jerusalem Journey (TJJ) Sponsorship for a public school teen to spend a summer in Israel`],
      [2500, `<span>Trustee $2,500</span>Sponsor a teen for a full year of leadership training, weekly Torah learning and weekend retreats`],
      [1800, `<span>Patron $1,800</span>Sponsor weekly Jewish Student Union (JSU) Clubs in public schools for a month`],
      [1000, `<span>Supporter $1,000</span>Sponsor a public school teen for Yarchei Kallah (5 day winter break retreat for learning Torah)`],
      [500, `<span>Friend $500</span>Send a teen to an NCSY Regional Shabbaton/ Weekend Retreat`],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-westcoast-region': {
    template: 'ncsy-region-template',
    emailTemplate: 'ncsy-westcoast',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Westcoast Donation Form',
    department: 'NCSY_WestCoast',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['tarkor@ncsy.org','wcoffice@ncsy.org', 'chana@ncsy.org', 'naimana@ncsy.org', 'goldmani@ou.org', 'bryan.borenstein@ncsy.org', 'steinera@ncsy.org'],
    adminFromEmail: 'NCSY Westcoast <wcoffice@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY West Coast - Donation',
    userFromEmail: 'NCSY West Coast <wcoffice@ncsy.org>',
    userSubject: 'NCSY West Coast - Donation',
    amounts: [
      [5000, `<span>Benefactor $5,000</span>Sponsor a teen for a full year of Jewish Experiences including a summer trip to Israel
      `],
      [3600, `<span>Partner $3,600</span>The Jerusalem Journey (TJJ) Sponsorship for a public school teen to spend a summer in Israel`],
      [2500, `<span>Trustee $2,500</span>Sponsor a teen for a full year of leadership training, weekly Torah learning and weekend retreats`],
      [1800, `<span>Patron $1,800</span>Sponsor weekly Jewish Student Union (JSU) Clubs in public schools for a month`],
      [1000, `<span>Supporter $1,000</span>Sponsor a public school teen for Yarchei Kallah (5 day winter break retreat for learning Torah)`],
      [500, `<span>Friend $500</span>Send a teen to an NCSY Regional Shabbaton/ Weekend Retreat`],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-northeast': {
    emailTemplate: 'ncsy-northeast',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Northeast Donation Form',
    department: 'NCSY_UpstateNewYork',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['northeast@ncsy.org', 'weinstockd@ncsy.org', 'yudi@ncsy.org', 'goldringh@ncsy.org', 'goldmani@ou.org'],
    adminFromEmail: 'NCSY Northeast <northeast@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Northeast.',
    userFromEmail: 'NCSY Northeast <northeast@ncsy.org>',
    userSubject: 'NCSY Northeast - Donation',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-boston': {
    template: 'ncsy-boston',
    emailTemplate: 'ncsy-northeast',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Boston Donation Form',
    department: 'NCSY_Boston',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['northeast@ncsy.org', 'riesely@ncsy.org', 'goldmani@ou.org', 'riesele@ncsy.org'],
    adminFromEmail: 'NCSY Boston <northeast@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Boston.',
    userFromEmail: 'NCSY Boston <northeast@ncsy.org>',
    userSubject: 'NCSY Boston - Donation',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-boston-region': {
    template: 'ncsy-region-template',
    emailTemplate: 'ncsy-northeast',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Boston Donation Form',
    department: 'NCSY_Boston',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['northeast@ncsy.org', 'riesely@ncsy.org', 'goldmani@ou.org', 'riesele@ncsy.org'],
    adminFromEmail: 'NCSY Boston <northeast@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Boston.',
    userFromEmail: 'NCSY Boston <northeast@ncsy.org>',
    userSubject: 'NCSY Boston - Donation',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional()
        })
      })
    }
  },
  'ncsy-atlanticseaboard': {
    template: 'ncsy-standard',
    emailTemplate: 'ncsy',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Atlanticseaboard Donation Form',
    department: 'NCSY_AtlanticSeaboard',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['atsoffice@ncsy.org', 'lernerj@ncsy.org', 'mossb@ncsy.org', 'sperom@ncsy.org', 'mizrahia@ncsy.org', 'lauren@ncsy.org'],
    adminFromEmail: 'NCSY Atlanticseaboard <atsoffice@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Atlanticseaboard.',
    userFromEmail: 'NCSY Atlanticseaboard <atsoffice@ncsy.org>',
    userSubject: 'NCSY Atlantic Seaboard - Donation',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-atlanticseaboard-modal': {
    template: 'ncsy-modal',
    emailTemplate: 'ncsy',
    thankYouTemplate: 'ncsy',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Atlanticseaboard Donation Form',
    department: 'NCSY_AtlanticSeaboard',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['atsoffice@ncsy.org', 'lernerj@ncsy.org', 'mossb@ncsy.org', 'sperom@ncsy.org', 'mizrahia@ncsy.org', 'lauren@ncsy.org'],
    adminFromEmail: 'NCSY Atlanticseaboard <atsoffice@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Atlanticseaboard.',
    userFromEmail: 'NCSY Atlanticseaboard <atsoffice@ncsy.org>',
    userSubject: 'NCSY Atlantic Seaboard - Donation',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-centraleast': {
    template: 'ncsy-standard',
    emailTemplate: 'ncsy',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Central East - Donation Form',
    department: 'NCSY_CentralEast',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['centraleast@ncsy.org'],
    adminFromEmail: 'NCSY Central East <centraleast@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Central East.',
    userFromEmail: 'NCSY Central East <centraleast@ncsy.org>',
    userSubject: 'NCSY Central East - Donation',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-centraleast-region': {
    template: 'ncsy-region-template',
    emailTemplate: 'ncsy',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Central East - Donation Form',
    department: 'NCSY_CentralEast',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['centraleast@ncsy.org'],
    adminFromEmail: 'NCSY Central East <centraleast@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Central East.',
    userFromEmail: 'NCSY Central East <centraleast@ncsy.org>',
    userSubject: 'NCSY Central East - Donation',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional()
        })
      })
    }
  },
  'ncsy-oregon': {
    template: 'ncsy-standard',
    emailTemplate: 'ncsy',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Oregon Donation Form',
    department: 'NCSY_Portland',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['oregonoffice@ncsy.org', 'spivakm@ncsy.org'],
    adminFromEmail: 'NCSY Oregon <oregonoffice@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Oregon.',
    userFromEmail: 'NCSY Oregon <oregonoffice@ncsy.org>',
    userSubject: 'NCSY Oregon - Donation',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-oregon-region': {
    template: 'ncsy-region-template',
    emailTemplate: 'ncsy',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Oregon Donation Form',
    department: 'NCSY_Portland',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['oregonoffice@ncsy.org', 'spivakm@ncsy.org'],
    adminFromEmail: 'NCSY Oregon <oregonoffice@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Oregon.',
    userFromEmail: 'NCSY Oregon <oregonoffice@ncsy.org>',
    userSubject: 'NCSY Oregon - Donation',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional()
        })
      })
    }
  },
  'ncsy-sandiego': {
    template: 'ncsy-standard',
    emailTemplate: 'ncsy',
    honoreeTemplate: 'ncsy',
    title: 'NCSY San Diego Donation Form',
    department: 'NCSY_SanDiego',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['wcoffice@ncsy.org', 'simona@ncsy.org', 'chana@ncsy.org', 'chermakn@ncsy.org'],
    adminFromEmail: 'NCSY San Diego <wcoffice@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY San Diego.',
    userFromEmail: 'NCSY San Diego <wcoffice@ncsy.org>',
    userSubject: 'NCSY San Diego - Donation',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-seattle': {
    template: 'ncsy-standard',
    emailTemplate: 'ncsy-seattle',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Seattle Donation Form',
    department: 'NCSY_Seattle',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['benmeirn@ncsy.org', 'naimana@ncsy.org', 'chana@ncsy.org'],
    adminFromEmail: 'NCSY Seattle <benmeirn@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Seattle.',
    userFromEmail: 'NCSY Seattle <benmeirn@ncsy.org>',
    userSubject: 'NCSY Seattle - Donation',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-southern': {
    template: 'ncsy-standard',
    emailTemplate: 'ncsy-southern',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Southern Donation Form',
    department: 'NCSY_Southern',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: '<h1>Thank you for choosing to inspire the Jewish future - our teens</h1><br><br>NCSY is part of the OU, a registered 501c3, therefore 100% of your gift is tax deductible.',
    adminEmails: ['southernoffice@ncsy.org', 'waxe@ncsy.org', 'levittev@ncsy.org'],
    adminFromEmail: 'NCSY Southern <southernoffice@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Southern.',
    userFromEmail: 'NCSY Southern <southernoffice@ncsy.org>',
    userSubject: 'NCSY Southern - Donation',
    amounts: [
      5000,
      3600,
      1000,
      500,
      360,
      180,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-southern-region': {
    template: 'ncsy-region-template',
    emailTemplate: 'ncsy-southern',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Southern Donation Form',
    department: 'NCSY_Southern',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: '<h1>Thank you for choosing to inspire the Jewish future - our teens</h1><br><br>NCSY is part of the OU, a registered 501c3, therefore 100% of your gift is tax deductible.',
    adminEmails: ['southernoffice@ncsy.org', 'waxe@ncsy.org', 'levittev@ncsy.org'],
    adminFromEmail: 'NCSY Southern <southernoffice@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Southern.',
    userFromEmail: 'NCSY Southern <southernoffice@ncsy.org>',
    userSubject: 'NCSY Southern - Donation',
    amounts: [
      5000,
      3600,
      1000,
      500,
      360,
      180,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional()
        })
      })
    }
  },
  'ncsy-southern-facts': {
    template: 'ncsy-standard',
    emailTemplate: 'ncsy-southern',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Southern Donation Form',
    department: 'NCSY_Southern',
    campaign: 'F.A.C.T.S. JSU',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: '<h1>Thank you For Supporting JSU F.A.C.T.S</h1><br><br>NCSY is part of the OU, a registered 501c3, therefore 100% of your gift is tax deductible.',
    adminEmails: ['Rosem@ncsy.org','muschels@ncsy.org','Levitte@ncsy.org','Southern@ncsy.org'],
    adminFromEmail: 'NCSY Southern <southernoffice@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Southern F.A.C.T.S..',
    userFromEmail: 'NCSY Southern <southernoffice@ncsy.org>',
    userSubject: 'NCSY Thank you For Supporting JSU F.A.C.T.S',
    amounts: [
      5000,
      3600,
      1000,
      500,
      360,
      180,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-argentina': {
    emailTemplate: 'ncsy-southern',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Argentina Donation Form',
    department: 'NCSY_Argentina',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Gracias por su generosa contribución a NCSY Argentina.',
    adminEmails: ['Argentina@ncsy.org', 'goldmani@ou.org'],
    adminFromEmail: 'NCSY Argentina <Argentina@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Argentina.',
    userFromEmail: 'NCSY Argentina <Argentina@ncsy.org>',
    userSubject: 'NCSY Argentina - Gracias',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-argentina-region': {
    template: 'ncsy-region-template',
    emailTemplate: 'ncsy-southern',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Argentina Donation Form',
    department: 'NCSY_Argentina',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Gracias por su generosa contribución a NCSY Argentina.',
    adminEmails: ['Argentina@ncsy.org', 'goldmani@ou.org'],
    adminFromEmail: 'NCSY Argentina <Argentina@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Argentina.',
    userFromEmail: 'NCSY Argentina <Argentina@ncsy.org>',
    userSubject: 'NCSY Argentina - Gracias',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional()
        })
      })
    }
  },
  'ncsy-southwest': {
    template: 'ncsy-standard',
    emailTemplate: 'ncsy-southwest',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Southwest Donation Form',
    department: 'NCSY_Southwest',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['yaffee@ncsy.org', 'southwest@ncsy.org','steinbergk@ncsy.org', 'marchuck@ncsy.org'],
    adminFromEmail: 'NCSY Southwest <southwest@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Southwest.',
    userFromEmail: 'NCSY Southwest <southwest@ncsy.org>',
    userSubject: 'NCSY Southwest - Donation',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-southwest-region': {
    template: 'ncsy-region-template',
    emailTemplate: 'ncsy-southwest',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Southwest Donation Form',
    department: 'NCSY_Southwest',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['yaffee@ncsy.org', 'southwest@ncsy.org','steinbergk@ncsy.org', 'marchuck@ncsy.org'],
    adminFromEmail: 'NCSY Southwest <southwest@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Southwest.',
    userFromEmail: 'NCSY Southwest <southwest@ncsy.org>',
    userSubject: 'NCSY Southwest - Donation',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional()
        })
      })
    }
  },
  'ncsy-cleveland': {
    template: 'ncsy-standard',
    emailTemplate: 'ncsy',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Cleveland Donation Form',
    department: 'NCSY_Cleveland',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['centraleast@ncsy.org', 'rabbit@ncsy.org'],
    adminFromEmail: 'NCSY Cleveland <centraleast@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY Cleveland.',
    userFromEmail: 'NCSY Cleveland <centraleast@ncsy.org>',
    userSubject: 'NCSY Cleveland - Donation',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-sandieg': {
    template: 'ncsy-standard',
    emailTemplate: 'ncsy',
    honoreeTemplate: 'ncsy',
    title: 'NCSY San Diego Donation Form',
    department: 'NCSY_Cleveland',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['centraleast@ncsy.org', 'rabbit@ncsy.org'],
    adminFromEmail: 'NCSY San Diego <centraleast@ncsy.org>',
    adminSubject: 'A donation has been made to NCSY San Diego.',
    userFromEmail: 'NCSY San Diego <centraleast@ncsy.org>',
    userSubject: 'NCSY San Diego - Donation',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-mexico': {
    emailTemplate: 'ncsy-mexico',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Mexico Donation Form',
    department: 'NCSY_Mexico',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you so much for helping NCSY continue to inspire the Jewish future in Latin America. We look forward to sharing only joy from this beautiful project',
    adminEmails: ['danield@ncsy.org', 'goldmani@ou.org', 'mexicodonations@ncsy.org'],
    adminFromEmail: 'NCSY Mexico <danield@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Mexico - Donation',
    userFromEmail: 'NCSY Mexico <danield@ncsy.org',
    userSubject: 'Thank you for your support!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-5' : 'UA-3009720-5',
    amounts: [
    ],
  },
  'ncsy-mexico-region': {
    template: 'ncsy-region-template',
    emailTemplate: 'ncsy-mexico',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Mexico Donation Form',
    department: 'NCSY_Mexico',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you so much for helping NCSY continue to inspire the Jewish future in Latin America. We look forward to sharing only joy from this beautiful project',
    adminEmails: ['danield@ncsy.org', 'goldmani@ou.org', 'mexicodonations@ncsy.org'],
    adminFromEmail: 'NCSY Mexico <danield@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Mexico - Donation',
    userFromEmail: 'NCSY Mexico <danield@ncsy.org',
    userSubject: 'Thank you for your support!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-5' : 'UA-3009720-5',
    amounts: [
    ],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional(),
          listingName: z.string().nullable().optional()
        })
      })
    }
  },
  'ncsy-campsports-parnes': {
    ...ncsyParnesBasic,
    template: 'ncsy-parnes',
    amounts: [
      [360, `Day of Learning `],
      [500, `Thursday Night Mishmar `],
      [3600, `Week of Learning`],
    ],
  },
  'ncsy-kollel-parnes': {
    ...ncsyParnesBasic,
    template: 'ncsy-parnes',
    title: 'NCSY Kollel Donation Form',
    department: 'NCSY_Summer',
    campaign: 'Kollel Parnes HaYom',
    subCampaign: '',
    adminFromEmail: 'NCSY Kollel <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Kollel Parnes - Donation',
    userFromEmail: 'NCSY Kollel <info@ncsy.org>',
    amounts: [
      [360, `Day of Learning `],
      [500, `Thursday Night Mishmar `],
      [3600, `Week of Learning`],
    ],
  },
  'ncsy-euro-parnes': {
    ...ncsyParnesBasic,
    template: 'ncsy-parnes',
    title: 'NCSY Euro Donation Form',
    department: 'NCSY_Summer',
    campaign: 'Euro Parnes HaYom',
    subCampaign: '',
    adminFromEmail: 'NCSY Euro <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Euro Parnes - Donation',
    userFromEmail: 'NCSY Euro <info@ncsy.org>',
    amounts: [
      [360, `Day of Learning `],
      [500, `Thursday Night Mishmar `],
      [3600, `Week of Learning`],
    ],
  },
  'ncsy-give-parnes': {
    ...ncsyParnesBasic,
    template: 'ncsy-parnes',
    title: 'NCSY GIVE Donation Form',
    department: 'NCSY_Summer',
    campaign: 'GIVE Parnes HaYom',
    subCampaign: '',
    adminFromEmail: 'NCSY GIVE <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY GIVE Parnes - Donation',
    userFromEmail: 'NCSY GIVE <info@ncsy.org>',
    amounts: [
      [360, `Sponsor Toys for a Chessed Bat Mitzvah`],
      [360, `Day of Learning `],
      [360, `Day of Chesed`],
      [500, `Thursday Night Mishmar `],
      [1000, `Sponsor a BBQ for Soldiers`],
      [1800, `Unity day`],
      [3600, `Week of Learning`],
    ],
  },
  'ncsy-tjj-parnes': {
    ...ncsyParnesBasic,
    template: 'ncsy-parnes',
    title: 'NCSY TJJ Donation Form',
    department: 'NCSY_Summer',
    campaign: 'TJJ Donation',
    subCampaign: '',
    adminFromEmail: 'NCSY TJJ <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY TJJ Parnes - Donation',
    userFromEmail: 'NCSY TJJ <info@ncsy.org>',
    amounts: [
      [360, `Day of Learning `],
      [500, `Sponsor a Challah Bake`],
    ],
  },
  'ncsy-michlelet-parnes': {
    ...ncsyParnesBasic,
    template: 'ncsy-parnes',
    title: 'NCSY Michlelet Donation Form',
    department: 'NCSY_Summer',
    campaign: 'Michlelet Parnes HaYom',
    subCampaign: '',
    adminFromEmail: 'NCSY Michlelet <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Michlelet Parnes - Donation',
    userFromEmail: 'NCSY Michlelet <info@ncsy.org>',
    amounts: [
      [360, `Day of Learning`],
      [360, `Day of Chesed`],
      [500, `Thursday Night Mishmar `],
      [3600, `Week of Learning `],
    ],
  },
  'ncsy-joltisrael-parnes': {
    ...ncsyParnesBasic,
    template: 'ncsy-parnes',
    title: 'NCSY JOLT Israel	Donation Form',
    department: 'NCSY_Summer',
    campaign: 'JOLT Israel Parnes HaYom',
    subCampaign: '',
    adminFromEmail: 'NCSY JOLT Israel <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY JOLT Israel	Parnes - Donation',
    userFromEmail: 'NCSY JOLT Israel <info@ncsy.org>',
    amounts: [
      [360, `Day of Leadership Training`],
      [3600, `Week of Leadership Training`],
      [360, `Day of Learning`],
    ],
  },
  'ncsy-lead-parnes': {
    ...ncsyParnesBasic,
    template: 'ncsy-parnes',
    title: 'NCSY Lead Donation Form',
    department: 'NCSY_Summer',
    campaign: 'LEAD',
    subCampaign: '',
    adminFromEmail: 'NCSY Lead <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Lead	Parnes - Donation',
    userFromEmail: 'NCSY Lead <info@ncsy.org>',
    amounts: [
      [360, `Day of Learning`],
      [360, `Day of Chessed`],
    ],
  },
  'ncsy-nextstep-parnes': {
    ...ncsyParnesBasic,
    template: 'ncsy-parnes',
    title: 'NCSY Next Step Israel Donation Form',
    department: 'NCSY_Summer',
    campaign: 'Next Step Israel',
    subCampaign: '',
    adminFromEmail: 'NCSY Next Step Israel <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Next Step Israel	Parnes - Donation',
    userFromEmail: 'NCSY Next Step Israel <info@ncsy.org>',
    amounts: [
      [360, `Day of Learning`],
      [500, `Thursday Night Mishmar`],
    ],
  },
  'ncsy-hatzalahrescue-parnes': {
    ...ncsyParnesBasic,
    template: 'ncsy-parnes',
    title: 'NCSY Hatzalah Rescue Donation Form',
    department: 'NCSY_Summer',
    campaign: 'Hatzalah Rescue Israel',
    subCampaign: '',
    adminFromEmail: 'NCSY Hatzalah Rescue <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Hatzalah Rescue	Parnes - Donation',
    userFromEmail: 'NCSY Hatzalah Rescue <info@ncsy.org>',
    amounts: [
      [360, `Day of Learning`],
      [500, `Day of Paramedic Training`],
    ],
  },
  'ncsy-derech-parnes': {
    ...ncsyParnesBasic,
    template: 'ncsy-parnes',
    title: 'NCSY Derech Donation Form',
    department: 'NCSY_Summer',
    campaign: 'Derech',
    subCampaign: '',
    adminFromEmail: 'NCSY Derech <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Derech	Parnes - Donation',
    userFromEmail: 'NCSY Derech <info@ncsy.org>',
    amounts: [
      [360, `Day of Learning`],
      [3600, `Week of Learning`],
      [500, `Thursday Night Mishmar `],
    ],
  },
  'ncsy-israelid-parnes': {
    ...ncsyParnesBasic,
    template: 'ncsy-parnes',
    title: 'NCSY Israel ID Donation Form',
    department: 'NCSY_Summer',
    campaign: 'Euro Israel',
    subCampaign: '',
    adminFromEmail: 'NCSY Israel ID <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Israel ID	Parnes - Donation',
    userFromEmail: 'NCSY Israel ID <info@ncsy.org>',
    amounts: [
      [360, `Day of Learning`],
    ],
  },
  'ncsy-givewest-parnes': {
    ...ncsyParnesBasic,
    template: 'ncsy-parnes',
    title: 'NCSY GIVE West Donation Form',
    department: 'NCSY_Summer',
    campaign: 'GIVE West Parnes HaYom',
    subCampaign: '',
    adminFromEmail: 'NCSY GIVE West <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY GIVE West	Parnes - Donation',
    userFromEmail: 'NCSY GIVE West <info@ncsy.org>',
    amounts: [
      [360, `Day of Learning `],
      [360, `Day of Chesed`],
      [3600, `Week of Learning`],
    ],
  },
  'ncsy-impact-parnes': {
    ...ncsyParnesBasic,
    template: 'ncsy-parnes',
    title: 'NCSY Impact West Donation Form',
    department: 'NCSY_Summer',
    campaign: 'Impact Parnes HaYom',
    subCampaign: '',
    adminFromEmail: 'NCSY Impact <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Impact	Parnes - Donation',
    userFromEmail: 'NCSY Impact <info@ncsy.org>',
    amounts: [
      [360, `Day of Learning `],
      [3600, `Week of Learning`],
    ],
  },
  'ncsy-chile': {
    ...ncsyChileBasic,
    amounts: [
      [6250, `A week of Torah Learning`],
      [1400, `Yearly cost of one student`],
      [300, `Weekly cost of one  study group`],
      [115, `Monthly cost of one student`],
    ],
  },
  'ncsy-chile-region': {
    ...ncsyChileBasic,
    template: 'ncsy-region-template',
    amounts: [
      [6250, `A week of Torah Learning`],
      [1400, `Yearly cost of one student`],
      [300, `Weekly cost of one  study group`],
      [115, `Monthly cost of one student`],
    ],
    customValidations(form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional(),
          listingName: z.string().nullable().optional(),
        }),
      })
    },
  },
  'ncsy-chile-latin': {
    ...ncsyChileBasic,
    emailTemplate: 'ncsy-chile-latin',
    title: 'NCSY Chile Latin Donation Form',
    message: 'Gracias por ayudarnos a continuar con nuestra misión de inspirar el futuro de los jóvenes judíos de Latinoamérica. Esperamos poder compartir solo alegrías de este maravilloso proyecto.',
    adminSubject: 'A payment has been made to NCSY Chile Latin - Donation',
    userSubject: 'Gracias por ayudarnos a continuar con nuestra misión de inspirar el futuro de los jóvenes judíos de Latinoamérica. Esperamos poder compartir solo alegrías de este maravilloso proyecto.',
    amounts: [
      [6250, `Una semana de NCSY cuesta`],
      [1400, `Un alumno al año cuesta`],
      [300, `Un grupo semanal de  NCSY cuesta`],
      [115, `Un alumno al mes cuesta`],
    ]
  },
  'ncsy-chile-latin-region': {
    ...ncsyChileBasic,
    template: 'ncsy-region-template',
    emailTemplate: 'ncsy-chile-latin',
    title: 'NCSY Chile Latin Donation Form',
    message: 'Gracias por ayudarnos a continuar con nuestra misión de inspirar el futuro de los jóvenes judíos de Latinoamérica. Esperamos poder compartir solo alegrías de este maravilloso proyecto.',
    adminSubject: 'A payment has been made to NCSY Chile Latin - Donation',
    userSubject: 'Gracias por ayudarnos a continuar con nuestra misión de inspirar el futuro de los jóvenes judíos de Latinoamérica. Esperamos poder compartir solo alegrías de este maravilloso proyecto.',
    amounts: [
      [6250, `Una semana de NCSY cuesta`],
      [1400, `Un alumno al año cuesta`],
      [300, `Un grupo semanal de  NCSY cuesta`],
      [115, `Un alumno al mes cuesta`],
    ],
    customValidations(form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional(),
          listingName: z.string().nullable().optional(),
        }),
      })
    },
  },
  'ncsy-campsports': {
    emailTemplate: 'ncsy-campsports',
    honoreeTemplate: 'ncsy',
    title: 'NCSY Alumni Donation Form',
    department: 'NCSY_Alumni',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['alumni@ncsy.org', 'lernerj@ncsy.org', 'marchuck@ncsy.org', 'goldmani@ou.org'],
    adminFromEmail: 'NCSY Alumni Camp Sports <alumni@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Alumni - Camp Sports',
    userFromEmail: 'NCSY Alumni Camp Sports <alumni@ncsy.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      5,
      18,
      36,
      72,
      100,
      180,
      250,
      360,
      500,
      1000,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-midwest-impact': {
    emailTemplate: 'ncsy-midwest',
    title: 'NCSY Midwest Donation Form',
    department: 'NCSY_Midwest',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['levitanskym@ncsy.org', 'friedmans@ncsy.org', 'goldmani@ou.org', 'francojo@ncsy.org', 'strulowitza@ncsy.org', 'wedglej@ncsy.org'],
    adminFromEmail: 'NCSY Midwest <info@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY',
    userFromEmail: 'NCSY Midwest <info@ncsy.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      18,
      36,
      100,
      250,
      500,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-30413964-1' : 'UA-30413964-3',
  },
  'ncsy-havdalah': {
    emailTemplate: 'ncsy-havdalah',
    title: 'NCSY Alumni - Havdalah Donation',
    department: 'NCSY_Midwest',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['goldmani@ou.org', 'alumni@ncsy.org', 'friedmans@ncsy.org', 'strulowitza@ncsy.org', 'wedglej@ncsy.org'],
    adminFromEmail: 'NCSY Alumni <alumni@ncsy.org>',
    adminSubject: 'A payment has been made to NCSY Alumni - Havdalah',
    userFromEmail: 'NCSY Alumni <alumni@ncsy.org>',
    userSubject: 'A special thank you from NCSY Alumni!',
    amounts: [
      6,
      18,
      36,
      54,
      108,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
  },

  'teach-albany': {
    emailTemplate: 'teach-albany',
    title: 'Teach Albany Donation Form',
    department: 'TeachCoalition_NYS',
    campaign: 'Virtual Albany Mission',
    subCampaign: '',
    message: 'Thank you for donating to Teach Albany',
    adminEmails: ['nymission@teachcoalition.org', 'goldmani@ou.org',  'benshabats@teachcoalition.org','altfields@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'rachel.spinner@teachcoalition.org'],
    adminFromEmail: 'Teach Albany <nymission@teachcoalition.org>',
    adminSubject: 'A payment has been made to 2024 Mission to Albany Sponsor',
    userFromEmail: 'Teach Albany <nymission@teachcoalition.org>',
    userSubject: 'Thank You For Donation - Teach NYS Mission to Albany 2024',
    amounts: [
      [10000, `Assembly Circle<span>Upgrade to a suite, your name on marketing materials, introduce key note speaker</span>`],
      [5000, `Council Circle<span>Upgrade to a suite, your name on marketing materials, and introduce speaker</span>`],
      [3600, `Capitol Circle<span>Upgrade to a suite and your name on marketing materials</span>`],
      [1800, `State Circle<span>Upgrade to a suite</span>`],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional(),
        })
      })
    }
  },
  'teach-moms-teaneck': {
    emailTemplate: 'teach-moms',
    title: 'Teach NJ - Moms Night Out - Teaneck',
    department: 'TeachCoalition_NJ',
    campaign: 'Moms Night Out',
    subCampaign: 'Teaneck',
    message: 'Thank you for registering!<br>The event will take place at the home of Deena Friedman at 119 Copley Ave Teaneck, NJ 07666.<br>If you have any questions, contact Maya Joyandeh at 201-613-2447.',
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','KatzK@teachcoalition.org','joyandehm@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'rachel.spinner@teachcoalition.org'],
    adminFromEmail: 'Teach NJ <teachnj@teachcoalition.org>',
    adminSubject: 'A payment has been made to Teach NJ - Moms Night Out - Teaneck',
    userFromEmail: 'Teach NJ <teachnj@teachcoalition.org>',
    userSubject: 'Thank you for registering!',
    amounts: [
      [36, `Bronze`],
      [72, `Silver`],
      [180, `Gold`],
      [360, `Diamond`]
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          ticketsQnt: z.number().positive()
        })
      })
    }
  },
  'teach-moms-englewood': {
    emailTemplate: 'teach-moms-englewood',
    title: 'Teach NJ - Moms Night Out - Englewood',
    department: 'TeachCoalition_NJ',
    campaign: 'Moms Night Out',
    subCampaign: 'Englewood',
    message: 'Thank you for registering!<br>The event will take place at the home of Rebecca Markoff at 21 Chestnut Court, Englewood, NJ 07631. Parking is limited, so carpooling is encouraged!<br>If you have any questions, contact Maya Joyandeh at 201-613-2447.',
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','KatzK@teachcoalition.org','joyandehm@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'rachel.spinner@teachcoalition.org'],
    adminFromEmail: 'Teach NJ <teachnj@teachcoalition.org>',
    adminSubject: 'A payment has been made to Teach NJ - Moms Night Out - Englewood',
    userFromEmail: 'Teach NJ <teachnj@teachcoalition.org>',
    userSubject: 'Thank you for registering!',
    amounts: [
      [36, `Bronze`],
      [72, `Silver`],
      [180, `Gold`],
      [360, `Diamond`]
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'teach-vision': {
    emailTemplate: 'teach-vision',
    title: '2024 Teach Coalition Visionaries Retreat',
    department: 'TeachCoalition',
    campaign: 'General Donation',
    subCampaign: '',
    message: "Thank you for registering for the 2024 Teach Coalition Visionaries Retreat.<br><br>We are excited to welcome you on November 13th to the Diplomat Hotel in Hollywood Florida.<br><br>Please be at the hotel by 5:30 pm for Cocktail Reception followed by a Dinner Reception and Keynote Speaker.<br><br>We invite you to extend your stay at the Diplomat Hotel in Hollywood Florida with an exclusive Teach Coalition group discount code. Please email Ariana Dalezman at <a href='dalezmana@teachcoalition.org'>dalezmana@teachcoalition.org</a>.",
    amounts: [
      [1000, `Individual without Hotel`],
      [1500, `Individual - Standard Room`],
      [1800, `Individual - Diplomat Suite`],
      [2000, `Individual - Corner Suite`],
      [2500, `Individual - Presidential Suite`],
      [1500, `Couple without Hotel`],
      [2000, `Couple - Standard Room`],
      [2300, `Couple - Diplomat Suite`],
      [2500, `Couple - Corner Suite`],
      [3000, `Couple - Presidential Suite`],
      [5000, `Bronze Sponsor`],
      [18000, `Silver Sponsor`],
      [25000, `Gold Sponsor`],
    ],
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition Visionaries Retreat <Information@Teachcoalition.Org>',
    adminSubject: 'New Submission - Teach Coalition Visionaries Retreat',
    userFromEmail: 'Teach Coalition Visionaries Retreat <Information@Teachcoalition.Org>',
    userSubject: 'Payment Confirmed!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          coupon: z.string().nullable().optional(),
          PackageType: z.string().nullable().optional(),
        })
      })
    }
  },


  'teach-fl-breakfast': {
    emailTemplate: 'teach-fl-breakfast',
    title: 'Teach Florida Annual Legislative Breakfast',
    department: 'TeachCoalition_FL',
    campaign: 'Florida Legislative Breakfast',
    subCampaign: '',
    message: 'Thank you for registering for Teach Florida 2025 Legislative Breakfast.<br><br>Thousands of students depend on our work and we appreciate your partnership.<br><br> We look forward to seeing you there.',
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','danielaqua@teachcoalition.org','GlaserM@teachcoalition.org', 'yarkonye@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'elisheva.luck@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <information@teachcoalition.org>',
    adminSubject: 'A payment has been made to the Teach Florida Breakfast',
    userFromEmail: 'Teach Coalition <information@teachcoalition.org>',
    userSubject: 'Thank You For Donation - Teach Florida',
    amounts: [
      [36000, `Governor's Circle <span>(incl. Table of 10 & preferred seating)</span>`],
      [25000, `Lieutenant Governor Circle <span>(incl. Table of 10 & preferred seating)</span>`],
      [18000, `Senate Circle <span>(incl. Table of 10 & preferred seating)</span>`],
      [10000, `House Circle <span>(incl. Table of 10 & preferred seating)</span>`],
      [5000, `Capitol Circle <span>(incl. 4 tickets & preferred seating)</span>`],
      [3600, `Judicial Circle <span>(incl. 4 tickets & preferred seating)</span>`],
      [2500, `Legislative Circle <span>(incl. 4 tickets & preferred seating)</span>`],
      [1800, `Leadership Circle (incl. 2 tickets & preferred seating)`],
      [500, `<span>(incl. 2 tickets)</span>`],
      [180, `Per ticket`],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          mediaRelease: zBool().refine((v) => v === true, { message: "Required" }),
          guests: z.string().nullable(),
          ticketsQnt: z.string().nullable().optional(),
          guestsName1: z.string().nullable().optional(),
          guestsEmail1: z.string().nullable().optional(),
          guestsName2: z.string().nullable().optional(),
          guestsEmail2: z.string().nullable().optional(),
          guestsName3: z.string().nullable().optional(),
          guestsEmail3: z.string().nullable().optional(),
          guestsName4: z.string().nullable().optional(),
          guestsEmail4: z.string().nullable().optional(),
          guestsName5: z.string().nullable().optional(),
          guestsEmail5: z.string().nullable().optional(),
          guestsName6: z.string().nullable().optional(),
          guestsEmail6: z.string().nullable().optional(),
          guestsName7: z.string().nullable().optional(),
          guestsEmail7: z.string().nullable().optional(),
          guestsName8: z.string().nullable().optional(),
          guestsEmail8: z.string().nullable().optional(),
          guestsName9: z.string().nullable().optional(),
          guestsEmail9: z.string().nullable().optional(),
          guestsName10: z.string().nullable().optional(),
          guestsEmail10: z.string().nullable().optional(),
          schoolName: z.string().nullable().optional(),
          schoolName2: z.string().nullable().optional(),
        })
      })
    }
  },
  'teach-florida-breakfast-payment': {
    emailTemplate: 'teach-florida-breakfast-payment',
    title: 'Teach Florida Annual Legislative Breakfast - Payment',
    department: 'TeachCoalition_FL',
    campaign: 'Florida Legislative Breakfast',
    subCampaign: '',
     amounts: [
            [36000, `Governor's Circle`],
      [25000, `Lieutenant Governor Circle`],
      [18000, `Senate Circle`],
      [10000, `House Circle`],
      [5000, `Capitol Circle`],
      [3600, `Judicial Circle`],
      [2500, `Legislative Circle`],
      [1800, `Leadership Circle`],
      [500, `Two Tickets`],
     ],
    message: 'Thank you for registering for Teach Florida 2025 Legislative Breakfast.<br><br>Thousands of students depend on our work and we appreciate your partnership.<br><br> We look forward to seeing you there.',
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','danielaqua@teachcoalition.org','GlaserM@teachcoalition.org', 'yarkonye@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'elisheva.luck@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <information@teachcoalition.org>',
    adminSubject: 'A payment has been made to the Teach Florida Breakfast - Payment',
    userFromEmail: 'Teach Coalition <information@teachcoalition.org>',
    userSubject: 'Thank You For Donation - Teach Florida',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
  },
  'teach-fl-cyc': {
    template: 'teach-fl-breakfast',
    emailTemplate: 'teach-fl-breakfast',
    title: 'Teach Florida Annual Legislative Breakfast',
    department: 'TeachCoalition_FL',
    campaign: 'Florida Legislative Breakfast',
    subCampaign: '',
    message: 'Thank you for registering for Teach Florida 2023 Legislative Breakfast.<br><br>Thousands of students depend on our work and we appreciate your partnership.<br><br> We look forward to seeing you there.',
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','danielaqua@teachcoalition.org','GlaserM@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'elisheva.luck@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <information@teachcoalition.org>',
    adminSubject: 'A payment has been made to the Teach Florida Breakfast',
    userFromEmail: 'Teach Coalition <information@teachcoalition.org>',
    userSubject: 'Thank You For Donation - Teach Florida',
    amounts: [
      [36000, `Governor's Circle*`],
      [25000, `Lieutenant Governor Circle*`],
      [18000, `Senate Circle*`],
      [10000, `House Circle*`],
      [5000, `Capitol Circle*`],
      [3600, `Judicial Circle*`],
      [2500, `Legislative Circle*`],
      [1800, `State Circle* (4 tickets)`],
      [500, `Contributor (2 tickets)`],
      [360, `Contributor (2 tickets)`],
      [100, `Per ticket (Cost + Donation)`],
      [30, `Cover Your Cost`],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          guests: z.string().nullable(),
          ticketsQnt: z.string().nullable().optional(),
          guestsName1: z.string().nullable().optional(),
          guestsEmail1: z.string().nullable().optional(),
          guestsName2: z.string().nullable().optional(),
          guestsEmail2: z.string().nullable().optional(),
          guestsName3: z.string().nullable().optional(),
          guestsEmail3: z.string().nullable().optional(),
          guestsName4: z.string().nullable().optional(),
          guestsEmail4: z.string().nullable().optional(),
          guestsName5: z.string().nullable().optional(),
          guestsEmail5: z.string().nullable().optional(),
          guestsName6: z.string().nullable().optional(),
          guestsEmail6: z.string().nullable().optional(),
          guestsName7: z.string().nullable().optional(),
          guestsEmail7: z.string().nullable().optional(),
          guestsName8: z.string().nullable().optional(),
          guestsEmail8: z.string().nullable().optional(),
          guestsName9: z.string().nullable().optional(),
          guestsEmail9: z.string().nullable().optional(),
          guestsName10: z.string().nullable().optional(),
          guestsEmail10: z.string().nullable().optional(),
        })
      })
    }
  },
  'teach-fl-tallahasse': {
    emailTemplate: 'teach-fl-tallahasse',
    title: 'Teach Florida Tallahassee Mission',
    department: 'TeachCoalition_FL',
    campaign: 'Teach Florida Mission to Tallahassee',
    subCampaign: '',
    message: 'Thank you for registering for Teach Florida 2026 Tallahassee Mission<br><br> We look forward to seeing you there.',
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','danielaqua@teachcoalition.org','GlaserM@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'elisheva.luck@teachcoalition.org'],
    adminFromEmail: 'Jason Grossman <grossmanj@teachcoalition.org>',
    adminSubject: 'A payment has been made to the Teach Tallahassee Mission',
    userFromEmail: 'Jason Grossman <grossmanj@teachcoalition.org>',
    userSubject: 'Thank You For Registering - Teach FL Mission to Tallahassee 2026',
    amounts: [
      [250, `Registration (Tampa & Orlando No Flight)`],
      [500, `Registration (Early Bird)`],
      [3600, `VIP Experience (1 ticket)`],
      [10000, `VIP Experience (2 tickets)`],
      [10000, `Lunch Sponsor`],
      [10000, `Dinner Sponsor`],
      [20000, `Mission Sponsor`],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          schoolName: z.string().nullable().optional(),
          student: zBool().nullable().optional(),
        })
      })
    }
  },
  'teach-nj-dinner': {
    emailTemplate: 'teach-nj-dinner',
    title: 'Teach NJ Annual Legislative Dinner',
    department: 'TeachCoalition_NJ',
    campaign: 'Dinner Donation',
    subCampaign: '',
    message: 'We are looking forward to see you at the Annual Teach NJ Dinner on Tuesday, May 16 at Ahavath Torah! <br><br>For any questions, please contact Maya Joyandeh at 617-913-8140 or <a href="mailto:joyandehm@teachcoalition.org">joyandehm@teachcoalition.org</a>.',
    adminEmails: ['teachnj@teachcoalition.org', 'teachnjdinner@teachcoalition.org', 'benshabats@teachcoalition.org','altfields@teachcoalition.org','KatzK@teachcoalition.org','joyandehm@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'rachel.spinner@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <teachnj@teachcoalition.org>',
    adminSubject: 'A payment has been made to the Teach NJ Legislative Dinner',
    userFromEmail: 'Teach Coalition <teachnj@teachcoalition.org>',
    userSubject: 'Thank You For Donation - Teach NJ',
    amounts: [
      [50000, `$50,000 - Visionary Circle <span>(includes 10 tickets and full page journal)</span>`],
      [36000, `$36,000 - Governor’s Circle <span>(includes 10 tickets and full page journal)</span>`],
      [18000, `$18,000 - Speaker Circle <span>(includes 10 tickets and half page journal)</span>`],
      [10000, `$10,000 - Senate Circle <span>(includes 10 tickets and half page journal)</span>`],
      [5000, `$5,000 - Assembly Circle <span>(includes 5 tickets and quarter page journal)</span>`],
      [3600, `$3,600 - Capitol Circle <span>(includes 2 tickets and full page journal)</span>`],
      [1800, `$1,800 - State Circle <span>(includes 2 tickets and quarter page journal)</span>`],
      [1000, `$1,000 - Supporter <span>(includes 2 tickets and quarter page journal)</span>`],
      [720, `$720 - Contributor <span>(includes 2 tickets and journal listing)</span>`],
      [500, `$500 - Friend <span>(includes 2 tickets)</span>`],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          mediaRelease: zBool().refine((v) => v === true, { message: "Required" }),
          AttendInPerson: z.string().nullable().optional(),
          preK: z.string().nullable().optional(),
          guests: z.string().nullable().optional(),
          tablename: z.string().nullable().optional(),
          seatingRequests: z.string().nullable().optional(),
          scrollhonor: z.string().nullable().optional(),
          ticketsQnt: z.string().nullable().optional(),
          honorOf: z.string().array().nullable().optional(),
          journal: z.string().nullable().optional()
        })
      }).refine((data) => {
        const amount = data.amount
        if (amount >= 250) {
          return data.custom?.AttendInPerson !== null && data.custom?.AttendInPerson !== undefined
        }
        return true
      }, {
        message: "Attendance selection is required for donations of $250 or more",
        path: ["custom", "AttendInPerson"]
      })
    }
  },
  'teach-nys-dinner': {
    emailTemplate: 'teach-nys-dinner',
    title: 'Teach NYS Annual Legislative Dinner',
    department: 'TeachCoalition_NYS',
    campaign: 'Dinner Donation',
    subCampaign: '',
    message: 'We are looking forward to seeing you at the Annual Teach NY Dinner on Wednesday, May 10 at Woodmere Country Club! For any questions, please contact Ariana Dalezman at (551) 336-4437 or <a href="mailto:dalezmana@teachcoaltion.org">dalezmana@teachcoaltion.org</a>',
    adminEmails: ['teachnysdinner@ou.org', 'benshabats@teachcoalition.org','altfields@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'rachel.spinner@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <teachnys@teachcoalition.org>',
    adminSubject: 'A payment has been made to the Teach NYS Legislative Dinner',
    userFromEmail: 'Teach Coalition <teachnys@teachcoalition.org>',
    userSubject: 'Thank You For Donation - Teach NYS',
    amounts: [
      [50000, `$50,000 - Governor's Circle <span>(includes 2 tables and full page journal)</span>`],
      [36000, `$36,000 - Speaker’s Circle <span>(includes 2 tables and half page journal)</span>`],
      [18000, `$18,000 - Senate Circle <span>(includes 2 tables and quarter page journal)</span>`],
      [10000, `$10,000 - Assembly Circle <span>(includes 10 tickets and full page journal)</span>`],
      [5000, `$5,000 - Capitol Circle <span>(includes 10 tickets and half page journal)</span>`],
      [1800, `$1,800 - State Circle <span>(includes 2 tickets and half page journal)</span>`],
      [720, `$720 - Contributor <span>(includes 2 tickets and journal entry)</span>`],
      [500, `$500 - Friend <span>(includes 2 tickets)</span>`],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          mediaRelease: zBool().refine((v) => v === true, { message: "Required" }),
          AttendInPerson: z.string().nullable().optional(),
          preK: z.string().nullable().optional(),
          guests: z.string().nullable().optional(),
          tablename: z.string().nullable().optional(),
          seatingRequests: z.string().nullable().optional(),
          scrollhonor: z.string().nullable().optional(),
          ticketsQnt: z.string().nullable().optional(),
          schoolHonorName: z.string().nullable().optional(),
          schoolName: z.string().nullable().optional(),
          honorOf: z.string().array().nullable().optional(),
          journal: z.string().nullable().optional()
        })
      })
    }
  },
  'teach-li-breakfast': {
    emailTemplate: 'teach-li-breakfast',
    title: 'Teach NYS Annual Legislative Breakfast',
    department: 'TeachCoalition_NYS',
    campaign: 'Dinner Donation',
    subCampaign: '',
    message: 'Thank you for registering for Teach NYS 2024 Legislative Breakfast.<br><br> We look forward to seeing you there.',
    adminEmails: ['teachnysdinner@ou.org', 'benshabats@teachcoalition.org','altfields@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'rachel.spinner@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <teachnys@teachcoalition.org>',
    adminSubject: 'A payment has been made to the Teach NYS Breakfast',
    userFromEmail: 'Teach Coalition <teachnys@teachcoalition.org>',
    userSubject: 'Thank You For Donation - Teach NYS',
    amounts: [
      [36000, `Governor's Circle*`],
      [25000, `Lieutenant Governor Circle*`],
      [18000, `Senate Circle*`],
      [10000, `House Circle*`],
      [5000, `Capitol Circle*`],
      [3600, `Judicial Circle*`],
      [2500, `Legislative Circle*`],
      [1800, `Leadership Circle* (2 tickets)`],
      [118, `Per ticket`],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          guests: z.string().nullable(),
          ticketsQnt: z.string().nullable().optional(),
          guestsName1: z.string().nullable().optional(),
          guestsEmail1: z.string().nullable().optional(),
          guestsName2: z.string().nullable().optional(),
          guestsEmail2: z.string().nullable().optional(),
          guestsName3: z.string().nullable().optional(),
          guestsEmail3: z.string().nullable().optional(),
          guestsName4: z.string().nullable().optional(),
          guestsEmail4: z.string().nullable().optional(),
          guestsName5: z.string().nullable().optional(),
          guestsEmail5: z.string().nullable().optional(),
          guestsName6: z.string().nullable().optional(),
          guestsEmail6: z.string().nullable().optional(),
          guestsName7: z.string().nullable().optional(),
          guestsEmail7: z.string().nullable().optional(),
          guestsName8: z.string().nullable().optional(),
          guestsEmail8: z.string().nullable().optional(),
          guestsName9: z.string().nullable().optional(),
          guestsEmail9: z.string().nullable().optional(),
          guestsName10: z.string().nullable().optional(),
          guestsEmail10: z.string().nullable().optional(),
          schoolName: z.string()
        })
      })
    }
  },
    'teach-fl-vendors': {
    emailTemplate: 'teach-fl-vendors',
    title: 'Teach Florida School Conference - Vendor Registration',
    department: 'TeachCoalition_FL',
    campaign: 'Mini Conference',
    subCampaign: '',
    message: 'Thank you for your generous sponsorship of the Teach Florida 5th Annual Member School & Vendor Conference.<br><br>We truly appreciate your partnership and look forward to seeing you at the event!',
    adminEmails: ['goldmani@ou.org', 'KownatM@teachcoalition.org', 'GlaserM@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'elisheva.luck@teachcoalition.org'],
    adminFromEmail: 'Teach FL <Information@Teachcoalition.Org>',
    adminSubject: 'A payment has been made to Teach Florida School Conference - Vendor Registration',
    userFromEmail: 'Teach FL <Information@Teachcoalition.Org>',
    userSubject: 'Thank You For Registration - Teach FL',
    amounts: [
      [2500, `
        <span>STANDARD SPONSORSHIP</span> <em>Entry Level</em> <strong>$2,500</strong>
        <p>Your sponsorship includes:</p>
        <ul>
          <li><strong>2 Conference Tickets</strong>: Join school leaders and fellow vendors at our 5th Annual Member School Conference.</li>
          <li><strong>Exhibition Booth</strong>: Showcase your products and services with a dedicated booth at the event.</li>
          <li><strong>Digital Brand Visibility</strong>: Your company's logo will be featured in conference-related digital and email communications.</li>
        </ul>
      `],
      [5000, `
        <span>GOLD SPONSORSHIP</span> <em>Featured Partner</em> <strong>$5,000</strong>
        <p>Your sponsorship includes:</p>
        <ul>
          <li><strong>2 Conference Tickets:</strong> Attend the 5th Annual Member School Conference and engage with school leaders.</li>
          <li><strong>2 VIP Tickets:</strong> Enjoy VIP access to the Teach Florida Annual Breakfast.</li>
          <li><strong>Premium Double Booth:</strong> Showcase your company with an expanded double booth in a preferred, high-visibility location, with placement following Platinum Sponsors.</li>
          <li><strong>Prominent Event Signage:</strong> Your company's logo will be featured on prominent signage at both events, enhancing your brand visibility.</li>
          <li><strong>Enhanced Digital Promotion:</strong> Your logo will be featured in social media and email promotions leading up to both events.</li>
        </ul>
      `],
      [10000, `
        <span>PLATINUM SPONSORSHIP</span> <em>Premier Partner</em> <strong>$10,000</strong>
        <p>Your sponsorship includes:</p>
        <ul>
          <li><strong>4 Conference Tickets:</strong> Bring your team to the 5th Annual Member School Conference and engage with school leaders.</li>
          <li><strong>4 VIP Tickets:</strong> Enjoy VIP access to the Teach Florida Annual Breakfast.</li>
          <li><strong>Premier Double Booth</strong>: Showcase your company with an expanded double booth in a premier, high-visibility location at the conference.</li>
          <li><strong>Prominent Event Signage</strong>: Your company's logo will be prominently displayed on signage throughout both events.</li>
          <li><strong>Featured Digital Promotion</strong>: Your logo will be featured in social media and email promotions supporting both events.</li>
          <li><strong>Exclusive Presentation Opportunity</strong>: Deliver a short presentation to introduce your organization, share your expertise, and connect with our member school leaders.</li>
        </ul>
      `]
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          businessName: z.string().nullable(),
          subjectCategory: z.string().nullable().optional(),
          booth: z.string().nullable().optional(),
          presentSession: z.string().nullable().optional()
        })
      })
    }
  },
  'teach-schools-dues': {
    emailTemplate: 'teach-general',
    title: 'Teach Pledge',
    department: '',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for donating to Teach Coalition',
    thankYouTemplate: 'teach',
    adminEmails: (request) => {
      const to = 'benshabats@teachcoalition.org,goldmani@ou.org,Caleb.Fischer@teachcoalition.org'.split(',')
      const mapping = {
        TeachCoalition_Gov_Max: 'altfields@teachcoalition.org,Kanofskyy@teachcoalition.org,paserm@teachcoalition.org',
        TeachCoalition_NJ: 'altfields@teachcoalition.org,KatzK@teachcoalition.org,joyandehm@teachcoalition.org,rachel.spinner@teachcoalition.org',
        TeachCoalition_PA: 'altfields@teachcoalition.org,jenny.sved@teachcoalition.org,LevensonH@teachcoalition.org',
        TeachCoalition_MD: 'altfields@teachcoalition.org,jenny.sved@teachcoalition.org,Mickey.Rubin@teachcoalition.org',
        TeachCoalition_NYS: 'kanofskyy@teachcoalition.org,altfields@teachcoalition.org,paserm@teachcoalition.org,rachel.spinner@teachcoalition.org',
        TeachCoalition_FL: 'altfields@teachcoalition.org,danielaqua@teachcoalition.org,GlaserM@teachcoalition.org,elisheva.luck@teachcoalition.org'
      } as any
      const app = request.applications?.[0]
      const email = app && app.department ? mapping[app.department] : undefined
      if (email) to.push(email)
      // Custom conditional: add Rybsteinr when Gov_Max + Project Protect Grantwork
      if (app?.department === 'TeachCoalition_Gov_Max' && app?.campaign === 'Project Protect Grantwork') {
        to.push('Rybsteinr@teachcoalition.org')
      }
      // Deduplicate
      return Array.from(new Set(to))
    },
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s Pledge',
    userFromEmail: 'Teach Coalition <Information@teachcoalition.org>',
    userSubject: 'Thank you for your support - Teach Coalition'
  },
  'teach-fl-schools': {
    emailTemplate: 'teach-fl-schools',
    title: 'Teach Florida School Dues',
    department: 'TeachCoalition_FL',
    campaign: 'School Dues',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Teach Florida',
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','danielaqua@teachcoalition.org','GlaserM@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'elisheva.luck@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A payment has been made to Teach Florida School Dues',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Florida School Dues',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          schoolName: z.string(),
          schoolId: z.string()
        })
      })
    }
  },
  'teach-nys-schools': {
    emailTemplate: 'teach-nys-schools',
    title: 'Teach NYS School Dues',
    department: 'TeachCoalition_NYS',
    campaign: 'School Dues',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Teach New York',
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','Kanofskyy@teachcoalition.org', 'paserm@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'rachel.spinner@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A payment has been made to Teach New York School Dues',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach New York School Dues',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          schoolName: z.string(),
          schoolId: z.string()
        })
      })
    }
  },
  'teach-nj-schools': {
    emailTemplate: 'teach-nj-schools',
    title: 'Teach New Jersey School Dues',
    department: 'TeachCoalition_NJ',
    campaign: 'School Dues',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Teach New Jersey',
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','Kanofskyy@teachcoalition.org','KatzK@teachcoalition.org','joyandehm@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'rachel.spinner@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A payment has been made to Teach New Jersey School Dues',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach New Jersey School Dues',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          schoolName: z.string(),
          schoolId: z.string()
        })
      })
    }
  },
  'teach-fl-donate': {
    emailTemplate: 'teach-general',
    title: 'Teach Coalition’s Florida Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_FL',
    campaign: 'General Donation',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Teach Florida',
    amounts: [
      10000,
      5000,
      2500,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','danielaqua@teachcoalition.org','GlaserM@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'elisheva.luck@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A payment has been made to Teach Florida Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Florida Donation',
    silverPopContactLists (request) {
      const map = {
        NYS: 4761244,
        FL: 7084757,
        MD: 4761251,
        CA: 4761249,
        PA: 4761247,
        NJ: 4761246
      } as const

      let state: string

      if (request.applications![0].department == 'TeachCoalition') {
        state = request.address!.street
        if (state == 'NY') state = 'NYS'
      }
      else {
        state = request.applications![0].department!.split('_')[1]
      }

      //@ts-ignore
      if (state in map) return [map[state]]

      return [6995481]
    }
  },
  'teach-fl-eofy': {
    emailTemplate: 'teach-fl-eofy',
    title: 'Teach Coalition’s Florida Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_FL',
    campaign: 'General Donation',
    subCampaign: '',
    thankYouTemplate: 'teach-fl-eofy',
    message: 'Thank you for donating to Teach Florida',
    amounts: [
      10000,
      5000,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','danielaqua@teachcoalition.org','GlaserM@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'elisheva.luck@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A payment has been made to Teach Florida EOFY Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Thank You For Donation - Teach Florida',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    silverPopContactLists: [7084757],
  },
  'teach-nys-donate': {
    emailTemplate: 'teach-general',
    title: 'Teach Coalition’s New York Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_NYS',
    campaign: 'General Donation',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Teach New York',
    amounts: [
      5000,
      1800,
      500,
      360
    ],
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','Rebecca.Ehrlich@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'rachel.spinner@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A payment has been made to Teach New York Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach New York Donation',
    silverPopContactLists (request) {
      const map = {
        NYS: 4761244,
        FL: 7084757,
        MD: 4761251,
        CA: 4761249,
        PA: 4761247,
        NJ: 4761246
      } as const

      let state: string

      if (request.applications![0].department! == 'TeachCoalition') {
        state = request.address!.street
        if (state == 'NY') state = 'NYS'
      }
      else {
        state = request.applications![0].department!.split('_')[1]
      }

      //@ts-ignore
      if (state in map) return [map[state]]

      return [6995481]
    }
  },
  'teach-nj-donate': {
    emailTemplate: 'teach-general',
    title: 'Teach Coalition’s New Jersey Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_NJ',
    campaign: 'General Donation',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Teach New Jersey',
    amounts: [
      10000,
      5000,
      2500,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','KatzK@teachcoalition.org','joyandehm@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'rachel.spinner@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A payment has been made to Teach New Jersey Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach New Jersey Donation',
    silverPopContactLists (request) {
      const map = {
        NYS: 4761244,
        FL: 7084757,
        MD: 4761251,
        CA: 4761249,
        PA: 4761247,
        NJ: 4761246
      } as const

      let state: string

      if (request.applications![0].department! == 'TeachCoalition') {
        state = request.address!.street
        if (state == 'NY') state = 'NYS'
      }
      else {
        state = request.applications![0].department!.split('_')[1]
      }

      //@ts-ignore
      if (state in map) return [map[state]]

      return [6995481]
    }
  },
  'teach-general': {
    emailTemplate: 'teach-general',
    title: 'Teach Coalition’s General Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition',
    campaign: 'General Donation',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
      10000,
      5000,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: (request) => {
      const to = 'benshabats@teachcoalition.org,goldmani@ou.org,Caleb.Fischer@teachcoalition.org'.split(',')
      const mapping = {
        TeachCoalition_NJ: 'altfields@teachcoalition.org,KatzK@teachcoalition.org,joyandehm@teachcoalition.org,rachel.spinner@teachcoalition.org',
        TeachCoalition_PA: 'altfields@teachcoalition.org,jenny.sved@teachcoalition.org,LevensonH@teachcoalition.org',
        TeachCoalition_MD: 'altfields@teachcoalition.org,jenny.sved@teachcoalition.org,Mickey.Rubin@teachcoalition.org',
        TeachCoalition_NYS: 'altfields@teachcoalition.org,rachel.spinner@teachcoalition.org',
        TeachCoalition_FL: 'altfields@teachcoalition.org,danielaqua@teachcoalition.org,GlaserM@teachcoalition.org,elisheva.luck@teachcoalition.org',
        TeachCoalition_CA: 'markm@teachcoalition.org,neuriel.shore@teachcoalition.org',
        TeachCoalition_NV: 'markm@teachcoalition.org',
        TeachCoalition_ProjectProtect: 'goldmani@ou.org,projectprotect@teachcoalition.org'
      } as any
      const email = mapping[request.applications![0].department!]
      if (email) to.push(email)
      return to
    },
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s General Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s General Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    silverPopContactLists (request) {
      const map = {
        NYS: 4761244,
        FL: 7084757,
        MD: 4761251,
        CA: 4761249,
        PA: 4761247,
        NJ: 4761246
      } as const

      let state: string

      if (request.applications![0].department! == 'TeachCoalition') {
        state = request.address!.street
        if (state == 'NY') state = 'NYS'
      }
      else {
        state = request.applications![0].department!.split('_')[1]
      }

      //@ts-ignore
      if (state in map) return [map[state]]

      return [6995481]
    }
  },
    'teach-safe': {
    emailTemplate: 'teach-safe',
    title: 'Support Teach Coalition Campaign for Safe, Affordable, High-Quality Jewish Education',
    department: 'TeachCoalition',
    campaign: 'Year End Campaign',
    subCampaign: '',
    message: "Thank you for supporting Teach Coalition. <br><br>Your gift fuels our advocacy efforts across seven states—New York, New Jersey, Florida, Pennsylvania, Maryland, California, and Nevada—helping us fight for safe, affordable, high-quality Jewish education for tens of thousands of students and families. <br><br>Because of supporters like you, Teach Coalition can continue securing critical funding for school security, expanding educational opportunity, reducing the cost burden on families, and ensuring that Jewish schools have the resources they need to thrive. <br><br>Together, we are building a stronger future for Jewish education.<br><br> Thank you for standing with us.",
    amounts: [
      36,
      72,
      120,
      180,
      250,
      360,
      500,
      720,
      1000
    ],
    adminEmails: (request) => {
      const to = 'benshabats@teachcoalition.org,goldmani@ou.org,elina.mosheyeva@teachcoalition.org,Caleb.Fischer@teachcoalition.org'.split(',')
      const mapping = {
        TeachCoalition_NJ: 'altfields@teachcoalition.org,KatzK@teachcoalition.org,joyandehm@teachcoalition.org,rachel.spinner@teachcoalition.org',
        TeachCoalition_PA: 'altfields@teachcoalition.org,jenny.sved@teachcoalition.org,LevensonH@teachcoalition.org',
        TeachCoalition_MD: 'altfields@teachcoalition.org,jenny.sved@teachcoalition.org,Mickey.Rubin@teachcoalition.org',
        TeachCoalition_NYS: 'altfields@teachcoalition.org,rachel.spinner@teachcoalition.org',
        TeachCoalition_FL: 'altfields@teachcoalition.org,danielaqua@teachcoalition.org,GlaserM@teachcoalition.org,elisheva.luck@teachcoalition.org',
        TeachCoalition_CA: 'markm@teachcoalition.org,neuriel.shore@teachcoalition.org',
        TeachCoalition_NV: 'markm@teachcoalition.org',
        TeachCoalition_ProjectProtect: 'goldmani@ou.org,projectprotect@teachcoalition.org'
      } as any
      const email = mapping[request.applications![0].department!]
      if (email) to.push(email)
      return to
    },
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s Campaign for Safe Form',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Thank you for your generous donation to Support Teach Coalition Campaign for Safe, Affordable, High-Quality Jewish Education',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'teach-strongfuture': {
    emailTemplate: 'teach-general',
    title: 'Teach Coalition’s General Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition',
    campaign: 'Strong Future Campaign',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
      10000,
      5000,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: (request) => {
      const to = 'benshabats@teachcoalition.org,goldmani@ou.org,Caleb.Fischer@teachcoalition.org'.split(',')
      const mapping = {
        TeachCoalition_NJ: 'benshabats@teachcoalition.org,altfields@teachcoalition.org,KatzK@teachcoalition.org,joyandehm@teachcoalition.org,rachel.spinner@teachcoalition.org',
        TeachCoalition_PA: 'benshabats@teachcoalition.org,altfields@teachcoalition.org,jenny.sved@teachcoalition.org,LevensonH@teachcoalition.org',
        TeachCoalition_MD: 'benshabats@teachcoalition.org,altfields@teachcoalition.org,jenny.sved@teachcoalition.org,Mickey.Rubin@teachcoalition.org',
        TeachCoalition_NYS: 'benshabats@teachcoalition.org,altfields@teachcoalition.org,rachel.spinner@teachcoalition.org',
        TeachCoalition_FL: 'benshabats@teachcoalition.org,altfields@teachcoalition.org,danielaqua@teachcoalition.org,GlaserM@teachcoalition.org,elisheva.luck@teachcoalition.org',
        TeachCoalition_ProjectProtect: 'benshabats@teachcoalition.org,goldmani@ou.org,projectprotect@teachcoalition.org'
      } as any
      const email = mapping[request.applications![0].department!]
      if (email) to.push(email)
      return to
    },
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s General Donation (strongfuture)',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s General Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    silverPopContactLists (request) {
      const map = {
        NYS: 4761244,
        FL: 7084757,
        MD: 4761251,
        CA: 4761249,
        PA: 4761247,
        NJ: 4761246
      } as const

      let state: string

      if (request.applications![0].department! == 'TeachCoalition') {
        state = request.address!.street
        if (state == 'NY') state = 'NYS'
      }
      else {
        state = request.applications![0].department!.split('_')[1]
      }

      //@ts-ignore
      if (state in map) return [map[state]]

      return [6995481]
    }
  },
  'teach-general-fl': {
    emailTemplate: 'teach-general',
    title: 'Teach Coalition’s FL Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_FL',
    campaign: 'General Donation',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
            18,
            36,
            54,
            180,
            360
    ],
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','danielaqua@teachcoalition.org','GlaserM@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'elisheva.luck@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s FL General Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s FL General Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    silverPopContactLists (request) {
      const map = {
        NYS: 4761244,
        FL: 7084757,
        MD: 4761251,
        CA: 4761249,
        PA: 4761247,
        NJ: 4761246
      } as const

      let state: string

      if (request.applications![0].department! == 'TeachCoalition') {
        state = request.address!.street
        if (state == 'NY') state = 'NYS'
      }
      else {
        state = request.applications![0].department!.split('_')[1]
      }

      //@ts-ignore
      if (state in map) return [map[state]]

      return [6995481]
    }
  },
  'teach-protecteducationfreedom': {
    emailTemplate: 'teach-general',
    title: 'Teach Coalition’s FL Donation (protecteducationfreedom)',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_FL',
    campaign: 'Protect Education Freedom',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
            18,
            36,
            54,
            180,
            360
    ],
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','danielaqua@teachcoalition.org','GlaserM@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'elisheva.luck@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s FL General Donation (protecteducationfreedom)',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s FL General Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    silverPopContactLists (request) {
      const map = {
        NYS: 4761244,
        FL: 7084757,
        MD: 4761251,
        CA: 4761249,
        PA: 4761247,
        NJ: 4761246
      } as const

      let state: string

      if (request.applications![0].department! == 'TeachCoalition') {
        state = request.address!.street
        if (state == 'NY') state = 'NYS'
      }
      else {
        state = request.applications![0].department!.split('_')[1]
      }

      //@ts-ignore
      if (state in map) return [map[state]]

      return [6995481]
    }
  },
    'teach-endofyear': {
    emailTemplate: 'teach-endofyear',
    title: 'Teach Florida End of School Year Fundraising Campaign',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_FL',
    campaign: 'Protect Education Freedom',
    subCampaign: '',
    message: 'Thank you for helping us fund the fight to stop the lawsuit',
    amounts: [
            18,
            36,
            54,
            180,
            360
    ],
    adminEmails: ['grossmanj@teachcoalition.org', 'benshabats@teachcoalition.org','altfields@teachcoalition.org','danielaqua@teachcoalition.org','GlaserM@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'elisheva.luck@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Florida End of School Year Fundraising Campaign',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Florida End of School Year Fundraising Campaign',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    silverPopContactLists (request) {
      const map = {
        NYS: 4761244,
        FL: 7084757,
        MD: 4761251,
        CA: 4761249,
        PA: 4761247,
        NJ: 4761246
      } as const

      let state: string

      if (request.applications![0].department! == 'TeachCoalition') {
        state = request.address!.street
        if (state == 'NY') state = 'NYS'
      }
      else {
        state = request.applications![0].department!.split('_')[1]
      }

      //@ts-ignore
      if (state in map) return [map[state]]

      return [6995481]
    }
  },
  'teach-vote': {
    template: 'teach-vote',
    emailTemplate: 'teach-vote',
    title: 'Teach Coalition’s NYS Vote Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition',
    campaign: 'GOTV Voting Centers',
    subCampaign: '',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
      10000,
      5000,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: [ 'benshabats@teachcoalition.org','altfields@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s NYS Vote Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s Vote Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'teach-vote-nys': {
    template: 'teach-vote',
    emailTemplate: 'teach-vote',
    title: 'Teach Coalition’s NYS Vote Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_NYS',
    campaign: 'LI Unites Voting Center',
    subCampaign: '',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
      25000,
      10000,
      5000,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: [ 'benshabats@teachcoalition.org','altfields@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'rachel.spinner@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s NYS Vote Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s Vote Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'teach-nys-general': {
    template: 'teach-vote',
    emailTemplate: 'teach-general',
    title: 'Teach Coalition’s NYS Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_NYS',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
      25000,
      10000,
      5000,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: [ 'benshabats@teachcoalition.org','altfields@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'rachel.spinner@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s NYS Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'teach-md-general': {
    template: 'teach-vote',
    emailTemplate: 'teach-general',
    title: 'Teach Coalition’s MD Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_MD',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
      25000,
      10000,
      5000,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: [ 'benshabats@teachcoalition.org', 'jenny.sved@teachcoalition.org', 'altfields@teachcoalition.org', 'Mickey.Rubin@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s MD Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'teach-nj-general': {
    template: 'teach-vote',
    emailTemplate: 'teach-general',
    title: 'Teach Coalition’s NJ Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_NJ',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
      25000,
      10000,
      5000,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','KatzK@teachcoalition.org','joyandehm@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'rachel.spinner@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s NJ Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'teach-vote-nv': {
    template: 'teach-vote',
    emailTemplate: 'teach-vote',
    title: 'Teach Coalition’s NV Vote Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_NV',
    campaign: 'GOTV',
    subCampaign: '',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
      10000,
      5000,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: [ 'benshabats@teachcoalition.org', 'jenny.sved@teachcoalition.org', 'altfields@teachcoalition.org', 'MarkM@teachcoalition.org', 'neuriel.shore@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s NV Vote Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s NV Vote Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'teach-nv-general': {
    template: 'teach-vote',
    emailTemplate: 'teach-general',
    title: 'Teach Coalition’s NV Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_NV',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
      10000,
      5000,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: [ 'benshabats@teachcoalition.org', 'jenny.sved@teachcoalition.org', 'altfields@teachcoalition.org', 'MarkM@teachcoalition.org', 'neuriel.shore@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s NV Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s NV Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'teach-vote-fl': {
    template: 'teach-vote',
    emailTemplate: 'teach-vote',
    title: 'Teach Coalition’s FL Vote Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_FL',
    campaign: 'FL Unites Voting Center',
    subCampaign: '',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
      10000,
      5000,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: [ 'benshabats@teachcoalition.org','altfields@teachcoalition.org','danielaqua@teachcoalition.org','GlaserM@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'elisheva.luck@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s FL Vote Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s Vote Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'teach-fl-general': {
    template: 'teach-vote',
    emailTemplate: 'teach-general',
    title: 'Teach Coalition’s FL Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_FL',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
      10000,
      5000,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','danielaqua@teachcoalition.org','GlaserM@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'elisheva.luck@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s FL Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'teach-fl-services': {
    template: 'teach-vote',
    emailTemplate: 'teach-general',
    title: 'Teach Coalition’s FL Services for All Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_FL',
    campaign: 'Services for All',
    subCampaign: '',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
      3600,
      2500,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: [ 'benshabats@teachcoalition.org','altfields@teachcoalition.org','danielaqua@teachcoalition.org','GlaserM@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'elisheva.luck@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s FL Services for All Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s FL Services for All Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'teach-vote-pa': {
    template: 'teach-vote',
    emailTemplate: 'teach-vote',
    title: 'Teach Coalition’s PA Vote Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_PA',
    campaign: 'PA Unites Voting Center',
    subCampaign: '',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
      10000,
      5000,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: [ 'benshabats@teachcoalition.org', 'jenny.sved@teachcoalition.org', 'altfields@teachcoalition.org','levensonh@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s PA Vote Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s Vote Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'teach-pa-general': {
    template: 'teach-vote',
    emailTemplate: 'teach-general',
    title: 'Teach Coalition’s PA Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_PA',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
      10000,
      5000,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: [ 'benshabats@teachcoalition.org', 'jenny.sved@teachcoalition.org', 'altfields@teachcoalition.org','levensonh@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s PA Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'teach-vote-ca': {
    template: 'teach-vote',
    emailTemplate: 'teach-vote',
    title: 'Teach Coalition’s CA Vote Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_CA',
    campaign: 'LA Unites Voting Center',
    subCampaign: '',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
      10000,
      5000,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: [ 'benshabats@teachcoalition.org', 'jenny.sved@teachcoalition.org', 'altfields@teachcoalition.org', 'MarkM@teachcoalition.org', 'neuriel.shore@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s CA Vote Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s Vote Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'teach-ca-general': {
    template: 'teach-vote',
    emailTemplate: 'teach-general',
    title: 'Teach Coalition’s CA Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_CA',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
      10000,
      5000,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: [ 'benshabats@teachcoalition.org', 'jenny.sved@teachcoalition.org', 'altfields@teachcoalition.org', 'MarkM@teachcoalition.org', 'neuriel.shore@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s CA Donation',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5'
  },
  'project-protect': {
    emailTemplate: 'project-protect',
    title: 'Project Protect General Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_Gov_Max',
    campaign: 'General Donation',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Project Protect',
    amounts: [
      18,
      36,
      100,
      250,
      500
    ],
    adminEmails: [ 'benshabats@teachcoalition.org','altfields@teachcoalition.org', 'projectprotect@teachcoalition.org', 'kanofskyy@teachcoalition.org', 'paserm@teachcoalition.org', 'rybsteinr@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <projectprotect@teachcoalition.org>',
    adminSubject: 'A Donation has been made to Project Protect General Donation',
    userFromEmail: 'Teach Coalition <projectprotect@teachcoalition.org>',
    userSubject: 'Project Protect’s General Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
  },
  'teach-trailblazers': {
    emailTemplate: 'teach-general',
    title: 'Teach Coalition’s Trailblazers Donation',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition',
    campaign: 'Trailblazer',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Teach Coalition',
    amounts: [
      10000,
      5000,
      1800,
      500,
      100,
    ],
    adminEmails: (request) => {
      const to = 'benshabats@teachcoalition.org,Trailblazers@teachcoalition.org,goldmani@ou.org,Caleb.Fischer@teachcoalition.org'.split(',')
      const mapping = {
        TeachCoalition_NYS: 'altfields@teachcoalition.org,rachel.spinner@teachcoalition.org',
      } as any
      const email = mapping[request.applications![0].department!]
      if (email) to.push(email)
      return to
    },
    adminFromEmail: 'Teach Coalition Trailblazers <Trailblazers@teachcoalition.org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s Trailblazers Donation',
    userFromEmail: 'Teach Coalition Trailblazers <Trailblazers@teachcoalition.org>',
    userSubject: 'Teach Coalition’s Trailblazers Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    silverPopContactLists: [8327817],
  },
  'teach-camps': {
    emailTemplate: 'teach-general',
    title: 'Support Teach Coalition Summer Camp Network',
    honoreeTemplate: 'teach',
    department: 'Teach Coalition',
    campaign: 'Summer Camp Network',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Teach Coalition Summer Camp Network',
    amounts: [
      10000,
      5000,
      1800,
      500,
      180,
      54,
      36,
      18
    ],
    adminEmails: (request) => {
      const to = 'benshabats@teachcoalition.org,goldmani@ou.org,Caleb.Fischer@teachcoalition.org'.split(',')
      const mapping = {
        TeachCoalition_NJ: 'benshabats@teachcoalition.org,altfields@teachcoalition.org,KatzK@teachcoalition.org,joyandehm@teachcoalition.org,rachel.spinner@teachcoalition.org',
        TeachCoalition_PA: 'benshabats@teachcoalition.org,altfields@teachcoalition.org,jenny.sved@teachcoalition.org,LevensonH@teachcoalition.org',
        TeachCoalition_MD: 'benshabats@teachcoalition.org,altfields@teachcoalition.org,jenny.sved@teachcoalition.org,Mickey.Rubin@teachcoalition.org',
        TeachCoalition_NYS: 'benshabats@teachcoalition.org,altfields@teachcoalition.org,rachel.spinner@teachcoalition.org',
        TeachCoalition_FL: 'benshabats@teachcoalition.org,altfields@teachcoalition.org,danielaqua@teachcoalition.org,GlaserM@teachcoalition.org,elisheva.luck@teachcoalition.org'
      } as any
      const email = mapping[request.applications![0].department!]
      if (email) to.push(email)
      return to
    },
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A Donation has been made to Teach Coalition’s Summer Camp Network',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s Summer Camp Network Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    silverPopContactLists (request) {
      const map = {
        NYS: 4761244,
        FL: 7084757,
        MD: 4761251,
        CA: 4761249,
        PA: 4761247,
        NJ: 4761246
      } as const

      let state: string

      if (request.applications![0].department! == 'TeachCoalition') {
        state = request.address!.street
        if (state == 'NY') state = 'NYS'
      }
      else {
        state = request.applications![0].department!.split('_')[1]
      }

      //@ts-ignore
      if (state in map) return [map[state]]

      return [6995481]
    }
  },
  'teach-pledge': {
    emailTemplate: 'teach-pledge',
    title: 'Teach Coalition’s Pledge',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition',
    campaign: 'General Donation',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Teach Coalition',
    adminEmails: (request) => {
      const to = 'benshabats@teachcoalition.org,goldmani@ou.org,Caleb.Fischer@teachcoalition.org'.split(',')
      const mapping = {
        TeachCoalition_Gov_Max: 'altfields@teachcoalition.org,Kanofskyy@teachcoalition.org,paserm@teachcoalition.org',
        TeachCoalition_NJ: 'altfields@teachcoalition.org,KatzK@teachcoalition.org,joyandehm@teachcoalition.org,rachel.spinner@teachcoalition.org',
        TeachCoalition_PA: 'altfields@teachcoalition.org,jenny.sved@teachcoalition.org,LevensonH@teachcoalition.org',
        TeachCoalition_MD: 'altfields@teachcoalition.org,jenny.sved@teachcoalition.org,Mickey.Rubin@teachcoalition.org',
        TeachCoalition_NYS: 'kanofskyy@teachcoalition.org,altfields@teachcoalition.org,paserm@teachcoalition.org,rachel.spinner@teachcoalition.org',
        TeachCoalition_FL: 'altfields@teachcoalition.org,danielaqua@teachcoalition.org,GlaserM@teachcoalition.org,elisheva.luck@teachcoalition.org',
        TeachCoalition_ProjectProtect: 'kanofskyy@teachcoalition.org,altfields@teachcoalition.org,paserm@teachcoalition.org'
      } as any
      const email = mapping[request.applications![0].department!]
      if (email) to.push(email)
      return to
    },
    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A payment has been made to Teach Coalition’s Pledge Form',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s Pledge',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    silverPopContactLists (request) {
      const map = {
        NYS: 4761244,
        FL: 7084757,
        MD: 4761251,
        CA: 4761249,
        PA: 4761247,
        NJ: 4761246
      } as const

      let state: string

      if (request.applications![0].department == 'TeachCoalition') {
        state = request.address!.street
        if (state == 'NY') state = 'NYS'
      }
      else {
        state = request.applications![0].department!.split('_')[1]
      }

      //@ts-ignore
      if (state in map) return [map[state]]

      return [6995481]
    }
  },
  'teach-legacy-circle': {
    emailTemplate: 'teach-legacy-circle',
    title: 'Teach Coalition’s Leadership Circle',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition',
    campaign: 'General Donation',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Teach Coalition’s Leadership Circle',
    amounts: [
      [50000, `Visionary Circle`],
      [36000, `Governor Circle`],
      [18000, `Speaker Circle`],
      [10000, `Senate Circle`],
      [5000, `Assembly Circle`],
      [2500, `Legislative Circle`],
      [3600, `Capitol Circle`],
      [1800, `State Circle`],
    ],
    adminEmails: (request) => {
      const to = 'goldmani@ou.org,Caleb.Fischer@teachcoalition.org'.split(',')
      const mapping = {
        TeachCoalition_Gov_Max: 'benshabats@teachcoalition.org,altfields@teachcoalition.org,Kanofskyy@teachcoalition.org,paserm@teachcoalition.org',
        TeachCoalition_NJ: 'benshabats@teachcoalition.org,altfields@teachcoalition.org,KatzK@teachcoalition.org,joyandehm@teachcoalition.org,rachel.spinner@teachcoalition.org',
        TeachCoalition_PA: 'benshabats@teachcoalition.org,altfields@teachcoalition.org,jenny.sved@teachcoalition.org,LevensonH@teachcoalition.org',
        TeachCoalition_MD: 'benshabats@teachcoalition.org,altfields@teachcoalition.org,jenny.sved@teachcoalition.org,Mickey.Rubin@teachcoalition.org',
        TeachCoalition_NYS: 'benshabats@teachcoalition.org,altfields@teachcoalition.org,rachel.spinner@teachcoalition.org',
        TeachCoalition_FL: 'benshabats@teachcoalition.org,altfields@teachcoalition.org,danielaqua@teachcoalition.org,GlaserM@teachcoalition.org,elisheva.luck@teachcoalition.org'
      } as any
      const email = mapping[request.applications![0].department!]
      if (email) to.push(email)
      return to
    },

    adminFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    adminSubject: 'A payment has been made to Teach Coalition’s Leadership Circle',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s Leadership Circle',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    silverPopContactLists (request) {
      const map = {
        NYS: 4761244,
        FL: 7084757,
        MD: 4761251,
        CA: 4761249,
        PA: 4761247,
        NJ: 4761246
      } as const

      let state: string

      if (request.applications![0].department == 'TeachCoalition') {
        state = request.address!.street
        if (state == 'NY') state = 'NYS'
      }
      else {
        state = request.applications![0].department!.split('_')[1]
      }

      //@ts-ignore
      if (state in map) return [map[state]]

      return [6995481]
    },
    customValidations (form) {
      return form.extend({
        custom: z.object({
          honorscroll: z.string().nullable().optional(),
        })
      })
    }
  },
  'teach-nj-legacy': {
    emailTemplate: 'teach-legacy-circle',
    title: 'Teach Coalition’s NJ Leadership Circle',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_NJ',
    campaign: 'General Donation',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Teach Coalition’s NJ Leadership Circle',
    amounts: [
      [50000, `Visionary Circle`],
      [36000, `Governor Circle`],
      [18000, `Speaker Circle`],
      [10000, `Senate Circle`],
      [5000, `Assembly Circle`],
      [3600, `Capitol Circle`],
      [1800, `State Circle`],
    ],
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org','KatzK@teachcoalition.org','joyandehm@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'rachel.spinner@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition NJ <Information@Teachcoalition.Org>',
    adminSubject: 'A payment has been made to Teach Coalition’s NJ Leadership Circle',
    userFromEmail: 'Teach Coalition <Information@Teachcoalition.Org>',
    userSubject: 'Teach Coalition’s NJ Leadership Circle',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    silverPopContactLists (request) {
      const map = {
        NYS: 4761244,
        FL: 7084757,
        MD: 4761251,
        CA: 4761249,
        PA: 4761247,
        NJ: 4761246
      } as const

      let state: string

      if (request.applications![0].department == 'TeachCoalition') {
        state = request.address!.street
        if (state == 'NY') state = 'NYS'
      }
      else {
        state = request.applications![0].department!.split('_')[1]
      }

      //@ts-ignore
      if (state in map) return [map[state]]

      return [6995481]
    },
    customValidations (form) {
      return form.extend({
        custom: z.object({
          guests: z.string().nullable().optional(),
          honorscroll: z.string().nullable().optional(),
        })
      })
    }
  },
  'teach-nys-babka': {
    emailTemplate: 'teach-nys-babka',
    title: 'Teach NYS’s Babka Bake',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_NYS',
    campaign: 'Babka Bake',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Teach NYS’s Babka Bake',
    amounts: [
      [36, `Single Ticket`],
      [72, `$72 - Spatula`],
      [180, `$180 - Rolling Pin`],
      [360, `$360 - Flour`],
      [540, `$540 - Sugar`],
      [720, `$720 - Chocolate Filling`],
      [1800, `$1,800 - Babka Sponsor`],
    ],
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'rachel.spinner@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition NYS <teachnys@teachcoalition.org>',
    adminSubject: 'A payment has been made to Teach NYS’s Babka Bake',
    userFromEmail: 'Teach Coalition NYS <teachnys@teachcoalition.org>',
    userSubject: 'Teach NYS’s Babka Bake',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    silverPopContactLists (request) {
      const map = {
        NYS: 4761244,
        FL: 7084757,
        MD: 4761251,
        CA: 4761249,
        PA: 4761247,
        NJ: 4761246
      } as const

      let state: string

      if (request.applications![0].department == 'TeachCoalition') {
        state = request.address!.street
        if (state == 'NY') state = 'NYS'
      }
      else {
        state = request.applications![0].department!.split('_')[1]
      }

      //@ts-ignore
      if (state in map) return [map[state]]

      return [6995481]
    },
    customValidations (form) {
      return form.extend({
        custom: z.object({
          ticketsQnt: z.string().nullable().optional(),
          coupleName: z.string().nullable().optional(),
          schoolName: z.string().nullable().optional(),
        })
      })
    }
  },
  'teach-visionaries-retreat': {
    emailTemplate: 'teach-visionaries-retreat',
    title: 'The 2023 Visionaries Retreat',
    department: 'TeachCoalition',
    campaign: 'Visionaries Retreat',
    subCampaign: '',
    thankYouTemplate: 'teach-visionaries-retreat',
    message: '<p>Thank you for registering for the 2023 Teach Coalition Visionary Retreat.</p><p>We are excited to welcome you on November 8th to The Mandarin Oriental Hotel in Miami Florida.</p><p>Please be at the hotel by 7pm for Dinner and the Keynote Speaker.</p><br /><p>We invite you to extend your stay at The Mandarin Oriental Hotel in Miami Florida with an exclusive Teach Coalition group discount code. Please call the hotel directly 305-913-8288 to extend your stay.</p>',
    amounts: [
      [1500, `Price per person - $1,500`],
      [2000, `Price per couple - $2,000`],
      [1000, `Price per person not staying at the hotel - $1,000`],
    ],
    adminEmails: ['neumanj@ou.org', 'Caleb.Fischer@teachcoalition.org'], // ['dalezmana@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition <information@teachcoalition.org>',
    adminSubject: 'A payment has been made to 2023 Teach Coalition Visionary Retreat',
    userFromEmail: 'Teach Coalition <information@teachcoalition.org>',
    userSubject: '2023 Teach Coalition Visionary Retreat Confirmation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          coupleName: z.string().nullable().optional(),
          schoolName: z.string().nullable().optional(),
        })
      })
    }
  },
  'teach-nys-bbq': {
    emailTemplate: 'teach-nys-bbq',
    title: 'Teach NYS’s Westchester Summer BBQ',
    honoreeTemplate: 'teach',
    department: 'TeachCoalition_NYS',
    campaign: 'Summer BBQ',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for donating to Teach NYS’s Westchester Summer BBQ',
    amounts: [
      [100, `Single BBQ Ticket`],
      [180, `Couple BBQ Ticket`],
      [1800, `$1,800 - State Circle <span>(includes 2 free tickets)</span>`],
      [3600, `$3,600 - Capitol Circle <span>(includes 2 free tickets)</span>`],
      [5000, `$5,000 - Benefactors Circle <span>(includes 2 free tickets)</span>`],
      [10000, `$10,000 - Senate Circle <span>(includes 2 free tickets)</span>`],
    ],
    adminEmails: ['benshabats@teachcoalition.org','altfields@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org', 'rachel.spinner@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition NYS <teachnys@teachcoalition.org>',
    adminSubject: 'A payment has been made to Teach NYS’s Westchester Summer BBQ',
    userFromEmail: 'Teach Coalition NYS <teachnys@teachcoalition.org>',
    userSubject: 'Teach NYS’s Westchester Summer BBQ',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    silverPopContactLists (request) {
      const map = {
        NYS: 4761244,
        FL: 7084757,
        MD: 4761251,
        CA: 4761249,
        PA: 4761247,
        NJ: 4761246
      } as const

      let state: string

      if (request.applications![0].department == 'TeachCoalition') {
        state = request.address!.street
        if (state == 'NY') state = 'NYS'
      }
      else {
        state = request.applications![0].department!.split('_')[1]
      }

      //@ts-ignore
      if (state in map) return [map[state]]

      return [6995481]
    },
    customValidations (form) {
      return form.extend({
        custom: z.object({
          ticketsQnt: z.string().nullable().optional(),
          coupleName: z.string().nullable().optional(),
          schoolName: z.string().nullable().optional(),
        })
      })
    }
  },
  yachad,
  'yachad-dallas': yachad,
  'yachad-detroit': yachad,
  'yachad-los-angeles': yachad,
  'yachad-new-england': yachad,
  'yachad-new-jersey': yachad,
  'yachad-new-york': yachad,
  'yachad-south-florida': yachad,
  'yachad-philadelphia': yachad,
  'yachad-toronto': yachad,
  'jlic-campus': {
    emailTemplate: 'yachad',
    title: 'JLIC Campus Donation form',
    department: 'JLIC',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for donating to JLIC',
    amounts: [
      1000,
      500,
      360,
      180,
      100,
      90,
      72,
      36,
      18,
      5
    ],
    adminEmails: ['rosenthalp@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your support',
    userFromEmail: 'atblock@gmail.com',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-1957949-1' : 'UA-1957949-2'
  },
  'jlic-maaser': {
    emailTemplate: 'jlic',
    title: 'JLIC Maaser Campaign',
    honoreeTemplate: 'jlic',
    department: 'JLIC',
    campaign: 'Maaser',
    subCampaign: '',
    message: "Thank you for donating to JLIC",
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['raviva@ou.org', 'goldmani@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to OU-JLIC Maaser Campaign!',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC at Maaser Campaign!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-general': {
    emailTemplate: 'jlic-general',
    title: 'JLIC General Donation',
    honoreeTemplate: 'jlic',
    department: 'JLIC',
    campaign: 'General Donation',
    subCampaign: '',
    message: "Thank you for donating to JLIC",
    amounts: [
      5000,
      3600,
      1800,
      1200,
      720,
      500,
      360,
      180
    ],
    adminEmails: (request:any) => {
      const to = 'goldmani@ou.org,jlic@ou.org,steinreiche@ou.org,rossj@ou.org,rossj@ou.org,rosenthalp@ou.org,zemas@ou.org'.split(',')
      const mapping = {
        JLIC_Jerusalem: 'shulmanj@ou.org',
      } as any
      const email = mapping[request.applications![0].department!]
      if (email) to.push(email)
    if (request.currency == 'ILS') {
         to.push('shoshi.harari@ou.org')
         to.push('fadlunl@ou.org')
      }
      return to
    },
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to OU-JLIC!',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          naming: z.string().nullable().optional(),
        })
      })
    },
  },
  'ecp': {
    emailTemplate: 'ecp',
    title: 'ECP General Donation',
    department: 'ECP',
    campaign: 'General Donation',
    subCampaign: '',
    message: "Thank you for donating to Jewish Emerging Career Professionals",
    amounts: [
      18,
      36,
      50,
      100,
      180,
      250,
      360,
      500,
      720,
      1000
    ],
    adminEmails: ['goldmani@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to Jewish Emerging Career Professionals!',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to Jewish Emerging Career Professionals!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'ecp-general': {
    template: 'ecp',
    emailTemplate: 'ecp',
    title: 'ECP General Donation #2',
    department: 'ECP',
    campaign: 'General Donation',
    subCampaign: '',
    message: "Thank you for donating to Jewish Emerging Career Professionals #2",
    amounts: [
      18,
      36,
      50,
      100,
      180,
      250,
      360,
      500,
      720,
      1000
    ],
    adminEmails: ['goldmani@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to Jewish Emerging Career Professionals! #2',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to Jewish Emerging Career Professionals! #2',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-yavneh': {
    emailTemplate: 'jlic-yavneh',
    title: 'Yavneh Donation',
    honoreeTemplate: 'jlic',
    department: 'JLIC_Yavneh',
    campaign: 'General Donation',
    subCampaign: '',
    message: "<p>Thank you for your support!</p> <p>Your tax- deductible donation is a great opportunity to connect more young Jews to their Judaism and empower budding Jewish leaders.</p> <p>Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p> <p>Yavneh is developing leadership. The Yavneh Fellowship trains, mentors and supports hundreds of fellows on over 35 campuses around the world, including trips to Israel, inter-collegiate shabbatons and personalized mentorship to help each leader develop and implement his/her unique vision for Jewish life on campus.</p> <p>Yavneh is campus leadership. Yavneh is a group of Jewish college students sharing Jewish life with their peers. Based on the values of loving all Jews and caring for others, we're changing the face of Jewish communal life: connecting Jews to meaningful Jewish life, vibrant communities, and to each other - all through peer-based relationships, intimate Jewish experiences and innovative community building and programming.</p> <p>Yavneh is a leadership network. We are developing a shared vision for religious life on campus and a unique network of student leaders around the world.</p> <p>Yavneh is a partnership between the Orthodox Union and Mizrachi.</p> <p>Website: <a href='http://www.yavnehoncampus.org/' target='_blank' rel='nofollow noopener noreferrer'>www.yavnehoncampus.org</a></p> <p>abbi Jonathan Shulman (<a href='http://rabbijonathanshulman.com/' target='_blank' rel='nofollow noopener noreferrer'>http://rabbijonathanshulman.com</a>) Director of Yavneh <a href='http://www.yavnehoncampus.org/' target='_blank' rel='nofollow noopener noreferrer'>www.yavnehoncampus.org</a> 058-441-3909 917-628-2366</p>",
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org', 'gracie@yavnehoncampus.org', 'jlic@ou.org', 'steinreiche@ou.org', 'rossj@ou.org', 'rabbishulman@yavnehoncampus.org', 'rosenthalp@ou.org', 'zemas@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to Yavneh!',
    userFromEmail: 'Yavneh Donation <jlic@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          naming: z.string().nullable().optional(),
        })
      })
    },
  },
  'jlic-yavneh-rimon': {
    template:'jlic-25',
    emailTemplate: 'jlic-yavneh',
    title: 'Yavneh Rimon Donation',
    honoreeTemplate: 'jlic',
    department: 'JLIC_Yavneh',
    campaign: 'Rimon Movement',
    subCampaign: '',
    message: "<p>Thank you so much for your donation to the Rimon Movement.<br><br> We value your belief in us and are so excited to show you our accomplishments thanks to your generous support and belief in us.<br><br> Make sure to follow our social media accounts for updates on how we are doing, and always feel free to reach out to us at <a href='mailto:rimonmovement@gmail.com' rel='noreferrer'>rimonmovement@gmail.com</a> with any questions or suggestions for us.<br><br> Thank you again!</p>",
    amounts: [
      [1000, `Diamond sponsor`],
      [500, `Platinum sponsor`],
      [250, `Gold sponsor`],
      [100, `Silver sponsor`],
      [50, `Bronze sponsor`],
      [36, `Friend`],
      [18, `Contributor`],
    ],
    adminEmails: ['goldmani@ou.org', 'eliecodron@gmail.com', 'jlic@ou.org', 'jordana.schiff@gmail.com', 'jsjonathanshulman@gmail.com', 'zemas@ou.org'],
    adminFromEmail: 'The Rimon Movement <jlic@ou.org>',
    adminSubject: 'Thank you for your generous donation to Yavneh Rimon!',
    userFromEmail: 'Yavneh Rimon Donation <rabbishulman@yavnehoncampus.org>',
    userSubject: 'Thank you very kindly for contributing to the important work done by the The Rimon Movement',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          naming: z.string().nullable().optional(),
        })
      })
    },
  },
  'jlic-alumni': {
    template:'jlic-general',
    emailTemplate: 'jlic',
    title: 'JLIC Alumni Donation',
    honoreeTemplate: 'jlic',
    department: 'JLIC',
    campaign: 'Alumni Donation',
    subCampaign: '',
    message: "<p>Thank you for your support!<br><br> Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.<br><br> Through your partnership, we will continue to expand and reach more Jewish students than ever before.</p>",
    amounts: [
      72,
      180,
      360,
      500,
      1000,
      5000
    ],
    adminEmails: ['goldmani@ou.org', 'gracie@yavnehoncampus.org', 'jlic@ou.org', 'steinreiche@ou.org', 'rossj@ou.org', 'rosenthalp@ou.org','zemas@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to JLIC Alumni!',
    userFromEmail: 'JLIC Alumni Donation <jlic@ou.org>',
    userSubject: 'Thank you for your generous donation to JLIC Alumni!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          naming: z.string().nullable().optional(),
        })
      })
    },
  },
  'jlic-25': {
    emailTemplate: 'jlic-25',
    title: '$25 for 25',
    honoreeTemplate: 'jlic',
    department: 'JLIC_Yavneh',
    campaign: 'General Donation',
    subCampaign: '',
    message: "<p dir='auto'>Thank you for helping Hashem’s children connect to him and his Torah!</p> <p dir='auto'>May this generous donation be a Zechus for you and your family in the Torah and inspiration that our participants experience through $25 for 25. Feel free to share with your friends and family!</p> <p dir='auto'>Thanks again!</p> The $25 for 25 Team <p dir='auto'>Thank you for your help! Jacob Bach</p>",
    amounts: [
      [1800, `Diamond sponsor`],
      [500, `Platinum sponsor`],
      [250, `Gold sponsor`],
      [100, `Silver sponsor`],
      [50, `Bronze sponsor`],
      [36, `Friend`],
      [18, `Contributor`],
    ],
    adminEmails: ['goldmani@ou.org', 'gracie@yavnehoncampus.org', 'jlic@ou.org', 'steinreiche@ou.org', 'rossj@ou.org', 'rabbishulman@yavnehoncampus.org', 'jacob.e.bach@gmail.com', 'zemas@ou.org', 'Ershlert@ou.org'],
    adminFromEmail: 'Yavneh Student Program - 25 for 25 <jlic@ou.org>',
    adminSubject: 'Thank you for your generous donation to Yavneh!',
    userFromEmail: 'Thank you for helping Hashem’s children connect to him and his Torah! <rabbishulman@yavnehoncampus.org>',
    userSubject: 'Thank you for helping Hashem’s children connect to him and his Torah!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2',
  },
  'jlic-barilan': {
    emailTemplate: 'jlic-general',
    title: 'JLIC Bar Ilan Donation',
    honoreeTemplate: 'jlic',
    department: 'JLIC_BarIlan',
    campaign: 'General Donation',
    subCampaign: '',
    message: "Thank you for your support of OU-JLIC at Bar Ilan!<br><br> Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.<br><br> Through your partnership, we will continue to expand and reach more Jewish students than ever before.",
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: (request:any) => {
      const to ='goldmani@ou.org,jlic@ou.org,steinreiche@ou.org,ttw613@gmail.com,jsjonathanshulman@gmail.com,tzviw08@gmail.com,zemas@ou.org,newmany@ou.org'.split(',')
      if (request.currency == 'ILS') {
         to.push('shoshi.harari@ou.org')
         to.push('fadlunl@ou.org')
      }
      return to
    },
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to OU-JLIC!',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          naming: z.string().nullable().optional(),
        })
      })
    },
  },
  'jlic-shulchanrivkah': {
    emailTemplate: 'jlic-shulchanrivkah',
    title: 'JLIC Shulchan Rivkah Donation',
    honoreeTemplate: 'jlic',
    department: 'JLIC_BarIlan',
    campaign: 'Shulchan Rivkah',
    subCampaign: '',
    message: "Thank you for your generous support of the Shulchan Rivkah discretionary fund!<br><br>Your tax- deductible donation is a great opportunity to help more young adults. Through your partnership, we will continue to expand and reach more people than ever before.",
    amounts: [
      5000,
      3600,
      1800,
      1200,
      720,
      500,
      360,
      180
    ],
    adminEmails: ['goldmani@ou.org', 'rossj@ou.org', 'rosenthalp@ou.org', 'steinreiche@ou.org', 'wohlgelerntertali@ou.org', 'wohlgelerntertzvi@ou.org', 'shulmanj@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to OU-JLIC!',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2',
  },
  'jlic-summer': {
    emailTemplate: 'jlic-summer',
    title: 'OU-JLIC Summer Program Donation Form',
    honoreeTemplate: 'jlic',
    department: 'JLIC',
    campaign: 'Summer Donation',
    subCampaign: '',
    message: "Thanks for your donation to OU-JLIC Summer programs!",
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['goldmani@ou.org',  'jlic@ou.org', 'steinreiche@ou.org', 'rossj@ou.org', 'rossj@ou.org', 'rosenthalp@ou.org', 'yonina@oujlicsummer.org','rabbishulman@yavnehoncampus.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you from OU-JLIC Summer Programs',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you from OU-JLIC Summer Programs',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-minyan-yavneh': {
    emailTemplate: 'jlic-o-minyan',
    title: 'University of Michigan - O-Minyan',
    honoreeTemplate: 'jlic',
    department: 'JLIC_Yavneh',
    campaign: 'Univ of Michigan',
    subCampaign: '',
    message: "Thank you so much for donating to O-Minyan! Many of our programs depend on donations like yours so we really appreciate your generosity. With your gift, we will continue to ensure that the needs of our growing community are met, such as having shabbat meals and services, holiday programming, learning opportunities, and social events. Thank you again for helping our students and Go Blue!",
    amounts: [
      [3000, `Partner`],
      [1800, `Benefactor`],
      [1000, `Platinum`],
      [720, `Builder`],
      [540, `Wolverine`],
      [360, `Mich-pacha`],
      [180, `Donor`],
      [18, `Student Holiday Meal, or Standard Donation`],
      [54, `Birthday Oneg`],
      [72, `Night of Learning`],
      [180, `Full Shabbat (Oneg + Kiddush Club)`],
      [360, `Community Holiday Lunch`],
      [720, `Community Holiday Dinner`],
      [1000, `Scholar in Residence`],
    ],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          naming: z.string().nullable().optional(),
        })
      })
    },
    adminEmails: ['spstein@umich.edu', 'newcorre@umich.edu', 'goldmani@ou.org',  'gracie@yavnehoncampus.org', 'mjedwab@umich.edu', 'Atarakre@umich.edu', 'zemas@ou.org'],
    adminFromEmail: 'Michigan O-Minyan <jlic@ou.org>',
    adminSubject: 'Donation to Michigan O-Minyan',
    userFromEmail: 'Michigan O-Minyan powered by Yavneh <jlic@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-yavneh-cooper': {
    emailTemplate: 'jlic-cooper-yavneh',
    title: 'Cooper Union Hillel',
    honoreeTemplate: 'jlic',
    department: 'JLIC_Yavneh',
    campaign: 'Cooper Challenge Grant',
    subCampaign: '',
    message: "Thank you for supporting the Cooper Union Midrash. Your donation goes a long way toward our mission to establish the Midrash as a center for spiritual growth and learning and enhance the existing Hillel community.",
    amounts: [
      [36, `Friend`],
      [72, `Supporter`],
      [180, `Copper`],
      [360, `Silver`],
      [900, `Gold`],
      [1800, `Community Builder`],
      [3600, `Legacy Leader`],
      [36, `Mincha Snacks`],
      [75, `Shabbat Oneg`],
      [200, `Chillel with Hillel`],
      [175, `CU Midrash Class`],
      [250, `Holiday Programming`],
      [600, `Shabbat meal`],
    ],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          naming: z.string().nullable().optional(),
        })
      })
    },
    adminEmails: ['jacobkhalili@gmail.com', 'RosenzweigGila@gmail.com', 'goldmani@ou.org', 'effiebluestone34@gmail.com', 'gracie@yavnehoncampus.org', 'zemas@ou.org'],
    adminFromEmail: 'Yavneh Cooper Union <jlic@ou.org>',
    adminSubject: 'Donation to Yavneh Cooper Union',
    userFromEmail: 'Cooper Union powered by Yavneh <jlic@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-princeton': {
    emailTemplate: 'jlic',
    title: 'JLIC Princeton Donation form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_Princeton',
    campaign: 'General Donation',
    subCampaign: '',
    message: "Thank you for your support of OU-JLIC at Princeton University!<br>Your tax-deductible donation is a great opportunity to help more young adults stay connected to their Judaism.<br>Through your partnership, we will continue to expand and reach more Jewish students than ever before",
    amounts: [
      [{ USD: 10000, ILS: 10000 }, ``],
      [{ USD: 7500, ILS: 7500 }, ``],
      [{ USD: 5000, ILS: 5000 }, ``],
      [{ USD: 3600, ILS: 3600 }, ``],
      [{ USD: 1800, ILS: 1800 }, ``],
      [{ USD: 1000, ILS: 1000 }, ``],
      [{ USD: 500, ILS: 500 }, ``],
      [{ USD: 360, ILS: 360 }, ``],
    ],
    adminEmails: ['goldmani@ou.org',  'rossj@ou.org', 'rosenthalp@ou.org', 'smkahn@princeton.edu'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to OU-JLIC at Princeton!',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC at Princeton!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-yavneh-bayit': {
    emailTemplate: 'jlic-yavneh-bayit',
    title: 'Yavneh Bayit Donation Form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_Yavneh',
    campaign: 'Yavneh bayit',
    subCampaign: '',
    message: "On behalf of the entire Yavneh Bayit team, thank you for your generous donation. As a grassroots community building effort, we rely upon donations like yours to support our mission of community, engagement, and connection among Orthodox young professionals worldwide. In achieving this, we support not only the vibrancy of local Jewish communities, but also the continuing vitality of the Jewish people. Thank you for making this possible. To stay updated on our fellow’s accomplishments and upcoming events, please visit our website at https://yavnehbayit.org/.",
    amounts: [
      [36000, `Sponsor a City`],
      [18000, `Sponsor a House`],
      [5000, `Sponsor a Fellow`],
      [1000, ``],
      [500, ``],
      [360, ``],
      [250, ``],
      [180, ``],
      [90, ``],
      [36, ``],
      [18, ``]
    ],
    adminEmails: ['goldmani@ou.org',  'yavnehbayit@gmail.com', 'zemas@ou.org'],
    adminFromEmail: 'Yavneh Bayit <jlic@ou.org>',
    adminSubject: 'Thank you for your generous donation to Yavneh Bayit!',
    userFromEmail: 'Yavneh Bayit <jlic@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-o-minyan': {
    emailTemplate: 'jlic-o-minyan',
    title: 'University of Michigan - O-Minyan',
    honoreeTemplate: 'jlic',
    department: 'JLIC_Yavneh',
    campaign: 'Welcome week',
    message: "Thank you so much for donating to O-Minyan! Many of our programs depend on donations like yours so we really appreciate your generosity. With your gift, we will continue to ensure that the needs of our growing community are met, such as having shabbat meals and services, holiday programming, learning opportunities, and social events. Thank you again for helping our students and Go Blue!",
    amounts: [
      [1800, `Welcome week`],
      [150, `for a week`],
      [180, `for a week`],
      [540, `for a month`],
      [1800, `for a semester`],
      [3600, `for a year`],
    ],
    adminEmails: ['spstein@umich.edu', 'newcorre@umich.edu', 'goldmani@ou.org',  'yavnehbayit@gmail.com', 'eliorw@umich.edu', 'Atarakre@umich.edu', 'zemas@ou.org'],
    adminFromEmail: 'Michigan O-Minyan <jlic@ou.org>',
    adminSubject: 'Donation to Michigan O-Minyan - OU/University of Michigan',
    userFromEmail: 'OU/University of Michigan <jlic@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-idc': {
    emailTemplate: 'jlic-general',
    title: 'JLIC IDC Herzeliha Donation form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_IDCHerzliya',
    campaign: 'General Donation',
    subCampaign: '',
    message: "Thank you for your support to Mizrachi OU-JLIC at IDC Herzliya. Your donation goes 100% towards helping olim, lone soldiers, bnot sheirut, and young leaders build a stronger Jewish community for themselves and future students. Through your partnership, we will continue to expand and reach more Jewish students than ever before as well as give back to our community!",
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['jsjonathanshulman@gmail.com', 'rabbishulman@yavnehoncampus.org', 'Friedmann@ou.org', 'goldmani@ou.org', 'rosenthalp@ou.org','zemas@ou.org', 'rosenthals@ou.org', 'Yiska.Guberman@ou.org', 'Elisha.Guberman@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to OU-JLIC at IDC!',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC at IDC!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-hertzidc': {
    template: 'jlic-idc',
    emailTemplate: 'jlic-idc',
    title: 'JLIC IDC Herzeliha Donation form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_IDCHerzliya',
    campaign: 'Programming Fund',
    subCampaign: '',
    message: "Thank you for your support to Mizrachi OU-JLIC at IDC Herzliya. Your donation goes 100% towards helping olim, lone soldiers, bnot sheirut, and young leaders build a stronger Jewish community for themselves and future students. Through your partnership, we will continue to expand and reach more Jewish students than ever before as well as give back to our community!",
    amounts: [
      [10000, ``],
      [7500, ``],
      [5000, ``],
      [3600, ``],
      [1800, ``],
      [1000, ``],
      [500, ``],
      [360, ``],
    ],
    adminEmails: ['jsjonathanshulman@gmail.com', 'rabbishulman@yavnehoncampus.org', 'Friedmann@ou.org', 'shifraf@ou.org', 'goldmani@ou.org', 'rosenthalp@ou.org', 'steinreiche@ou.org','rossj@ou.org', 'rosenthals@ou.org', 'Yiska.Guberman@ou.org', 'Elisha.Guberman@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to OU-JLIC at IDC!',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC at IDC!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-northamericawar': {
    template: 'jlic-idc',
    emailTemplate: 'jlic-northamericawar',
    title: 'JLIC Students Under Attack Donation form',
    honoreeTemplate: 'jlic',
    department: 'JLIC',
    campaign: 'Students Under Attack',
    subCampaign: '',
    message: "Thank you for your generous donation to JLIC during this time. Your support will go directly towards supporting thousands of our students and young professionals as they face new challenges on the campus environment. <br><br>Thank you for being a partner with us in this endeavor.",
    amounts: [
      [10000, ``],
      [7500, ``],
      [5000, ``],
      [3600, ``],
      [1800, ``],
      [1000, ``],
      [500, ``],
      [360, ``],
    ],
    adminEmails: ['raviva@ou.org', 'shulmanj@ou.org', 'zemas@ou.org', 'goldmani@ou.org', 'rosenthalp@ou.org', 'steinreiche@ou.org','rossj@ou.org','zemas@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to OU-JLIC - Students Under Attack!',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC - Students Under Attack!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-reichman': {
    emailTemplate: 'jlic-reichman',
    title: 'JLIC Reichman University Donation form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_IDCHerzliya',
    campaign: 'Spring Campaign',
    subCampaign: '',
    message: "Thank you for your support to OU-JLIC at Reichman University, Herzliya!",
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: ['steinreiche@ou.org', 'rosenthalp@ou.org', 'Friedmann@ou.org', 'rossj@ou.org', 'goldmani@ou.org', 'shifraf@ou.org', 'zemas@ou.org', 'rosenthals@ou.org', 'Yiska.Guberman@ou.org', 'Elisha.Guberman@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to OU-JLIC at Reichman University!',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC at Reichman University!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          anonymous: zBool().nullable().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    },
  },
  'jlic-michigan-uni': {
    ...jlicCampusBasics,
    title: 'JLIC at University of Michigan Donation form',
    department: 'JLIC_Michigan',
    campaign: 'General Donation',
    subCampaign: '',
    message: "Thank you for your contribution to JLIC at University of Michigan! We appreciate your support.",
    adminEmails: [...jlicCampusBasics.adminEmails, 'scott.sandor@ou.org', 'talya.schreiber@ou.org'],
    adminFromEmail: 'JLIC at University of Michigan jlic@ou.org',
    userFromEmail: 'JLIC at University of Michigan jlic@ou.org',
    adminSubject: 'You have received a new donation from JLIC at University of Michigan',
    userSubject: 'Thank you from JLIC at University of Michigan',
  },
  'jlic-washu-uni': {
    ...jlicCampusBasics,
    title: 'JLIC at Washington University Donation form',
    department: 'JLIC_WashingtonUniversity',
    campaign: 'General Donation',
    subCampaign: '',
    adminEmails: ['steinreiche@ou.org', 'rosenthalp@ou.org', 'zemas@ou.org', 'rossj@ou.org', 'goldmani@ou.org','zemas@ou.org', 'ashkanazys@ou.org', 'ashkanazyk@ou.org'],
    message: "Thank you for your contribution to JLIC at Washington University! We appreciate your support.",
    adminFromEmail: 'JLIC at Washington University jlic@ou.org',
    userFromEmail: 'JLIC at Washington University jlic@ou.org',
    adminSubject: 'You have received a new donation from JLIC at Washington University',
    userSubject: 'Thank you from JLIC at Washington University',
  },
  'jlic-technion-uni': {
    template: 'jlic-simple-il',
    ...jlicCampusBasics,
    emailTemplate: 'jlic-general',
    title: 'JLIC at Technion University Donation form',
    department: 'JLIC_Technion',
    campaign: 'General Donation',
    subCampaign: '',
    message: "Thank you for your contribution to JLIC at Technion University! We appreciate your support.",
    adminFromEmail: 'JLIC at Technion University jlic@ou.org',
    userFromEmail: 'JLIC at Technion University jlic@ou.org',
    adminSubject: 'You have received a new donation from JLIC at Technion University',
    userSubject: 'Thank you from JLIC at Technion University',
    adminEmails: (request:any) => {
      const to ='steinreiche@ou.org,rosenthalp@ou.org,zemas@ou.org,rossj@ou.org,goldmani@ou.org,zemas@ou.org'.split(',')
      if (request.currency == 'ILS') {
         to.push('shoshi.harari@ou.org')
         to.push('fadlunl@ou.org')
      }
      return to
    },
  },
  'jlic-machonlev-uni': {
    ...jlicCampusBasics,
    template: 'jlic-simple-il',
    title: 'JLIC at Machonlev Donation form',
    department: 'JLIC_MachonLev',
    campaign: 'General Donation',
    subCampaign: '',
    message: "Thank you for your contribution to JLIC at Machonlev! We appreciate your support.",
    adminFromEmail: 'JLIC at Machonlev jlic@ou.org',
    adminEmails: ['steinreiche@ou.org', 'rosenthalp@ou.org', 'zemas@ou.org', 'rossj@ou.org', 'goldmani@ou.org','zemas@ou.org'],
    userFromEmail: 'JLIC at Machonlev jlic@ou.org',
    adminSubject: 'You have received a new donation from JLIC at Machonlev',
    userSubject: 'Thank you from JLIC at Machonlev',
  },
  'jlic-mizrachi-kiddush': {
    emailTemplate: 'jlic-mizrachi-kiddush',
    title: 'Mizrachi OU-JLIC Reichman University Herzliya Kiddush Sponsorship Form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_IDCHerzliya',
    campaign: 'General Donation',
    subCampaign: '',
    message: "Thank you for your kiddush sponsorship and your support of Mizrachi OU-JLIC Herzliya!",
    amounts: [
      [360, `Shabbat morning Kiddush sponsorship`],
      [500, `Deluxe Shabbat morning Kiddush sponsorship`],
    ],
    defaultCurrency: 'USD',
    adminEmails: ['steinreiche@ou.org', 'rossj@ou.org', 'rabbishulman@yavnehoncampus.org', 'goldmani@ou.org', 'rosenthalp@ou.org', 'rosenthals@ou.org', 'Yiska.Guberman@ou.org', 'Elisha.Guberman@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'New Submission - Mizrachi OU-JLIC Reichman University Herzliya Kiddush Sponsorship Page',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your kiddush sponsorship and your support of Mizrachi OU-JLIC Herzliya!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          anonymous: zBool().nullable().optional(),
          honorOf: z.string().nullable().optional(),
          date: z.string().nullable().optional(),
        })
      })
    },
  },
  'jlic-mizrachi-alumni': {
    emailTemplate: 'jlic-idc',
    title: 'Mizrachi JLIC Alumni NEW YEAR Campaign Form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_IDCHerzliya',
    campaign: 'Alumni Donation',
    subCampaign: '',
    message: "Thank you for your support of Mizrachi OU-JLIC Herzliya!",
    amounts: [
      [100, `Sponsor a shabbat meal at a Mizrachi JLIC home for one person`],
      [360, `Sponsor a dvar torah over shabbat`],
      [500, `Sponsor 5 shabbat meals at a Mizrachi JLIC home`],
      [1000, `Sponsor a JLIC communal seudah shlishit`],
      [1800, `Sponsor a full Shabbat meal at a Mizrachi JLIC home`],
      [3600, `Sponsor a full Shabbat of meals and programming in Herzliya`],
    ],
    adminEmails: ['rossj@ou.org', 'rabbishulman@yavnehoncampus.org', 'Friedmann@ou.org', 'goldmani@ou.org', 'rosenthalp@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'New Submission - Mizrachi JLIC Alumni NEW YEAR Campaign Page',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you your support of Mizrachi OU-JLIC Herzliya!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-yavneh-mit': {
    emailTemplate: 'jlic-yavneh-mit',
    title: 'JLIC MIT campaign donation page',
    honoreeTemplate: 'jlic',
    department: 'JLIC',
    campaign: 'MIT Campaign',
    subCampaign: '',
    message: "Your donation makes a difference—thank you for supporting our MIT community!",
    amounts: [
      [10000, `Many Speakers to Come to Our Community`],
      [7500, `Multiple Fun/Educational Community Trips`],
      [3000, `A Semester of Seuda Shlishit`],
      [1500, `A Semester of Breakfast After Minyan`],
      [720, `A Month of Thursday Night Mishmar`],
      [360, `A Special Meat Shabbos Kiddush`],
      [180, `A Standard Shabbos Kiddush`],
    ],
    adminEmails: ['goldmani@ou.org', 'steinreiche@ou.org', 'rossj@ou.org', 'Hannahf4@mit.edu', 'Nfenster@mit.edu'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'New Submission - JLIC MIT campaign donation page',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Your donation makes a difference—thank you for supporting our MIT community!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-mdktau': {
    emailTemplate: 'jlic-general',
    title: 'JLIC M.D Katz OU-JLIC at Tel Aviv University program Donation form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_TAU',
    campaign: 'General Donation',
    subCampaign: '',
    message: "Thank you for your support to the M.D Katz OU-JLIC at Tel Aviv University program. Your donation goes 100% towards helping students and young leaders build a stronger Jewish community for themselves and future students. Through your partnership, we will continue to expand and reach more Jewish students than ever before as well as give back to our community!",
    amounts: [
      [18000, `Annual Friday Night Sponsor`],
      [10000, `Annual Shabbat Day Sponsor`],
      [7200, `Annual Torah Study Sponsorship`],
      [5400, `Holiday Sponsorship`],
      [3600, `Monthly Torah Study Sponsorship`],
      [1800, `Speaker Event Sponsorship`],
      [500, `Friday Night Dinner Sponsor`],
      [360, `Shabbat Kiddush Sponsorship`],
      [180, `Seuda Shlishit Sponsor`],
    ],
    adminEmails: (request:any) => {
      const to ='rossj@ou.org,rabbishulman@yavnehoncampus.org,steinreiche@ou.org,goldmani@ou.org,rosenthalp@ou.org,Keren.Tahover@ou.org,Uri.Tahover@ou.org'.split(',')
      if (request.currency == 'ILS') {
         to.push('shoshi.harari@ou.org')
         to.push('fadlunl@ou.org')
      }
      return to
    },
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to M.D Katz OU-JLIC at Tel Aviv University program!',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to M.D Katz OU-JLIC at Tel Aviv University program!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-mdktau-campaign': {
    emailTemplate: 'jlic-mdktau',
    title: 'JLIC M.D Katz OU-JLIC at Tel Aviv University program Donation form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_TAU',
    campaign: 'Challenge Grant',
    subCampaign: '',
    message: "Thank you so much for your generous contribution to M.D Katz JLIC at Tel Aviv University. Your gift enables us to continue enriching the Jewish experience of hundreds of students, young professionals and lone soldiers annually. We are so grateful for your support and appreciate your commitment to strengthening Jewish life in Tel Aviv.",
    amounts: [
    ],
    adminEmails: ['rossj@ou.org', 'rabbishulman@yavnehoncampus.org', 'steinreiche@ou.org', 'goldmani@ou.org', 'Keren.Tahover@ou.org', 'Uri.Tahover@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to M.D Katz OU-JLIC at Tel Aviv University program!',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you so much for your generous contribution to M.D Katz JLIC at Tel Aviv University',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-chanukah': {
    emailTemplate: 'jlic-chanukah',
    title: 'JLIC Chanukah Donation form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_Cornell',
    campaign: 'Chanukah Campaign',
    subCampaign: '',
    message: 'Thank you for your generous support of OU-JLIC at Cornell this Chanukah!',
    amounts: [
      [1800, `Full Chanukah sponsorship`],
      [1250, `Shabbos Chanukah meals and programming`],
      [720, `Chanukah Brunch and Learn`],
      [500, `Chanukah shiur or program`],
      [360, `Post Shacharit Chanukah festive breakfasts`],
      [180, `Chanukah cookie sponsorship`],
    ],
    adminEmails: [ 'dgk65@cornell.edu', 'sbk92@cornell.edu', 'rossj@ou.org', 'rosenthalp@ou.org', 'steinreiche@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to OU-JLIC!',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-jerusalem-yavneh': {
    emailTemplate: 'jlic-general',
    title: 'JLIC Jerusalem Donation form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_Jerusalem',
    campaign: 'General Donation',
    subCampaign: 'General Donation',
    message: 'Thank you for your generous donation to OU-JLIC Jerusalem!',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: (request:any) => {
      const to ='jeremy@yavnehoncampus.org,ejzimmer95@gmail.com,rabbishulman@yavnehoncampus.org,rosenthalp@ou.org,rossj@ou.org,goldmani@ou.org,zemas@ou.org,shulmanj@ou.org'.split(',')
      if (request.currency == 'ILS') {
         to.push('shoshi.harari@ou.org')
         to.push('fadlunl@ou.org')
      }
      return to
    },
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'New JLIC Form Donation - Jerusalem',
    userFromEmail: 'OU-JLIC Jerusalem jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU JLIC - Jerusalem!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-jerusalem-emergency': {
    emailTemplate: 'jlic-jeru-emergency',
    title: 'OU-JLIC Jerusalem/Emergency Fund donation',
    adminEmails: [ 'Rossj@ou.org','Rosenthalp@ou.org','Shulmanj@ou.org','Steinreiche@ou.org','Tibbettsj@ou.org','Tibbettse@ou.org', 'goldmani@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'New OU-JLIC Jerusalem/Emergency Fund donation',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to JLIC Jerusalem emergency fund.Your support will go directly towards supporting students and young professionals across the country. Thank you again for your support and partnership.',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    campaign: 'Emergency Fund',
    subCampaign: '',
    defaultCurrency: 'USD',
    department: 'JLIC_Jerusalem',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional(),
        })
      })
    },
  },
  'jlic-tlv': {
    emailTemplate: 'jlic-general',
    title: 'JLIC TLV Donation form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_TLV',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your generous donation to OU-JLIC TLV!',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: (request:any) => {
      const to ='steinreiche@ou.org,rabbishulman@yavnehoncampus.org,rosenthalp@ou.org,goldmani@ou.org,wolfsonj@ou.org,shmuelc@ou.org,rossj@ou.org,zemas@ou.org'.split(',')
      if (request.currency == 'ILS') {
         to.push('shoshi.harari@ou.org')
         to.push('fadlunl@ou.org')
      }
      return to
    },
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'New TLV Form Donation - Submission',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC - TLV!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-bgu': {
    template: 'jlic-bgu-il',
    emailTemplate: 'jlic-general',
    title: 'JLIC Ben Gurion Donation form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_BenGurion',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your generous donation to OU-JLIC Ben Gurion!',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: (request:any) => {
      const to ='rabbishulman@yavneh.us,rossj@ou.org,rosenthalp@ou.org,steinreiche@ou.org,zemas@ou.org'.split(',')
      if (request.currency == 'ILS') {
         to.push('shoshi.harari@ou.org')
         to.push('fadlunl@ou.org')
      }
      return to
    },
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'New Ben Gurion Form Donation - Submission',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC - Ben Gurion!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-ariel': {
    template: 'jlic-bgu-il',
    emailTemplate: 'jlic-general',
    title: 'JLIC Ariel University Donation form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_Ariel',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your generous donation to OU-JLIC Ariel University!',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      1000,
      500,
      360
    ],
    adminEmails: (request:any) => {
      const to ='rabbishulman@yavneh.us,rossj@ou.org,rosenthalp@ou.org,steinreiche@ou.org,zemas@ou.org'.split(',')
      if (request.currency == 'ILS') {
         to.push('shoshi.harari@ou.org')
         to.push('fadlunl@ou.org')
      }
      return to
    },
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'New Ariel University Form Donation - Submission',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC - Ariel University!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-chanukah-rutgers': {
    template: 'jlic-chanukah',
    emailTemplate: 'jlic-chanukah-rutgers',
    title: 'JLIC Chanukah Donation form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_Rutgers',
    campaign: 'Chanukah Campaign',
    subCampaign: '',
    message: 'Thank you for your generous support of OU-JLIC at Rutgers this Chanukah!',
    amounts: [
      [1250, `Full Chanukah sponsorship`],
      [750, `Shabbos Chanukah meals and programming`],
      [500, `A full night of programming`],
      [360, `Chanukah shiur or program`],
      [180, `Shabbas Chanukah oneg`],
      [100, `Donut sponsor`],
    ],
    adminEmails: [ 'schwartzs@ou.org', 'schwartzavi@ou.org', 'rossj@ou.org', 'rosenthalp@ou.org', 'steinreiche@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to OU-JLIC!',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-chanukah-brandeis': {
    template: 'jlic-chanukah',
    emailTemplate: 'jlic-chanukah-brandeis',
    title: 'JLIC Chanukah Donation form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_Brandeis',
    campaign: 'Chanukah Campaign',
    subCampaign: '',
    message: 'Thank you for your generous support of OU-JLIC at Brandeis this Hanukkah!',
    amounts: [
      [1800, `Full Hanukkah programming`],
      [1250, `Shabbat Hanukkah meals and programming`],
      [720, `Hanukkah evening program`],
      [500, `Hanukkah shiur`],
      [360, `Shabbat Hanukkah oneg`],
      [180, `Donut sponsorship`],
    ],
    adminEmails: [ 'doritcohen@ou.org', 'cohenar@ou.org', 'rossj@ou.org', 'rosenthalp@ou.org', 'steinreiche@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to OU-JLIC!',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-chanukah-chicago': {
    template: 'jlic-chanukah',
    emailTemplate: 'jlic-chanukah-chicago',
    title: 'JLIC Chanukah Donation form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_Chicago',
    campaign: 'Chanukah Campaign',
    subCampaign: '',
    message: 'Thank you for your generous support of OU-JLIC at UChicago this Hanukkah!',
    amounts: [
      [1800, `Full Chanukah sponsorship`],
      [1250, `Panopoly Chanukah event`],
      [720, `Chanukah Gala Kiddush`],
      [540, `Chanukah Mishmar`],
      [360, `Chanukah Study Break`],
    ],
    adminEmails: [ 'auerbachy@ou.org', 'auerbachh@ou.org', 'rossj@ou.org', 'rosenthalp@ou.org', 'steinreiche@ou.org','zemas@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to OU-JLIC!',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-chanukah-princeton': {
    template: 'jlic-chanukah',
    emailTemplate: 'jlic-chanukah-princeton',
    title: 'JLIC Chanukah Donation form',
    honoreeTemplate: 'jlic',
    department: 'JLIC_Princeton',
    campaign: 'Chanukah Campaign',
    subCampaign: '',
    message: 'Thank you for your generous support of OU-JLIC at Princeton this Hanukkah!',
    amounts: [
      [1200, `Community Chanukah Party`],
      [500, `Chanukah Fabragen`],
      [360, `Chanukah Lunch and Learn`],
      [250, `Chanukah Chesed Baking event`],
    ],
    adminEmails: [ 'rossj@ou.org', 'rosenthalp@ou.org', 'steinreiche@ou.org', 'KahnS@ou.org', 'Mkahn@princeton.edu'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'Thank you for your generous donation to OU-JLIC!',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for your generous donation to OU-JLIC!',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-givat-shmuel-sweatshirt': {
    emailTemplate: 'jlic-givat-shmuel-sweatshirt',
    title: 'OU-JLIC Givat Shmuel fundraiser',
    adminEmails: ['pelesgvs@gmail.com', 'rosenthalp@ou.org'],
    adminFromEmail: 'pelesgvs@gmail.com',
    adminSubject: 'New Sweatshirt Donation',
    userFromEmail: 'pelesgvs@gmail.com',
    userSubject: 'Thank you for supporting our community!',
    amounts: [],
    campaign: 'Swag Sweatshirts Campaign',
    defaultCurrency: 'USD',
    department: 'JLIC_BarIlan',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-yad-eliezer-purim': {
    emailTemplate: 'jlic-yad-eliezer-purim',
    template: 'jlic-yad-eliezer-purim',
    title: 'OU-JLIC Yad Eliezer PURIM',
    adminEmails: (request:any) => {
      const to ='rosenthalp@ou.org,steinreiche@ou.org,goldmani@ou.org'.split(',')
      if (request.currency == 'ILS') {
         to.push('shoshi.harari@ou.org')
         to.push('fadlunl@ou.org')
      }
      return to
    },
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'New Yad Eliezer Purim Donation',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for supporting our community!',
    amounts: [],
    campaign: 'Yad Eliezer Purim',
    department: 'JLIC',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-deposit': {
    emailTemplate: 'jlic',
    title: 'JLIC Deposit Form',
    honoreeTemplate: 'jlic',
    department: 'JLIC',
    campaign: 'General Donation',
    subCampaign: '',
    message: "Thank you for donating to JLIC",
    template: 'jlic-deposit',
    adminEmails: ['lowensteinh@ou.org','yonina@oujlicsummer.org', 'rosenthalp@ou.org',  'steinreiche@ou.org', 'goldmani@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'New JLIC Deposit Form Donation',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you for supporting our community!',
    amounts: [],
    defaultCurrency: 'USD',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-machatzit-hashekel': {
    emailTemplate: 'jlic-machatzit-hashekel',
    title: 'OU-JLIC Givat Shmuel Machatzit haShekel',
    adminEmails: ['pelesgvs@gmail.com', 'rosenthalp@ou.org'],
    adminFromEmail: 'pelesgvs@gmail.com',
    adminSubject: 'New Machatzit haShekel Donation',
    userFromEmail: 'pelesgvs@gmail.com',
    userSubject: 'Thank you for supporting our community!',
    amounts: [
        8,
        26,
        72,
        180,
        500,
        1000
    ],
    campaign: 'Machatzit haShekel',
    defaultCurrency: 'USD',
    department: 'JLIC_BarIlan',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-chesed': {
    emailTemplate: 'jlic-chesed',
    title: 'OU-JLIC Chesed donation',
    adminEmails: (request:any) => {
      const to ='wolfsonj@ou.org,rosenthalp@ou.org,steinreiche@ou.org,rossj@ou.org,goldmani@ou.org,jjwolfson@gmail.com,zemas@ou.org'.split(',')
      if (request.currency == 'ILS') {
         to.push('shoshi.harari@ou.org')
         to.push('fadlunl@ou.org')
      }
      return to
    },
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'New OU-JLIC Chesed donation',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you from JLIC Good for the World',
    message: 'Thank you for your generous contribution to OU-JLIC’s Good for the World Chesed campaigns! Your donation will go towards creating more programming and opportunities to help our larger community. We could not bring hundreds of students and young professionals together in this way without your support.',
    amounts: [
      18000,
      10000,
      7200,
      3600,
      1800,
      1000,
      720,
      360,
    ],
    campaign: 'JLIC Chesed',
    subCampaign: 'Abrahams House',
    department: 'JLIC',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          designation: z.string().nullable().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    },
  },
  'jlic-chesed-hanukkah': {
    emailTemplate: 'jlic-chesed-hanukkah',
    title: 'OU-JLIC TLV - Hanukkah donation',
    adminEmails: ['wolfsonj@ou.org', 'rosenthalp@ou.org', 'steinreiche@ou.org', 'rossj@ou.org','goldmani@ou.org','jjwolfson@gmail.com'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'New OU-JLIC TLV - Hanukkah donation',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank you from JLIC TLV Lights in the Darkness event',
    message: 'Thank you for your generous donation to the JLIC TLV Lights in the Darkness event. Over the past few months, we have come together to support Israel on multiple fronts. This event will work to bring our community together to celebrate Hanukkah and all that we have achieved together.',
    amounts:[
        180,
        360,
        720,
        1200,
        1800,
      ],
    campaign: 'JLIC Chesed',
    subCampaign: 'Hanukkah',
    department: 'JLIC',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional(),
        })
      })
    },
  },
  'jlic-mishloach-manot': {
    emailTemplate: 'jlic-mishloach-manot',
    title: 'OU-JLIC Givat Shmuel Mishloach Manot',
    adminEmails: ['pelesgvs@gmail.com', 'rosenthalp@ou.org'],
    adminFromEmail: 'pelesgvs@gmail.com',
    adminSubject: 'New Mishloach Manot Donation',
    userFromEmail: 'pelesgvs@gmail.com',
    userSubject: 'Thank you for supporting our community!',
    amounts: [],
    campaign: 'Mishloach Manot',
    defaultCurrency: 'USD',
    department: 'JLIC_BarIlan',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'jlic-israel-shabbaton': {
    emailTemplate: 'jlic-israel-shabbaton',
    title: 'OU-JLIC Israel 25+ Inter-Community Shabbaton',
    adminEmails: ['levinet@ou.org'],
    adminFromEmail: 'levinet@ou.org',
    adminSubject: 'New Inter-Community Shabbaton',
    userFromEmail: 'levinet@ou.org',
    userSubject: 'Thank You For Registering',
    message: '<div>Thank you for signing up for the JLIC Israel Shabbaton!</div><br><div>If you have any questions, please speak to your JLIC Director or email Tova Levine at <a href="mailto:levinet@ou.org" rel="noreferrer">levinet@ou.org</a>.</div> <br><div>We will be in touch if we need any more information from you, and we look forward to seeing you there!</div>',
    amounts: [
      [{ USD: 96, ILS: 350 }, `Shabbaton cost, early bird special`],
      [{ USD: 66, ILS: 250 }, `Jerusalem Participants Price (for meals and programming and NO accommodation), early bird price`],
    ],
    department: 'JLIC',
    campaign: 'General Donation',
    defaultCurrency: 'USD',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          isGender: z.string(),
          campus: z.string(),
          age: z.string(),
          countryOfOrigin: z.string(),
          dietaryRestrictions: z.string().nullable().optional(),
          allergyInformation: z.string().nullable().optional(),
          emergencyContactName: z.string(),
          emergencyContactPhone: z.string(),
          Notes: z.string().nullable().optional(),
          wouldYouParticipate: z.string().nullable().optional(),
          wouldYouBeInvolved: z.string().nullable().optional(),
        })
      })
    }
  },
  'jlic-miluim-shabbaton': {
    emailTemplate: 'jlic-miluim-shabbaton',
    title: 'OU-JLIC Miluim Shabbaton',
    adminEmails: ['phillipsei@ou.org'],
    adminFromEmail: 'jlic@ou.org',
    adminSubject: 'OU-JLIC Miluim Shabbaton',
    userFromEmail: 'jlic@ou.org',
    userSubject: 'Thank You For Registering',
    message: '<div>Thank you so much for your generous donation to the JLIC Miluim Shabbaton. Your support made a meaningful difference in creating a Shabbat that was uplifting, restorative, and deeply impactful miluim.</div>',
    amounts: [
      [5000, `Sponsor therapists`],
      [1000, `Sponsor a family`],
      [500, `Sponsor a chayal`],
      [100, `Sponsor Friday night meal for a family`],
    ],
    department: 'JLIC_TAU',
    campaign: 'Miluim Shabbaton',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'ou-pledge': {
    template: 'general-pledge',
    emailTemplate: 'ou',
    title: 'OU Payment Dues',
    department: '',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your donation.',
    adminSubject: 'New OU Payment Dues',
    adminEmails: (request) => {
        const to = 'goldmani@ou.org'.split(',')
        const mapping = {
        TeachCoalition_Gov_Max: 'Caleb.Fischer@teachcoalition.org,benshabats@teachcoalition.org,altfields@teachcoalition.org,Kanofskyy@teachcoalition.org,paserm@teachcoalition.org',
        TeachCoalition_NJ: 'Caleb.Fischer@teachcoalition.org,benshabats@teachcoalition.org,altfields@teachcoalition.org,KatzK@teachcoalition.org,joyandehm@teachcoalition.org,rachel.spinner@teachcoalition.org',
        TeachCoalition_PA: 'Caleb.Fischer@teachcoalition.org,benshabats@teachcoalition.org,altfields@teachcoalition.org,jenny.sved@teachcoalition.org,LevensonH@teachcoalition.org',
        TeachCoalition_MD: 'Caleb.Fischer@teachcoalition.org,benshabats@teachcoalition.org,altfields@teachcoalition.org,jenny.sved@teachcoalition.org,Mickey.Rubin@teachcoalition.org',
        TeachCoalition_NYS: 'Caleb.Fischer@teachcoalition.org,kanofskyy@teachcoalition.org,benshabats@teachcoalition.org,altfields@teachcoalition.org,paserm@teachcoalition.org,rachel.spinner@teachcoalition.org',
        TeachCoalition_FL: 'Caleb.Fischer@teachcoalition.org,benshabats@teachcoalition.org,altfields@teachcoalition.org,danielaqua@teachcoalition.org,GlaserM@teachcoalition.org,elisheva.luck@teachcoalition.org',
        TeachCoalition_ProjectProtect: 'Caleb.Fischer@teachcoalition.org,kanofskyy@teachcoalition.org,benshabats@teachcoalition.org,altfields@teachcoalition.org,paserm@teachcoalition.org'
      } as any
      const email = mapping[request.applications![0].department!]
      if (email) to.push(email)

      if (request.applications![0].department == 'TeachCoalition_ProjectProtect' && request.applications![0].campaign == 'Grantwork') { to.push('kanofskyy@teachcoalition.org,rybsteinr@teachcoalition.org') }

      return to
    },
    adminFromEmail: 'OU Payment Dues <info@ou.org>',
    userFromEmail: 'OU.org <info@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2'
  },
  'ou-membership': {
    emailTemplate: 'oumembership',
    title: 'OU Membership',
    department: 'OU',
    campaign: 'ADM Membership Dues Campaign',
    subCampaign: 'OU Web',
    message: 'Thank you for joining us in sustaining Jewish life for every segment of our community',
    amounts: [
      108,
      360,
      500,
      1000
    ],
    adminEmails: (request) => {
      const to = 'hannahf@ou.org,neumanj@ou.org'.split(',')
      if (request.custom?.planningSpecialist) { to.push('legacygiving@ou.org') }
      return to
    },
    adminFromEmail: 'OU Membership <info@ou.org>',
    adminSubject: 'New OU Membership donation',
    userFromEmail: 'OU.org <info@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists (request) {
      const list = []

      if (request.custom?.newsletterSHSH) list.push(1850355)
      if (request.custom?.newsletterJa) list.push(1850365)

      return list
    },
    customValidations (form) {
      return form.extend({
        custom: z.object({
          newsletterSHSH: zBool().nullable().optional(),
          newsletterJa: zBool().nullable().optional(),
          planningSpecialist: zBool().nullable().optional(),
          membershipType: z.string().nullable().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    }
  },
  'ou-membership-eoy25': {
    template: 'ou-membership',
    department: 'OU',
    campaign: 'ADM Membership Dues Campaign',
    subCampaign: 'Telemarketing - Spring',
    emailTemplate: 'oumembership',
    title: 'OU Membership',
    message: 'Thank you for joining us in sustaining Jewish life for every segment of our community',
    amounts: [
      108,
      360,
      500,
      1000
    ],
    adminEmails: (request) => {
      const to = 'hannahf@ou.org,neumanj@ou.org'.split(',')
      if (request.custom?.planningSpecialist) { to.push('legacygiving@ou.org') }
      return to
    },
    adminFromEmail: 'OU Membership <info@ou.org>',
    adminSubject: 'New OU Membership donation',
    userFromEmail: 'OU.org <info@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists (request) {
      const list = []

      if (request.custom?.newsletterSHSH) list.push(1850355)
      if (request.custom?.newsletterJa) list.push(1850365)

      return list
    },
    customValidations (form) {
      return form.extend({
        custom: z.object({
          newsletterSHSH: zBool().nullable().optional(),
          newsletterJa: zBool().nullable().optional(),
          planningSpecialist: zBool().nullable().optional(),
          membershipType: z.string().nullable().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    }
  },
  'ou-membership-rejoin25': {
    template: 'ou-membership',
    emailTemplate: 'oumembership',
    title: 'OU Membership',
    department: 'OU',
    campaign: 'ADM Membership Dues Campaign',
    subCampaign: 'Direct Mail – Spring',
    message: 'Thank you for joining us in sustaining Jewish life for every segment of our community',
    amounts: [
      108,
      360,
      500,
      1000
    ],
    adminEmails: (request) => {
      const to = 'hannahf@ou.org,lovettya@ou.org'.split(',')
      if (request.custom?.planningSpecialist) { to.push('legacygiving@ou.org') }
      return to
    },
    adminFromEmail: 'OU Membership <info@ou.org>',
    adminSubject: 'New OU Membership - Direct Mail (Rejoin) – Spring donation',
    userFromEmail: 'OU.org <info@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists (request) {
      const list = []

      if (request.custom?.newsletterSHSH) list.push(1850355)
      if (request.custom?.newsletterJa) list.push(1850365)

      return list
    },
    customValidations (form) {
      return form.extend({
        custom: z.object({
          newsletterSHSH: zBool().nullable().optional(),
          newsletterJa: zBool().nullable().optional(),
          planningSpecialist: zBool().nullable().optional(),
          membershipType: z.string().nullable().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    }
  },
  'ou-membership25': {
    emailTemplate: 'oumembership',
    title: 'OU Membership',
    department: 'OU',
    campaign: 'General Donation',
    subCampaign: 'Direct Mail – Spring',
    message: 'Thank you for joining us in sustaining Jewish life for every segment of our community',
    amounts: [
      108,
      360,
      500,
      1000
    ],
    adminEmails: (request) => {
      const to = 'hannahf@ou.org,lovettya@ou.org'.split(',')
      if (request.custom?.planningSpecialist) { to.push('legacygiving@ou.org') }
      return to
    },
    adminFromEmail: 'OU Membership <info@ou.org>',
    adminSubject: 'New OU Membership - Direct Mail – Spring donation',
    userFromEmail: 'OU.org <info@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists (request) {
      const list = []

      if (request.custom?.newsletterSHSH) list.push(1850355)
      if (request.custom?.newsletterJa) list.push(1850365)

      return list
    },
    customValidations (form) {
      return form.extend({
        custom: z.object({
          newsletterSHSH: zBool().nullable().optional(),
          newsletterJa: zBool().nullable().optional(),
          planningSpecialist: zBool().nullable().optional(),
          membershipType: z.string().nullable().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    }
  },
  'ou-membership-tubshvat': {
    template: 'ou-membership',
    emailTemplate: 'ou-membership-tubshvat',
    title: 'OU Membership',
    department: 'OU',
    campaign: 'ADM Membership Dues Campaign',
    subCampaign: 'Direct Mail – Tu BShevat',
    message: 'Thank you for joining us in sustaining Jewish life for every segment of our community',
    amounts: [
      108,
      360,
      500,
      1000
    ],
    adminEmails: (request) => {
      const to = 'hannahf@ou.org,neumanj@ou.org'.split(',')
      if (request.custom?.planningSpecialist) { to.push('legacygiving@ou.org') }
      return to
    },
    adminFromEmail: 'OU Membership <info@ou.org>',
    adminSubject: 'New OU Membership donation',
    userFromEmail: 'OU.org <info@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists (request) {
      const list = []

      if (request.custom?.newsletterSHSH) list.push(1850355)
      if (request.custom?.newsletterJa) list.push(1850365)

      return list
    },
    customValidations (form) {
      return form.extend({
        custom: z.object({
          newsletterSHSH: zBool().nullable().optional(),
          newsletterJa: zBool().nullable().optional(),
          planningSpecialist: zBool().nullable().optional(),
          membershipType: z.string().nullable().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    }
  },
  'ou-renewtoday': {
    template: 'oumembership',
    emailTemplate: 'oumembership',
    title: 'OU Membership',
    department: 'OU',
    campaign: 'ADM Membership Dues Campaign',
    subCampaign: 'Direct Mail - Fall',
    message: 'Thank you for joining us in sustaining Jewish life for every segment of our community',
    amounts: [
      108,
      360,
      500,
      1000
    ],
    adminEmails: (request) => {
      const to = 'hannahf@ou.org,neumanj@ou.org'.split(',')
      if (request.custom?.planningSpecialist) { to.push('legacygiving@ou.org') }
      return to
    },
    adminFromEmail: 'OU Membership <info@ou.org>',
    adminSubject: 'New OU Membership donation',
    userFromEmail: 'OU.org <info@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists (request) {
      const list = []

      if (request.custom?.newsletterSHSH) list.push(1850355)
      if (request.custom?.newsletterJa) list.push(1850365)

      return list
    },
    customValidations (form) {
      return form.extend({
        custom: z.object({
          newsletterSHSH: zBool().nullable().optional(),
          newsletterJa: zBool().nullable().optional(),
          planningSpecialist: zBool().nullable().optional(),
          membershipType: z.string().nullable().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    }
  },
  'tisha-bav': {
    emailTemplate: 'tisha-bav',
    title: "Tisha B'av",
    department: 'OU_Torah',
    campaign: "Tisha B'av",
    subCampaign: '',
    message: 'Thank you for your support.',
    amounts: [
      1000,
      360,
      180,
      72,
      36
    ],
    adminEmails: ['info@ou.org',  'miriamw@ou.org','gmagder@ou.org','chernikoffj@ou.org', 'hirschr@ou.org'],
    adminFromEmail: 'OU.org <info@ou.org>',
    adminSubject: "New Tisha B'av sponsorship",
    userFromEmail: 'OU.org <info@ou.org>',
    userSubject: 'Thank you for your support.',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists (request) {
      const list = []
      if (request.custom?.membershipType === 'Yes') list.push(1850355)
      return list
    },
    customValidations (form) {
      return form.extend({
        custom: z.object({
          membershipType: z.string().nullable().optional(),
          selectedDescription: z.string().nullable().optional(),
        })
      })
    }
  },
  'ou-board': {
    emailTemplate: 'ou',
    title: 'OU Board',
    department: 'OU',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your donation',
    amounts: [
      18,
      36,
      54,
      100,
      250
    ],
    adminEmails: ['neumanj@ou.org', 'ouinternet@ou.org', 'goldmani@ou.org'],
    adminFromEmail: 'OU Donation <info@ou.org>',
    adminSubject: 'Orthodox Union - Donation - Board',
    userFromEmail: 'Orthodox Union <info@ou.org>',
    userSubject: 'Thank you for Supporting the OU',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists: [1850355],
  },
  'hauer-memorial': {
    emailTemplate: 'hauer-memorial',
    title: 'Rabbi Hauer Memorial Donation Form -- for JA',
    honoreeTemplate: 'ou',
    department: 'JewishMediaAndPublications',
    campaign: 'Jewish Action Donation',
    subCampaign: 'Jewish Action R. Hauer Memorial Edition',
    message: "Thank you for your donation!",
    amounts: [
      [36000, `Naming`],
      [10000, `Dedication of biography`],
      [10000, `Dedication of inside front page`],
      [3600, `Dedication of back page`],
      [2500, `Dedication of inside back page`],
      [1800, `Dedication of sections/visions (2 remaining)`],
      [360, `Dedication of individual pages`],
      [180, `Dedication with 140-character message`],
      [36000, `Naming`],
      [5000, `Inside cover`],
      [1800, `Quarter-page dedications`],
      [7200, `Biography dedication (8 remaining)`],
    ],
    customValidations (form) {
      return form.extend({
        custom: z.object({
           Notes: z.string().max(140, "Message must be 140 characters or less").nullable().optional(),
           SpecialEdition: z.string().nullable().optional(),
           Book: z.string().nullable().optional(),
          dedication: z.string().nullable().optional(),
        })
      })
    },
    adminEmails: ['goldmani@ou.org','gil.student@ou.org'],
    adminFromEmail: '<info@ou.org>',
    adminSubject: 'Thank You for Supporting Rabbi Hauer’s Legacy',
    userFromEmail: '<info@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'ou-hauer-donation': {
    emailTemplate: 'hauer-memorial',
    title: 'Special Rabbi Hauer zt"l Issue of Jewish Action Donation Form',
    honoreeTemplate: 'ou',
    department: 'JewishMediaAndPublications',
    campaign: 'Jewish Action Donation',
    subCampaign: 'Jewish Action R. Hauer Memorial Edition',
    message: "Thank you for your donation!",
    amounts: [
      [36000, `Naming`],
      [10000, `Dedication of biography`],
      [3600, `Dedication of back page`],
      [2500, `Dedication of inside back page`],
      [1800, `Dedication of sections/visions`],
      [360, `Dedication of individual pages`],
      [180, `Dedication with 140-character message`],

    ],
    customValidations (form) {
      return form.extend({
        custom: z.object({
           SpecialEdition: z.string().nullable().optional(),
           dedication: z.string().nullable().optional(),
        })
      })
    },
    adminEmails: ['goldmani@ou.org','gil.student@ou.org'],
    adminFromEmail: '<info@ou.org>',
    adminSubject: 'Special Rabbi Hauer zt"l Issue of Jewish Action Donation Form',
    userFromEmail: '<info@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },

  'ou-hauer-book-donation': {
    emailTemplate: 'hauer-memorial',
    title: 'Book of Inspiring, Historical Biographies by Rabbi Hauer zt"l Donation Form',
    honoreeTemplate: 'ou',
    department: "JewishMediaAndPublications",
    campaign: "Rabbi Hauer Book of Biographies",
    subCampaign: '',
    message: "Thank you for your donation!",
    amounts: [
      [36000, `Naming`],
      [10000, `Dedication of biography`],
      [10000, `Dedication of inside front page`],
      [3600, `Dedication of back page`],
      [2500, `Dedication of inside back page`],
      [1800, `Dedication of sections/visions (2 remaining)`],
      [360, `Dedication of individual pages`],
      [180, `Dedication with 140-character message`],
      [36000, `Naming`],
      [5000, `Inside cover`],
      [1800, `Quarter-page dedications`],
      [7200, `Biography dedication (8 remaining)`],
    ],
    customValidations (form) {
      return form.extend({
        custom: z.object({
           SpecialEdition: z.string().nullable().optional(),
           dedication: z.string().nullable().optional(),
        })
      })
    },
    adminEmails: ['goldmani@ou.org','gil.student@ou.org'],
    adminFromEmail: '<info@ou.org>',
    adminSubject: 'Book of Inspiring, Historical Biographies by Rabbi Hauer zt"l Donation Form',
    userFromEmail: '<info@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009829-1' : 'UA-3009829-2'
  },
  'ou-giving': {
    ...ougivingBasic,
    amounts: [

      18,
      54,
      100,
      360,
      500
    ],
    thankYouTemplate: 'ougiving',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'ohrbit': {
    ...ougivingBasic,
    amounts: [
      18,
      54,
      100,
      360,
      500
    ],
    thankYouTemplate: 'ougiving',
  },
    'donation-ou-gt2': {
    ...ougivingBasic,
  title: 'Giving Tuesday Control Donation Form',
  department: 'OU',
  campaign: 'Giving Tuesday',
  subCampaign: 'OU Web',
    amounts: [
      720,
      250,
      108,
      54,
      36
    ],
    thankYouTemplate: 'ougiving',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'donation-ou-eoy1': {
    ...ougivingBasic,
    title: 'End of Year Control Donation Form',
    template: 'donation-ou-gt1',
  department: 'OU',
  campaign: 'ADM Membership Dues Campaign',
  subCampaign: 'OU Web - Year End',
    amounts: [
      720,
      250,
      108,
      54,
      36
    ],
    thankYouTemplate: 'ougiving',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'oudonation-evergreen': {
    ...ougivingBasic,
  title: 'Strengthen Jewish Life. Support the OU - Page Donation Form',
  template: 'donation-ou-gt1',
  department: 'OU',
  campaign: 'General Donation',
  subCampaign: '',
    amounts: [
      720,
      250,
      108,
      54,
      36
    ],
    thankYouTemplate: 'ougiving',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'ou-giving-israel': {
    ...ougivingBasic,
    amounts: [
      18,
      54,
      100,
      360,
      500
    ],
    thankYouTemplate: 'ougiving',
    defaultCurrency: 'ILS' as Currency,
    customValidations (form) {
      return form.extend({
        custom: z.object({
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'ou-giving-give25': {
    ...ougivingBasic,
    department: 'OU',
    campaign: 'General Donation',
    subCampaign: 'Telemarketing - Spring',
    amounts: [
      18,
      54,
      100,
      360,
      500
    ],
    thankYouTemplate: 'ougiving',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'ou-donation': {
  emailTemplate: 'ougiving',
  title: 'OU Giving',
  department: 'OU',
  campaign: 'General Donation',
  subCampaign: '',
  message: 'Thank you for your donation',
  adminEmails: (request:any) => {
    const to = 'ouinternet@ou.org,rebecca.diamond@ou.org'.split(',')
    const mapping = {
      OU: 'goldmani@ou.org',
      CommunalResearch: 'shainm@ou.org,bebermanr@ou.org',
      CommunityProjectsAndPartnerships: 'staylor@ou.org',
      IsraelFreeSpirit: 'tamariy@ou.org',
      NCSY: 'goldmani@ou.org,lernerj@ncsy.org',
      NCSY_Alumni: 'marchuck@ncsy.org,lernerj@ncsy.org',
      Advocacy: 'tarer@ou.org,bojanl@ou.org',
      ReliefFunds: 'staylor@ou.org',
      ImpactAccelerator: 'rochlina@ou.org,bebermanr@ou.org,frydmant@ou.org',
      OUIsrael: 'bareld@ou.org,joshc@ouisrael.org,jordanr@ouisrael.org,lizb@ouisrael.org',
      JLIC: 'rosenthalp@ou.org',
      OUPress: 'breed@ou.org',
      OU_Torah: 'schwedm@ou.org,hirschr@ou.org',
      SynagogueInitiatives: 'posya@ou.org,naftalih@ou.org',
      TeachCoalition: 'benshabats@teachcoalition.org',
      WomensInitative: 'ashmidman@ou.org,epsteinn@ou.org',
      Yachad: 'saibelk@ou.org',
    } as any
    const email = mapping[request.applications![0].department!]
    if (email) to.push(email)

    if (request.custom?.planningSpecialist) { to.push('legacygiving@ou.org') }

    return to
  },
  adminFromEmail: 'OU Donation <info@ou.org>',
  adminSubject: 'Orthodox Union - Donation',
  userFromEmail: 'Orthodox Union <info@ou.org>',
  userSubject: 'Thank you for Supporting the OU',
  gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
  silverPopContactLists: [1850355],
    amounts: [
      18,
      54,
      108,
      250,
      720
    ],
    thankYouTemplate: 'oumodal',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'ou-iran': {
  emailTemplate: 'ouiran',
  title: 'Iran Emergency Response 2026',
  department: 'OUIsrael',
  campaign: 'Iran Emergency Response 2026',
  subCampaign: '',
  defaultCurrency: 'USD',
  message: 'Thank you for your donation',
  adminEmails: ['jordanr@ouisrael.org','MagnusonM@ou.org','Joshc@ouisrael.org','aberman@ouisrael.org','lizb@ouisrael.org'],
  adminFromEmail: 'OU Donation <info@ou.org>',
  adminSubject: 'Orthodox Union - Donation',
  userFromEmail: 'Orthodox Union <info@ou.org>',
  userSubject: 'Confirmation of Your Gift Supporting Teens Impacted by the War with Iran',
  gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
  amounts: [
    [{ USD: 125, ILS: 385, CAD: 170 }, `funds one crisis support session with a professional advisor`],
    [{ USD: 250, ILS: 770, CAD: 340 }, `funds two crisis support sessions with a professional advisor`],
    [{ USD: 500, ILS: 1540, CAD: 680 }, `funds four crisis support sessions with a professional advisor`],
    [{ USD: 1500, ILS: 4620, CAD: 2040 }, `underwrites a full month of consistent support with a professional advisor`],
    [{ USD: 5000, ILS: 15400, CAD: 6800 }, `supports a month of crisis response for a small caseload of teens with a social worker`],
    [{ USD: 10000, ILS: 30800, CAD: 13600 }, `underwrites an emergency caseload for a month with a social worker`],
  ],
  thankYouTemplate: 'oumodal',
  silverPopContactLists: [1850355],
},
'donation-ou-gt1': {
  emailTemplate: 'ougiving',
  title: 'Giving Tuesday Test Donation Form',
  department: 'OU',
  campaign: 'Giving Tuesday',
  subCampaign: 'OU Web',
  message: 'Thank you for your donation',
  adminEmails: (request:any) => {
    const to = 'ouinternet@ou.org,rebecca.diamond@ou.org'.split(',')
    const mapping = {
      OU: 'goldmani@ou.org',
      CommunalResearch: 'shainm@ou.org,bebermanr@ou.org',
      CommunityProjectsAndPartnerships: 'staylor@ou.org',
      IsraelFreeSpirit: 'tamariy@ou.org',
      NCSY: 'goldmani@ou.org,lernerj@ncsy.org',
      NCSY_Alumni: 'marchuck@ncsy.org,lernerj@ncsy.org',
      Advocacy: 'tarer@ou.org,bojanl@ou.org',
      ReliefFunds: 'staylor@ou.org',
      ImpactAccelerator: 'rochlina@ou.org,bebermanr@ou.org,frydmant@ou.org',
      OUIsrael: 'bareld@ou.org,joshc@ouisrael.org,jordanr@ouisrael.org,lizb@ouisrael.org',
      JLIC: 'rosenthalp@ou.org',
      OUPress: 'breed@ou.org',
      OU_Torah: 'schwedm@ou.org,hirschr@ou.org',
      SynagogueInitiatives: 'posya@ou.org,naftalih@ou.org',
      TeachCoalition: 'benshabats@teachcoalition.org',
      WomensInitative: 'ashmidman@ou.org,epsteinn@ou.org',
      Yachad: 'saibelk@ou.org',
    } as any
    const email = mapping[request.applications![0].department!]
    if (email) to.push(email)

    if (request.custom?.planningSpecialist) { to.push('legacygiving@ou.org') }

    return to
  },
  adminFromEmail: 'OU Donation <info@ou.org>',
  adminSubject: 'Orthodox Union - Donation',
  userFromEmail: 'Orthodox Union <info@ou.org>',
  userSubject: 'Thank you for Supporting the OU',
  gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
  silverPopContactLists: [1850355],
    amounts: [
      18,
      54,
      108,
      250,
      720
    ],
    thankYouTemplate: 'oumodal',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'donation-ou-eoy2': {
  template: 'donation-ou-gt2',
  emailTemplate: 'ougiving',
  title: 'End of Year Test Donation Form',
  department: 'OU',
  campaign: 'ADM Membership Dues Campaign',
  subCampaign: 'OU Web - Year End',
  message: 'Thank you for your donation',
  adminEmails: (request:any) => {
    const to = 'ouinternet@ou.org,rebecca.diamond@ou.org'.split(',')
    const mapping = {
      OU: 'goldmani@ou.org',
      CommunalResearch: 'shainm@ou.org,bebermanr@ou.org',
      CommunityProjectsAndPartnerships: 'staylor@ou.org',
      IsraelFreeSpirit: 'tamariy@ou.org',
      NCSY: 'goldmani@ou.org,lernerj@ncsy.org',
      NCSY_Alumni: 'marchuck@ncsy.org,lernerj@ncsy.org',
      Advocacy: 'tarer@ou.org,bojanl@ou.org',
      ReliefFunds: 'staylor@ou.org',
      ImpactAccelerator: 'rochlina@ou.org,bebermanr@ou.org,frydmant@ou.org',
      OUIsrael: 'bareld@ou.org,joshc@ouisrael.org,jordanr@ouisrael.org,lizb@ouisrael.org',
      JLIC: 'rosenthalp@ou.org',
      OUPress: 'breed@ou.org',
      OU_Torah: 'schwedm@ou.org,hirschr@ou.org',
      SynagogueInitiatives: 'posya@ou.org,naftalih@ou.org',
      TeachCoalition: 'benshabats@teachcoalition.org',
      WomensInitative: 'ashmidman@ou.org,epsteinn@ou.org',
      Yachad: 'saibelk@ou.org',
    } as any
    const email = mapping[request.applications![0].department!]
    if (email) to.push(email)

    if (request.custom?.planningSpecialist) { to.push('legacygiving@ou.org') }

    return to
  },
  adminFromEmail: 'OU Donation <info@ou.org>',
  adminSubject: 'Orthodox Union - Donation',
  userFromEmail: 'Orthodox Union <info@ou.org>',
  userSubject: 'Thank you for Supporting the OU',
  gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
  silverPopContactLists: [1850355],
    amounts: [
      18,
      54,
      108,
      250,
      720
    ],
    thankYouTemplate: 'oumodal',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'ou-giving-test': {
    ...ougivingBasic,
    amounts: [
      18,
      36,
      54,
      100,
      250
    ],
    thankYouTemplate: 'ougiving',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'ou-antisemitism': {
  emailTemplate: 'ougiving',
  title: 'OU Antisemitism',
  department: 'OU',
  campaign: 'General Donation',
  subCampaign: '',
  message: 'Thank you for your donation',
  amounts: [
    18,
    36,
    54,
    100,
    250
  ],
  adminEmails: (request) => {
    const to = 'goldmani@ou.org,combatantisemitism@ou.org'.split(',')
    if (request.custom?.planningSpecialist) { to.push('legacygiving@ou.org') }
    return to
  },
  adminFromEmail: 'OU Antisemitism Donation <info@ou.org>',
  adminSubject: 'Orthodox Union - Antisemitism Donation',
  userFromEmail: 'Orthodox Union Antisemitism <combatantisemitism@ou.org>',
  userSubject: 'Thank you for Supporting the OU Antisemitism',
  gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
  silverPopContactLists: [8673507],
    thankYouTemplate: 'ougiving',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'ou-backtoschool': {
    template: 'ou-support',
    emailTemplate: 'ou-backtoschool',
    title: 'OU Giving',
    department: 'OU',
    campaign: 'Back to School',
    subCampaign: '',
    message: 'Thank you for your donation',
    adminEmails: (request) => {
      const to = 'hannahf@ou.org,abayevk@ou.org,marcusj@ou.org'.split(',')
      if (request.custom?.planningSpecialist) { to.push('legacygiving@ou.org') }
      return to
    },
    adminFromEmail: 'OU Back to School Donation <info@ou.org>',
    adminSubject: 'Orthodox Union - Back to School Donation',
    userFromEmail: 'Orthodox Union <info@ou.org>',
    userSubject: 'Thank you for Supporting the OU',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists: [1850355],
    amounts: [
      1000,
      500,
      250,
      100,
      54,
      36,
      18
    ],
    thankYouTemplate: 'ougiving',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'ou-support': {
    emailTemplate: 'ougiving',
    title: 'OU Giving',
    department: 'OU',
    campaign: 'ADM Membership Dues Campaign',
    subCampaign: 'Direct Mail - Spring',
    message: 'Thank you for your donation',
    adminEmails: (request) => {
      const to = 'hannahf@ou.org,abayevk@ou.org,marcusj@ou.org'.split(',')
      if (request.custom?.planningSpecialist) { to.push('legacygiving@ou.org') }
      return to
    },
    adminFromEmail: 'OU Donation <info@ou.org>',
    adminSubject: 'Orthodox Union - Donation',
    userFromEmail: 'Orthodox Union <info@ou.org>',
    userSubject: 'Thank you for Supporting the OU',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists: [1850355],
    amounts: [
      72,
      100,
      360,
      500,
      1000
    ],
    thankYouTemplate: 'ougiving',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'ou-one-eighty': {
    ...ougivingBasic,
    department: 'OU',
    campaign: '180 for 180',
    adminEmails: ['goldmani@ou.org', 'marcusj@ou.org'],
    amounts: [
      250,
      100,
      54,
      36,
      18
    ],
    thankYouTemplate: 'ougiving',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'yomhazikarontribute': {
    ...ougivingBasic,
    template: 'ou-one-eighty',
    department: 'OU',
    campaign: 'Yom Hazikaron',
    adminEmails: ['goldmani@ou.org', 'marcusj@ou.org', 'schlossm@ou.org'],
    amounts: [
      1800,
      500,
      180,
      72,
      18
    ],
    thankYouTemplate: 'ougiving',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'ou-actnow': {
    amounts: [
      18,
      36,
      54,
      100,
      250
    ],
    emailTemplate: 'ougiving',
    title: 'OU Giving',
    department: 'OU',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your donation',
    adminEmails: (request:any) => {
      const to = 'ouinternet@ou.org'.split(',')
      const mapping = {
        OU: 'goldmani@ou.org',
        CommunalResearch: 'shainm@ou.org,bebermanr@ou.org',
        CommunityProjectsAndPartnerships: 'staylor@ou.org',
        IsraelFreeSpirit: 'tamariy@ou.org',
        NCSY: 'goldmani@ou.org,lernerj@ncsy.org',
        NCSY_Alumni: 'marchuck@ncsy.org,lernerj@ncsy.org',
        Advocacy: 'tarer@ou.org,bojanl@ou.org',
        ReliefFunds: 'staylor@ou.org',
        ImpactAccelerator: 'rochlina@ou.org,bebermanr@ou.org,frydmant@ou.org',
        OUIsrael: 'bareld@ou.org,joshc@ouisrael.org,jordanr@ouisrael.org,lizb@ouisrael.org',
        JLIC: 'rosenthalp@ou.org',
        OUPress: 'breed@ou.org',
        OU_Torah: 'schwedm@ou.org,hirschr@ou.org',
        SynagogueInitiatives: 'posya@ou.org,naftalih@ou.org',
        TeachCoalition: 'benshabats@teachcoalition.org',
        WomensInitative: 'ashmidman@ou.org,epsteinn@ou.org',
        Yachad: 'saibelk@ou.org',
      } as any
      const email = mapping[request.applications![0].department!]
      if (email) to.push(email)
      return to
    },
    adminFromEmail: 'OU Donation <info@ou.org>',
    adminSubject: 'Orthodox Union - Actnow Donation',
    userFromEmail: 'Orthodox Union <info@ou.org>',
    userSubject: 'Thank you for Supporting the OU',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    silverPopContactLists: [1850355],
    thankYouTemplate: 'ougiving'
  },
  'ou-hrtributes': {
    template: 'ou-hrtributes',
    ...ougivingBasic,
    campaign: 'HR Tributes',
    emailTemplate: 'ou-hrtributes',
    amounts: [
      18,
      36,
      54,
      100,
      250
    ],
    adminEmails: (request:any) => {
      const to = 'marcusj@ou.org,hermani@ou.org,hannahf@ou.org,ouinternet@ou.org'.split(',')
      const mapping = {
        OU: 'goldmani@ou.org',
        CommunalResearch: 'shainm@ou.org,bebermanr@ou.org',
        CommunityProjectsAndPartnerships: 'staylor@ou.org',
        IsraelFreeSpirit: 'tamariy@ou.org',
        NCSY: 'goldmani@ou.org,lernerj@ncsy.org',
        NCSY_Alumni: 'marchuck@ncsy.org,lernerj@ncsy.org',
        Advocacy: 'tarer@ou.org,bojanl@ou.org',
        ReliefFunds: 'staylor@ou.org',
        ImpactAccelerator: 'rochlina@ou.org,bebermanr@ou.org,frydmant@ou.org',
        OUIsrael: 'bareld@ou.org,joshc@ouisrael.org,jordanr@ouisrael.org,lizb@ouisrael.org',
        JLIC: 'rosenthalp@ou.org',
        OUPress: 'breed@ou.org',
        OU_Torah: 'schwedm@ou.org,hirschr@ou.org',
        SynagogueInitiatives: 'posya@ou.org,naftalih@ou.org',
        TeachCoalition: 'benshabats@teachcoalition.org',
        WomensInitative: 'ashmidman@ou.org,epsteinn@ou.org',
        Yachad: 'saibelk@ou.org',
      } as any
      const email = mapping[request.applications![0].department!]
      if (email) to.push(email)

        if (request.custom?.planningSpecialist) { to.push('legacygiving@ou.org') }

      return to
    },
    thankYouTemplate: 'ougiving',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          recipientAddress: z.string().nullable().optional(),
          planningSpecialist: zBool().nullable().optional(),

        })
      })
    }
  },
  'ou-senliebermantribute': {
    template: 'ou-senliebermantribute',
    ...ougivingBasic,
    campaign: 'HR Tributes',
    emailTemplate: 'ou-senliebermantribute',
    amounts: [
      250,
      100,
      54,
      36,
      18
    ],
    adminEmails: (request:any) => {
      const to = 'marcusj@ou.org,hermani@ou.org,hannahf@ou.org,ouinternet@ou.org'.split(',')
      const mapping = {
        OU: 'goldmani@ou.org',
        CommunalResearch: 'shainm@ou.org,bebermanr@ou.org',
        CommunityProjectsAndPartnerships: 'staylor@ou.org',
        IsraelFreeSpirit: 'tamariy@ou.org',
        NCSY: 'goldmani@ou.org,lernerj@ncsy.org',
        NCSY_Alumni: 'marchuck@ncsy.org,lernerj@ncsy.org',
        Advocacy: 'tarer@ou.org,bojanl@ou.org',
        ReliefFunds: 'staylor@ou.org',
        ImpactAccelerator: 'rochlina@ou.org,bebermanr@ou.org,frydmant@ou.org',
        OUIsrael: 'bareld@ou.org,joshc@ouisrael.org,jordanr@ouisrael.org,lizb@ouisrael.org',
        JLIC: 'rosenthalp@ou.org',
        OUPress: 'breed@ou.org',
        OU_Torah: 'schwedm@ou.org,hirschr@ou.org',
        SynagogueInitiatives: 'posya@ou.org,naftalih@ou.org',
        TeachCoalition: 'benshabats@teachcoalition.org',
        WomensInitative: 'ashmidman@ou.org,epsteinn@ou.org',
        Yachad: 'saibelk@ou.org',
      } as any
      const email = mapping[request.applications![0].department!]
      if (email) to.push(email)
      if (request.custom?.planningSpecialist) { to.push('legacygiving@ou.org') }

      return to
    },
    thankYouTemplate: 'ou-senliebermantribute',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          recipientAddress: z.string().nullable().optional(),
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'ou-communities': {
    emailTemplate: 'ou-communities',
    title: 'OU Safety Reflector Belt Initiative',
    department: 'CommunityProjectsAndPartnerships',
    campaign: 'Reflector Belts',
    subCampaign: '',
    message: 'Thank you for your donation',
    adminEmails: ['miriamw@ou.org', 'steinigj@ou.org'],
    adminFromEmail: 'OU Community Engagement <info@ou.org>',
    adminSubject: 'OU Safety Reflector Belt Initiative - Donation',
    userFromEmail: 'OU Safety Reflector Belt Initiative <info@ou.org>',
    userSubject: 'Thank you so much for your donation for the OU Safety Reflector Belt Initiative. <br><br> Your generosity enables the Orthodox Union to continue providing synagogues with reflector belts to ensure the safety of those who are walking at night. ',
    amounts: [
      36,
      72,
      118,
      180,
      250
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          organization: z.string().nullable().optional(),
        })
      })
    },
  },
  'ou-giving-spirit-program-donations': {
    amounts: [
      10000,
      5000,
      2500,
      1000,
      500,
      250,
      180,
      100,
       72,
       54,
       36,
       25,
       18
    ],
    thankYouTemplate: 'ougiving',
    emailTemplate: 'ougiving',
  title: 'OU Giving',
  department: 'CommunityProjectsAndPartnerships',
  campaign: 'Spirit / Retiree',
  subCampaign: '',
  message: 'Thank you for your donation',
  adminEmails: (request) => {
      const to = 'miriamw@ou.org,steinigj@ou.org'.split(',')
      if (request.custom?.planningSpecialist) { to.push('legacygiving@ou.org,steinigj@ou.org') }
      return to
    },
  adminFromEmail: 'OU Community Engagement <info@ou.org>',
  adminSubject: 'Orthodox Union - Donation',
  userFromEmail: 'OU Community Engagement <info@ou.org>',
  userSubject: 'Thank you for your support - Spirit Program Donations',
  gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
      customValidations (form) {
      return form.extend({
        custom: z.object({
          NameOfOrganizationOrBusiness: z.string().nullable().optional(),
          WebsiteOfOrganizationOrBusiness: z.string().nullable().optional(),
          NameOfContactPerson: z.string().nullable().optional(),
          PhoneOfContactPerson: z.string().nullable().optional(),
          EmailOfContactPerson: z.string().nullable().optional(),
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'ou-benzakai': {
    emailTemplate: 'ougiving',
    honoreeTemplate: 'ougiving',
    title: 'Ben Zakkai Donation Form',
    department: 'BenZakkai',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['miriamw@ou.org', 'goldmani@ou.org', 'grossmane@ou.org'],
    adminFromEmail: 'Ben Zakkai <grossmane@ou.org>',
    adminSubject: 'A payment has been made to Ben Zakkai',
    userFromEmail: 'Ben Zakkai <grossmane@ou.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-1',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          honorOf: z.string().array().nullable().optional()
        })
      })
    }
  },
  'ou-tehillim': {
    emailTemplate: 'outehillim',
    title: 'Daily Tehillim call sponsorship',
    department: 'SynagogueInitiatives',
    campaign: 'Tehillim Call',
    subCampaign: '',
    message: 'Thank you for your generous sponsorship. Please join our daily Tehillim and chizuk calls each day at 1PM EDT.<br>You can listen to all of the archived calls here: <a href="https://www.ou.org/callarchive/">https://www.ou.org/callarchive/</a>',
    amounts: [
    ],
    adminEmails: ['naftalih@ou.org','chernikoffj@ou.org', 'lubiankerm@ou.org', 'goldmani@ou.org', 'Rebecca.Levy@ou.org'],
    adminFromEmail: 'Daily Tehillim call sponsorship <info@ou.org>',
    adminSubject: 'Orthodox Union - Daily Tehillim call sponsorship',
    userFromEmail: 'Orthodox Union <info@ou.org>',
    userSubject: 'Thank You',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          startingDate: z.string(),
          endingDate: z.string().nullable().optional(),
          dedication: z.string(),
          listingName: z.string().nullable().optional(),
        })
      })
    },
  },
  'multiple': {
    ...ougivingBasic,
    template: 'multiple',
    emailTemplate: 'multiple',
    title: 'OU Giving - Multiple',
    amounts: [
      18,
      36,
      50,
      100,
      250
    ],
    adminFromEmail: 'Orthodox Union <info@ou.org>',
    adminSubject: 'Orthodox Union - Multiple',
    userFromEmail: 'Orthodox Union <info@ou.org>'
  },
  'oupress-donation': {
    template: 'ou-giving-plain',
    ...ougivingBasic,
    amounts: [
      180,
      360,
      500
    ],
    emailTemplate: 'oupress',
    title: ' OU Press Donation Form',
    department: 'OUPress',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'Thank you for your donation.',
    adminEmails: ['gellers@ou.org','breed@ou.org','miriamw@ou.org', 'goldmani@ou.org'],
    adminSubject: ' OU Press Donation Form',
    silverPopContactLists: [1876575],
  },
  'ou-giving-torah': {
    ...ougivingBasic,
    template: 'ou-giving-stripped',
    emailTemplate: 'ougivingtorah',
    title: 'OU Giving - Community Torah',
    amounts: [
      18,
      36,
      50,
      100,
      250
    ],
    department: 'OU',
    campaign: 'OU_Torah',
    adminFromEmail: 'Orthodox Union <info@ou.org>',
    adminSubject: 'Orthodox Union - Community Torah',
    userFromEmail: 'Orthodox Union <info@ou.org>'
  },
  'outorah-semichaschaver': {
    emailTemplate: 'ougivingtorah',
    title: 'Semichas Chaver Program - Sponsorship Form',
    department: 'OU_Torah',
    campaign: 'Semichas Chaver',
    subCampaign: '',
    message: 'Thank you for your donation.',
    adminEmails: ['renasoclof@gmail.com', 'goldmani@ou.org', 'elyadag@gmail.com', 'hirschr@ou.org'],
    adminFromEmail: 'OU Torah <info@ou.org>',
    adminSubject: 'Semichas Chaver Program Sponsorship',
    userFromEmail: 'OU Torah <info@ou.org>',
    userSubject: 'Thank you for your support',
    amounts: [
      [1800, `Sponsor a Family`],
      [1050, `Sponsor a Couple`],
      [500, ``],
      [360, ``],
      [180, ``],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-29' : 'UA-32047-29'
  },
  'eruv-sponsorship': {
    emailTemplate: 'eruv-sponsorship',
    title: 'Eruv - Sponsorship Form',
    department: 'CommunityProjectsAndPartnerships',
    campaign: 'Eruv Initiative',
    subCampaign: '',
    message: 'Thank you for your donation.',
    adminEmails: ['sarnae@ou.org, dobkins@ou.org'],
    adminFromEmail: 'OU Eruv <info@eruvinitiative.org>',
    adminSubject: 'Eruv - Sponsorship',
    userFromEmail: 'OU Eruv <info@eruvinitiative.org>',
    userSubject: 'Eruv - Thank you for your sponsorship',
    amounts: [
      [1800, `Conference Supporter`],
      [3600, `Conference Partner`],
      [5000, `Conference Pillar`],
      [10000, `Conference Sponsor`],
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-29' : 'UA-32047-29'
  },
  'ou-giving-hunger': {
    ...ougivingBasic,
    template: 'ou-giving-hunger',
    emailTemplate: 'ougivinghunger',
    title: 'Orthodox Union - Maos Chitim',
    adminEmails: ['neumanj@ou.org', 'ouinternet@ou.org'],
    message: "Thank you for donating to the OU's Maos Chitim Campaign for Passover.  With your help, we'll provide a chag kasher vsamaech to as many of our fellow Jews as possible. Explore Passover recipes, kashrut Q & As and much more at <a href='https://oukosher.org/passover/'>oukosher.org/passover</a>.",
    amounts: [
      360,
      180,
      72,
      54,
      720,
      1800,
    ],
    department: 'OU',
    campaign: 'ADM Passover Food Campaign',
    subCampaign: '',
    adminSubject: 'Orthodox Union - Maos Chitim'
  },
  'ou-eoy': {
    ...ougivingBasic,
    emailTemplate: 'ou-eoy',
    template: 'ou-eoy',
    amounts: [
      18,
      36,
      54,
      100,
      250
    ],
    title: 'OU Giving - End of Year',
    department: 'OU',
    campaign: 'EOFY24',
    subCampaign: '',
    message: 'Thank you for your donation',
    adminFromEmail: 'OU Donation End of Year<info@ou.org>',
    adminSubject: 'Orthodox Union - End of Year Donation',
    userFromEmail: 'Orthodox Union <info@ou.org>',
    adminEmails: (request:any) => {
      const to = 'marcusj@ou.org'.split(',')
      const mapping = {
        OU: 'goldmani@ou.org',
        CommunalResearch: 'shainm@ou.org,bebermanr@ou.org',
        CommunityProjectsAndPartnerships: 'staylor@ou.org',
        IsraelFreeSpirit: 'tamariy@ou.org',
        NCSY: 'goldmani@ou.org,lernerj@ncsy.org',
        NCSY_Alumni: 'marchuck@ncsy.org,lernerj@ncsy.org',
        Advocacy: 'tarer@ou.org,bojanl@ou.org',
        ReliefFunds: 'staylor@ou.org',
        ImpactAccelerator: 'rochlina@ou.org,bebermanr@ou.org,frydmant@ou.org',
        OUIsrael: 'bareld@ou.org,joshc@ouisrael.org,jordanr@ouisrael.org,lizb@ouisrael.org',
        JLIC: 'rosenthalp@ou.org',
        OUPress: 'breed@ou.org',
        OU_Torah: 'schwedm@ou.org,hirschr@ou.org',
        SynagogueInitiatives: 'posya@ou.org,naftalih@ou.org',
        TeachCoalition: 'benshabats@teachcoalition.org',
        WomensInitative: 'ashmidman@ou.org,epsteinn@ou.org',
        Yachad: 'saibelk@ou.org',
      } as any
      const email = mapping[request.applications![0].department!]
      if (email) to.push(email)
      if (request.custom?.planningSpecialist) { to.push('legacygiving@ou.org') }

      return to
    },
    userSubject: 'Thank you for Supporting the OU',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          planningSpecialist: zBool().nullable().optional(),
        })
      })
    }
  },
  'ou-ashley-blaker': {
    template: 'ou-member-events',
    emailTemplate: 'ou-ashley-blaker',
    title: 'OU Membership',
    department: 'OU',
    campaign: 'ADM Membership Dues Campaign',
    subCampaign: '',
    message: 'Thank you for your donation',
    amounts: [
      54,
      100,
      360,
      500,
      720,
      1000,
      2500,
      12
    ],
    adminEmails: ['schragers@ou.org'],
    adminFromEmail: 'OU Ashley Blaker event <info@ou.org>',
    adminSubject: 'New OU Ashley Blaker donation+registration',
    userFromEmail: 'OU.org <info@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2'
  },
  'ou-acquisition': {
    template: 'ou-acquisition',
    emailTemplate: 'ou',
    title: 'OU Membership',
    department: 'OU',
    campaign: 'ADM Membership Dues Campaign',
    subCampaign: 'Direct Mail Acquisition - Spring',
    message: 'Thank you for your donation',
    amounts: [
      108,
      360,
      500,
      1000
    ],
    adminEmails: ['abayevk@ou.org'],
    adminFromEmail: 'OU Acquisition <info@ou.org>',
    adminSubject: 'New OU Acquisition donation',
    userFromEmail: 'OU.org <info@ou.org>',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-1' : 'UA-25738407-2'
  },
  'ou-member-events': {
    emailTemplate: 'ou',
    title: 'OU Members Event',
    department: 'OU',
    campaign: 'ADM Membership Dues Campaign',
    subCampaign: '',
    message: 'Thank you for your donation',
    amounts: [
      108,
      360,
      500,
      1000
    ],
    adminEmails: ['goldmani@ou.org'],
    adminFromEmail: 'OU Membership <info@ou.org>',
    adminSubject: 'New OU Member Events donation',
    userFromEmail: 'OU.org <info@ou.org>',
    userSubject: 'Thank you for your support',
  },
  'kol-echad': {
    emailTemplate: 'ou',
    template: 'ou-member-events',
    title: 'Kol Echad',
    department: 'CommunityProjectsAndPartnerships',
    campaign: 'OU - Kol Echad Support',
    subCampaign: '',
    message: 'Thank you for your donation',
    amounts: [
      18,
      54,
      100,
      250,
      1000,
    ],
    adminEmails: ['MotzenY@ou.org'],
    adminFromEmail: 'OU - Kol Echad <info@ou.org>',
    adminSubject: 'New OU - Kol Echad donation',
    userFromEmail: 'OU.org Kol Echad<info@ou.org>',
    userSubject: 'Thank you for your support',
  },
  'ja-subscribe': {
    emailTemplate: 'ja-subscribe',
    title: 'JA Subscription',
    recaptcha: {
      enabled: true
    },
    department: 'OU',
    campaign: 'Jewish Action Subscription',
    subCampaign: '',
    honoreeTemplate: 'ja',
    message: 'Thank you for your Jewish Action subscription',
    amounts: [
      [16, `1 Year (4 Issues)`],
      [28, `2 Year (8 Issues)`],
      [0, `For Students`],
      [20, `1 Year (4 Issues)`],
      [32, `2 Year (8 Issues)`],
      [60, `1 Year (4 Issues)`],
      [105, `2 Year (8 Issues)`],
    ],
    adminEmails: ['goldmani@ou.org','ja@ou.org','weiners@ou.org', 'carmeln@ou.org'],
    adminFromEmail: 'carmeln@ou.org',
    adminSubject: 'New Entry - Jewish Action subscription',
    userFromEmail: 'carmeln@ou.org',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-24' : 'UA-32047-23',
    silverPopContactLists: [1850365],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          isGift: zBool().nullable().optional(),
          recipientFirstName: z.string().nullable().optional(),
          recipientLastName: z.string().nullable().optional(),
          recipientStreet: z.string().nullable().optional(),
          recipientStreet2: z.string().nullable().optional(),
          recipientCountry: z.string().nullable().optional(),
          recipientCity: z.string().nullable().optional(),
          recipientState: z.string().nullable().optional(),
          recipientZip: z.string().nullable().optional(),
          recipientEmail: z.string().nullable().optional(),
          recipientPhone: z.string().nullable().optional(),
          hearAboutUs: z.string().nullable().optional(),
        })
      })
    }
  },
  'jewishaction-subscribe': {
    emailTemplate: 'ja-subscribe',
    title: 'JA Subscription',
    recaptcha: {
      enabled: true
    },
    department: 'OU',
    campaign: 'Jewish Action Subscription',
    subCampaign: '',
    honoreeTemplate: 'ja',
    message: 'Thank you for your Jewish Action subscription',
    amounts: [
      [16, `1 Year (4 Issues)`],
      [28, `2 Year (8 Issues)`],
      [0, `For Students`],
      [20, `1 Year (4 Issues)`],
      [32, `2 Year (8 Issues)`],
      [60, `1 Year (4 Issues)`],
      [105, `2 Year (8 Issues)`],
    ],
    adminEmails: ['goldmani@ou.org','ja@ou.org','weiners@ou.org', 'carmeln@ou.org'],
    adminFromEmail: 'carmeln@ou.org',
    adminSubject: 'New Entry - Jewish Action subscription',
    userFromEmail: 'carmeln@ou.org',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-24' : 'UA-32047-23',
    silverPopContactLists: [1850365],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional(),
          isGift: zBool().nullable().optional(),
          location: z.string().nullable().optional(),
          recipientFirstName: z.string().nullable().optional(),
          recipientLastName: z.string().nullable().optional(),
          recipientStreet: z.string().nullable().optional(),
          recipientStreet2: z.string().nullable().optional(),
          recipientCountry: z.string().nullable().optional(),
          recipientCity: z.string().nullable().optional(),
          recipientState: z.string().nullable().optional(),
          recipientZip: z.string().nullable().optional(),
          recipientEmail: z.string().nullable().optional(),
          recipientPhone: z.string().nullable().optional(),
          hearAboutUs: z.string().nullable().optional(),
        })
      })
    }
  },
  'jewishaction-subscribe-gift': {
    emailTemplate: 'ja-subscribe',
    title: 'JA Subscription - Gift',
    recaptcha: {
      enabled: true
    },
    department: 'OU',
    campaign: 'Jewish Action Subscription',
    subCampaign: '',
    honoreeTemplate: 'ja',
    message: 'Thank you for your Jewish Action subscription',
    amounts: [
      [16, `1 Year (4 Issues)`],
      [28, `2 Year (8 Issues)`],
      [0, `For Students`],
      [20, `1 Year (4 Issues)`],
      [32, `2 Year (8 Issues)`],
      [60, `1 Year (4 Issues)`],
      [105, `2 Year (8 Issues)`],
    ],
    adminEmails: ['goldmani@ou.org','ja@ou.org','weiners@ou.org', 'carmeln@ou.org'],
    adminFromEmail: 'carmeln@ou.org',
    adminSubject: 'New Entry - Jewish Action subscription',
    userFromEmail: 'carmeln@ou.org',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-24' : 'UA-32047-23',
    silverPopContactLists: [1850365],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional(),
          isGift: zBool().nullable().optional(),
          location: z.string().nullable().optional(),
          recipientFirstName: z.string().nullable().optional(),
          recipientLastName: z.string().nullable().optional(),
          recipientStreet: z.string().nullable().optional(),
          recipientStreet2: z.string().nullable().optional(),
          recipientCountry: z.string().nullable().optional(),
          recipientCity: z.string().nullable().optional(),
          recipientState: z.string().nullable().optional(),
          recipientZip: z.string().nullable().optional(),
          recipientEmail: z.string().nullable().optional(),
          recipientPhone: z.string().nullable().optional(),
          hearAboutUs: z.string().nullable().optional(),
        })
      })
    }
  },
  'jewishaction-donate': {
    emailTemplate: 'ja-donate',
    title: 'Jewish Action Donations',
    recaptcha: {
      enabled: true
    },
    department: 'Jewish Media And Publications',
    campaign: 'General Donation',
    subCampaign: '',
    honoreeTemplate: 'ja',
    message: 'Thank you for supporting Jewish Action, the magazine of the Orthodox Union!',
    amounts: [
    ],
    adminEmails: ['goldmani@ou.org','ja@ou.org', 'carmeln@ou.org'],
    adminFromEmail: 'carmeln@ou.org',
    adminSubject: 'New Entry - Jewish Action Donation',
    userFromEmail: 'carmeln@ou.org',
    userSubject: 'Thank you for your support',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-24' : 'UA-32047-23',
    silverPopContactLists: [1850365],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional(),
          subjectCategory: z.string().min(1, 'Category is required'),
        })
      })
    }
  },
  'ouisrael-general': {
    ...ouisraelBasic,
    title: 'OUIsrael General Support',
    campaign: 'General Donation',
    subCampaign: '',
    adminEmails: (request:any) => {
      const to ='ABerman@ouisrael.org,joshc@ouisrael.org,jordanr@ouisrael.org,lizb@ouisrael.org'.split(',')
      if (request.currency == 'CAD') {
         to.push('rozenglish@gmail.com')
      }
      return to
    },
    amounts: {
      USD: [
        100,
        180,
        500,
        1000,
        1800,
        5000
      ],
      ILS: [
        100,
        180,
        500,
        1000,
        1800,
        5000
      ],
      CAD: [
        100,
        180,
        500,
        1000,
        1800,
        5000
      ]
    }
  },
  'ouisrael-full': {
    ...ouisraelBasic,
    title: 'OUIsrael General Support',
    campaign: 'General Donation',
    subCampaign: '',
    adminEmails: (request:any) => {
      const to ='ABerman@ouisrael.org,joshc@ouisrael.org,jordanr@ouisrael.org,lizb@ouisrael.org'.split(',')
      if (request.currency == 'CAD') {
         to.push('rozenglish@gmail.com')
      }
      return to
    },
    amounts: {
      USD: [
        1800,
        1000,
        500,
        360,
        180,
        100,
      ],
      ILS: [
        1800,
        1000,
        500,
        360,
        180,
        100,
      ],
      CAD: [
        1800,
        1000,
        500,
        360,
        180,
        100,
      ]
    }
  },
  'ouisrael-general-give': {
    ...ouisraelBasic,
    template: 'ouisrael-general',
    title: 'OUIsrael General Support',
    campaign: 'General Donation',
    subCampaign: 'Telemarketing - Fall',
    adminEmails: (request:any) => {
      const to ='ABerman@ouisrael.org,JoshC@ouisrael.org,joshc@ouisrael.org,jordanr@ouisrael.org,lizb@ouisrael.org'.split(',')
      if (request.currency == 'CAD') {
         to.push('rozenglish@gmail.com')
      }
      return to
    },
    amounts: {
      USD: [
        18,
        54,
        100,
        360,
        500
      ],
      ILS: [
        18,
        54,
        100,
        360,
        500
      ],
      CAD: [
        18,
        54,
        100,
        360,
        500
      ]
    }
  },
  'ouisrael-rh': {
    ...ouisraelBasic,
    title: 'OUIsrael General Support',
    emailTemplate: 'ouisrael-rh',
    campaign: 'RHLaunch',
    defaultCurrency: 'USD' as Currency,
    userSubject: 'Thank you for giving them a different year',
    subCampaign: '',
    amounts: [
      [{ USD: 180, ILS: 540, CAD: 250 }, `One Week of Belonging - Sponsor one teen’s participation in an OU Israel Teen Center for one week, including meals, activities, mentorship, and a safe place to go.`],
      [{ USD: 360, ILS: 1085, CAD: 500 }, `Professional Support - Provide two sessions with a social worker for a teen navigating serious challenges.`],
      [{ USD: 720, ILS: 2170, CAD: 1000 }, ` Four Weeks of Support - Sponsor four weeks of Teen Center participation, giving a teen consistent access to the people, programs, and support they need to move forward.`],
      [{ USD: 1800, ILS: 5420, CAD: 2485 }, `Intensive Support When It Matters Most - Help provide a month of individualized support for a teen facing serious challenges, combining professional counseling, ongoing mentorship, and coordinated guidance to help them regain stability and move forward.`],
    ],
  },
  'ouisrael-rh-heb': {
    ...ouisraelBasic,
    title: 'OUIsrael General Support',
    emailTemplate: 'ouisrael-rh-heb',
    message: 'תודה על תרומתך',
    campaign: 'RHLaunch',
    userSubject: 'תודה שעזרתם להם להתחיל שנה אחרת',
    subCampaign: '',
    amounts: [
      180,
      360,
      540,
      1800,
    ],
  },
  'zula-he': {
    ...ouisraelBasic,
    title: 'OUIsrael Zula',
    campaign: 'Zula',
    subCampaign: '',
    emailTemplate: 'zula-he'
  },
  'kashrut-education': {
    ...ouisraelBasic,
    title: 'Kashrut Education - OU Israel Center',
    campaign: 'Kashrut Education',
    subCampaign: '',
    amounts: [
      [{ USD: 56, ILS: 180 }, `to sponsor a Kashrut Shiur online or at the Center`],
      [{ USD: 110, ILS: 360 }, `to sponsor an individual Kashrut Article in Torah Tidbits`],
      [{ USD: 440, ILS: 1400 }, `to sponsor a Kashrut Article in Torah Tidbits for a month`],
      [{ USD: 1560, ILS: 5000 }, `to sponsor a Kashrut workshop`],
      [{ USD: 4690, ILS: 15000 }, `to sponsor a Kashrut series`],
      [{ USD: 10940, ILS: 35000 }, `to sponsor a Kashrut Video`],
      [{ USD: 16875, ILS: 54000 }, `to sponsor a Kashrut Brochure / Guide`],
    ],
    adminEmails: ['efriedman@ouisrael.org', 'JoshC@ouisrael.org', 'joshc@ouisrael.org', 'jordanr@ouisrael.org', 'lizb@ouisrael.org'],
    emailTemplate: 'ouisrael'
  },
  'ouisrael-purim': {
    ...ouisraelBasic,
    title: 'Pre-purim Mishloach Manot - OU Israel Center',
    campaign: 'Purim Baskets IDF',
    subCampaign: '',
    amounts: [
      [{ USD: 36, ILS: 120, CAD: 50 }, `for one Mishloach Manot`],
      [{ USD: 180, ILS: 600, CAD: 245 }, `for 5 Mishloach Manot`],
      [{ USD: 360, ILS: 1200, CAD: 490 }, `for 10 Mishloach Manot`],
      [{ USD: 648, ILS: 2160, CAD: 885 }, `for 18 Mishloach Manot`],
    ],
    emailTemplate: 'ouisrael-purim'
  },
  'ouisrael-purim-he': {
    ...ouisraelBasic,
    title: 'Pre-purim Mishloach Manot - OU Israel Center',
    campaign: 'Purim Baskets IDF',
    subCampaign: '',
    amounts: [
      [{ USD: 36, ILS: 120, CAD: 50 }, `למשלוח מנה אחת`],
      [{ USD: 180, ILS: 600, CAD: 245 }, `ל-5 משלוח מנות`],
      [{ USD: 360, ILS: 1200, CAD: 490 }, `ל-10 משלוח מנות`],
      [{ USD: 648, ILS: 2160, CAD: 885 }, `ל-18 משלוח מנות`],
    ],
    emailTemplate: 'ouisrael-purim-he'
  },
  'ouisrael-kaparot': {
    ...ouisraelBasic,
    title: 'OU Israel Pedyon Kaparot',
    campaign: 'Youth Centers Pidyon Kaparot',
    defaultCurrency: 'ILS' as Currency,
    subCampaign: '',
    amounts: [
      [{ USD: 18, ILS: 18, CAD: 18 }, ``],
    ],
    emailTemplate: 'ouisrael-kaparot'
  },
  'ouisrael-kaparot-en': {
    ...ouisraelBasic,
    title: 'OU Israel Pedyon Kaparot',
    campaign: 'Youth Centers Pidyon Kaparot',
    subCampaign: '',
    amounts: [
      [{ USD: 18, ILS: 18 }, ``],
    ],
    emailTemplate: 'ouisrael-kaparot-en'
  },
  'ouisrael-zecher-lmachatzit-hashekel': {
    ...ouisraelBasic,
    title: 'Zecher L`machatzit Hashekel',
    campaign: 'Zecher Lmachatzit Hashekel',
    subCampaign: '',
    emailTemplate: 'ouisrael-zecher-lmachatzit-hashekel'
  },
  'ouisrael-zecher-lmachatzit-hashekel-he': {
    ...ouisraelBasic,
    title: 'Zecher L`machatzit Hashekel',
    campaign: 'Zecher Lmachatzit Hashekel',
    subCampaign: '',
    emailTemplate: 'ouisrael-zecher-lmachatzit-hashekel-he'
  },
  'ouisrael-games': {
    title: 'המשחק שיסחוף אתכם ויכיר לכם תרבות מדהימה ומרתקת',
    campaign: 'General Donation',
    subCampaign: '',
    message: 'תודה על רכישתך',
    thankYouTemplate: 'ouisraelgames',
    amounts: [
    ],
    emailTemplate: 'ouisrael-games',
    department: 'OUIsrael',
    adminEmails: ['guzo@ouisrael.org','goldmani@ou.org','JoshC@ouisrael.org', 'joshc@ouisrael.org', 'lizb@ouisrael.org'],
    adminFromEmail: 'OUIsrael <ABerman@ouisrael.org>',
    adminSubject: 'OUIsrael Games notification',
    userFromEmail: 'OUIsrael <ABerman@ouisrael.org>',
    userSubject: 'איזה כיף שרכשת את גוזו',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-18329904-1' : 'UA-25738407-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          gameType: z.string().nullable().optional(),
          smallGameQnt: z.string().nullable().optional(),
          bigGameQnt: z.string().nullable().optional(),
          deliveryFee: z.string().nullable().optional(),
          chesbonitmas: z.string().nullable().optional(),
          houseNumber: z.string(),
          customHouseNumber: z.string().nullable().optional(),
          customCountry: z.string().nullable().optional(),
          customStreet: z.string().nullable().optional(),
          customCity: z.string().nullable().optional(),
          customZip: z.string().nullable().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    }
  },
  'sponsor-a-shiur': {
    ...ouisraelBasic,
    title: 'Inspire Others and Empower a Community - OU Israel Center',
    campaign: 'Israel Center Shiur Donation',
    subCampaign: '',
    amounts: [
      [{ USD: 72, ILS: 250, CAD: 100 }, `Sponsor a Shiur`],
      [{ USD: 220, ILS: 720, CAD: 305 }, `Dedicate a Morning of Learning`],
      [{ USD: 540, ILS: 1800, CAD: 700 }, `Sponsor a Shiur at a Community Torah Summit event`],
      [{ USD: 3000, ILS: 10000, CAD: 3810 }, `Sponsor a Shiur for the Year`],
    ],
    adminEmails: ['cspivack@ouisrael.org', 'sshor@ouisrael.org', 'JoshC@ouisrael.org', 'joshc@ouisrael.org', 'lizb@ouisrael.org'],
    emailTemplate: 'ouisrael'
  },
  'teen-centers': {
    ...ouisraelBasic,
    title: 'Teen Centers',
    campaign: 'Teen Centers',
    subCampaign: '',
    amounts: [
      [{ USD: 1000, ILS: 1000, CAD: 1000 }, `2 weeks of group activities & outings for 15 teens`],
      [{ USD: 1800, ILS: 1800, CAD: 1800 }, `Sponsor 1 at-risk teen to attend a therapeutic workshop series`],
      [{ USD: 5000, ILS: 5000, CAD: 5000 }, `Mentor 1 at-risk teen for 1 year`],
      [{ USD: 10000, ILS: 10000, CAD: 10000 }, `Support the Zula Outreach Center for 1 week (approx. 2,500 teens)`],
      [{ USD: 18000, ILS: 18000, CAD: 18000 }, `Train 20 advisors in assisting teens suffering from PTSD`],
      [{ USD: 36000, ILS: 36000, CAD: 36000 }, `Refurbish a war damaged Teen Center with furniture and equipment`],
      [{ USD: 54000, ILS: 54000, CAD: 54000 }, `Sponsor social worker for 1 year`],
      [{ USD: 100000, ILS: 100000, CAD: 100000 }, `Sponsor therapeutic and other programming for 180 teens`],
      [{ USD: 150000, ILS: 150000, CAD: 150000 }, `Annual sponsorship for a Teen Center for 1 year`],
      [{ USD: 350000, ILS: 350000, CAD: 350000 }, `Build a Teen Center in place of the one destroyed during the war`],
      [{ USD: 500000, ILS: 500000, CAD: 500000 }, `Sponsorship of the Teen Crisis Hotline, operating cost for 3 years`],
      [{ USD: 750000, ILS: 750000, CAD: 750000 }, `Build a Teen Center in place of the one destroyed during the war, including program & operating costs for 3 years`],
      [{ USD: 1000000, ILS: 1000000, CAD: 1000000 }, `To establish a new Teen Center in the hard hit south (Ofakim or Netivot)`],
    ],
    adminEmails: ['JoshC@ouisrael.org', 'joshc@ouisrael.org', 'lizb@ouisrael.org'],
    emailTemplate: 'ouisrael-teencenters'
  },
  'torah-tidbits': {
    ...ouisraelBasic,
    title: 'Uplift Others with Torah Tidbits',
    template: 'ouisrael-summer-camps',
    campaign: 'Torah Tidbits',
    subCampaign: '',
    amounts: [
      [{ USD: 30, ILS: 100, CAD: 39 }, `for a Torah Tidbits Appreciation`],
      [{ USD: 60, ILS: 200, CAD: 78 }, `to place an in memory / in honor / Simcha notice`],
      [{ USD: 101, ILS: 360, CAD: 140 }, `to sponsor an individual Torah Tidbits article`],
      [{ USD: 424, ILS: 1400, CAD: 545 }, `to sponsor a Torah Tidbits article for a month`],
      [{ USD: 1910, ILS: 6300, CAD: 2450 }, `to sponsor a whole Torah Tidbits edition`],
    ],
    emailTemplate: 'ouisrael'
  },
    'ouisrael-partnership': {
    ...ouisraelBasic,
    title: 'OU Israel - Partnership',
    template: 'ouisrael-summer-camps',
    campaign: 'Partnership/Donorship',
    subCampaign: '',
    amounts: [
      [{ USD: 75, ILS: 250, CAD: 100 }, `Single Partnership`],
      [{ USD: 100, ILS: 360, CAD: 135 }, `Couple Partnership`],
    ],
    adminEmails: ['aberman@ouisrael.org', 'jordanr@ouisrael.org', 'joshc@ouisrael.org', 'lizb@ouisrael.org'],
    emailTemplate: 'ouisrael'
  },

  'adopt-a-soldier': {
    ...ouisraelBasic,
    title: 'Adopt a Soldier',
    template: 'ouisrael',
    campaign: 'Mashiv HaRuach',
    subCampaign: '',
    amounts: [
      [{ USD: 54, ILS: 180, CAD: 70 }, `to provide a holiday care package to an IDF soldier`],
      [{ USD: 135, ILS: 445, CAD: 175 }, `to enable one group of five youth to attend a Weekend Seminar`],
      [{ USD: 200, ILS: 660, CAD: 256 }, `to provide mentoring & guided counseling for one youth in the Pre-Army Preparation program`],
      [{ USD: 1000, ILS: 3295, CAD: 1285 }, `to enable one group of five youth with Pre-Army preparation`],
      [{ USD: 3100, ILS: 10215, CAD: 3980 }, `to provide one young adolescent with post-army leadership & professional mentoring`],
    ],
    adminEmails: ['kziat@ouisrael.org', 'JoshC@ouisrael.org','akarov@ouisrael.org', 'joshc@ouisrael.org', 'lizb@ouisrael.org'],
    emailTemplate: 'ouisrael',
  },
  'campdror': {
    title: 'Support OU Israels Camp Dror',
    campaign: 'Camp Dror',
    subCampaign: '',
    department: 'OUIsrael',
    message: 'Thank you for donating to OUIsrael',
    adminFromEmail: 'OUIsrael <ABerman@ouisrael.org>',
    adminSubject: 'OUIsrael donation notification',
    userFromEmail: 'OUIsrael <ABerman@ouisrael.org>',
    userSubject: 'OUIsrael thanks you for your support',
    amounts: [
      [{ USD: 65, ILS: 180 }, `Daily Mishna Club.`],
      [{ USD: 130, ILS: 360 }, `Parnas Hayom.`],
      [{ USD: 360, ILS: 1000 }, `Shabbat Kiddush Sponsor.`],
      [{ USD: 360, ILS: 1000 }, `Camper Friday Night Tish Sponsor.`],
      [{ USD: 360, ILS: 1000 }, `Staff Friday Night Tish Sponsor.`],
      [{ USD: 640, ILS: 1800 }, `Staff Appreciation Banquet.`],
    ],
    adminEmails: ['campdror@ouisrael.org',  'goldmani@ou.org', 'JoshC@ouisrael.org', 'joshc@ouisrael.org', 'lizb@ouisrael.org', 'kpollack@ouisrael.org', 'joshc@ouisrael.org'],
    emailTemplate: 'ouisrael__campdror',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-18329904-1' : 'UA-25738407-2'
  },
  'campdror-test': {
    title: 'Support OU Israels Camp Dror',
    campaign: 'Camp Dror',
    subCampaign: '',
    department: 'OUIsrael',
    message: 'Thank you for donating to OUIsrael',
    adminFromEmail: 'OUIsrael <ABerman@ouisrael.org>',
    adminSubject: 'OUIsrael donation notification',
    userFromEmail: 'OUIsrael <ABerman@ouisrael.org>',
    userSubject: 'OUIsrael thanks you for your support',
    amounts: [
      [{ USD: 2445, ILS: 8850 }, `Provide a full scholarship, covering the entire summer experience for one camper.`],
      [{ USD: 860, ILS: 3100 }, `Give the gift of a full week of camp to one camper.`],
      [{ USD: 500, ILS: 1800 }, `Sponsor a day of camp-wide programming, such as a themed event, special guest, or Shabbat celebration, creating unforgettable memories for all campers.`],
      [{ USD: 360, ILS: 1325 }, `Dedicate one day of Parnas Hayom learning. Your name and dedication will be included in our special dedication book.`],
      [{ USD: 165, ILS: 590 }, `Sponsor a camper’s off-campus adventure day.`],
      [{ USD: 115, ILS: 445 }, `Sponsor a camper’s on-campus day of camp.`],
      [{ USD: 25, ILS: 90 }, `Sponsor one enriching activity for a camper.`],
    ],
    adminEmails: ['campdror@ouisrael.org',  'goldmani@ou.org', 'JoshC@ouisrael.org', 'joshc@ouisrael.org', 'lizb@ouisrael.org'],
    emailTemplate: 'ouisrael__campdror',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-18329904-1' : 'UA-25738407-2'
  },
  'campdror-heb': {
    title: 'Support OU Israels Camp Dror',
    campaign: 'Camp Dror',
    subCampaign: '',
    defaultCurrency: 'USD' as Currency,
    department: 'OUIsrael',
    message: 'Thank you for donating to OUIsrael',
    adminFromEmail: 'OUIsrael <ABerman@ouisrael.org>',
    adminSubject: 'OUIsrael donation notification',
    userFromEmail: 'OUIsrael <ABerman@ouisrael.org>',
    userSubject: 'OUIsrael thanks you for your support',
    amounts: [
      [{ USD: 65, ILS: 180 }, `מועדון משנה יומי`],
      [{ USD: 130, ILS: 360 }, `פרנס היום`],
      [{ USD: 360, ILS: 1000 }, `חסות לקידוש שבת`],
      [{ USD: 360, ILS: 1000 }, `חסות לטיש ליל שישי למחנאים`],
      [{ USD: 360, ILS: 1000 }, `חסות לטיש ליל שישי לצוות`],
      [{ USD: 640, ILS: 1800 }, `סעודת הוקרה לצוות`],
    ],
    adminEmails: ['campdror@ouisrael.org',  'goldmani@ou.org','JoshC@ouisrael.org', 'joshc@ouisrael.org', 'lizb@ouisrael.org', 'kpollack@ouisrael.org', 'joshc@ouisrael.org'],
    emailTemplate: 'ouisrael__campdror',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-18329904-1' : 'UA-25738407-2'
  },
  'ouisrael-tkh': {
    ...ouisraelBasic,
    title: 'Support the Toni Kohn House in Sderot Israel',
    emailTemplate: 'ouisrael-tkh',
    campaign: 'Toni Kohn House',
    subCampaign: '',
    defaultCurrency: 'CAD' as Currency,
    amounts: [
      [{ USD: 55, CAD: 72 }, `to provide one youth with a day of Jewish educational respite activities`],
      [{ USD: 155, CAD: 180 }, `to prepare one youth for their Bar / Bat Mitzvah`],
      [{ USD: 270, CAD: 360 }, `to provide one youth with an educational fieldtrip or identity building activity`],
      [{ USD: 540, CAD: 720 }, `to strengthen one Israeli youth for one year`],
      [{ USD: 5400, CAD: 7200 }, `to strengthen one group of 10 Israeli youth for one year`],
      [{ USD: 8000, CAD: 10000 }, `to prepare a group of youth for their Bar / Bat Mitzvah`],
      [{ USD: 15000, CAD: 18750 }, `to provide one group with an educational fieldtrip or identity building activity`],
      [{ USD: 50000, CAD: 62500 }, `to support a group of 50 youth at the Toni Kohn House in Sderot for one year`],
      [{ USD: 100000, CAD: 125000 }, `to support the entire operation of the Toni Kohn House in Sderot for one year`],
    ],
  },
  'ouisrael-summer': {
    ...ouisraelBasic,
    title: 'OU Israel Summer Campaign',
    emailTemplate: 'ouisrael-summer',
    campaign: 'Summer Campaign',
    subCampaign: '',
  },
  'ouisrael-emergency': {
    ...ouisraelBasic,
    title: 'OU Israel Emergency Campaign',
    emailTemplate: 'ouisrael-emergency',
    campaign: 'Relief Fund',
    subCampaign: '',
    amounts: {
      USD: [
        10000,
        5000,
        1800,
        1000,
        500,
        360,
        180,
        100
      ],
      ILS: [
        10000,
        5000,
        1800,
        1000,
        500,
        360,
        180,
        100
      ],
    }
  },
  'ouisrael-promise': {
    ...ouisraelBasic,
    emailTemplate: 'ouisrael-promise',
    title: 'OU Israel Promise Us Tomorrow Campaign',
    campaign: 'Promise Us Tomorrow',
    subCampaign: 'OU Web',
    defaultCurrency: 'USD',
    message: 'Thank you for your generous donation. A receipt is on the way to your inbox. With gratitude from OU Israel',
    adminEmails: ['JoshC@ouisrael.org','jordanr@ouisrael.org', 'joshc@ouisrael.org', 'lizb@ouisrael.org'],
    amounts: {
      USD: [
        5000,
        2500,
        1800,
        1000,
        500,
        180,
        54,
        36,
      ],
      ILS: [
        5000,
        2500,
        1800,
        1000,
        500,
        180,
        54,
        36,
      ],
      CAD: [
        5000,
        2500,
        1800,
        1000,
        500,
        180,
        54,
        36,
      ],
    }
  },
  'ouisrael-israel-strong': {
    ...ouisraelBasic,
    template: 'ouisrael-promise',
    emailTemplate: 'ouisrael-israel-strong',
    title: 'OU Israel Israel Strong Campaign',
    campaign: 'General Donation',
    subCampaign: 'Direct Mail - Fall',
    defaultCurrency: 'USD',
    message: 'Thank you for your generous donation. A receipt is on the way to your inbox. With gratitude from OU Israel',
    adminEmails: ['JoshC@ouisrael.org','jordanr@ouisrael.org', 'joshc@ouisrael.org', 'lizb@ouisrael.org'],
    amounts: {
      USD: [
        5000,
        2500,
        1800,
        1000,
        500,
        180,
        54,
        36,
      ],
      ILS: [
        5000,
        2500,
        1800,
        1000,
        500,
        180,
        54,
        36,
      ],
      CAD: [
        5000,
        2500,
        1800,
        1000,
        500,
        180,
        54,
        36,
      ],
    }
  },
  'ouisrael-israelstrong': {
    ...ouisraelBasic,
    template: 'ouisrael',
    emailTemplate: 'ouisrael-israelstrong',
    title: 'OU Israel Israel Strong Campaign',
    campaign: 'General Donation',
    subCampaign: 'Direct Mail - Fall',
    defaultCurrency: 'USD',
    message: 'Thank you for your generous donation. A receipt is on the way to your inbox. With gratitude from OU Israel',
    adminEmails: ['JoshC@ouisrael.org','jordanr@ouisrael.org', 'joshc@ouisrael.org', 'lizb@ouisrael.org'],
    amounts: [
      [{ USD: 1800, ILS: 5950, CAD: 2510 }, `Sends 4 Israelis teen to healing retreats and programs`],
      [{ USD: 1000, ILS: 3310, CAD: 1395 }, `Provides comprehensive programs for Anglo-Olim families building new lives in Israel`],
      [{ USD: 500, ILS: 1655, CAD: 700 }, `Supports a counselor for a month, serving 30 teens navigating trauma and uncertainty`],
      [{ USD: 360, ILS: 1190, CAD: 500 }, `Powers an Israeli teen to become emergency responders instead of victims`],
      [{ USD: 180, ILS: 595, CAD: 250 }, `Funds the crisis hotline support reaching hundreds of teens each week`],
    ],
  },
  'ouisrael-saveisraelsteens': {
    ...ouisraelBasic,
    template: 'ouisrael-promise',
    emailTemplate: 'ouisrael-saveisraelsteens',
    title: 'OU Israel Israeli At-risk Teens Campaign',
    campaign: 'Save Israeli Teens',
    subCampaign: '10000 List',
    defaultCurrency: 'USD',
    message: 'Thank you for your generous donation. A receipt is on the way to your inbox. With gratitude from OU Israel',
    adminEmails: ['JoshC@ouisrael.org', 'joshc@ouisrael.org', 'lizb@ouisrael.org'],
    amounts: {
      USD: [
        5000,
        2500,
        1800,
        1000,
        500,
        180,
        54,
        36,
      ],
      ILS: [
        5000,
        2500,
        1800,
        1000,
        500,
        180,
        54,
        36,
      ],
      CAD: [
        5000,
        2500,
        1800,
        1000,
        500,
        180,
        54,
        36,
      ],
    }
  },
  'ouisrael-promise-mail': {
    ...ouisraelBasic,
    template: 'ouisrael-promise',
    emailTemplate: 'ouisrael-promise',
    title: 'OU Israel Promise Us Tomorrow Campaign',
    campaign: 'Promise Us Tomorrow',
    subCampaign: 'Direct Mail',
    defaultCurrency: 'USD',
    message: 'Thank you for your generous donation. A receipt is on the way to your inbox. With gratitude from OU Israel',
    adminEmails: ['JoshC@ouisrael.org','jordanr@ouisrael.org', 'joshc@ouisrael.org', 'lizb@ouisrael.org'],
    amounts: {
      USD: [
        5000,
        2500,
        1800,
        1000,
        500,
        180,
        54,
        36,
      ],
      ILS: [
        5000,
        2500,
        1800,
        1000,
        500,
        180,
        54,
        36,
      ],
      CAD: [
        5000,
        2500,
        1800,
        1000,
        500,
        180,
        54,
        36,
      ],
    }
  },
  'ouisrael-teenlifeline': {
    ...ouisraelBasic,
    title: 'OU Israel Teen Lifeline Campaign',
    emailTemplate: 'ouisrael-teenlifeline',
    campaign: 'Relief Fund',
    subCampaign: '',
    amounts: {
      USD: [
        36,
        118,
        180,
        540,
        1000,
        1800,
        5000
      ],
      ILS: [
        36,
        118,
        180,
        540,
        1000,
        1800,
        5000
      ],
      CAD: [
        36,
        118,
        180,
        540,
        1000,
        1800,
        5000
      ]
    }
  },
  'ouisrael-womensgivingcircle': {
    ...ouisraelBasic,
    template: 'ouisrael-womensgivingcircle',
    title: 'OU Israel Teen Lifeline Campaign',
    emailTemplate: 'ouisrael-womensgivingcircle',
    message: 'Thank you for donating, your support is greatly appreciated.',
    campaign: "OU Israel Women's Division (Layla)",
    defaultCurrency: 'ILS',
    subCampaign: '',
    adminEmails: ['zgarfinkel@ouisrael.org','zemiraoz@ouisrael.org', 'joshc@ouisrael.org', 'lizb@ouisrael.org'],
    amounts: [
    [{ USD: 85, ILS: 250, CAD: 115 }, `Individual Shiur`],
      [{ USD: 250, ILS: 750, CAD: 350 }, `Mother daughter event`],
      [{ USD: 340, ILS: 1000, CAD: 470 }, `ATID or NextGen Event`],
      [{ USD: 500, ILS: 1450, CAD: 680 }, `Rosh Chodesh Musical Hallel`],
      [{ USD: 620, ILS: 1800, CAD: 845 }, `Nach Yomi seminar`],
      [{ USD: 3400, ILS: 10000, CAD: 4700 }, `Sponsor a Shiur for the Year`],
    ],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          selectedDescription: z.string().nullable().optional(),
          nameOfChild: z.string().nullable().optional(),
          birthdate: z.string().nullable().optional(),
          childAge: z.string().nullable().optional(),
          shiurDate: z.string().nullable().optional(),
          eventDate: z.string().nullable().optional(),
          eventName: z.string().nullable().optional(),
          eventLocation: z.string().nullable().optional(),
          eventType: z.string().nullable().optional(),
          seriesName: z.string().nullable().optional(),
          shiurORTeacher: z.string().nullable().optional(),
        })
      })
    }
  },
  'ouisrael-tubshvat': {
    ...ouisraelBasic,
    title: 'First Annual Erev Tu BShvat Lunchon',
    emailTemplate: 'ouisrael_tubshvat',
    campaign: 'Zula Anniversary Event',
    subCampaign: '',
    amounts: [
      [{ USD: 510, ILS: 1800, CAD: 690 }, `SUPPORTER - Sponsor 1 teen; 2 tickets`],
      [{ USD: 1025, ILS: 3600, CAD: 1380 }, `SILVER - Sponsor 2 teens; 4 tickets`],
      [{ USD: 1420, ILS: 5000, CAD: 1915 }, `GOLD - Sponsor 3 teens; 6 tickets`],
      [{ USD: 2840, ILS: 10000, CAD: 3830 }, `PLATINUM - Sponsor 6 teens; Table (8 seats)`],
      [{ USD: 5115, ILS: 18000, CAD: 6900 }, `DIAMOND - Sponsor 10 teens; Table (8 seats)`],
    ],
    amountsAlt: [
      [{ USD: 54, ILS: 180, CAD: 72 }, `to keep one youth at risk safe off the streets for one week`],
      [{ USD: 360, ILS: 1190, CAD: 460 }, `to heal one youth at risk with holistic counseling for one year`],
      [{ USD: 540, ILS: 1800, CAD: 700 }, `to sponsor a support group for youth at risk`],
      [{ USD: 1200, ILS: 3950, CAD: 1520 }, `to sponsor a musical performance for the youth`],
      [{ USD: 3250, ILS: 10835, CAD: 4170 }, `to enable one Zula alumna to participate in the Zula's Beit Midrash program for one year`],
    ],
    adminEmails: ['JoshC@ouisrael.org','shaynab@ouisrael.org','joshc@ouisrael.org','jordanr@ouisrael.org','aberman@ouisrael.org','lizb@ouisrael.org'],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          noAttend: zBool().nullable().optional(),
          transportation: zBool().nullable().optional(),

        })
      })
    }
  },
  'ouisrael-tubshvat-he': {
    ...ouisraelBasic,
    title: 'First Annual Erev Tu BShvat Lunchon',
    emailTemplate: 'ouisrael_tubshvat-he',
    campaign: 'Zula Anniversary Event',
    subCampaign: '',
    amounts: [
      [1800, `(תומך) – תמיכה בבן/בת נוער :2 כרטיסים`],
      [3600, `(כסף) – תמיכה ב 2 בני נוער: 4 כרטיסים`],
      [5000, `(זהב) – תמיכה ב 3 בני נוער: 6 כרטיסים`],
      [10000, `(פלטינה) – תמיכה ב 6 בני נוער: שולחן (8 מקומות)`],
      [18000, `(יהלום) – תמיכה ב 10 בני נוער: שלחן (8 מקומות)`],
    ],
    adminEmails: ['JoshC@ouisrael.org','shaynab@ouisrael.org','joshc@ouisrael.org','jordanr@ouisrael.org','aberman@ouisrael.org','lizb@ouisrael.org'],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          noAttend: zBool().nullable().optional(),
          transportation: zBool().nullable().optional(),

        })
      })
    }
  },
  'ouisrael-tubshvat-raffle': {
    ...ouisraelBasic,
    title: 'First Annual Erev Tu BShvat Raffle',
    emailTemplate: 'ouisrael_tubshvat-raffle',
    message: 'Thank you for participating in our raffle to save the lives of Zula youth',
    campaign: 'Zula Anniversary Event Raffle',
    subCampaign: '',
    amounts: [
      [100, `1 Ticket`],
      [180, `2 Tickets`],
      [250, `3 Tickets`],
      [300, `4 Tickets`],
      [360, `5 Tickets`],
      [500, `10 Tickets`]
    ],
    adminEmails: ['JoshC@ouisrael.org','shaynab@ouisrael.org','joshc@ouisrael.org','jordanr@ouisrael.org','aberman@ouisrael.org','lizb@ouisrael.org'],
  },
  'adopt-a-youth': {
    ...ouisraelBasic,
    template: 'ouisrael-summer-camps',
    title: 'OUIsrael Adopt a Youth',
    campaign: 'Makom Balev',
    subCampaign: '',
    amounts: [
      [{ USD: 54, ILS: 180, CAD: 70 }, `to provide one youth with one enrichment activity`],
      [{ USD: 101, ILS: 360, CAD: 140 }, `to prepare one youth for his coming-of-age Bar / Bat Mitzvah celebration`],
      [{ USD: 190, ILS: 630, CAD: 240 }, `to prepare youth for their upcoming national or army service`],
      [{ USD: 360, ILS: 1190, CAD: 460 }, `to strengthen one of Yerushalayim's youth at risk with counseling for one year`],
      [{ USD: 540, ILS: 1800, CAD: 690 }, `to support one of Israel's peripheral youth for one year with mentoring`],
      [{ USD: 1200, ILS: 3950, CAD: 1540 }, `to enable one youth to attend a therapeutic or empowerment program`],
    ],
    adminEmails: ['kziat@ouisrael.org', 'JoshC@ouisrael.org','shaynab@ouisrael.org','joshc@ouisrael.org','jordanr@ouisrael.org','lizb@ouisrael.org'],
    emailTemplate: 'ouisrael'
  },
  'oraita': {
    ...ouisraelBasic,
    template: 'ouisrael',
    title: "Strengthen Israel's Local Communities through OU Israel Youth Centers",
    department: 'OUIsrael',
    campaign: 'Oraita',
    subCampaign: '',
    amounts: [
      [{ USD: 54, ILS: 180, CAD: 70 }, `to enable one youth counselor to impact a local community for one day`],
      [{ USD: 200, ILS: 670, CAD: 256 }, `to enable a Youth Center to empower a group of youth for one day`],
      [{ USD: 300, ILS: 990, CAD: 385 }, `to plan and implement a community empowerment initiative`],
      [{ USD: 540, ILS: 1800, CAD: 690 }, `to instill one of Israel's peripheral youth with leadership & community activist training`],
      [{ USD: 1050, ILS: 3460, CAD: 1350 }, `to enrich one group of youth with an educational fieldtrip or identity building activity`],
    ],
    adminEmails: ['kziat@ouisrael.org', 'JoshC@ouisrael.org','shaynab@ouisrael.org','joshc@ouisrael.org','jordanr@ouisrael.org','lizb@ouisrael.org'],
    emailTemplate: 'ouisrael-teens'
  },
  'ouisrael-summer-camps': {
    ...ouisraelBasic,
    title: 'Gift a Meaningful Summer',
    campaign: 'Camp Dror',
    subCampaign: '',
    amounts: [
      [{ USD: 54, ILS: 180, CAD: 70 }, `to fund one activity for a child in a summer camp in Israel`],
      [{ USD: 101, ILS: 360, CAD: 140 }, `to sponsor one day of פרנס היום learning in the memory or honor of a loved one`],
      [{ USD: 150, ILS: 500, CAD: 195 }, `to provide one youth with a Shabbat experience at Camp Dror`],
      [{ USD: 760, ILS: 2500, CAD: 970 }, `to provide one youth with a week of empowering summer activities`],
      [{ USD: 1820, ILS: 6000, CAD: 2335 }, `to provide one youth with a full scholarship`],
    ],
    emailTemplate: 'ouisrael__summer'
  },
  'zula': {
    ...ouisraelBasic,
    title: "Save Israel's Youth at Risk",
    template: 'ouisrael-summer-camps',
    campaign: 'Zula',
    subCampaign: '',
    amounts: [
      [{ USD: 54, ILS: 180, CAD: 72 }, `to keep one youth at risk safe off the streets for one week`],
      [{ USD: 360, ILS: 1190, CAD: 460 }, `to heal one youth at risk with holistic counseling for one year`],
      [{ USD: 540, ILS: 1800, CAD: 700 }, `to sponsor a support group for youth at risk`],
      [{ USD: 1200, ILS: 3950, CAD: 1520 }, `to sponsor a musical performance for the youth`],
      [{ USD: 3250, ILS: 10835, CAD: 4170 }, `to enable one Zula alumna to participate in the Zula's Beit Midrash program for one year`],
    ],
    adminEmails: ['JoshC@ouisrael.org','shaynab@ouisrael.org','joshc@ouisrael.org','jordanr@ouisrael.org','aberman@ouisrael.org','lizb@ouisrael.org'],
    emailTemplate: 'ouisrael'
  },
  'zula-multicampaigns': {
    ...ouisraelBasic,
    title: "Save Israel's Youth at Risk",
    campaign: 'Zula',
    subCampaign: '',
    amounts: [
      [{ USD: 54, ILS: 180, CAD: 72 }, `to keep one youth at risk safe off the streets for one week`],
      [{ USD: 360, ILS: 1190, CAD: 460 }, `to heal one youth at risk with holistic counseling for one year`],
      [{ USD: 540, ILS: 1800, CAD: 700 }, `to sponsor a support group for youth at risk`],
      [{ USD: 1200, ILS: 3950, CAD: 1520 }, `to sponsor a musical performance for the youth`],
      [{ USD: 3250, ILS: 10835, CAD: 4170 }, `to enable one Zula alumna to participate in the Zula's Beit Midrash program for one year`],
    ],
    adminEmails: ['JoshC@ouisrael.org','shaynab@ouisrael.org','joshc@ouisrael.org','jordanr@ouisrael.org','aberman@ouisrael.org','lizb@ouisrael.org'],
    emailTemplate: 'ouisrael'
  },
  'ouisrael-tidbits': {
    ...ouisraelBasic,
    template: 'ouisrael-tidbits',
    emailTemplate: 'ouisrael-tidbits',
    title: "Support OU Israel Torah Tidbits",
    campaign: 'Torah Tidbits',
    subCampaign: '',
    amounts: {
      USD: [
        18,
        36,
        100,
        250,
        500
      ],
      ILS: [
        18,
        36,
        100,
        250,
        500
      ]
    },
    adminEmails: ['office@ouisrael.org', 'joshc@ouisrael.org', 'lizb@ouisrael.org'],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          designation: z.string().nullable().optional(),
        })
      })
    }
  },
  'ouisrael-camp-scholarships': {
    ...ouisraelBasic,
    emailTemplate: 'ouisrael-camp-scholarships',
    title: 'OU Israel Promise Us Tomorrow Campaign',
    campaign: 'Legacy Camp',
    subCampaign: '',
    defaultCurrency: 'USD',
    message: 'Thank you for your generous donation. A receipt is on the way to your inbox. With gratitude from OU Israel',
    adminEmails: ['JoshC@ouisrael.org','shaynab@ouisrael.org','joshc@ouisrael.org','jordanr@ouisrael.org','aberman@ouisrael.org','lizb@ouisrael.org'],
    amounts: {
      USD: [
        5000,
        2500,
        1800,
        1000,
        500,
        180,
        54,
        36,
      ],
      ILS: [
        5000,
        2500,
        1800,
        1000,
        500,
        180,
        54,
        36,
      ],
      CAD: [
        5000,
        2500,
        1800,
        1000,
        500,
        180,
        54,
        36,
      ],
    }
  },
  'ouisrael-teens-in-crisis': {
    ...ouisraelBasic,
    emailTemplate: 'ouisrael-teens-in-crisis',
    title: 'OU Israel Promise Us Tomorrow Campaign',
    campaign: 'Teen Emergency Campaign',
    subCampaign: '',
    defaultCurrency: 'USD',
    message: 'Thank you for your generous donation. A receipt is on the way to your inbox. With gratitude from OU Israel',
    adminEmails: ['JoshC@ouisrael.org','shaynab@ouisrael.org','joshc@ouisrael.org','jordanr@ouisrael.org','aberman@ouisrael.org','lizb@ouisrael.org'],
    amounts: {
      USD: [
        5000,
        3600,
        1800,
        1000,
        500,
        360,
        180,
        100,
      ],
      ILS: [
        5000,
        3600,
        1800,
        1000,
        500,
        360,
        180,
        100,
      ],
      CAD: [
        5000,
        3600,
        1800,
        1000,
        500,
        360,
        180,
        100,
      ],
    }
  },
  'ouisrael-resilient-teensrh5786': {
    ...ouisraelBasic,
    template: 'ouisrael-teens-in-crisis',
    title: 'Teen Centers',
    campaign: 'Resilient Teens 5786',
    emailTemplate: 'ouisrael-teens',
    subCampaign: '',
    userSubject: 'Your Partnership Illuminates Their Path - Thank You for Supporting Israel`s Teens',
    defaultCurrency: 'USD',
    message: 'Thank you for your generous donation. A receipt is on the way to your inbox. With gratitude from OU Israel',
    adminEmails: ['JoshC@ouisrael.org','shaynab@ouisrael.org','joshc@ouisrael.org','jordanr@ouisrael.org','aberman@ouisrael.org','lizb@ouisrael.org'],
    amounts: {
      USD: [
        5000,
        3600,
        1800,
        1000,
        500,
        360,
        180,
        100,
      ],
      ILS: [
        5000,
        3600,
        1800,
        1000,
        500,
        360,
        180,
        100,
      ],
      CAD: [
        5000,
        3600,
        1800,
        1000,
        500,
        360,
        180,
        100,
      ],
    }
  },
  'outorah-amudei': {
    emailTemplate: 'outorah-amudei',
    title: 'Amudei All Torah Donation Form',
    department: 'OU_Torah',
    campaign: 'Amudei Torah',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-29' : '',
    subCampaign: '',
    message: 'Thank you for your support!',
    adminEmails: ['jacka@ou.org','schwedm@ou.org','goldmani@ou.org','torah@ou.org','ashmidman@ou.org','miriamw@ou.org','Markovitchn@ou.org','hirschr@ou.org'],
    adminFromEmail: 'Amudei All Torah <torah@ou.org>',
    silverPopContactLists: [7645130],
    adminSubject: 'A payment has been made to Amudei All Torah Donation Form',
    userFromEmail: 'Amudei All Torah <torah@ou.org>',
    userSubject: 'Thank you for your support!',
    amounts: [
      [3600, `Supporter`],
      [5400, `Partner`],
      [10000, `Benefactor`],
      [18000, `Pillar`],
      [25000, `Ambassador`],
      [36000, `Legacy`],
    ],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          specialInstructions: z.string().nullable().optional(),
        })
      })
    },
  },
  'outorah-alldaf': {
    emailTemplate: 'outorah-alldaf',
    title: 'OUTorah All Daf Dedication',
    adminEmails: ['schwedm@ou.org',  'hirschr@ou.org'],
    adminFromEmail: 'OU Torah <info@ou.org>',
    adminSubject: 'All Daf General Donation',
    userFromEmail: 'OU Torah <info@ou.org>',
    userSubject: 'Thank you for Supporting All Daf!',
    amounts: [],
    department: 'OU_Torah',
    campaign: 'All Daf',
    subCampaign: 'General Donation',
    defaultCurrency: 'ILS',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-29' : ''
  },
  'outorah-chanukah': {
    emailTemplate: 'outorah-chanukah',
    message: 'Thank you so much for your recent contribution',
    thankYouTemplate: 'outroahchanukah',
    title: 'OU Torah Campaign Donation',
    adminEmails: (request) => {
      const to = 'jacka@ou.org,schwedm@ou.org,goldmani@ou.org,torah@ou.org,miriamw@ou.org,Markovitchn@ou.org,hirschr@ou.org'.split(',')
      const mapping = {
        'Daf Yomi with Rabbi Rosner': 'rosnerts@gmail.com',
        'Rabbi Shalom Rosner on The Parsha': 'rosnerts@gmail.com',
        'Torat Imecha Nach Yomi': 'ashmidman@ou.org,epsteinn@ou.org',
        'Torat Imecha Parsha': 'ashmidman@ou.org',
      } as any
      const email = mapping[request.applications![0].subCampaign!]
      if (email) to.push(email)
      if(request.custom?.donationEmail ! == ''){
        to.push(request.custom?.donationEmail)
      }
      return to
    },
    adminFromEmail: 'OU Torah <torah@ou.org>',
    adminSubject: 'OU Torah Campaign Donation',
    userFromEmail: 'OU Torah <torah@ou.org>',
    userSubject: 'Thank you for Supporting OU Torah!',
    amounts: [
      36,
      54,
      72,
      180,
      250
    ],
    department: 'OU_Torah',
    campaign: 'ADM OU Torah Campaign',
    subCampaign: 'Direct Mail',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-29' : '',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          amudei: zBool().nullable().optional(),
          seriesType: z.string().nullable().optional(),
          programId: z.string().nullable().optional(),
          dafType: z.string().nullable().optional(),
          startDate: z.string().nullable().optional(),
          dateOption: z.string().nullable().optional(),
          endDate: z.string().nullable().optional(),
          sponsorshipType: z.string().nullable().optional(),
          dedicationMessage: z.string().nullable().optional(),
          comments: z.string().nullable().optional(),
          donationEmail: z.string().nullable().optional(),
        })
      })
    }
  },
  'outorah-general': {
    emailTemplate: 'outorah-chanukah',
    title: 'OU Torah General Donation',
    adminEmails: (request) => {
      const to = 'jacka@ou.org,schwedm@ou.org,goldmani@ou.org,torah@ou.org,miriamw@ou.org,Markovitchn@ou.org,hirschr@ou.org'.split(',')
      const mapping = {
        'Daf Yomi with Rabbi Rosner': 'rosnerts@gmail.com',
        'Rabbi Shalom Rosner on The Parsha': 'rosnerts@gmail.com',
        'Torat Imecha Nach Yomi': 'ashmidman@ou.org,epsteinn@ou.org',
        'Torat Imecha Parsha': 'ashmidman@ou.org',
        'Nach Yomi Daily Email': 'jacka@ou.org,ashmidman@ou.org',
        'HaShoneh Halachos Daily Email': 'jacka@ou.org',
        'Mishna Yomit Daily Email': 'jacka@ou.org',
        'Shnayim Mikra Daily Email': 'jacka@ou.org',
      } as any
      const email = mapping[request.applications![0].subCampaign!]
      if (email) to.push(email)
      if(request.custom?.donationEmail ! == ''){
        to.push(request.custom?.donationEmail)
      }
      return to
    },
    adminFromEmail: 'OU Torah <torah@ou.org>',
    message: 'Thank you so much for your recent contribution',
    thankYouTemplate: 'outroahchanukah',
    adminSubject: 'OU Torah General Donation',
    userFromEmail: 'OU Torah <torah@ou.org>',
    userSubject: 'Thank you for Supporting OU Torah!',
    amounts: [18, 36, 54, 72, 100, 180, 250, 360],
    department: 'OU_Torah',
    campaign: 'General Donation',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-29' : '',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          amudei: zBool().optional(),
          seriesType: z.string().nullable().optional(),
          programId: z.string().nullable().optional(),
          dafType: z.string().nullable().optional(),
          startDate: z.string().nullable().optional(),
          dateOption: z.string().nullable().optional(),
          endDate: z.string().nullable().optional(),
          sponsorshipType: z.string().nullable().optional(),
          dedicationMessage: z.string().nullable().optional(),
          comments: z.string().nullable().optional(),
          donationEmail: z.string().nullable().optional(),
          dailyEmail: z.string().nullable().optional(),
          contributionType: z.enum(['sponsorship', 'custom']).nullable().optional(),
        })
      })
    }
  },
  // outorah.org/give
  'outorah-give': {
    ...outorahGiveBasic,
    amounts: [18, 54, 100, 360, 500],
  },
  // outorah.org/givetorah
  'outorah-givetorah': {
    ...outorahGiveBasic,
    amounts: [180, 250, 360, 500, 1000],
  },
  'alltorah-general': {
    emailTemplate: 'alltorah-general',
    title: 'All Torah General Donation',
    adminEmails: [ 'tessera@ou.org', 'schwedm@ou.org', 'goldmani@ou.org', 'hirschr@ou.org'],
    adminFromEmail: 'All Torah <torah@ou.org>',
    message: 'Thank you so much for your recent contribution',
    adminSubject: 'All Torah General Donation',
    userFromEmail: 'All Torah <torah@ou.org>',
    userSubject: 'Thank you for Supporting All Torah!',
    amounts: [
      18,
      36,
      54,
      180,
      360
    ],
    department: 'OU_Torah',
    campaign: 'All Torah',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-29' : '',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          amudei: zBool().optional(),
          seriesType: z.string().nullable().optional(),
          programId: z.string().nullable().optional(),
          dafType: z.string().nullable().optional(),
          startDate: z.string().nullable().optional(),
          dateOption: z.string().nullable().optional(),
          endDate: z.string().nullable().optional(),
          sponsorshipType: z.string().nullable().optional(),
          dedicationMessage: z.string().nullable().optional(),
          comments: z.string().nullable().optional(),
          anonymous: zBool().nullable().optional(),
        })
      })
    }
  },
  'alltorah-general25': {
    emailTemplate: 'alltorah-general',
    title: 'All Torah General Donation',
    adminEmails: [ 'schwedm@ou.org', 'goldmani@ou.org', 'hirschr@ou.org'],
    adminFromEmail: 'All Torah <torah@ou.org>',
    message: 'Thank you so much for your recent contribution',
    adminSubject: 'All Torah General Donation (2025)',
    userFromEmail: 'All Torah <torah@ou.org>',
    userSubject: 'Thank you for Supporting All Torah!',
    department: 'OU_Torah',
    campaign: 'All Torah',
    subCampaign: 'Direct Mail',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-29' : '',
    amounts: [
      [1800, `Supporter`],
      [3600, `Partner`],
      [5400, `Benefactor`],
      [10000, `Pillar`],
      [18000, `Ambassador`],
    ],
    silverPopContactLists: [7645130],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          specialInstructions: z.string().nullable().optional(),
        })
      })
    },
  },
  'alltorah-moedmarathan': {
    emailTemplate: 'alltorah-moedmarathan',
    title: 'AMJ Moed Marathon',
    adminEmails: ['goldmani@ou.org'],
    adminFromEmail: 'AMJ Moed Marathon <info@allmishnah.org>',
    message: 'Thank you for signing up!<br><br> Please stay tuned for more information regarding transportation and trip details. In the meantime, please spread the word to your AMJ friends. Send them this link - <a href="https://moedmarathon.org/#register">https://moedmarathon.org/#register</a>',
    adminSubject: 'AMJ Moed Marathon',
    userFromEmail: 'AMJ Moed Marathon <info@allmishnah.org>',
    userSubject: 'Thank you for registering for the Inaugural AMJ Moed Marathon!',
    amounts: [
    ],
    department: 'OU_Torah',
    campaign: 'AllMishna Moed Marathon',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-32047-29' : '',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          childsFirstName: z.string(),
          childsLastName: z.string(),
          isGender: z.string().nullable().optional(),
          parentsAgeRelase: z.string().nullable().optional(),
          schoolName: z.string(),
          birthDate: z.string(),
          signature: z.string()
        })
      })
    }
  },
  'ouwomen-siyum-ny26': {
    ...womensSiyumBase,
    template: 'ouwomen-siyum-ny',
    emailTemplate: 'ouwomen-siyum-ny',
    subCampaign: 'Siyum NY',
    message: "Thank you for your support of Torat Imecha Nach Yomi. If you will be attending the event, you will receive an email with event details by February 10th.  If you do not receive an email, please check your spam folder.  If it is not there, please reach out to us at <a href='mailto:ouwomen@ou.org'>ouwomen@ou.org</a>.",
    adminSubject: 'OU Women Siyum Donation - NY event',
    userSubject: 'OU Women Siyum Donation - NY event',
    amounts: [
      [10000, `10 siyum reservations, platinum page in the global virtual journal, dedicate a sefer of Nach Yomi`],
      [5000, `6 siyum reservations, diamond page in the global virtual journal, dedicate a week of Nach Yomi`],
      [3600, `4 siyum reservations, gold page in the global virtual journal, dedicate 4 perakim of Nach Yomi`],
      [2500, `4 siyum reservations, silver page in the global virtual journal, dedicate 3 perakim of Nach Yomi`],
      [1800, `2 siyum reservations, bronze page in the global virtual journal, dedicate 2 perakim of Nach Yomi`],
      [1000, `2 siyum reservations, full page in the global virtual journal, dedicate one perek of Nach Yomi`],
      [500, `1 siyum reservation, 1/2 page in the global virtual journal, dedicate one perek of Nach Yomi`],
      [360, `1 siyum reservation, 1/4 page in the global virtual journal`],
      [250, `1 siyum reservation, 1/8 page in the global virtual journal`],
      [100, `1 siyum reservation, listing in the global virtual journal`],
      [36, `Student Ticket `],
    ]
  },
  'ouwomen-siyum-il26': {
    ...womensSiyumBase,
    template: 'ouwomen-siyum-il',
    silverPopContactLists: [8957751],
    emailTemplate: 'ouwomen-siyum-il',
    subCampaign: 'Siyum Israel',
    message: "Thank you for your support of Torat Imecha Nach Yomi. If you will be attending the event, you will receive an email with event details by February 10th. If you do not receive the email, please check your spam folder.  If it is not there, please reach out to us at <a href='mailto:ouwomen@ou.org'>ouwomen@ou.org</a>.",
    defaultCurrency: 'ILS' as Currency,
    adminSubject: 'OU Women Siyum Donation - Israel event',
    userSubject: 'OU Women Siyum Donation - Israel event',
    amounts: [
      [{ ILS: 10000, USD: 3152 }, `10 siyum reservations, platinum page in the global virtual journal, dedicate 4 perakim of Nach Yomi`],
      [{ ILS: 5000, USD: 1570 }, `6 siyum reservations, diamond page in the global virtual journal, dedicate 3 perakim of Nach Yomi`],
      [{ ILS: 3600, USD: 1130 }, `4 siyum reservations, gold page in the global virtual journal, dedicate 2 perakim of Nach Yomi`],
      [{ ILS: 2500, USD: 785 }, `4 siyum reservations, silver page in the global virtual journal, dedicate 1 perek of Nach Yomi`],
      [{ ILS: 1800, USD: 565 }, `2 siyum reservations, bronze page in the global virtual journal, dedicate 1 perek of Nach Yomi`],
      [{ ILS: 1000, USD: 314 }, `2 siyum reservations, full page in the global virtual journal`],
      [{ ILS: 500, USD: 157 }, `1 siyum reservation, 1/2 page in the global virtual journal`],
      [{ ILS: 360, USD: 113 }, `1 siyum reservation, 1/4 page in the global virtual journal`],
      [{ ILS: 250, USD: 80 }, `1 siyum reservation, 1/8 page in the global virtual journal`],
      [{ ILS: 100, USD: 32 }, `1 siyum reservation, listing in the global virtual journal`],
      [{ ILS: 36, USD: 11 }, `Student Ticket`],
    ]
  },
  'ouwomen-photo-tour-2023': {
    campaign: 'Torat Imecha',
    department: 'WomensInitative',
    silverPopContactLists: [2754371],
    title: 'Torat Imecha Siyum Neviim Street Photography Tour 2023',
    adminEmails: ['ashmidman@ou.org'],
    adminFromEmail: 'OU Women <info@ou.org>',
    userFromEmail: 'OU Women <ashmidman@ou.org>',
    emailTemplate: 'ouwomen-tour-2023',
    message: "We have received your tour submission.  You should receive an email confirmation with meeting times and places.  If you do not receive this email, please check your spam folder.",
    defaultCurrency: 'ILS' as Currency,
    adminSubject: 'Torat Imecha Siyum Neviim Street Photography Tour 2023',
    userSubject: 'Torat Imecha Siyum Neviim Street Photography Tour 2023',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          mediaRelease: zBool().refine((v) => v === true, { message: "Required" }),
          AttendInPerson: zBool().refine((v) => v === true, { message: "Required" }),
        })
      })
    }
  },
  'ouwomen-tagrin-tour-2023': {
    campaign: 'Torat Imecha',
    department: 'WomensInitative',
    silverPopContactLists: [2754371],
    title: 'Torat Imecha Siyum Neviim Shani Taragin tour 2023',
    adminEmails: ['ashmidman@ou.org'],
    adminFromEmail: 'OU Women <info@ou.org>',
    userFromEmail: 'OU Women <ashmidman@ou.org>',
    emailTemplate: 'ouwomen-tour-2023',
    message: "We have received your tour submission.  You should receive an email confirmation with meeting times and places.  If you do not receive this email, please check your spam folder.",
    defaultCurrency: 'ILS' as Currency,
    adminSubject: 'Torat Imecha Siyum Neviim Shani Taragin tour 2023',
    userSubject: 'Torat Imecha Siyum Neviim Shani Taragin tour 2023',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          mediaRelease: zBool().refine((v) => v === true, { message: "Required" }),
          AttendInPerson: zBool().refine((v) => v === true, { message: "Required" }),
        })
      })
    }
  },
  'ouwomen-donate': {
    emailTemplate: 'ouwomen-donate',
    campaign: 'General Donation',
    department: 'WomensInitative',
    title: 'OUWomen Donation',
    adminEmails: ['ashmidman@ou.org', 'epsteinn@ou.org'],
    adminFromEmail: 'OU Women <ouwomen@ou.org>',
    userFromEmail: 'OU Women <ouwomen@ou.org>',
    message: "Thank you for joining the Supporters of the OU Women’s Initiative.  Your gift will help us  support Orthodox communities worldwide in identifying and addressing women’s spiritual, educational, and communal needs through a range of innovative initiatives.",
    adminSubject: 'New OU Women Donation was submitted',
    honoreeTemplate: 'ouwomen',
    userSubject: "Thank you for your donation to the OU Women's Initiative",
    amounts: [
      [{ USD: 25000, ILS: 84000, CAD: 34300  }, `Aseret Yemei Teshuva Essay Compilation`],
      [{ USD: 15000, ILS: 50400, CAD: 20580  }, `Torat Imecha Parsha - One Year Sponsorship`],
      [{ USD: 15000, ILS: 50400, CAD: 20580  }, `Torat Imecha Haftorah - One Year sponsorship`],
      [{ USD: 10000, ILS: 33600, CAD: 13720  }, `Pesach Hagaddah Essay Compilation`],
      [{ USD: 7200, ILS: 23185, CAD: 9900  }, `Pillar Sponsor of the Month - A monthly sponsorship of all Torat Imecha programming`],
      [{ USD: 5000, ILS: 16100, CAD: 6874  }, `One day OU Women's Initiative program at the OU Headquarters`],
      [{ USD: 2500, ILS: 8400, CAD: 3430  }, `Torat Imecha Parsha - One Sefer Sponsorship`],
      [{ USD: 2500, ILS: 8400, CAD: 3430  }, `Ideas and Inspiration programming - One series`],
      [{ USD: 1800, ILS: 6048, CAD: 2469  }, `Torat Imecha Halacha - One Month Sponsorship`],
      [{ USD: 1800, ILS: 6048, CAD: 2469  }, `Pillar Sponsor of the Week - A weekly sponsorship of all Torat Imecha programming`],
      [{ USD: 1000, ILS: 3360, CAD: 1372  }, `Torat Imecha Nach Yomi Audio Shiur - One Week Sponsorship`],
      [{ USD: 1000, ILS: 3360, CAD: 1372  }, `Torat Imecha Parsha - One Month sponsorship`],
      [{ USD: 1000, ILS: 3360, CAD: 1372  }, `Torat Imecha Haftorah - One Month sponsorship`],
      [{ USD: 600, ILS: 1932, CAD: 824  }, `Torat Imecha Halacha - One week sponsorship`],
      [{ USD: 500, ILS: 1680, CAD: 686  }, `Rosh Chodesh Lunch N' Learn Video - Monthly Sponsorship`],
      [{ USD: 250, ILS: 840, CAD: 343  }, `Torat Imecha Nach Yomi Audio Shiur - One Day Sponsorship`],
      [{ USD: 250, ILS: 840, CAD: 343  }, `Torat Imecha Parsha - Weekly Sponsorship`],
      [{ USD: 250, ILS: 840, CAD: 343  }, `Torat Imecha Haftorah Audio Shiur - Weekly Sponsorship`],
      [{ USD: 100, ILS: 336, CAD: 137  }, `Torat Imecha Halacha - One Day Sponsorship`],
    ],
    silverPopContactLists: [8821611],
    customValidations (form) {
      return form.extend({
        custom: z.object({
          dedication_name: z.string().nullable().optional(),
          ti_nachyomi_day: z.string().nullable().optional(),
          ti_nachyomi_start_week: z.string().nullable().optional(),
          ti_nachyomi_month: z.string().nullable().optional(),
          ti_nachyomi_year: z.string().nullable().optional(),
          notes: z.string().nullable().optional(),
        })
      })
    }
  },
  'ouwomen-siyum-stream26': {
    ...womensSiyumBase,
    template: 'ouwomen-siyum-stream',
    subCampaign:'Siyum/Journal Donations',
    emailTemplate: 'ouwomen-siyum-stream',
    message: "Thank you so much for your generous support of Torat Imecha Nach Yomi.",
    adminSubject: 'OU Women Siyum Donation',
    userSubject: 'OU Women Siyum Donation',
    amounts: [
      [10000, `Siyum gift, platinum page in the global virtual journal, dedicate a sefer of Nach Yomi`],
      [5000, `Siyum gift, diamond page in the global virtual journal, dedicate a week of Nach Yomi`],
      [3600, `Siyum gift, gold page in the global virtual journal, dedicate 4 perakim of Nach Yomi`],
      [2500, `Siyum gift, silver page in the global virtual journal, dedicate 3 perakim of Nach Yomi`],
      [1800, `Siyum gift, bronze page in the global virtual journal, dedicate 2 perakim of Nach Yomi`],
      [1000, `Siyum gift, full page in the global virtual journal, dedicate one perek of Nach Yomi`],
      [500, `Siyum gift, 1/2 page in the global virtual journal, dedicate one perek of Nach Yomi`],
      [360, `Siyum gift, 1/4 page in the global virtual journal`],
      [250, `1/8 page in the global virtual journal`],
      [100, `Listing in the global virtual journal`],
    ],
    customValidations: form => form // overwrite the custom validations from the base
  },
  'ifs-general': {
    emailTemplate: 'ifs-general',
    title: 'Israel Free Spirit - General Donation',
    adminEmails: ['ytamari@ouisrael.org', 'rabbidave@ou.org'],
    adminFromEmail: 'Israel Free Spirit <info@israelfreespirit.com>',
    adminSubject: 'Israel Free Spirit General Donation',
    userFromEmail: 'Israel Free Spirit <info@israelfreespirit.com>',
    userSubject: 'Thank you for Donation!',
    amounts: [
      1800,
      1000,
      500,
      360,
      180,
      72,
      54,
      36,
    ],
    campaign: 'General Donation',
    department: 'IsraelFreeSpirit',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-25285684-1' : 'UA-25285684-2',
  },
  '18forty-general': {
    emailTemplate: '18forty-general',
    title: '18forty - General Donation',
    adminEmails: ['info@18forty.org', 'dbashevkin@gmail.com', 'goldmani@ou.org'],
    adminFromEmail: '18forty <info@18forty.org>',
    adminSubject: '18forty General Donation',
    userFromEmail: '18forty <info@18forty.org>',
    userSubject: 'Thank you for Donation!',
    message: 'Thank you for the Donation!',
    amounts: [
      18.40,
      184,
      1840
    ],
    campaign: '18Forty Donation',
    department: 'JewishMediaAndPublications',
    gaID: '',
  },
  'talmuddaily': {
    emailTemplate: 'talmuddaily',
    title: 'Talmud Daily - General Donation',
    adminEmails: ['talmuddaily@18forty.org', 'dbash@18forty.org', 'dbashevkin@gmail.com', 'gil.student@ou.org', 'david@dryve.co', 'goldmani@ou.org', 'sara@dryve.co'],
    adminFromEmail: 'Talmud Daily <talmuddaily@18forty.org>',
    adminSubject: 'Talmud Daily General Donation',
    userFromEmail: 'Talmud Daily <talmuddaily@18forty.org>',
    userSubject: 'Thank You for Supporting Talmud Daily!',
    message: 'Thank You for Supporting Talmud Daily!<br><br>Your generosity is helping make the Talmud accessible to Jews everywhere. Because of your support, Talmud Daily can remain free and available to anyone who wants to learn—without paywalls or barriers. If you included a dedication with your gift, we\'ll make sure it is recorded as part of your sponsorship. We\'re deeply grateful to have you as a partner in bringing more Torah learning into the world.',
    amounts: [
      36,
      180,
      360,
      1000
    ],
    campaign: '18Forty Donation',
    department: 'JewishMediaAndPublications',
    subCampaign: 'Talmud Daily Sponsorship',
    gaID: '',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          version: z.string().nullable().optional(),
          sponsorshipType: z.string().nullable().optional(),
          sponsorshipAmount: z.string().nullable().optional(),
          date: z.string().nullable().optional(),
          seriesType: z.string().nullable().optional(),
          dedicationType: z.string().nullable().optional(),
          dedication: z.string().nullable().optional(),
          listingName: z.string().nullable().optional(),
          anonymous: z.string().nullable().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    },
  },
  '18forty-upcoming': {
    emailTemplate: '18forty-general',
    title: '18forty - General Donation',
    adminEmails: ['info@18forty.org', 'dbashevkin@gmail.com', 'goldmani@ou.org'],
    adminFromEmail: '18forty <info@18forty.org>',
    thankYouTemplate: '18forty',
    adminSubject: '18forty General Donation',
    userFromEmail: '18forty <info@18forty.org>',
    userSubject: 'Thank you for Donation!',
    message: 'Thank you for the Donation!',
        amounts: [
      [{ USD: 360, ILS: 1200},  `Dedicate a Day for`],
      [{ USD: 1840, ILS: 6000 }, `Sponsor a Week for`],
      [{ USD: 5000, ILS: 16000 }, `Power a Month for`],
      [{ USD: 18.40, ILS: 16000 }, ``],
      [{ USD: 184, ILS: 16000 }, ``],
      [{ USD: 1840, ILS: 16000 }, ``],
    ],
    campaign: '18Forty Donation',
    department: 'JewishMediaAndPublications',
    gaID: '',
  },
  '18forty-innovations': {
    emailTemplate: '18forty-innovations',
    title: '18forty - Innovations Donation',
    adminEmails: ['info@18forty.org', 'dbashevkin@gmail.com', 'goldmani@ou.org'],
    adminFromEmail: '18forty <info@18forty.org>',
    adminSubject: '18forty Innovations Donation',
    userFromEmail: '18forty <info@18forty.org>',
    userSubject: 'Thank you for Donation!',
    message: 'Thank you for the Donation!',
    amounts: [
      1840
    ],
    campaign: '18Forty Website',
    department: 'JewishMediaAndPublications',
    gaID: '',
  },
  '18forty-book': {
    emailTemplate: '18forty-book',
    title: 'New Book Journey Registration',
    adminEmails: ['info@18forty.org', 'dbashevkin@gmail.com', 'goldmani@ou.org'],
    adminFromEmail: '18forty <info@18forty.org>',
    adminSubject: 'New Book Journey Registration',
    userFromEmail: '18forty <info@18forty.org>',
    userSubject: 'New Book Journey Registration - Thank you for Purchase!',
    message: 'Thank you for the Purchase!',
    amounts: [
      55.20
    ],
    campaign: '18forty Book Club',
    department: 'JewishMediaAndPublications',
    gaID: '',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          version: z.string().nullable().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    },
  },
  '18forty-book-second': {
    emailTemplate: '18forty-book-second',
    title: 'New Book Journey exploring antisemitism with Malka Simkovich Registration',
    adminEmails: ['info@18forty.org', 'dbashevkin@gmail.com', 'goldmani@ou.org'],
    adminFromEmail: '18forty <info@18forty.org>',
    adminSubject: 'New Book Journey exploring antisemitism with Malka Simkovich Registration',
    userFromEmail: '18forty <info@18forty.org>',
    userSubject: 'New Book Journey exploring antisemitism with Malka Simkovich Registration - Thank you for Purchase!',
    message: 'Thank you for the Purchase!',
    amounts: [
      55.20
    ],
    campaign: '18forty Book Club',
    department: 'JewishMediaAndPublications',
    gaID: '',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          version: z.string().nullable().optional(),
          Notes: z.string().nullable().optional(),
        })
      })
    },
  },
  '18forty-book-third': {
    emailTemplate: '18forty-book-third',
    title: 'New Book Journey - Shlomo Brody',
    adminEmails: ['info@18forty.org', 'dbashevkin@gmail.com', 'goldmani@ou.org'],
    adminFromEmail: '18forty <info@18forty.org>',
    adminSubject: 'New Book Journey - Shlomo Brody',
    userFromEmail: '18forty <info@18forty.org>',
    userSubject: 'New Book Journey - Shlomo Brody - Thank you for Purchase!',
    message: 'Thank you for the Purchase!',
    amounts: [
      55.20
    ],
    campaign: '18forty Book Club',
    department: 'JewishMediaAndPublications',
    gaID: '',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          version: z.string().nullable().optional(),
          Notes: z.string().nullable().optional(),
          FaithAndHistory: zBool().nullable().optional(),
          EthicsOfOurFighters: zBool().nullable().optional(),
          ValueInHalakha: zBool().nullable().optional(),
        })
      })
    },
  },
  'torahyouth': {
    emailTemplate: 'torahyouth',
    title: 'torahyouth - General Donation',
    adminEmails: ['atsoffice@ncsy.org',  'goldmani@ou.org', 'neumanj@ou.org',  'lauren@ncsy.org', 'lernerj@ncsy.org', 'mossb@ncsy.org'],
    adminFromEmail: 'torahyouth <atsoffice@ncsy.org>',
    adminSubject: 'torahyouth General Donation - TYA',
    userFromEmail: 'torahyouth <lernerj@ncsy.org>',
    userSubject: 'Thank you for Donation!',
    message: 'Thank you for the Donation!',
    amounts: [
      18,
      36,
      100,
      250,
      500
    ],
    campaign: 'TYA',
    department: 'NCSY_AtlanticSeaboard',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-18' : 'UA-3009720-18',
  },
  'bgcubed': {
    emailTemplate: 'bgcubed',
    title: 'bgcubed - General Donation',
    adminEmails: ['sovaj@bgcubed.com',  'goldmani@ou.org', 'neumanj@ou.org', 'lernerj@ncsy.org', 'mossb@ncsy.org', 'lauren@ncsy.org'],
    adminFromEmail: 'bgcubed <sovaj@bgcubed.com>',
    adminSubject: 'bgcubed General Donation',
    userFromEmail: 'bgcubed <sovaj@bgcubed.com>',
    userSubject: 'Thank you for Donation!',
    message: 'Thank you for the Donation!',
    amounts: [
      18,
      36,
      100,
      250,
      500
    ],
    campaign: 'bgcubed',
    department: 'NCSY_AtlanticSeaboard',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-18' : 'UA-3009720-18',
  },
  'BMB': {
    emailTemplate: 'bgcubed',
    title: 'BMB - General Donation',
    adminEmails: ['goldmani@ou.org', 'elana.soffer@ou.org', 'aviva.benovitz@ou.org', 'ginsbergy@ncsy.org'],
    adminFromEmail: 'BMB <info@ncsy.org>',
    adminSubject: 'BMB General Donation',
    userFromEmail: 'BMB <info@ncsy.org>',
    userSubject: 'Thank you for Donation!',
    message: 'Thank you for the Donation!',
    amounts: [
      18,
      36,
      100,
      250,
      500
    ],
    campaign: 'BMB',
    department: 'NCSY_Israel',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-18' : 'UA-3009720-18',
  },
  'alldaf-lublin-siyum': {
    adminEmails: [],
    adminFromEmail: '',
    title: '',
    userFromEmail: ''
  },
  'edc': {
    adminEmails: [],
    department: 'OU',
    campaign: 'EDC',
    adminFromEmail: '',
    title: '',
    message: 'Amount entered successfully.',
    userFromEmail: ''
  },
  'upreaching-form': {
    adminEmails: [],
    adminFromEmail: '',
    title: '',
    userFromEmail: '',
    message: 'Card processed successfully'
  },
  'hiraise': {
    adminEmails: [],
    adminFromEmail: '',
    title: '',
    userFromEmail: '',
    message: 'Card processed successfully'
  },
  'ncsy-ny-tjjmoms': {
    emailTemplate: 'ncsy-ny-tjjmoms',
    title: 'NCSY Donation Form',
    department: 'NCSY_Tristate',
    campaign: 'TJJ for Moms',
    message: 'Thank you for joining the Chai Society and supporting TJJ for Moms. We appreciate you!',
    adminEmails: ['lernerj@ncsy.org','Tristate@ncsy.org', 'marchuck@ncsy.org'],
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'NCSY payment',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support',
    amounts: [
      36,
      54,
      72
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
  'ncsy-ny-tjjmoms-region': {
    emailTemplate: 'ncsy-ny-tjjmoms',
    template: 'ncsy-region-template',
    title: 'NCSY Donation Form',
    department: 'NCSY_Tristate',
    campaign: 'TJJ for Moms',
    message: 'Thank you for joining the Chai Society and supporting TJJ for Moms. We appreciate you!',
    adminEmails: ['lernerj@ncsy.org','Tristate@ncsy.org', 'marchuck@ncsy.org'],
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'NCSY payment',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support',
    amounts: [
      36,
      54,
      72
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
  'ncsy-ny-tjjmoms-eoy': {
    emailTemplate: 'ncsy-ny-tjjmoms',
    title: 'NCSY Donation Form',
    department: 'NCSY_Tristate',
    campaign: 'Gap Year Campaign',
    message: 'Thank you for joining the Chai Society and supporting TJJ for Moms. We appreciate you!',
    adminEmails: ['lernerj@ncsy.org','Tristate@ncsy.org', 'marchuck@ncsy.org'],
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'NCSY payment',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support',
    amounts: [
      500,
      1000,
      1800,
      2500,
      3600,
      5000,
      7500,
      10000
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
  'ncsy-ny-tjjmoms-eoy-region': {
    emailTemplate: 'ncsy-ny-tjjmoms',
    template: 'ncsy-region-template',
    title: 'NCSY Donation Form',
    department: 'NCSY_Tristate',
    campaign: 'Gap Year Campaign',
    message: 'Thank you for joining the Chai Society and supporting TJJ for Moms. We appreciate you!',
    adminEmails: ['lernerj@ncsy.org','Tristate@ncsy.org', 'marchuck@ncsy.org'],
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'NCSY payment',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support',
    amounts: [
      500,
      1000,
      1800,
      2500,
      3600,
      5000,
      7500,
      10000
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
  'ncsy-nj-simchateinu': {
    emailTemplate: 'ncsy',
    title: 'Simchateinu',
    department: 'NCSY_Tristate',
    campaign: 'Simchateinu Lulav',
    message: 'Thank you!',
    adminEmails: ['info@ncsy.org', 'strausss@ncsy.org', 'goldmani@ou.org', 'wielgusa@ncsy.org', 'marchuck@ncsy.org'],
    adminFromEmail: 'NJ NCSY Simchateinu <info@ncsy.org>',
    adminSubject: 'NCSY Simchateinu payment',
    userFromEmail: 'NJ NCSY Simchateinu <info@ncsy.org>',
    userSubject: 'Thank you for your support',
    amounts: [],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          ticketsQnt: z.string().nullable().optional(),
          sendaddress: z.string().nullable().optional(),
          namesAddress: z.string().nullable().optional(),
        })
      })
    }
  },
  'ncsy-ats-simchateinu': {
    emailTemplate: 'ncsy-ats-simchateinu',
    title: 'Simchateinu',
    department: 'NCSY_AtlanticSeaboard',
    campaign: 'Simchateinu Lulav',
    message: '<h1>Atlantic Seaboard NCSY Simchateinu Campaign – Confirmation</h1><br><div class="entry-content"> <p>Thank you so much for your support in providing public school teens with a lulav and etrog! If you have any questions, please reach out to Rabbi Benyamin Moss, Baltimore District Director, at mossb@ncsy.org.</p></div>',
    adminEmails: ['info@ncsy.org', 'mossb@ncsy.org', 'goldmani@ou.org', 'lauren@ncsy.org', 'lernerj@ncsy.org', 'sperom@ncsy.org'],
    adminFromEmail: 'Atlantic seaboard NCSY <atsoffice@ncsy.org>',
    adminSubject: 'NCSY ATS Simchateinu payment',
    userFromEmail: 'Atlantic Seaboard NCSY & JSU <atsoffice@ncsy.org>',
    userSubject: 'Thank you for supporting the Simchateinu Campaign!',
    amounts: [],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          ticketsQnt: z.string().nullable().optional(),
        })
      })
    },
  },
  'ncsy-southern-ype': {
    emailTemplate: 'ncsy-southern-ype',
    title: 'Young Jewish Professional Event Sign Up Form',
    department: 'NCSY_Southern',
    campaign: 'Young Professionals Event',
    message: 'Thank you for signing up for Young Jewish Professional Event',
    adminEmails: ['southern@ncsy.org', 'kaminetskym@ncsy.org', 'goldmani@ou.org', 'levyr@ncsy.org', 'SHess@ncsy.org', 'WassermanM@ncsy.org', 'Romanoffj@ncsy.org'],
    adminFromEmail: 'Southern NCSY <southern@ncsy.org>',
    adminSubject: 'Young Jewish Professional Event Sign Up Form payment',
    userFromEmail: 'Southern NCSY <southern@ncsy.org>',
    userSubject: 'Thank you for signing up for Young Jewish Professional Event',
    amounts: [],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          ticketsQnt: z.string().nullable().optional(),
          comments: z.string().nullable().optional(),
        })
      })
    },
  },
  'ncsy-ny-summer-scholarships': {
    emailTemplate: 'ncsy',
    title: 'NCSY Donation Form',
    department: 'NCSY_Tristate',
    campaign: 'Summer Scholarship',
    message: 'Thank you for your support of NCSY. We appreciate you!',
    adminEmails: ['lernerj@ncsy.org','Tristate@ncsy.org', 'marchuck@ncsy.org', 'beth.nussbaum@ncsy.org'],
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'NCSY payment',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support',
    amounts: [
      10000,
      5000,
      3600,
      1800,
      360
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
  'ncsy-ny': {
    emailTemplate: 'ncsy-nynj',
    title: 'NCSY NY Donation Form',
    department: 'NCSY_Tristate',
    campaign: 'General Donation',
    message: 'Thank you for your commitment to supporting Tri-State NCSY!<br><br>This year, generous supporters like you will empower thousands of teens to engage with NYNJ NCSY in various settings, including their public schools, local coffee shops, and community events. Your investment enables us to offer a wide range of transformative programs, including regional conventions, social action events, summer trips, and more. We are sincerely grateful for your contribution!<br><br>Your support of Tri-State NCSY not only sustains our impact in communities across New York, New Jersey, and Connecticut, but also fuels our ambitious expansion efforts to reach even more Jewish teens. Thank you for partnering with us to shape the future of the Jewish community!',
    adminEmails: ['lernerj@ncsy.org','Tristate@ncsy.org', 'Lebovitzr@ncsy.org', 'Shari.Glassman@ncsy.org', 'marchuck@ncsy.org', 'beth.nussbaum@ncsy.org'],
    adminFromEmail: 'NCSY <nyinfo@ncsy.org>',
    adminSubject: 'NCSY NY payment',
    userFromEmail: 'NCSY New York NCSY <nyinfo@ncsy.org>',
    userSubject: 'Thank you from Tri-State NCSY',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      720,
      360,
      180
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
  'ncsy-ny-region': {
    template: 'ncsy-region-template',
    emailTemplate: 'ncsy-nynj',
    title: 'NCSY NY Donation Form',
    department: 'NCSY_Tristate',
    campaign: 'General Donation',
    message: 'Thank you for your commitment to supporting Tri-State NCSY!<br><br>This year, generous supporters like you will empower thousands of teens to engage with NYNJ NCSY in various settings, including their public schools, local coffee shops, and community events. Your investment enables us to offer a wide range of transformative programs, including regional conventions, social action events, summer trips, and more. We are sincerely grateful for your contribution!<br><br>Your support of Tri-State NCSY not only sustains our impact in communities across New York, New Jersey, and Connecticut, but also fuels our ambitious expansion efforts to reach even more Jewish teens. Thank you for partnering with us to shape the future of the Jewish community!',
    adminEmails: ['lernerj@ncsy.org','Tristate@ncsy.org', 'Lebovitzr@ncsy.org', 'Shari.Glassman@ncsy.org', 'marchuck@ncsy.org', 'beth.nussbaum@ncsy.org'],
    adminFromEmail: 'NCSY <nyinfo@ncsy.org>',
    adminSubject: 'NCSY NY payment',
    userFromEmail: 'NCSY New York NCSY <nyinfo@ncsy.org>',
    userSubject: 'Thank you from Tri-State NCSY',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      720,
      360,
      180
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          Notes: z.string().nullable().optional()
        })
      })
    }
  },
    'ncsy-tristate-rh': {
    template: 'ncsy-standard',
    emailTemplate: 'ncsy-tristate-rh',
    title: 'NCSY Tristate Rosh Hashana Campaign Form',
    department: 'NCSY_Tristate',
    campaign: 'General Donation',
    message: 'Thank you for your generous gift to NCSY Tristate\'s Rosh Hashanah campaign. Your support helps strengthen the Jewish future, one teen at a time.<br><br> Wishing you and your family a happy, healthy, and sweet New Year. <br><br>Shanah Tovah, NCSY Tristate Staff',
    adminEmails: ['lernerj@ncsy.org','Tristate@ncsy.org', 'israel.heger@ncsy.org', 'beth.nussbaum@ncsy.org'],
    adminFromEmail: 'NCSY <nyinfo@ncsy.org>',
    adminSubject: 'NCSY Tristate Rosh Hashana Campaign',
    userFromEmail: 'NCSY Tristate <nyinfo@ncsy.org>',
    userSubject: 'Thank you from Tri-State NCSY',
    amounts: [
      1500,
      1000,
      720,
      500,
      360,
      180,
      100,
      72,
      50,
      36,
      18
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
    'ncsy-ny-4g': {
    template: 'ncsy-modal',
    emailTemplate: 'ncsy-ny-4g',
    thankYouTemplate: 'ncsy',
    title: 'NCSY NY Donation Form',
    department: 'NCSY_Tristate',
    campaign: '4G',
    message: 'Thank you for supporting 4G.<br><br>Your gift helps create the spaces where our girls come to learn, find connection, and grow through Torah. Across the Tristate area, teens are stepping into rooms filled with warmth, meaning, and genuine relationships because of supporters like you.<br><br>Thank you for investing in their growth and for believing in the work we are building together.',
    adminEmails: ['Tristate@ncsy.org', 'Rabinovitchs@ncsy.org', 'greenbergsh@ncsy.org', 'marchuck@ncsy.org', 'beth.nussbaum@ncsy.org'],
    adminFromEmail: 'NCSY <nyinfo@ncsy.org>',
    adminSubject: 'NCSY NY payment',
    userFromEmail: 'NCSY New York NCSY <nyinfo@ncsy.org>',
    userSubject: 'Thank you from Tri-State NCSY',
    amounts: [
      10000,
      7500,
      5000,
      3600,
      1800,
      720,
      360,
      180
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
  'ncsy-middos': {
    emailTemplate: 'ncsy',
    title: 'NCSY Middos Form',
    department: 'NCSY',
    campaign: 'TMI',
    message: 'Thank you for your support of NCSY. We appreciate you!',
    adminEmails: ['info@ncsy.org', 'szegedia@ncsy.org',  'goldmani@ou.org', 'lernerj@ncsy.org'],
    adminFromEmail: 'NCSY Middos <info@ncsy.org>',
    adminSubject: 'NCSY Middos payment',
    userFromEmail: 'NCSY Middos <info@ncsy.org>',
    userSubject: 'Thank you for your support',
    amounts: [
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
  'ncsy-columbus-campaign': {
    emailTemplate: 'ncsy-columbus-campaign',
    title: 'NCSY Columbus Campaign',
    department: 'NCSY_Tristate',
    campaign: 'Columbus Campaign',
    message: 'Thank you for your support. We appreciate you!',
    adminEmails: ['lernerj@ncsy.org','Tristate@ncsy.org', 'marchuck@ncsy.org', 'beth.nussbaum@ncsy.org'],
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'NCSY payment',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support',
    amounts: [
      2500,
      5000,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
  'ncsy-ny-greatneck': {
    emailTemplate: 'ncsy-ny-greatneck',
    title: 'NCSY Donation Form',
    department: 'NCSY_Tristate',
    campaign: 'Great Neck',
    message: 'Thank you for donating to NCSY',
    adminEmails: ['lernerj@ncsy.org','Tristate@ncsy.org', 'marchuck@ncsy.org', 'beth.nussbaum@ncsy.org'],
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'NCSY payment',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support',
    amounts: [
      26,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
  'ncsy-ny-greatneck-society': {
    emailTemplate: 'ncsy-ny-greatneck',
    title: 'NCSY Donation Form',
    department: 'NCSY_Tristate',
    campaign: 'Great Neck',
    adminFromEmail: 'NCSY <info@ncsy.org>',
    adminSubject: 'NCSY payment - ncsy ny greatneck society',
    userFromEmail: 'NCSY <info@ncsy.org>',
    userSubject: 'Thank you for your support',
    message: 'Thank you for donating to NCSY',
    adminEmails: ['lernerj@ncsy.org','Tristate@ncsy.org', 'marchuck@ncsy.org', 'beth.nussbaum@ncsy.org'],
    amounts: [
      18,
      26,
      36,
      54,
      72,
      101,
    ],
    gaID: process.env.NODE_ENV == 'production' ? 'UA-3009720-1' : 'UA-3009720-2'
  },
  'teach-miami-conf': {
    emailTemplate: 'teach-miami-conf',
    title: 'Teach Coalition Visionary Retreat',
    subCampaign: '',
    thankYouTemplate: 'teach',
    message: 'Thank you for signing up for the Teach Coalition Visionary Retreat',
    adminEmails: ['benshabats@teachcoalition.org', 'dalezmana@teachcoalition.org', 'altfields@teachcoalition.org', 'Caleb.Fischer@teachcoalition.org'],
    adminFromEmail: 'Teach Coalition Visionary Retreat <teachnys@teachcoalition.org>',
    adminSubject: 'A signup for the Teach Coalition Visionary Retreat',
    userFromEmail: 'Teach Coalition <teachnys@teachcoalition.org>',
    userSubject: 'Teach NYS’s Westchester Summer BBQ',
    gaID: process.env.NODE_ENV == 'production' ? 'UA-71428538-5' : 'UA-71428538-5',
    customValidations (form) {
      return form.extend({
        custom: z.object({
          spouseName: z.string().nullable().optional(),
          stayingAtHotel: zBool(),
          schoolName: z.string().nullable().optional(),
        })
      })
    }
  }
}
